export interface PackageImage {
  id: number;
  src: string;
  alt: string;
}

export interface TripInfo {
  accommodation: {
    text: string;
    icon: string;
  };
  arrivalCity: {
    text: string;
    icon: string;
  };
  bestSeason: {
    text: string;
    icon: string;
  };
  guide: {
    text: string;
    icon: string;
  };
  maximumAltitude: {
    text: string;
    icon: string;
  };
  meals?: {
    text: string;
    icon: string;
  };
  transportation: {
    text: string;
    icon: string;
  };
  pickupPoint?: {
    text: string;
    icon: string;
  };
}

export interface FAQ {
  question: string;
  answer: string;
}

export interface Stay {
  id: number;
  images: PackageImage[];
  amenities: string[];
}

export interface Package {
  id: number;
  slug: string;
  title: string;
  shortDescription: string;
  state: string;
  price: number;
  duration: string;
  activityTags: string[];
  images: PackageImage[];
  tripInfo?: TripInfo;
  overview?: {
    title: string;
    description: string;
    highlights?: Array<{
      id: number;
      highlight: string;
    }>;
    activities?: Array<{
      title: string;
      description: string;
      emoji: string;
    }>;
  };
  itinerary?: Array<{
    day: number;
    title: string;
    description: string;
    activities?: string[];
  }>;
  cost?: {
    includes: string[];
    excludes: string[];
    notes: string[];
  };
  stays?: Stay[];
  faqs?: FAQ[];
  mapUrl: string;
}

const packages: Package[] = [
  {
    id: 1,
    slug: "water-sports-tehri-dam",
    title: "Water Sports Tehri Dam",
    state: "Uttarakhand",
    shortDescription:
      "Embark on an unforgettable journey to Tehri Lake, a stunning destination nestled in the picturesque hills of Uttarakhand. Over the course of days and 1 night, immerse yourself in a variety of thrilling water sports and enjoy the serene beauty of this artificial lake.",
    price: 1200,
    duration: "2 Days & 1 Night",
    activityTags: ["river-rafting", "water-sports"],
    images: [
      {
        id: 1,
        src: "/images/packages/tehri-dam/tehri-dam.webp",
        alt: "Group of people white water rafting at Tehri Dam",
      },
      {
        id: 2,
        src: "/images/packages/tehri-dam/water-sports.jpg",
        alt: "Jet skiing on Tehri Lake",
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Subjected To Request With Extra Charge",
        icon: "building",
      },
      arrivalCity: {
        text: "Tehri Lake",
        icon: "city",
      },
      bestSeason: {
        text: "All Around The Year.",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "Between 1550 to 1950 mts (5086-6400 feet) above Sea level.",
        icon: "mountain",
      },
      transportation: {
        text: "Bus, Taxi, or other mode of transportation can be arranged on request for extra charges.( Package Price Does Not Include Transportation.)",
        icon: "bus",
      },
    },
    overview: {
      title: "Water Sports Overview",
      description:
        "Tehri Lake, nestled in the scenic hills, offers an array of exhilarating water sports that cater to all adventure enthusiasts. Whether you're seeking a thrilling experience or a leisurely exploration, Tehri Lake has something for everyone:",
      activities: [
        {
          title: "Jet skiing",
          description:
            "Feel the rush as you ride a high-speed water scooter across the expansive lake, exploring its hidden corners.",
          emoji: "🏄‍♂️",
        },
        {
          title: "Kayaking",
          description:
            "Navigate the peaceful waters at your own pace, perfect for both beginners and experienced paddlers.",
          emoji: "🛶",
        },
        {
          title: "Banana Boat Rides",
          description:
            "Enjoy the thrill of balancing on this inflatable boat as it zips across the lake, a perfect activity for groups.",
          emoji: "🍌",
        },
        {
          title: "Rafting",
          description:
            "Experience the adrenaline rush of navigating through the flowing waters with a team, supervised by professional guides.",
          emoji: "🚣‍♀️",
        },
        {
          title: "Swimming",
          description:
            "Take a refreshing dip in designated safe zones under the watchful eyes of lifeguards.",
          emoji: "🏊‍♂️",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival & Adventure Begins",
        description:
          "Arrive at Tehri Lake in the morning. After a safety briefing and equipment check, dive into your chosen water sports activities.",
        activities: [
          "Arrival at Tehri Lake",
          "Welcome refreshments",
          "Safety briefing and equipment distribution",
          "Start with beginner-friendly activities like swimming and banana boat rides",
          "Lunch at lakeside cafe",
          "Afternoon session of kayaking and jet skiing",
          "Evening relaxation with lakeside views",
          "Dinner and overnight stay (if accommodation booked)",
        ],
      },
      {
        day: 2,
        title: "Adventure Continues & Departure",

        description:
          "Continue your water adventure with remaining activities and finally depart with memories to cherish.",
        activities: [
          "Early morning lake view and breakfast",
          "Morning session of rafting",
          "Advanced jet skiing for interested participants",
          "Lunch break",
          "Leisure time for swimming or repeating favorite activities",
          "Souvenir shopping at local stalls",
          "Departure with certificates of participation",
        ],
      },
    ],
    cost: {
      includes: [
        "All water sports activities mentioned in the itinerary",
        "Safety equipment (life jackets, helmets, etc.)",
        "Professional instructors",
        "First aid facilities",
        "Activity completion certificates",
      ],
      excludes: [
        "Transportation to and from Tehri Lake",
        "Accommodation (can be arranged at extra cost)",
        "Meals and beverages",
        "Personal expenses",
        "Insurance",
        "Anything not mentioned in inclusions",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    faqs: [
      {
        question: "Is prior experience required for water sports?",
        answer:
          "No prior experience is required for most activities. Our professional instructors will guide you through each activity and ensure your safety.",
      },
      {
        question: "What should I bring for the water activities?",
        answer:
          "We recommend bringing swimming attire, towel, change of clothes, sunscreen, sunglasses, and any personal medications you might need. All safety equipment will be provided.",
      },
      {
        question: "Is it safe for non-swimmers?",
        answer:
          "Yes, all participants are provided with life jackets. However, please inform the instructors if you cannot swim so they can provide extra attention.",
      },
      {
        question: "Can children participate in the activities?",
        answer:
          "Yes, children above 8 years can participate in most activities under adult supervision. There are specific restrictions for some activities like jet skiing where age limits apply.",
      },
      {
        question: "What happens if the weather is unfavorable?",
        answer:
          "In case of unfavorable weather conditions, we may reschedule certain activities or offer alternative adventures. Safety is our primary concern.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3456.789012345678!2d78.12345678901234!3d30.12345678901234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzDCsDA3JzI0LjQiTiA3OMKwMDcnMjQuNSJF!5e0!3m2!1sen!2sin!4v1601234567890!5m2!1sen!2sin",
  },
  {
    id: 2,
    slug: "nag-tibba-trek",
    title: "Nag Tibba Trek",
    state: "Uttarakhand",
    shortDescription:
      "Embark on the Nag Tibba Trek with YK Travels, a perfect getaway for both novice and seasoned trekkers. Located in the Garhwal region of Uttarakhand, this trek offers stunning panoramic views of the Himalayas, lush forests, and serene landscapes. At 9,915 feet, Nag Tibba / Serpent's Peak, is the highest peak in the lower Himalayas of Uttarakhand.",
    duration: "2 Days & 1 Night",
    price: 5499,
    activityTags: ["camping", "trekking", "hiking", "peak climbing"],
    overview: {
      title: "Trek Overview",
      description:
        "The Nag Tibba Trek, curated by YK Travels, offers an enchanting journey through the serene landscapes of the Garhwal region in Uttarakhand. Rising to an altitude of 9,915 feet, Nag Tibba, also known as the “Serpent’s Peak,” is the highest peak in the lower Himalayas of Uttarakhand. This trek is a perfect blend of natural beauty, cultural immersion, and adventure, making it an ideal choice for both novice and seasoned trekkers.",
      activities: [
        {
          title: "Breathtaking Scenery",
          description:
            "Experience the diverse beauty of the Himalayas with dense oak and rhododendron forests, expansive meadows, and panoramic mountain vistas. Enjoy stunning views of peaks like Bandarpoonch, Kedarnath, Gangotri, and the Swargarohini range.",
          emoji: "🏞️",
        },
        {
          title: "Cultural Immersion",
          description:
            "Trek through quaint villages and interact with the warm locals of the Garhwal region. Immerse yourself in the rich culture and traditions, adding a unique cultural dimension to your adventure.",
          emoji: "🤝",
        },
        {
          title: "Accessible Adventure",
          description:
            "Perfect for beginners and experienced trekkers alike, this trek offers a moderate ascent suitable for various fitness levels, ensuring a manageable and rewarding experience.",
          emoji: "🥾",
        },
        {
          title: "Rich Biodiversity",
          description:
            "Explore varied landscapes filled with vibrant wildflowers, diverse bird species, and potential wildlife sightings, showcasing the rich flora and fauna of the region.",
          emoji: "🌿",
        },
      ],
    },
    tripInfo: {
      accommodation: {
        text: "Alpine tents or Decathlon tents (2-3 sharing base)",
        icon: "building",
      },
      arrivalCity: {
        text: "Pantwari",
        icon: "city",
      },
      bestSeason: {
        text: "All Around The Year.",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "9,915 feet (3,022 meters)",
        icon: "mountain",
      },

      transportation: {
        text: "Bus, Taxi",
        icon: "bus",
      },
    },

    cost: {
      includes: [
        "Alpine tents, Decathlon tent (2-3) with proper mattress, bedsheet and pillow and tent light.",
        "Veg.Meal (breakfast + lunch + eve. Snacks + dinner) included.",
        "Proper toilet with Bathroom.",
        "Trek lead or guide/cook/staff included.",
        "Proper sitting area with comfortable chair and bean bags.",
        "Game (Chess, Ludo, Carrom, String Hockey, Badminton, Football, Bat-Bowl)",
        "Power backup available for phones charging.",
        "Music system for party.",
        "Dental Kit.",
        "Bonfire included for winters.",
        "Camping Gears(torch, stick included)",
        "Hydration bag and Power Bank available additional on charges 50 and 100 each.",
        "Providing self camping gear for staying one night in summit at just 500 per couple.",
        "Body massager at @50 for 15 mint.",
        "Raincoat available @100 per head.",
      ],
      excludes: [
        "Personal trekking gear such as clothing, shoes, and personal toiletries.",
        "Snacks, beverages, and additional food items beyond the provided meals.",
        "Any personal medication or medical supplies.",
        "It is highly recommended to have personal travel insurance that covers trekking activities, medical emergencies, and evacuation.",
        "(Incase Pantwari Package) Travel costs to and from the starting point at Pantwari village are not included. You will need to arrange your transportation to and from Pantwari.",
        "Accommodation and meals in any city or town before the start of the trek and after the trek ends are not included.",
        "Any additional services such as laundry, phone calls, or room service if you stay in accommodations before or after the trek.",
        "Any meals and drinks not mentioned in the itinerary.",
        "Alcoholic beverages.",
        "Costs incurred in case of evacuation due to medical reasons  or other emergencies are not covered by the package.",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival and Trek to Base Camp",
        description:
          "Activities: Upon arriving at Pantwari, meet your trekking guide and group. Begin your trek from the village, gradually ascending through picturesque trails lined with lush greenery and captivating vistas. Reach the base camp by evening, where you’ll set up tents and enjoy a campfire under the starlit sky. Indulge in local cuisine and listen to stories of the region from your guide.",
      },
      {
        day: 2,
        title: "Summit Day",
        description:
          "Summit Ascent: Wake up early to a refreshing mountain morning and start your ascent to the Nag Tibba summit. The trail offers breathtaking views and diverse landscapes, from dense forests to open meadows. Summit Experience: Upon reaching the summit, relish the panoramic views of the snow-capped Himalayan peaks. Spend some time soaking in the magnificent scenery, taking photographs, and enjoying the sense of accomplishment. Descent: After savoring the summit experience, begin your descent back to the base camp. Enjoy a relaxed evening and a hearty meal at the camp.",
      },
      {
        day: 3,
        title: "Return Journey",
        description:
          "Return Trek: After breakfast, pack up and trek back to Pantwari village. Reflect on the memorable experience as you make your way down the trail. Departure: Arrive back in Pantwari and prepare for your return journey, carrying with you unforgettable memories and a deep appreciation for the beauty of the Himalayas.",
      },
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/nag-tibba/nag-tibba-1.jpg",
        alt: "Yk Travels Nag Tibba Trek",
      },
      {
        id: 2,
        src: "/images/packages/nag-tibba/nag-tibba2.jpg",
        alt: "Yk travesls nag tibba trek",
      },
      {
        id: 3,
        src: "/images/packages/nag-tibba/nag-tibba3.jpg",
        alt: "Yk travels nag tibba trek",
      },
      {
        id: 4,
        src: "/images/packages/nag-tibba/nag-tibba4.jpg",
        alt: "yk travels nag tibba trek",
      },
      {
        id: 5,
        src: "/images/packages/nag-tibba/nag-tibba5.jpg",
        alt: "yk travels nag tibba trek",
      },
    ],
    faqs: [
      {
        question: "What is the maximum altitude of the Nag Tibba Trek?",
        answer:
          "The Nag Tibba Trek reaches a maximum altitude of 9,915 feet (3,022 meters) above sea level.",
      },
      {
        question: "How difficult is the Nag Tibba Trek?",
        answer:
          "The Nag Tibba Trek is classified as an easy to moderate trek. It is suitable for beginners with a basic level of fitness as well as experienced trekkers looking for a short and refreshing adventure.",
      },
      {
        question: "What is the best time to do the Nag Tibba Trek?",
        answer:
          "The Nag Tibba Trek is accessible year-round, with each season offering a unique experience: \n Spring (March to April): Pleasant weather and blooming rhododendrons.  \nSummer (May to June): Clear skies and warm temperatures. \nMonsoon (July to September): Lush greenery, though trails can be slippery. \nAutumn (October to November): Crisp air and clear views of snow-capped peaks. \n Winter (December to February): Snowy landscapes, ideal for a winter trekking experience.",
      },
      {
        question: "How long does the Nag Tibba Trek take?",
        answer:
          "The Nag Tibba Trek typically takes 2-3 days to complete, making it an excellent option for a weekend getaway.",
      },
      {
        question: "What should I pack for the trek?",
        answer:
          "Essential items to pack include: \nComfortable trekking shoes \n Warm clothing (layers, thermal wear, jacket) \n Rain gear (if trekking in monsoon season) \n Sun protection (hat, sunglasses, sunscreen) \n Personal toiletries and first-aid kit \n Water bottle and snacks \n Trekking poles (optional but recommended) \n Backpack",
      },
      {
        question: "Are permits required for the Nag Tibba Trek?",
        answer:
          "Permits may be required for trekking in certain areas. Please check with YK Travels or local authorities to ensure you have the necessary permits before starting the trek.",
      },
      {
        question: "What kind of accommodation is provided during the trek?",
        answer:
          "Accommodation during the trek typically includes camping in tents. All the mentioned amenities in package such as sleeping bags and mats are provided all around good and fun adventure. In some cases, homestays or guesthouses in nearby villages may also be an option.",
      },
      {
        question: "Is there a provision for meals during the trek?",
        answer:
          "Yes, meals are provided during the trek. This usually includes breakfast, lunch, and dinner. Vegetarian options are available, and we can accommodate dietary restrictions if informed in advance.",
      },
      {
        question: "What about drinking water during the trek?",
        answer:
          "We provide safe drinking water during the trek. It’s advisable to carry a reusable water bottle that you can refill at designated points.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides flexible transportation options based on the package selection of the traveler: \n Delhi to Delhi: Transportation from Delhi to Pantwari and back. \n Dehradun to Dehradun: Transportation from Dehradun to Pantwari and back. \n Pantwari to Pantwari: For those who arrange their own transport to Pantwari, we start and end the trek at Pantwari.",
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, it is highly recommended to have travel insurance that covers trekking activities, medical emergencies, and evacuation.",
      },
      {
        question: "What safety measures are in place for the trek?",
        answer:
          "At YK Travels, your safety is our top priority. Our experienced guides are trained in first aid and emergency response. We use high-quality trekking and camping equipment to ensure your safety and comfort.",
      },
      {
        question: "Can children join the Nag Tibba Trek?",
        answer:
          "Yes, children can join the Nag Tibba Trek, provided they are in good health and have a basic level of fitness. It is advisable to consult with us for specific recommendations based on the age and fitness level of the children.",
      },
      {
        question: "What is not included in the Nag Tibba Trek package?",

        answer:
          "terms not included in the package are : \n  Personal expenses (trekking gear, snacks, beverages) \nTravel insurance \nAccommodation before and after the trek \nExtra services (laundry, phone calls, etc.) \n Tips for guides and staff \n Any permits and entry fees not specified",
      },
      {
        question: "How can I book the Nag Tibba Trek with Yk Travels? ",
        answer:
          "You can book the Nag Tibba Trek through our website or by contacting us directly via phone or email. Our team will assist you with all the details and help you prepare for an unforgettable trekking experience. \n For any additional questions or specific concerns, feel free to reach out to YK Travels. We're here to ensure you have a safe, enjoyable, and memorable trek to Nag Tibba.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d54955.144569258184!2d78.151389!3d30.586110999999995!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3908c5abdd38b5a9%3A0xd9c2fb3b7e664514!2sNag%20Tibba!5e0!3m2!1sen!2sin!4v1745789305326!5m2!1sen!2sin",
  },

  {
    id: 3,
    slug: "ruinsara-tal-trek",
    title: "Ruinsara Tal Trek",
    state: "Uttarakhand",
    shortDescription:
      "The Ruinsara Tal Trek is a breathtaking adventure through the heart of the Garhwal Himalayas in Uttarakhand, India. This trek offers an unparalleled blend of natural beauty, cultural encounters, and a glimpse into the pristine wilderness of the Himalayas. Ruinsara Tal, a high-altitude glacial lake, is the highlight of this trek, renowned for its serene beauty and the majestic peaks that surround it.",
    duration: "8 Days",
    price: 11499,
    activityTags: ["camping", "trekking", "hiking", "peak climbing"],
    overview: {
      title: "Trek Overview",
      description:
        "The Ruinsara Tal Trek is a breathtaking adventure through the heart of the Garhwal Himalayas in Uttarakhand, India. This trek offers an unparalleled blend of natural beauty, cultural encounters, and a glimpse into the pristine wilderness of the Himalayas. Ruinsara Tal, a high-altitude glacial lake, is the highlight of this trek, renowned for its serene beauty and the majestic peaks that surround it.",
      activities: [
        {
          title: "Ruinsara Tal Serenity",
          description:
            "Marvel at the pristine, crystal-clear waters of Ruinsara Tal, set against snow-capped peaks. This sacred lake offers a serene and peaceful environment, perfect for reflection and tranquility.",
          emoji: "💧",
        },
        {
          title: "Majestic Himalayan Views",
          description:
            "Enjoy panoramic views of stunning peaks like Swargarohini, Black Peak (Kalanag), and Bandarpoonch. The clear, crisp air makes this a photographer's paradise with breathtaking vistas.",
          emoji: "🏔️",
        },
        {
          title: "Diverse Landscapes",
          description:
            "Traverse dense pine, oak, and rhododendron forests, lush meadows, and rugged mountain terrains, offering a visually rewarding and exciting trekking experience.",
          emoji: "🌲",
        },
        {
          title: "Cultural Encounters",
          description:
            "Pass through Himalayan villages like Osla and Seema, experiencing the traditional lifestyle and warm hospitality of local communities, adding a rich cultural dimension to the trek.",
          emoji: "🤝",
        },
        {
          title: "Wildlife and Flora",
          description:
            "Journey through the Govind Wildlife Sanctuary, home to diverse flora and fauna. Spot Himalayan birds, langurs, and possibly the elusive Himalayan black bear amidst vibrant landscapes.",
          emoji: "🐾",
        },
      ],
    },
    tripInfo: {
      accommodation: {
        text: "Alpine tents or Decathlon tents (4 - 2 sharing base)",
        icon: "building",
      },
      arrivalCity: {
        text: "Sankri",
        icon: "city",
      },
      bestSeason: {
        text: "May to June, September to December",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "11,811 feet",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast + Lunch + Eve. Snacks + Dinner",
        icon: "bowl",
      },
      transportation: {
        text: "Bus, Taxi",
        icon: "bus",
      },
    },

    cost: {
      includes: [
        "Alpine tents, Decathlon tent (2-3) with proper mattress, bedsheet and pillow and tent light.",
        "Veg.Meal (breakfast + lunch + eve. Snacks + dinner) included.",
        "Proper toilet with Bathroom.",
        "Trek lead or guide/cook/staff included.",
        "Proper sitting area with comfortable chair and bean bags.",
        "Game (Chess, Ludo, Carrom, String Hockey, Badminton, Football, Bat-Bowl)",
        "Power backup available for phones charging.",
        "Music system for party.",
        "Dental Kit.",
        "Bonfire included for winters.",
        "Camping Gears(torch, stick included)",
        "Hydration bag and Power Bank available additional on charges 50 and 100 each.",
        "Providing self camping gear for staying one night in summit at just 500 per couple.",
        "Body massager at @50 for 15 mint.",
        "Raincoat available @100 per head.",
      ],
      excludes: [
        "Personal trekking gear such as clothing, shoes, and personal toiletries.",
        "Snacks, beverages, and additional food items beyond the provided meals.",
        "Any personal medication or medical supplies.",
        "It is highly recommended to have personal travel insurance that covers trekking activities, medical emergencies, and evacuation.",
        "(Incase Pantwari Package) Travel costs to and from the starting point at Pantwari village are not included. You will need to arrange your transportation to and from Pantwari.",
        "Accommodation and meals in any city or town before the start of the trek and after the trek ends are not included.",
        "Any additional services such as laundry, phone calls, or room service if you stay in accommodations before or after the trek.",
        "Any meals and drinks not mentioned in the itinerary.",
        "Alcoholic beverages.",
        "Costs incurred in case of evacuation due to medical reasons  or other emergencies are not covered by the package.",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    itinerary: [
      {
        day: 1,
        title:
          "Dehradun To Sankri (Drive: 210 km, Duration: 8–9 hrs, Altitude: 6,299 ft)",
        description:
          "The trekkers will be picked up from near Dehradun Railway Station at 7 a.m. from where the journey will begin. As part of the Kedarkantha trekking package, set out early in the morning on a road trip from Dehradun to Sankri Village. We have a 10- to 11-hour (approximately) enjoyable drive during which you can even see some priceless animals at the sanctuary! We arrive in Sankri village and check into a hotel, homestay, or guest house following an exciting drive through the alpine scenery.",
      },
      {
        day: 2,
        title:
          "Drive from Sankri To Taluka & Trek to Cheludgad (Drive: 11 km, Trek: 12 km, Duration: 5-6 hours, Altitude:8,858 ft)",
        description:
          "The drive from Sankri to Taluka offers picturesque views of the Himalayan landscape, winding through villages and lush forests. Upon reaching Taluka, we start the trek to Cheludgad, leading adventurers through serene trails adorned with alpine flora. Overnight stay in Cheludgad.",
      },
      {
        day: 3,
        title:
          "Trek From Cheludgad To Devsu Bugyal (Trek: 6 km, Duration: 4-5 hrs, Altitude: 9,842 ft)",
        description:
          "Trekking from Cheludgad to Devsu Bugyal offers trekkers a diverse and scenic route through lush meadows and dense forests. After reaching Taluka, adventurers embark on a scenic drive back to Sankri, reminiscing about the journey's stunning landscapes and memorable experiences. The serene ambience and stunning scenery make this trek a perfect escape. Overnight stay in Devsu, Bugyal.",
      },
      {
        day: 4,
        title:
          "Trek From Devsu Bugyal To Ruinsara Tal (Trek: 12 km, Duration: 7-8 hrs, Altitude: 11,482 ft)",
        description:
          "The trek from Devsu Bugyal to Ruinsara Tal is an awe-inspiring adventure, culminating in the majestic beauty of the lake's serene waters. As trekkers ascend towards the summit point, they are greeted with panoramic views of towering peaks and verdant valleys. Upon reaching the enchanting Ruinsara Lake, nestled amidst pristine wilderness, sunset at Ruinsara Lake is a truly magical moment. The serene lake waters reflect this magnificent spectacle of the sun setting, casting brilliant hues of pink and orange across the sky, creating a captivating symphony of colors. Overnight stay at Ruinsara Lake.",
      },
      {
        day: 5,
        title: "Exploring The Surroundings",
        description:
          "Enjoy the breathtaking views of Ruinsara Lake, surrounded by towering Himalayan peaks reflecting in its serene waters. Stroll along the lakeshore, soaking in the tranquility of the surroundings. The rest of the day is to explore nearby meadows, observe wildlife, or simply relax.",
      },
      {
        day: 6,
        title:
          "Trek from Ruinsara Tal To Devsu Bugyal (Drive: 12 km, Duration: 7-8 hours, Altitude: 9,842 ft)",
        description:
          "Bid farewell to the serene Ruinsara Lake as you descend towards Devsu Bugyal. Reluctantly leaving behind the lake's tranquil beauty, trekkers journey through lush forests and scenic trails. As Devsu Bugyal comes into view, adventurers cherish the journey and overnight stay.",
      },
      {
        day: 7,
        title:
          "Trek From Devsu Bugyal To Taluka & Drive To Sankri (Drive: 18 km, Duration: 8–9 hours, Altitude: 6,299 ft)",
        description:
          "Trekking from Devsu Bugyal to Taluka via Cheludgad offers trekkers a diverse and scenic route through lush meadows and dense forests. After reaching Taluka, adventurers embark on a scenic drive back to Sankri, reminiscing about the journey's stunning landscapes and memorable experiences.",
      },
      {
        day: 8,
        title:
          "Drive Back To Dehradun From Sankri (Drive: 210 km, Duration: 8–9 hrs, Altitude: 1,480 ft)",
        description:
          "We travel by road for ten to eleven hours, stopping occasionally for food and refreshments, on our way to Dehradun. After such a fulfilling experience, we feel changed and content even though we crossed the same alpine road on our way uphill. By late evening, we are back in Dehradun.",
      },
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/ruinsara/ruinsara-1.jpeg",
        alt: "Yk Travels Ruinsara Tal Trek",
      },
      {
        id: 2,
        src: "/images/packages/ruinsara/ruinsara-2.jpg",
        alt: "Yk travesls Ruinsara Tal Trek",
      },
    ],
    faqs: [
      {
        question: "What is the Ruinsara Tal Trek?",
        answer:
          "The Ruinsara Tal Trek is a high-altitude trek in the Garhwal Himalayas of Uttarakhand, India. It leads trekkers to the serene glacial lake of Ruinsara Tal, offering stunning views of Himalayan peaks and a diverse range of landscapes, including dense forests, alpine meadows, and traditional villages.",
      },
      {
        question: "What is the best time to undertake the Ruinsara Tal Trek?",
        answer:
          "The best time to undertake the Ruinsara Tal Trek is during the pre-monsoon (May to June) and post-monsoon (September to October) seasons. During these months, the weather is pleasant with clear skies and moderate temperatures, making it ideal for trekking.",
      },
      {
        question: "How difficult is the Ruinsara Tal Trek?",
        answer:
          "The Ruinsara Tal Trek is considered to be of moderate difficulty. It involves some steep ascents and descents, but the trails are well-marked. Basic fitness and stamina are required, making it suitable for both novice and experienced trekkers.",
      },
      {
        question: "How long is the Ruinsara Tal Trek?",
        answer:
          "The trek typically spans 8 days, including travel time from Dehradun to the starting point and back. The actual trekking distance is approximately 54 kilometers (33.5 miles).",
      },
      {
        question: "What kind of accommodation is provided during the trek?",
        answer:
          "Accommodation includes guesthouses or lodges in Lohajung and camping in tents at various points along the trek. YK Travels ensures comfortable stays with basic amenities to provide a good rest after trekking.",
      },
      {
        question: "Are permits required for the Nag Tibba Trek?",
        answer:
          "Permits may be required for trekking in certain areas. Please check with YK Travels or local authorities to ensure you have the necessary permits before starting the trek.",
      },
      {
        question: "What kind of accommodation is provided during the trek?",
        answer:
          "Accommodation during the trek typically includes camping in tents. All the mentioned amenities in package such as sleeping bags and mats are provided all around good and fun adventure. In some cases, homestays or guesthouses in nearby villages may also be an option.",
      },
      {
        question: "What should I pack for the Ali Bedni Bugyal Trek?",
        answer:
          "Essential items to pack include: \nWarm clothing (layers, thermal wear, fleece jackets) \n Waterproof trekking shoes with good grip \n Rain gear (poncho or raincoat) \n Personal toiletries and medications \n Sunglasses, sunscreen, and hats \n Water bottles and energy snacks \n A small first-aid kit \n Trekking poles (optional) \n A backpack with a rain cover",
      },
      {
        question: "What about drinking water during the trek?",
        answer:
          "We provide safe drinking water during the trek. It’s advisable to carry a reusable water bottle that you can refill at designated points.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides flexible transportation options based on the package selection of the traveler: \n Delhi to Delhi: Transportation from Delhi to Pantwari and back. \n Dehradun to Dehradun: Transportation from Dehradun to Pantwari and back. \n Pantwari to Pantwari: For those who arrange their own transport to Pantwari, we start and end the trek at Pantwari.",
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, it is highly recommended to have travel insurance that covers trekking activities, medical emergencies, and evacuation.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Sankri and back. Safe and comfortable vehicles with experienced drivers are arranged for the journey.",
      },
      {
        question: "Are there age restrictions for the trek?",
        answer:
          "There are no specific age restrictions, but the trek is recommended for individuals above the age of 12 who are physically fit and able to handle the demands of trekking.",
      },
      {
        question: "What are the meal arrangements during the trek?",
        answer:
          "YK Travels provides nutritious vegetarian meals throughout the trek. Breakfast, lunch, and dinner are included in the package, and special dietary requirements can be accommodated with prior notice.",
      },
      {
        question: "Is drinking water available during the trek?",
        answer:
          "Yes, drinking water is available at campsites. Trekkers are advised to carry their own water bottles and purification tablets or filters for use during the trek.",
      },
      {
        question: "What permits are required for the Ruinsara Tal Trek?",
        answer:
          "Trekkers need to obtain permits from the Forest Department to enter the trekking regions. YK Travels assists in obtaining all necessary permits and registrations as part of our service.",
      },
      {
        answer:
          "Basic medical facilities and first-aid are available at various points along the trek. However, it is advisable to carry a personal first-aid kit and any specific medications you may need. YK Travels’ guides are trained in first-aid and emergency response.",
        question: "Are there medical facilities available during the trek?",
      },
      {
        question: "What safety measures are in place during the trek?",
        answer:
          "YK Travels prioritizes the safety of all trekkers. Our experienced guides are trained in first aid and emergency response. We use high-quality trekking and camping equipment and ensure that all travel arrangements are secure and reliable.",
      },
      {
        question: "How can I book the Ruinsara Tal Trek with YK Travels?",
        answer:
          "You can book the Ruinsara Tal Trek through our website or by contacting us directly via phone or email. Our team will assist you with all the details and help you prepare for an unforgettable adventure. \n For any additional questions or specific concerns, feel free to reach out to YK Travels. We’re here to ensure you have a safe, enjoyable, and memorable Ruinsara Tal Trek experience.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d27336.191538728915!2d78.458088!3d31.081260999999998!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39089dec9859d14f%3A0x371ffec9ece1bc0a!2sRuinsara%20Lake!5e0!3m2!1sen!2sin!4v1745906655386!5m2!1sen!2sin",
  },
  {
    id: 4,
    slug: "har-ki-doon",
    title: "Har Ki Dun Trek",
    state: "Uttarakhand",
    shortDescription:
      "The Har Ki Dun Trek, organized by YK Travels, is an enchanting adventure through the majestic Garhwal Himalayas of Uttarakhand. Known as the “Valley of Gods,” Har Ki Dun is a cradle-shaped hanging valley, surrounded by towering peaks, verdant forests, and sparkling rivers. This trek is ideal for nature lovers, adventure enthusiasts, and those seeking a deep connection with the Himalayas’ natural beauty and cultural heritage. The trek reaches a maximum altitude of 11,675 feet (3,556 meters) and offers a unique blend of stunning landscapes, cultural encounters, and historical significance.",
    duration: "7 Days",
    price: 10999,
    activityTags: ["camping", "trekking", "adventure", "star gazing"],
    overview: {
      title: "Trek Overview",
      description:
        "The Har Ki Dun Trek, organized by YK Travels, is an enchanting adventure through the majestic Garhwal Himalayas of Uttarakhand. Known as the “Valley of Gods,” Har Ki Dun is a cradle-shaped hanging valley, surrounded by towering peaks, verdant forests, and sparkling rivers. This trek is ideal for nature lovers, adventure enthusiasts, and those seeking a deep connection with the Himalayas’ natural beauty and cultural heritage. The trek reaches a maximum altitude of 11,675 feet (3,556 meters) and offers a unique blend of stunning landscapes, cultural encounters, and historical significance.",
      activities: [
        {
          title: "Enchanting Landscapes",
          description:
            "Experience stunning views of lush green valleys, sparkling streams, and towering snow-capped peaks. Trek through ancient forests of pine, deodar, and oak, and expansive meadows adorned with vibrant wildflowers.",
          emoji: "🌄",
        },
        {
          title: "Cultural Richness",
          description:
            "Immerse yourself in the mythology and folklore of Har Ki Dun. Visit traditional villages like Osla and Sankri to experience the rich culture and warm hospitality of the Himalayan people.",
          emoji: "🤝",
        },
        {
          title: "Diverse Flora and Fauna",
          description:
            "Discover a variety of bird species and wildlife, including the Himalayan black bear, langurs, and wild boars, in the vibrant and biodiverse valley of Har Ki Dun.",
          emoji: "🐾",
        },
        {
          title: "Historical Significance",
          description:
            "Trek the legendary route believed to be taken by the Pandavas in the Mahabharata on their ascent to heaven, adding historical and spiritual depth to your journey.",
          emoji: "📜",
        },
      ],
    },
    tripInfo: {
      accommodation: {
        text: "Alpine tents or Decathlon tents (4-1 sharing base)",
        icon: "building",
      },
      arrivalCity: {
        text: "Sankri",
        icon: "city",
      },
      bestSeason: {
        text: "All Around The Year.",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "11,675 feet (3,556 meters)",
        icon: "mountain",
      },

      transportation: {
        text: "Bus, Taxi",
        icon: "bus",
      },
    },

    cost: {
      includes: [
        "Alpine tents, Decathlon tent (2-3) with proper mattress, bedsheet and pillow and tent light.",
        "Veg.Meal (breakfast + lunch + eve. Snacks + dinner) included.",
        "Trek lead or guide/cook/staff included.",
        "Transportation from Dehradun to Dehradun is covered.",
        "Some packages may offer a free jacket or rucksack, a customized printed travel card delivered after the trek, and discounts on health check-ups and health insurance.",
      ],
      excludes: [
        "Personal Expenses: Any personal spending during the trip, emergencies, or alcoholic beverages.",
        "Trekking Gear : You are responsible for bringing your trekking gear, such as trekking poles, shoes, cape, gloves, warm cap, backpack, and jacket.",
        "Porters/Mules : If you need help carrying your luggage, mules  or porters are not included in the base price.",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    itinerary: [
      {
        day: 1,
        title:
          "Dehradun To Sankri (Drive: 220 km, Duration: 10–11 hrs, Altitude: 6,400 ft)",
        description: `
          All the enthusiastic trekkers gather at Dehradun (departure at 7 am from Dehradun Bus Stop/Railway Station), a charming city. Our trek adventure begins with our skilled guides leading the way. We'll hop on board and set off on a scenic drive to Sankri, the starting point. The journey unfolds breathtaking mountain vistas, with the Tons River keeping us company most of the way. Nestled within Govind Pashu Vihar National Park at an altitude of 1950 meters, Sankri stuns with its postcard-worthy beauty and surprisingly comfortable amenities. After a roughly 10- to 11-hour drive with refreshment breaks, we'll arrive at the peaceful and secluded village of Sankri. Upon reaching, we'll check into our pre-booked accommodations and enjoy a delicious dinner. This is your time to unwind after the day's travel and gear up for the exciting trek that awaits us tomorrow. For those feeling energetic, Sankri offers a chance to explore and connect with the locals. Take a stroll through the charming town and its little markets. Discover local crafts, fresh produce, and other treasures Sankri offers. In the evening, the trek leader will provide a briefing on tomorrow's route. With that, you can retire to your comfortable beds and get a good night's sleep before the next adventure unfolds.`,
      },
      {
        day: 2,
        title:
          "Sankri To Taluka And Trek From Taluka To Ganghad (Drive: 12 km, Trek: 8.5 km, Duration: 6 hours, Altitude: 7,200 ft)",
        description:
          "Rise and shine! Sankri greets you with a mesmerizing sunrise—a sight not to be missed. Savour coffee or tea while watching the sun rise over Swargrohini's snow-capped peak. Today's the day for adventure! After a hearty breakfast, trekkers are advised to stock up on water bottles and energy snacks for the journey ahead. We'll depart Sankri by car and journey towards Taluka. The scenic route boasts pine forests and meadows galore. Our drive concludes in Taluka, marking the official start of the trek. From here, we ascend towards Ganghad, reaching an elevation of 2560 meters. Along the way, we'll cross the Tons River and pass through the charming village of Dhatmir. Look out for Himalayan plants and animals; if lucky, you might see deer, wild boars, and other local creatures barking. Our destination for the day is Osla, where we'll set up camp for the night. After settling into our tents, a merry bonfire awaits under a blanket of stars. Immerse yourself in the fresh mountain air and the beauty of nature. A delectable dinner will be served before we head into the night and get ready for an even more demanding and thrilling trek tomorrow.",
      },
      {
        day: 3,
        title:
          "Ganghad To Kalkatidhar/Osla (Trek: 7.4 km, Duration: 4-5 hrs, Altitude: 8,566 ft",
        description:
          "The moment we've all been waiting for—the trek to the heart of Har Ki Dun! Today's a challenging yet rewarding 11-kilometer journey. Bidding farewell to charming Osla village, we delve deeper into the lush Himalayan landscape with our guides. Picture meadows bursting with orchids and majestic mountains cradling the valley. Our destination is an astounding 3200 meters, a significant climb from Osla. Along the way, we'll navigate wooden bridges over streams and rivers and traverse fragrant coniferous forests. Finally, we reach the Har Ki Dun campsite, greeted by the awe-inspiring peaks of Har Ki Dun and Swargarohini towering above us. The temperature is perfect for trekking, with a gentle breeze and a serene atmosphere that will leave you spellbound. This adventure is about the journey and the breathtaking destination that awaits.",
      },
      {
        day: 4,
        title:
          "Osla/Kalkatidhar To Har Ki Dun And Back To Campsite (Trek: 10 km, Duration: 5–6 hours, Altitude: 11,800 ft)",
        description:
          "The Har Ki Dun Valley bathes in a magical sunrise as light spills over the mountains. After breakfast in this stunning setting, we're ready to delve deeper into the Uttarakhand wilderness. Accustomed to the Har Ki Dun routine and weather, we set off on another trek, this time a challenging 15-kilometre round trip to the crown jewel, Jaundhar Glacier. Depending on the season, the trail might be grassy or snowy, making the trek to Jaundhar Glacier a contender for one of the best in Uttarakhand. Witnessing a winter wonderland (if you visit in the winter) adds another layer of magic to the experience. Take your time capturing photos, exploring the area, or simply soaking in the beauty of the Har Ki Dun Valley. It's entirely up to you! After a day of adventure and exhaustion, we return to our campsite in Har Ki Dun for a well-deserved rest.",
      },
      {
        day: 5,
        title:
          ":Trek From Kalkatidhar/Osla To Ganghad (Trek: 7.4 km, Duration: 4-5 hrs, Altitude: 7,200 ft)",
        description:
          "Though it's a full-day trek, today's journey will be a breeze as we head downhill. The descent offers stunning scenery, like something straight out of a painting. The Har Ki Dun Valley Trek truly ignites your imagination and creativity. It's the perfect escape to reconnect with nature and yourself. We'll arrive in Ganghad feeling physically tired but mentally and emotionally rejuvenated. After a delicious dinner, rehydrate after your incredible trekking adventure. Finally, we'll rest in Ganghad for the night, preparing for our final Har Ki Dun trek tomorrow.",
      },
      {
        day: 6,
        title:
          "Trek From Ganghad To Sankri (Drive: 12 km, Trek: 8.5 km, Duration: 6 hours, Altitude: 7,200 ft)",
        description:
          "After a restful night in the charming village of Osla, we embark on our final descent back to Sankri. The downhill trail offers a relaxed journey, allowing you to soak in the beauty of the Himalayas one last time. Reaching Sankri marks the successful completion of our Har Ki Dun adventure! Take advantage of this opportunity to savour the local cuisine and explore Sankri's unique charm. As one of Uttarakhand's premier trekking destinations, Sankri offers a glimpse into the local way of life and hidden treasures. Beyond being a physical challenge, this trek has helped everyone overcome their fears and created lifelong friendships between strangers. The Har Ki Dun trek will undoubtedly impact your life, enriching it in countless ways.",
      },
      {
        day: 7,
        title:
          "Sankri To Dehradun (Drive: 220 km, Duration: 10–11 hours, Altitude: 6,400 ft)",
        description:
          "We checked out of Sankri and hit the road to Dehradun. We shall drive and reach Dehradun by late evening, around 10 pm. Thus, the excellent trekking adventure of Har Ki Dun comes to a beautiful end, and all the travellers depart for their respective destination",
      },
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/harkidun/Har-ki-Doon-1.jpeg",
        alt: "Yk Travels Har ki Dun Trek",
      },
      {
        id: 2,
        src: "/images/packages/harkidun/Har-ki-Doon-2.jpeg",
        alt: "Yk travesls har ki dun trek",
      },
      {
        id: 3,
        src: "/images/packages/harkidun/Har-ki-Doon-3.jpeg",
        alt: "Yk travels har ki dun trek",
      },
      {
        id: 4,
        src: "/images/packages/harkidun/Har-ki-Doon-4.jpeg",
        alt: "yk travels har ki dun trek",
      },
      {
        id: 5,
        src: "/images/packages/harkidun/Har-ki-Doon-5.jpeg",
        alt: "yk travels har ki dun trek",
      },
    ],
    faqs: [
      {
        question: "What is the maximum altitude of the Har ki Dun Trek?",
        answer:
          "The maximum altitude of the Har Ki Dun Trek is 11,675 feet (3,556 meters).",
      },
      {
        question: "How difficult is the Har ki Dun Trek?",
        answer:
          "The Har Ki Dun Trek is considered a moderate trek. It is suitable for trekkers with a good level of fitness. Prior trekking experience is beneficial but not necessary. The trail includes a mix of gradual ascents and descents.",
      },
      {
        question: "What is the best time to do the Har Ki Dun Trek?",
        answer: `
         The best time to undertake the Har Ki Dun Trek is during the pre-monsoon (April to June) and post-monsoon (September to November) seasons:

Spring (April to June): Pleasant weather, blooming flowers, and clear views.
Autumn (September to November): Crisp air, clear skies, and vibrant landscapes.
         `,
      },
      {
        question: "How long does the Har Ki Dun Trek take?",
        answer:
          "The Har Ki Dun Trek typically takes 7 days to complete, including travel to and from the starting point at Sankri.",
      },
      {
        question: "What should I pack for the trek?",
        answer: `
          Essential items to pack include:

Comfortable and sturdy trekking shoes
Warm clothing (layers, thermal wear, jacket)
Rain gear (if trekking in pre-monsoon season)
Sun protection (hat, sunglasses, sunscreen)
Personal toiletries and a first-aid kit
Water bottle and snacks
Trekking poles (optional but recommended)
Backpack
          
          `,
      },
      {
        question: "Are permits required for the Nag Tibba Trek?",
        answer:
          "Permits may be required for trekking in certain areas. Please check with YK Travels or local authorities to ensure you have the necessary permits before starting the trek.",
      },
      {
        question: "What kind of accommodation is provided during the trek?",
        answer:
          "Accommodation during the trek typically includes camping in tents. All the mentioned amenities in package such as sleeping bags and mats are provided all around good and fun adventure. In some cases, homestays or guesthouses in nearby villages may also be an option.",
      },
      {
        question: "Is there a provision for meals during the trek?",
        answer:
          "Yes, meals are provided during the trek. This usually includes breakfast, lunch, and dinner. Vegetarian options are available, and we can accommodate dietary restrictions if informed in advance.",
      },
      {
        question: "What about drinking water during the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Sankri and back. Depending on the package you select, we can also arrange transportation from other locations if needed.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Sankri and back. Depending on the package you select, we can also arrange transportation from other locations if needed.",
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, it is highly recommended to have travel insurance that covers trekking activities, medical emergencies, and evacuation.",
      },
      {
        question: "What safety measures are in place for the trek?",
        answer:
          "At YK Travels, your safety is our top priority. Our experienced guides are trained in first aid and emergency response. We use high-quality trekking and camping equipment to ensure your safety and comfort.",
      },
      {
        question: "Can children join the Har Ki Dun Trek?",
        answer:
          "The Har Ki Dun Trek is suitable for children above 10 years old, provided they are physically fit and capable of trekking long distances. It is advisable to consult with us for specific recommendations based on the age and fitness level of the children.",
      },
      {
        question: "Can children join the Nag Tibba Trek?",
        answer:
          "Yes, children can join the Nag Tibba Trek, provided they are in good health and have a basic level of fitness. It is advisable to consult with us for specific recommendations based on the age and fitness level of the children.",
      },
      {
        question: "What is not included in the Har Ki Dun Trek package?",
        answer: `
Items not included in the package are:

Personal expenses (trekking gear, snacks, beverages)
Travel insurance
Accommodation before and after the trek
Extra services (laundry, phone calls, etc.)
Tips for guides and staff
Any permits and entry fees not specified`,
      },
      {
        question: "How can I book the Har ki Dun Trek with Yk Travels? ",
        answer: `You can book the Har Ki Dun Trek through our website or by contacting us directly via phone or email. Our team will assist you with all the details and help you prepare for an unforgettable trekking experience.

For any additional questions or specific concerns, feel free to reach out to YK Travels. We’re here to ensure you have a safe, enjoyable, and memorable trek to Har Ki Dun.`,
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d252390.2682032945!2d78.430991!3d31.150718000000005!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390627eba9f05881%3A0x7f12a5dfe906eaa7!2sHar%20Ki%20Dun!5e1!3m2!1sen!2sin!4v1746012993709!5m2!1sen!2sin",
  },

  {
    id: 5,
    slug: "ali-bedni-bugyal-trek",
    title: "Ali Bedni Bugyal Trek",
    state: "Uttarakhand",
    shortDescription:
      "The Ali Bedni Bugyal Trek is a captivating journey through the enchanting high-altitude meadows of the Garhwal Himalayas in Uttarakhand, India. This trek is renowned for its lush, sprawling bugyals (meadows), which offer spectacular panoramic views of some of the most majestic Himalayan peaks. Ideal for both novice and experienced trekkers, the Ali Bedni Bugyal Trek combines natural beauty with cultural richness, providing a memorable experience in the lap of nature.",
    duration: "6 Days",
    price: 7999,
    activityTags: ["camping", "trekking", "hiking", "peak climbing"],
    overview: {
      title: "Trek Overview",
      description:
        "The Ali Bedni Bugyal Trek is a captivating journey through the enchanting high-altitude meadows of the Garhwal Himalayas in Uttarakhand, India. This trek is renowned for its lush, sprawling bugyals (meadows), which offer spectacular panoramic views of some of the most majestic Himalayan peaks. Ideal for both novice and experienced trekkers, the Ali Bedni Bugyal Trek combines natural beauty with cultural richness, providing a memorable experience in the lap of nature.",
      activities: [
        {
          title: "Scenic Meadows",
          description:
            "Trek through the breathtaking Ali Bugyal and Bedni Bugyal, adorned with vibrant wildflowers in summer and lush green expanses that stretch endlessly, offering a stunning alpine experience.",
          emoji: "🌼",
        },
        {
          title: "Panoramic Himalayan Views",
          description:
            "Witness unobstructed views of majestic Himalayan peaks like Trishul, Nanda Ghunti, and Chaukhamba, with unforgettable sunrise and sunset vistas from the meadows.",
          emoji: "🏔️",
        },
        {
          title: "Rich Flora and Fauna",
          description:
            "Explore dense oak, rhododendron, and deodar forests teeming with diverse wildlife and avian species, where the vibrant flora transforms with the seasons.",
          emoji: "🐾",
        },
        {
          title: "Cultural Experience",
          description:
            "Visit the traditional villages of Lohajung and Didna to engage with local communities, experiencing their unique culture, hospitality, and rustic charm.",
          emoji: "🤝",
        },
        {
          title: "Bedni Kund Serenity",
          description:
            "Discover the sacred Bedni Kund, a high-altitude lake steeped in myth and legend, offering a serene and reflective spot surrounded by spiritual significance.",
          emoji: "💧",
        },
      ],
    },
    tripInfo: {
      accommodation: {
        text: "Alpine tents or Decathlon tents (4 - 2 sharing base)",
        icon: "building",
      },
      arrivalCity: {
        text: "Lohajung",
        icon: "city",
      },
      bestSeason: {
        text: "May to June, September to December",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "11,500 feet",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast + Lunch + Eve. Snacks + Dinner",
        icon: "bowl",
      },
      transportation: {
        text: "Bus, Taxi",
        icon: "bus",
      },
      pickupPoint: {
        text: "Rishikesh, Lohajung",
        icon: "car",
      },
    },

    cost: {
      includes: [
        "Accommodation is provided in alpine and dome camps on a sharing basis.",
        "Meals include dinner from Day 1 to breakfast on the last day. They will be simple and primarily vegetarian (breakfast, lunch, supper, bed tea, afternoon tea, and snacks).",
        "Crampons (spikes for shoes) and gaiters for snow trekking.",
        "Professional and experienced certified trek guide.",
        "Transportation from Rishikesh to Rishikesh.",
        "Free Jacket or Rucksack if you opt for the offer package.",
        "Free customized printed travel card worth Rs. 500, delivery to home address post trek completion.",
        "50% off on health check-ups and health insurance.",
      ],
      excludes: [
        "Anything not mentioned in the inclusions above.",
        "Any personal expenses during the trip or out of any emergency or alcoholic beverages.",
        "Any personal trekking gear (like a pole, shoes, cape, gloves, warm cap, bag, or jacket).",
        "Mules or porters to carry personal luggage.",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    itinerary: [
      {
        day: 1,
        title:
          "Travel From Rishikesh To Lohajung (Drive: 250 km, Duration: 9–10 hours, 7,650 ft)",
        description:
          "Stay in the guest house. Drive 250 km. Local markets and networks are available. The pick-up time is 6:00 am at the ISBT Rishikesh parking lot. Route: Rishikesh-Devprayag-Srinagar-Rudraprayag—Karnaprayag—Dewal-Lohajung.",
      },
      {
        day: 2,
        title:
          "Lohajung To Didna Village (Trek: 9 km, Duration: 4-5 hours, Altitude: 8,100 ft)",
        description:
          "Reach Didna before lunch—accommodation is in the village homestay. No local market exists, but the network is available until Ruan Bagad Bridge's Ruangradual descent (4.5 km). Re-fill your water bottle at Ruan Bagad Bridge across the Neel Ganga River. After Ruan Bagad Bridge 2, there is a steep ascent to Didna Village (2 km) for hours.",
      },
      {
        day: 3,
        title:
          "Didna Village To Abin Kharik/Khobal Tal Via Ali Bugyal (Trek 10.5 km, Duration: 7-8 hrs). Altitude: 11,350 ft",
        description:
          "After breakfast, start the trek with a water bottle and phone. Reach Ali Bugyal by late noon. Keep your windbreaker and waterproof jacket handy. Tolani is the last point for drinking water. The first half of the trek is through oak and rhododendron forests (steep ascent). The second half is through extensive alpine meadows (gradual ascent).",
      },
      {
        day: 4,
        title:
          "Abin Kharik/Khobal Tal To Ghairoli Patal (Trek 7 km, Duration: 4-5 hrs). Altitude: 10,500 ft.",
        description:
          "The most accessible day of the trek. Accommodation in a tent. Reach Ghairoli Patal before lunch. Khobar Tal to Bedni Bugyal (3 km gradual descent). Bedni Bugyal to Doliya Dhar.(a brisk one-kilometer stroll). Ghaeroli Patal to Doliya Dhar (3 km of steep descent).",
      },
      {
        day: 5,
        title:
          "Ghairoli Patal To Lohajung. (Trek: 7 km, Duration: 5–6 hrs, Altitude: 7,650 ft)",
        description:
          "3 km of steep downhill from Ghaeroli Patal to Neel Ganga. Neel Ganga to Ranka Dhar, a moderate one-kilometer climb. Roadhead from Ranka Dhat to Wan Village (3 km gradually descending).",
      },
      {
        day: 6,
        title:
          "Drive Back To Rishikesh & From Lohajung (Drive: 250 km, Duration: 9–10 hours)",
        description:
          "After morning tea, I am Ready to move to Rishikesh (I will have breakfast and lunch on the way). I aim to reach Rishikesh by 5:00 pm.",
      },
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/ali-bedni/ali-bedni-1.jpeg",
        alt: "Yk Travels Ali Bedni Bugyal Trek",
      },
      {
        id: 2,
        src: "/images/packages/ali-bedni/ali-bedni-2.jpeg",
        alt: "Yk travesls Ali Bedni Bugyal Trek",
      },
      {
        id: 3,
        src: "/images/packages/ali-bedni/ali-bedni-3.jpeg",
        alt: "Yk Travels Ali Bedni Budyal Trek",
      },
      {
        id: 4,
        src: "/images/packages/ali-bedni/ali-bedni-5.webp",
        alt: "Yk travels Ali Bedni Bugyal Trek",
      },
    ],
    faqs: [
      {
        question: "What is the Ali Bedni Bugyal Trek?",
        answer:
          "The Ali Bedni Bugyal Trek is a high-altitude trek in the Garhwal Himalayas of Uttarakhand, India. It takes trekkers through the stunning alpine meadows of Ali Bugyal and Bedni Bugyal, offering panoramic views of some of the most majestic Himalayan peaks.",
      },
      {
        question:
          "What is the best time to undertake the Ali Bedni Bugyal Trek?",
        answer:
          "The best time to undertake the Ali Bedni Bugyal Trek is during the pre-monsoon (May to June) and post-monsoon (September to October) seasons. During these months, the weather is pleasant with clear skies and moderate temperatures.",
      },
      {
        question: "How difficult is the Ali Bedni Bugyal Trek?",
        answer:
          "The Ali Bedni Bugyal Trek is considered to be of moderate difficulty. It involves some steep ascents and descents, but the trails are well-defined. Basic fitness and stamina are required, making it suitable for both novice and experienced trekkers.",
      },
      {
        question: "How long is the Ali Bedni Bugyal Trek?",
        answer:
          "The trek typically spans 6 days, including travel time from Lohajung to the starting point and back. The actual trekking distance is approximately 35 kilometers (21.7 miles).",
      },
      {
        question: "What kind of accommodation is provided during the trek?",
        answer:
          "Accommodation includes guesthouses or lodges in Lohajung and camping in tents at various points along the trek. YK Travels ensures comfortable stays with basic amenities to provide a good rest after trekking.",
      },
      {
        question: "Are permits required for the Nag Tibba Trek?",
        answer:
          "Permits may be required for trekking in certain areas. Please check with YK Travels or local authorities to ensure you have the necessary permits before starting the trek.",
      },
      {
        question: "What kind of accommodation is provided during the trek?",
        answer:
          "Accommodation during the trek typically includes camping in tents. All the mentioned amenities in package such as sleeping bags and mats are provided all around good and fun adventure. In some cases, homestays or guesthouses in nearby villages may also be an option.",
      },
      {
        question: "What should I pack for the Ali Bedni Bugyal Trek?",
        answer:
          "Essential items to pack include: \nWarm clothing (layers, thermal wear, fleece jackets) \n Waterproof trekking shoes with good grip \n Rain gear (poncho or raincoat) \n Personal toiletries and medications \n Sunglasses, sunscreen, and hats \n Water bottles and energy snacks \n A small first-aid kit \n Trekking poles (optional) \n A backpack with a rain cover",
      },
      {
        question: "What about drinking water during the trek?",
        answer:
          "We provide safe drinking water during the trek. It’s advisable to carry a reusable water bottle that you can refill at designated points.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides flexible transportation options based on the package selection of the traveler: \n Delhi to Delhi: Transportation from Delhi to Pantwari and back. \n Dehradun to Dehradun: Transportation from Dehradun to Pantwari and back. \n Pantwari to Pantwari: For those who arrange their own transport to Pantwari, we start and end the trek at Pantwari.",
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, it is highly recommended to have travel insurance that covers trekking activities, medical emergencies, and evacuation.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Sankri and back. Safe and comfortable vehicles with experienced drivers are arranged for the journey.",
      },
      {
        question: "Are there age restrictions for the trek?",
        answer:
          "There are no specific age restrictions, but the trek is recommended for individuals above the age of 12 who are physically fit and able to handle the demands of trekking.",
      },
      {
        question: "What are the meal arrangements during the trek?",
        answer:
          "YK Travels provides nutritious vegetarian meals throughout the trek. Breakfast, lunch, and dinner are included in the package, and special dietary requirements can be accommodated with prior notice.",
      },
      {
        question: "Is drinking water available during the trek?",
        answer:
          "Yes, drinking water is available at campsites. Trekkers are advised to carry their own water bottles and purification tablets or filters for use during the trek.",
      },
      {
        question: "What permits are required for the Ruinsara Tal Trek?",
        answer:
          "Trekkers need to obtain permits from the Forest Department to enter the trekking regions. YK Travels assists in obtaining all necessary permits and registrations as part of our service.",
      },
      {
        answer:
          "Basic medical facilities and first-aid are available at various points along the trek. However, it is advisable to carry a personal first-aid kit and any specific medications you may need. YK Travels’ guides are trained in first-aid and emergency response.",
        question: "Are there medical facilities available during the trek?",
      },
      {
        question: "What safety measures are in place during the trek?",
        answer:
          "YK Travels prioritizes the safety of all trekkers. Our experienced guides are trained in first aid and emergency response. We use high-quality trekking and camping equipment and ensure that all travel arrangements are secure and reliable.",
      },
      {
        question: "How can I book the Ruinsara Tal Trek with YK Travels?",
        answer:
          "You can book the Ruinsara Tal Trek through our website or by contacting us directly via phone or email. Our team will assist you with all the details and help you prepare for an unforgettable adventure. \n For any additional questions or specific concerns, feel free to reach out to YK Travels. We’re here to ensure you have a safe, enjoyable, and memorable Ruinsara Tal Trek experience.",
      },
    ],
    mapUrl:
      " https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7966.581254712616!2d79.659788!3d30.183079!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39a76b8b11b11f5f%3A0x3379ca9d2dd1d896!2sAali%20Bugyal!5e1!3m2!1sen!2sin!4v1746017728803!5m2!1sen!2sin",
  },
  {
    id: 6,
    slug: "phulara-ridge-trek",
    title: "Phulara Ridge Trek",
    state: "Uttarakhand",
    shortDescription:
      "The Phulara Ridge Trek is an enchanting and relatively offbeat trekking route in the Garhwal region of Uttarakhand, India. This trek is renowned for its unique ridge walk, offering uninterrupted 360-degree panoramic views of the surrounding Himalayan peaks and valleys. It combines lush green meadows, dense forests, and stunning vistas, making it an excellent choice for trekkers seeking both adventure and tranquility.",
    duration: "6 Days",
    price: 10499,
    activityTags: ["camping", "trekking", "hiking", "peak climbing"],
    overview: {
      title: "Trek Overview",
      description:
        "The Phulara Ridge Trek is an enchanting and relatively offbeat trekking route in the Garhwal region of Uttarakhand, India. This trek is renowned for its unique ridge walk, offering uninterrupted 360-degree panoramic views of the surrounding Himalayan peaks and valleys. It combines lush green meadows, dense forests, and stunning vistas, making it an excellent choice for trekkers seeking both adventure and tranquility.",
      activities: [
        {
          title: "Unique Ridge Walk",
          description:
            "Experience the exhilarating ridge walk, a rare feature offering spectacular views of snow-capped peaks on both sides, providing an unmatched sense of openness and adventure.",
          emoji: "🥾",
        },
        {
          title: "Panoramic Himalayan Views",
          description:
            "Enjoy breathtaking views of prominent Himalayan peaks, including Swargarohini, Bandarpoonch, Kala Nag, and Kedarkantha, enhanced by clear skies and crisp mountain air.",
          emoji: "🏔️",
        },
        {
          title: "Diverse Landscapes",
          description:
            "Traverse dense oak and pine forests, expansive alpine meadows filled with wildflowers, and varied terrains, creating a visually rewarding and engaging trekking experience.",
          emoji: "🌲",
        },
        {
          title: "Rich Flora and Fauna",
          description:
            "Journey through the Govind Wildlife Sanctuary, spotting rare bird species and wildlife amidst diverse flora, adding an exciting wildlife dimension to your trek.",
          emoji: "🐾",
        },
        {
          title: "Beautiful Campsites",
          description:
            "Camp in scenic locations like the grassy meadows of Pushtara and serene Bhoj Gadi, offering stunning sunrise and sunset views for a memorable stay.",
          emoji: "🏕️",
        },
      ],
    },
    tripInfo: {
      accommodation: {
        text: "Alpine tents or Decathlon tents (4 - 2 sharing base)",
        icon: "building",
      },
      arrivalCity: {
        text: "Lohajung",
        icon: "city",
      },
      bestSeason: {
        text: "May to June, September to December",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "11,500 feet",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast + Lunch + Eve. Snacks + Dinner",
        icon: "bowl",
      },
      transportation: {
        text: "Bus, Taxi",
        icon: "bus",
      },
      pickupPoint: {
        text: "Rishikesh, Lohajung",
        icon: "car",
      },
    },

    cost: {
      includes: [
        "Accommodation is provided in alpine and dome camps on a sharing basis.",
        "Meals include dinner from Day 1 to breakfast on the last day. They will be simple and primarily vegetarian (breakfast, lunch, supper, bed tea, afternoon tea, and snacks).",
        "Crampons (spikes for shoes) and gaiters for snow trekking.",
        "Professional and experienced certified trek guide.",
        "Transportation from Rishikesh to Rishikesh.",
        "Free Jacket or Rucksack if you opt for the offer package.",
        "Free customized printed travel card worth Rs. 500, delivery to home address post trek completion.",
        "50% off on health check-ups and health insurance.",
      ],
      excludes: [
        "Anything not mentioned in the inclusions above.",
        "Any personal expenses during the trip or out of any emergency or alcoholic beverages.",
        "Any personal trekking gear (like a pole, shoes, cape, gloves, warm cap, bag, or jacket).",
        "Mules or porters to carry personal luggage.",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    itinerary: [
      {
        day: 1,
        title:
          "Dehradun to Sankri (200 km, 9–10 hours, 6,400 feet in altitude)",
        description:
          "Begin your Phulara Ridge trek by departing from Dehradun at 7 am. Your destination is Sankri, a quaint village in the Himalayas that serves as a gateway to several treks, including Kedarkantha and Har Ki Dun. The drive passes through towns like Nainbagh and Purola, offering picturesque views and a gradual introduction to the serene landscapes. In Sankri, explore local dhabas, shops, and the thrilling Zip700 zipline for an adventurous start to your trek.",
      },
      {
        day: 2,
        title:
          "Sankri to Juda Ka Talab (Trek: 5 km, Duration: 4-5 hrs, Altitude: 9,300 ft)",
        description:
          "Setting out from Sankri, your trek to Juda Ka Talab takes you through dense pine forests, ascending to the serene meadow of Sikolta. Along the way, you pass by relics of old dhabas and the scenic Jainot Thatch. Continue to Marikoda, where views of Dhunda and Phulara Ridge await. The day's journey ends at Sikolta, where you'll camp under the stars, surrounded by nature.",
      },
      {
        day: 3,
        title:
          "Juda Ka Talab to Bhoj Gadi (Trek: 4.5 km, Duration: 4-5 hrs, Altitude: 11,155 ft)",
        description:
          "From Sikolta, trek through a dense, dark forest to Bhoj Gadi. This part of the journey features challenging terrain with fallen trees and a mix of descents and sharp ascents. Refresh at a charming stream near a bridge before reaching the lush Karasu Thatch. Enjoy the view of Kedarkantha Peak and the expansive meadows as you approach your campsite at Bhoj Gadi.",
      },
      {
        day: 4,
        title:
          "Bhoj Gadi to Pushtara via Phulara Ridge (Trek: 8.4 km, Duration: 6-7 hrs, Altitude: 12,127 ft)",
        description:
          "Experience a breathtaking sunrise before trek across Phulara Ridge, where you'll enjoy panoramic views of the Himalayan range and its salt-painted mountains. This day involves a mix of ascents and descents through diverse landscapes, including pine forests and the Purola valley, with peaks like Bandarpoonch and Swargarohini in sight. Arrive at Pushtara to relax and absorb the day's experiences at your campsite.",
      },
      {
        day: 5,
        title:
          "Trek from Pushtara to Taluka & Drive Back to Sankri (Trek: 8.1 km, Duration: 5-6 hours, Altitude: 9,500 ft)",
        description:
          "The trek from Pushtara to Taluka involves passing over meadows and thick forests of pine and oak, encountering remnants of Gujjar huts along the way. The journey concludes with a scenic trek back to Sankri, where you can spend the evening exploring the village and its charming Himalayan backdrop.",
      },
      {
        day: 6,
        title:
          "Drive from Sankri to Dehradun (Drive: 220 km, Duration: 10–11 hours)",
        description:
          "On the final day, depart Sankri for Dehradun. The drive-back is reflective, allowing you to relive the memories of your trek through the stunning landscapes of Uttarakhand. Expect to arrive in Dehradun by late evening, marking the end of your Phulara Ridge adventure.",
      },
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/phulra-trek/phulra-trek-1.webp",
        alt: "Yk Travels Phulra Trek",
      },
      {
        id: 2,
        src: "/images/packages/phulra-trek/phulara-trek-2.webp",
        alt: "Yk travesls Phulra Trek",
      },
    ],
    faqs: [
      {
        question: "What is the Phulara Ridge Trek?",
        answer:
          "The Phulara Ridge Trek is a unique and scenic trekking route in the Garhwal region of Uttarakhand, India. This trek is known for its distinctive ridge walk, offering 360-degree panoramic views of the surrounding Himalayan peaks and valleys.",
      },
      {
        question:
          "What is the best time to undertake the Ali Bedni Bugyal Trek?",
        answer:
          "The best time to undertake the Phulara Ridge Trek is during the pre-monsoon (April to June) and post-monsoon (September to November) seasons. During these months, the weather is favorable, with clear skies and moderate temperatures, ensuring a comfortable trekking experience.",
      },
      {
        question: "What is the best time to undertake the Phulara Ridge Trek?",
        answer:
          "The best time to undertake the Phulara Ridge Trek is during the pre-monsoon (April to June) and post-monsoon (September to November) seasons. During these months, the weather is favorable, with clear skies and moderate temperatures, ensuring a comfortable trekking experience.",
      },
      {
        question: "How difficult is the Phulara Ridge Trek?",
        answer:
          "The Phulara Ridge Trek is considered to be of moderate difficulty. It involves some steep ascents and descents, but the trails are well-defined. Basic fitness and stamina are required, making it suitable for both novice and experienced trekkers.",
      },
      {
        question: "How long is the the Phulara Ridge Trek?",
        answer:
          "The trek typically spans 8 days, including travel time from Dehradun to the starting point and back. The actual trekking distance from Sankri to Phulara Ridge and back is approximately 25 kilometers (15.5 miles).",
      },
      {
        question: "What kind of accommodation is provided during the trek?",
        answer:
          "Accommodation includes guesthouses or lodges in Sankri and camping in tents at various points along the trek. YK Travels ensures comfortable stays with basic amenities to provide a good rest after trekking.",
      },
      {
        question: "What should I pack for the Phulara Ridge Trek?",
        answer: `
        Essential items to pack include:

- Warm clothing (layers, thermal wear, fleece jackets)
- Waterproof trekking shoes with good grip
- Rain gear (poncho or raincoat)
- Personal toiletries and medications
- Sunglasses, sunscreen, and hats
- Water bottles and energy snacks
- A small first-aid kit
- Trekking poles (optional)
- A backpack with a rain cover
        `,
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, travel insurance is highly recommended. It can cover medical emergencies, trip cancellations, and other unforeseen events during the trek.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "Essential items to pack include: \nWarm clothing (layers, thermal wear, fleece jackets) \n Waterproof trekking shoes with good grip \n Rain gear (poncho or raincoat) \n Personal toiletries and medications \n Sunglasses, sunscreen, and hats \n Water bottles and energy snacks \n A small first-aid kit \n Trekking poles (optional) \n A backpack with a rain cover",
      },
      {
        question: "What about drinking water during the trek?",
        answer:
          "We provide safe drinking water during the trek. It’s advisable to carry a reusable water bottle that you can refill at designated points.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Sankri and back. Safe and comfortable vehicles with experienced drivers are arranged for the journey.",
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, it is highly recommended to have travel insurance that covers trekking activities, medical emergencies, and evacuation.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Sankri and back. Safe and comfortable vehicles with experienced drivers are arranged for the journey.",
      },
      {
        question: "Are there age restrictions for the trek?",
        answer:
          "There are no specific age restrictions, but the trek is recommended for individuals above the age of 12 who are physically fit and able to handle the demands of trekking.",
      },
      {
        question: "What are the meal arrangements during the trek?",
        answer:
          "YK Travels provides nutritious vegetarian meals throughout the trek. Breakfast, lunch, and dinner are included in the package, and special dietary requirements can be accommodated with prior notice.",
      },
      {
        question: "Is drinking water available during the trek?",
        answer:
          "Yes, drinking water is available at campsites. Trekkers are advised to carry their own water bottles and purification tablets or filters for use during the trek.",
      },
      {
        question: "What permits are required for the Phulara Ridge Trek?",
        answer:
          "Trekkers need to obtain permits from the Forest Department to enter the trekking regions. YK Travels assists in obtaining all necessary permits and registrations as part of our service.",
      },
      {
        answer:
          "Basic medical facilities and first-aid are available at various points along the trek. However, it is advisable to carry a personal first-aid kit and any specific medications you may need. YK Travels’ guides are trained in first-aid and emergency response.",
        question: "Are there medical facilities available during the trek?",
      },
      {
        question: "What safety measures are in place during the trek?",
        answer:
          "YK Travels prioritizes the safety of all trekkers. Our experienced guides are trained in first aid and emergency response. We use high-quality trekking and camping equipment and ensure that all travel arrangements are secure and reliable.",
      },
      {
        question: "How can I book the Phulara Ridge Trek with YK Travels?",
        answer: `You can book the Phulara Ridge Trek through our website or by contacting us directly via phone or email. Our team will assist you with all the details and help you prepare for an unforgettable adventure.
          
For any additional questions or specific concerns, feel free to reach out to YK Travels. We’re here to ensure you have a safe, enjoyable, and memorable Phulara Ridge Trek experience.

`,
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d31589.596385802353!2d78.222325!3d31.027875000000005!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3908a18d7aaa8829%3A0x1bedabf5a174da4d!2sPhulara%20Ridge%20Start%20Point!5e1!3m2!1sen!2sin!4v1746017898441!5m2!1sen!2sin",
  },
  {
    id: 7,
    slug: "bali-pass-trek",
    title: "Bali Pass Trek",
    state: "Uttarakhand",
    shortDescription:
      "The Bali Pass Trek is a thrilling and adventurous high-altitude trek that takes you through the rugged beauty of the Garhwal Himalayas in Uttarakhand, India. This trek, known for its challenging terrain and breathtaking vistas, connects the picturesque Har Ki Dun Valley with the pristine Yamunotri Valley. With its combination of steep ascents, snow-capped peaks, and stunning alpine meadows, the Bali Pass Trek is perfect for experienced trekkers looking for an unforgettable adventure.",
    duration: "8 Days",
    price: 21999,
    activityTags: ["camping", "trekking", "hiking", "peak climbing"],
    overview: {
      title: "Trek Overview",
      description:
        "The Bali Pass Trek is a thrilling and adventurous high-altitude trek that takes you through the rugged beauty of the Garhwal Himalayas in Uttarakhand, India. This trek, known for its challenging terrain and breathtaking vistas, connects the picturesque Har Ki Dun Valley with the pristine Yamunotri Valley. With its combination of steep ascents, snow-capped peaks, and stunning alpine meadows, the Bali Pass Trek is perfect for experienced trekkers looking for an unforgettable adventure.",
      activities: [
        {
          title: "Stunning High-Altitude Pass",
          description:
            "Conquer the Bali Pass with panoramic views of Bandarpoonch, Swargarohini, and Black Peak ranges, where the challenging ascent rewards you with awe-inspiring scenery and a sense of accomplishment.",
          emoji: "🏞️",
        },
        {
          title: "Diverse Landscapes",
          description:
            "Traverse dense forests, quaint villages, lush meadows, and rocky moraines, each offering unique visual delights from verdant valleys to snow-laden paths.",
          emoji: "🌲",
        },
        {
          title: "Rich Cultural Experience",
          description:
            "Engage with local communities in the traditional villages of Sankri and Osla, experiencing their unique culture, hospitality, and rustic charm.",
          emoji: "🤝",
        },
        {
          title: "Har Ki Dun Valley",
          description:
            "Discover the Valley of Gods, Har Ki Dun, with stunning views of surrounding peaks, lush green meadows, and the serene Har Ki Dun River, perfect for nature lovers and photographers.",
          emoji: "🌄",
        },
        {
          title: "Yamunotri Shrine",
          description:
            "Conclude your trek at the sacred Yamunotri Temple, the source of the Yamuna River, a significant Char Dham pilgrimage site steeped in spiritual value.",
          emoji: "🛕",
        },
      ],
    },
    tripInfo: {
      accommodation: {
        text: "Alpine tents or Decathlon tents (4 - 2 sharing base)",
        icon: "building",
      },
      arrivalCity: {
        text: "Sankri",
        icon: "city",
      },
      bestSeason: {
        text: "May to June, September to December",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "16207 feet",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast + Lunch + Eve. Snacks + Dinner",
        icon: "bowl",
      },
      transportation: {
        text: "Bus, Taxi",
        icon: "bus",
      },
      pickupPoint: {
        text: "Dehradun, Sankri",
        icon: "car",
      },
    },

    cost: {
      includes: [
        "Accommodation:Shared tents in alpine or dome camps throughout the trek.",
        "Meals: From dinner on the first day until breakfast on the last, all meals are simple vegetarian fare (bed tea, breakfast, lunch, evening tea with snacks, and dinner)",
        "Snow Gear: Crampons (spikes for shoes) and gaiters for snow trekking.",
        "Guide :  Professional and experienced certified trek guide.",
        "Transportation: Roundtrip transportation from Dehradun to Dehradun.",
        `Bonus Offers: 
        (if you choose the package with these offers)
Free jacket or rucksack.
A free customized travel card worth Rs.500 will be delivered to your home after the trek.
50% discount on health check-ups and health insurance
        `,
      ],
      excludes: [
        "Anything not mentioned in the inclusions above.",
        "Any personal expenses during the trip or out of any emergency or alcoholic beverages.",
        "Any personal trekking gear (like a pole, shoes, cape, gloves, warm cap, bag, or jacket).",
        "Mules or porters to carry personal luggage.",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Dehradun to Sankri (Drive)",
        description:
          "The trek begins with a scenic 10-hour drive from Dehradun to Sankri, a charming village nestled amidst the Govind Wildlife Sanctuary. Savor breathtaking views of the Himalayas and surrounding peaks, such as Swargarohini. Overnight stay in hotels or homestays in Sankri.",
      },
      {
        day: 2,
        title: "Sankri to Seema (Drive & Trek)",
        description:
          "Take a morning drive to Taluka, the starting point of the trek. Enjoy a scenic hike through streams, bamboo forests, and deodar trees. Pass through charming villages like Ghangad, which have unique wooden architecture. Camp overnight near the Supin River at Seema.",
      },
      {
        day: 3,
        title: "Seema to Rainbasera (Trek)",
        description:
          "The trail ascends towards Debshu Bugyal, offering panoramic views of the Himalayas. Cross the Supin River and admire the surrounding landscape. Reach Rainbasera, a campsite with stunning views of the gushing river. Pitch your tents and enjoy the serenity of the Himalayas.",
      },
      {
        day: 4,
        title: "Rainbasera to Ruinsara Tal (Trek)",
        description:
          "Embark on a moderate trek to reach the beautiful Ruinsara Lake. The surrounding mountains, including Bandarpoonch and Dhumdhar Kandi Khai, are breathtakingly visible from the campsite. Explore the area around the lake and the nearby Yamunotri temple.",
      },
      {
        day: 5,
        title: "Ruinsara Tal to Odari (Trek)",
        description:
          "Today's shorter trek involves descents, river crossings, and ascents through meadows. Enjoy the gradual increase in altitude and the changing vegetation. Reach Odari, a campsite near a rock cave believed to have been the home of Lord Krishna's brother, Bali. Prepare for an instructional session from the trek leader to get you ready for higher climbs.",
      },
      {
        day: 6,
        title: "Odari to Bali Col Camp (Trek)",
        description:
          "This challenging day involves a steep climb through loose scree and rocky terrain. Ensure proper hydration and rest to avoid altitude sickness. Camp overnight at Bali Col Camp, preparing for the summit day.",
      },
      {
        day: 7,
        title: "Bali Col Camp to Lower Dhamni via Bali Pass (Trek)",
        description:
          "Conquer the Bali Pass, the highlight of the trek, offering stunning 360-degree views. Descend through a narrow, challenging path, following your guide's instructions carefully. Reach Lower Dhamni, a campsite nestled amidst forests.",
      },
      {
        day: 8,
        title: "Lower Dhamni to Dehradun (Trek & Drive)",
        description:
          "The final day involves an easy descent on a paved path to Yamunotri. Take a scenic drive back to Dehradun, marking the end of the adventure.",
      },
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/bali-pass/bali-pass-trek.jpg",
        alt: "Yk Travels Bali Pass Trek",
      },
      {
        id: 2,
        src: "/images/packages/bali-pass/bali-pass-2.jpg",
        alt: "Yk travesls Bali Pass Trek",
      },
    ],
    faqs: [
      {
        question: "What is the Phulara Ridge Trek?",
        answer:
          "The Bali Pass Trek is a high-altitude trekking route in the Garhwal Himalayas of Uttarakhand, India. This challenging trek takes you from the picturesque Har Ki Dun Valley to the sacred Yamunotri Valley, crossing the Bali Pass at an elevation of 4,800 meters (15,748 feet).",
      },
      {
        question: "What is the best time to undertake the Bali Pass Trek?",
        answer:
          "The best time to undertake the Bali Pass Trek is during the pre-monsoon (May to June) and post-monsoon (September to October) seasons. During these months, the weather is favorable, with clear skies and moderate temperatures, ensuring a comfortable trekking experience.",
      },
      {
        question: "How difficult is the Bali Pass Trek?",
        answer:
          "The Bali Pass Trek is considered difficult and is recommended for experienced trekkers. It involves steep ascents, high-altitude trekking, and walking on rugged and sometimes snow-covered terrain. Good physical fitness and prior trekking experience are essential.",
      },
      {
        question: "How long is the Bali Pass Trek?",
        answer:
          "The trek typically spans 8 days, including travel time from Dehradun to the starting point and back. The actual trekking distance varies but generally covers around 65-70 kilometers (40-43 miles) from Sankri to Yamunotri.",
      },
      {
        question: "What kind of accommodation is provided during the trek?",
        answer:
          "Accommodation includes guesthouses or lodges in Sankri and camping in tents at various points along the trek. YK Travels ensures comfortable stays with basic amenities to provide a good rest after trekking.",
      },
      {
        question: "What should I pack for the Bali Pass Trek?",
        answer: `
        Essential items to pack include:

- Warm clothing (layers, thermal wear, fleece jackets)
- Waterproof trekking shoes with good grip
- Rain gear (poncho or raincoat)
- Personal toiletries and medications
- Sunglasses, sunscreen, and hats
- Water bottles and energy snacks
- A small first-aid kit
- Trekking poles (optional)
- A backpack with a rain cover
        `,
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, travel insurance is highly recommended. It can cover medical emergencies, trip cancellations, and other unforeseen events during the trek.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "Essential items to pack include: \nWarm clothing (layers, thermal wear, fleece jackets) \n Waterproof trekking shoes with good grip \n Rain gear (poncho or raincoat) \n Personal toiletries and medications \n Sunglasses, sunscreen, and hats \n Water bottles and energy snacks \n A small first-aid kit \n Trekking poles (optional) \n A backpack with a rain cover",
      },
      {
        question: "What about drinking water during the trek?",
        answer:
          "We provide safe drinking water during the trek. It’s advisable to carry a reusable water bottle that you can refill at designated points.",
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, it is highly recommended to have travel insurance that covers trekking activities, medical emergencies, and evacuation.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Sankri and from Janki Chatti back to Dehradun. Safe and comfortable vehicles with experienced drivers are arranged for the journey.",
      },
      {
        question: "Are there age restrictions for the trek?",
        answer:
          "There are no specific age restrictions, but the trek is recommended for individuals above the age of 12 who are physically fit and able to handle the demands of trekking.",
      },
      {
        question: "What are the meal arrangements during the trek?",
        answer:
          "YK Travels provides nutritious vegetarian meals throughout the trek. Breakfast, lunch, and dinner are included in the package, and special dietary requirements can be accommodated with prior notice.",
      },
      {
        question: "Is drinking water available during the trek?",
        answer:
          "Yes, drinking water is available at campsites. Trekkers are advised to carry their own water bottles and purification tablets or filters for use during the trek.",
      },
      {
        question: "What permits are required for the Bali Pass Trek?",
        answer:
          "Trekkers need to obtain permits from the Forest Department to enter the trekking regions. YK Travels assists in obtaining all necessary permits and registrations as part of our service.",
      },
      {
        answer:
          "Basic medical facilities and first-aid are available at various points along the trek. However, it is advisable to carry a personal first-aid kit and any specific medications you may need. YK Travels’ guides are trained in first-aid and emergency response.",
        question: "Are there medical facilities available during the trek?",
      },
      {
        question: "What safety measures are in place during the trek?",
        answer:
          "YK Travels prioritizes the safety of all trekkers. Our experienced guides are trained in first aid and emergency response. We use high-quality trekking and camping equipment and ensure that all travel arrangements are secure and reliable.",
      },
      {
        question: "How can I book the Bali Pass Trek with YK Travels?",
        answer: `You can book the Bali Pass Trek through our website or by contacting us directly via phone or email. Our team will assist you with all the details and help you prepare for an unforgettable adventure.


          For any additional questions or specific concerns, feel free to reach out to YK Travels. We’re here to ensure you have a safe, enjoyable, and memorable Bali Pass Trek experience.


`,
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d13662.745469340593!2d78.342619!3d31.118448!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390621cce9db9633%3A0x9d8d10068b57906f!2sBali%20Pass%20Trek!5e0!3m2!1sen!2sin!4v1746034890355!5m2!1sen!2sin",
  },
  {
    id: 8,
    slug: "gaumukh-tapovan-trek",
    title: "Gaumukh Tapovan Trek",
    state: "Uttarakhand",
    shortDescription:
      "The Gaumukh Tapovan Trek, offered by YK Travels, is a captivating journey through the stunning landscapes of the Garhwal Himalayas. This trek takes you to the source of the holy Ganges River, known as Gaumukh, and further into the pristine meadows of Tapovan. Renowned for its spiritual significance and breathtaking scenery, the Gaumukh Tapovan Trek is a perfect blend of adventure, spirituality, and natural beauty.",
    duration: "3 Days",
    price: 15999,
    activityTags: ["camping", "trekking", "hiking", "peak climbing"],
    overview: {
      title: "Trek Overview",
      description:
        "The Gaumukh Tapovan Trek, offered by YK Travels, is a captivating journey through the stunning landscapes of the Garhwal Himalayas. This trek takes you to the source of the holy Ganges River, known as Gaumukh, and further into the pristine meadows of Tapovan. Renowned for its spiritual significance and breathtaking scenery, the Gaumukh Tapovan Trek is a perfect blend of adventure, spirituality, and natural beauty.",
      activities: [
        {
          title: "Gaumukh Glacier Trek",
          description:
            "Trek to Gaumukh, the snout of the Gangotri Glacier at 4,463 meters, where the sacred Bhagirathi River emerges, offering stunning glacier views and spiritual significance as a revered Hindu pilgrimage site.",
          emoji: "🧊",
        },
        {
          title: "Tapovan Meditation Meadow",
          description:
            "Reach Tapovan at 4,463 meters, a serene high-altitude meadow with lush greenery, crystal-clear streams, and spectacular views of Shivling and Bhagirathi peaks, ideal for meditation and reflection.",
          emoji: "🌾",
        },
        {
          title: "Panoramic Himalayan Scenery",
          description:
            "Enjoy breathtaking views of the Gangotri Glacier, Shivling peak, Bhagirathi peaks, and other majestic Himalayan mountains, accompanied by the serene Bhagirathi River along the trek.",
          emoji: "🏔️",
        },
        {
          title: "Spiritual Significance",
          description:
            "Experience the sacredness of Gaumukh, where a dip in the icy Bhagirathi waters is believed to cleanse the soul, and Tapovan, a tranquil haven frequented by sadhus for meditation.",
          emoji: "🙏",
        },
      ],
    },
    tripInfo: {
      accommodation: {
        text: "Alpine tents or Decathlon tents (4 - 2 sharing base)",
        icon: "building",
      },
      arrivalCity: {
        text: "Sankri",
        icon: "city",
      },
      bestSeason: {
        text: "May to June, September to December",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "16207 feet",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast + Lunch + Eve. Snacks + Dinner",
        icon: "bowl",
      },
      transportation: {
        text: "Bus, Taxi",
        icon: "bus",
      },
      pickupPoint: {
        text: "Dehradun, Sankri",
        icon: "car",
      },
    },

    cost: {
      includes: [
        "Accommodation: Shared tents in alpine or dome camps throughout the trek.",
        "Meals: From dinner on the first day until breakfast on the last, all meals are simple vegetarian fare (bed tea, breakfast, lunch, evening tea with snacks, and dinner)",
        "Snow Gear: Crampons (spikes for shoes) and gaiters for snow trekking.",
        "Guide :  Professional and experienced certified trek guide.",
        "Transportation: Roundtrip transportation from Dehradun to Dehradun.",
        `Bonus Offers: 
          (if you choose the package with these offers)
          `,
      ],
      excludes: [
        "Anything not listed above.",
        "Personal expenses during the trip or emergencies, including alcohol.",
        "Personal trekking gear (poles, shoes, cape, gloves, warm hat, backpack, jacket).",
        "Mules or porters to carry personal luggage.",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Dehradun to Gangotri (Drive):",
        description:
          "The journey begins in Dehradun with a morning departure (7 am) by bus or train. Enjoy a scenic 9-10 hour drive (242 km) to Gangotri, stopping at Gangnani, known for its natural hot springs. Overnight stay in a comfortable guesthouse at Gangotri.",
      },
      {
        day: 2,
        title: "Gangotri to Chirbasa (Trek):",
        description:
          "Embark on the first day's trek from Gangotri, following a well-defined trail along the roaring Bhagirathi River. Reach Chirbasa, a beautiful campsite nestled amidst the tree line, at 11,811 ft. Enjoy the rest of the day surrounded by stunning mountain views.",
      },
      {
        day: 3,
        title: "Chirbasa to Bhojbasa (Trek)",
        description:
          "The landscape transforms into a more barren and desolate terrain, resembling the Arctic Tundra. Trek through dense forests to reach Bhojbasa, a campsite with a small temple near the river. Spend the afternoon exploring the area and marveling at the majestic Bhagirathi peaks during sunset.",
      },
      {
        day: 4,
        title: "Bhojbasa to Tapovan via Gaumukh (Trek)",
        description:
          "Start the day with a breathtaking sunrise overlooking the Himalayas. After breakfast, embark on a challenging trek to Gaumukh (14,203 ft), the source of the mighty Ganges. Explore the sacred site and trek to Tapovan, a stunning high-altitude meadow. Ascend a steeper path, witnessing the surrounding peaks seemingly closer with each step. Tapovan's serene beauty makes it a popular meditation spot for spiritual seekers.",
      },
      {
        day: 5,
        title: "Tapovan to Bhojbasa",
        description:
          "Witness another spectacular sunrise painting the peaks in vibrant colors. Descend back to Bhojbasa for breakfast and enjoy the tranquility of the campsite.",
      },
      {
        day: 6,
        title: "Bhojbasa to Gangotri",
        description:
          "The last part of the journey is 15.5 km in length and returns you to Gangotri. Celebrate your accomplishments and enjoy a farewell dinner at the guesthouse.",
      },
      {
        day: 7,
        title: "Gangotri to Dehradun ",
        description:
          "After a final breakfast in Gangotri, depart for Dehradun by car. The scenic return journey takes approximately 9-10 hours, arriving in Dehradun by 8-9 pm.vvv",
      },
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/gau-tapo/go-trek-1.jpg",
        alt: "Yk Travels Gaumukh Tapovan Trek",
      },
      {
        id: 2,
        src: "/images/packages/gau-tapo/go-trek-2.jpeg",
        alt: "Yk travesls Gaumukh Tapovan Trek",
      },
    ],
    faqs: [
      {
        question: "What is the Gaumukh Tapovan Trek?",
        answer:
          "The Gaumukh Tapovan Trek is a high-altitude trek in the Garhwal Himalayas of Uttarakhand, India. It takes trekkers to the source of the Ganges River at Gaumukh and further to the stunning meadows of Tapovan, offering spectacular views of the Shivling and Bhagirathi peaks.",
      },
      {
        question: "What is the best time to go for the Gaumukh Tapovan Trek?",
        answer:
          "The best time to undertake the Gaumukh Tapovan Trek is from May to June and September to October. During these months, the weather is favorable, with clear skies and moderate temperatures, ensuring a comfortable trekking experience.",
      },
      {
        question: "How difficult is the Gaumukh Tapovan Trek?",
        answer:
          "The Gaumukh Tapovan Trek is considered a moderate to challenging trek. It involves steep ascents, glacier traverses, and high altitudes, requiring good physical fitness and some prior trekking experience.",
      },
      {
        question: "How long is the  Gaumukh Tapovan Trek?",
        answer:
          "The Gaumukh Tapovan Trek typically spans 10 days, including travel time from Dehradun to Gangotri and back. The trekking distance from Gangotri to Tapovan and back is approximately 46 kilometers (28.6 miles).",
      },
      {
        question: "What kind of accommodation is provided during the trek?",
        answer:
          "Accommodation during the trek includes guesthouses or lodges in Gangotri and camping in tents at Chirbasa, Bhojbasa, and Tapovan. YK Travels ensures comfortable and safe accommodation arrangements at each stop.",
      },
      {
        question: "What should I pack for the  Gaumukh Tapovan Trek?",
        answer: `
          Essential items to pack include:
  
  - Warm clothing (layers, thermal wear, fleece jackets)
  - Waterproof trekking shoes with good grip
  - Rain gear (poncho or raincoat)
  - Personal toiletries and medications
  - Sunglasses, sunscreen, and hats
  - Water bottles and energy snacks
  - A small first-aid kit
  - Trekking poles (optional)
  - Sleeping bag (provided by YK Travels if needed
          `,
      },
      {
        question: "Is it necessary to have travel insurance for the trek?",
        answer:
          "While not mandatory, travel insurance is highly recommended. It can cover medical emergencies, trip cancellations, and other unforeseen events during the trek.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Gangotri and back. Comfortable and safe vehicles with experienced drivers are arranged for the journey.",
      },
      {
        question: "What about drinking water during the trek?",
        answer:
          "We provide safe drinking water during the trek. It’s advisable to carry a reusable water bottle that you can refill at designated points.",
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, it is highly recommended to have travel insurance that covers trekking activities, medical emergencies, and evacuation.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Gangotri and back. Comfortable and safe vehicles with experienced drivers are arranged for the journey.",
      },
      {
        question: "Are there age restrictions for the trek?",
        answer:
          "There are no specific age restrictions, but the trek is recommended for individuals above the age of 12 who are physically fit and able to handle the demands of trekking.",
      },
      {
        question: "What are the meal arrangements during the trek?",
        answer:
          "YK Travels provides nutritious vegetarian meals throughout the trek. Breakfast, lunch, and dinner are included in the package, and special dietary requirements can be accommodated with prior notice.",
      },
      {
        question: "Is drinking water available during the trek?",
        answer:
          "Yes, drinking water is available at campsites. Trekkers are advised to carry their own water bottles and purification tablets or filters for use during the trek.",
      },
      {
        question: "What permits are required for the Gaumukh Tapovan Trek?",
        answer:
          "Trekkers need to obtain permits from the Forest Department to enter the trekking regions. YK Travels assists in obtaining all necessary permits and registrations as part of our service.",
      },
      {
        answer:
          "Basic medical facilities and first-aid are available at various points along the trek. However, it is advisable to carry a personal first-aid kit and any specific medications you may need. YK Travels’ guides are trained in first-aid and emergency response.",
        question: "Are there medical facilities available during the trek?",
      },
      {
        question: "What safety measures are in place during the trek?",
        answer:
          "YK Travels prioritizes the safety of all trekkers. Our experienced guides are trained in first aid and emergency response. We use high-quality trekking and camping equipment and ensure that all travel arrangements are secure and reliable.",
      },
      {
        question: "How can I book the Gaumukh Tapovan Trek with YK Travels?",
        answer: `You can book the Gaumukh Tapovan Trek through our website or by contacting us directly via phone or email. Our team will assist you with all the details and help you prepare for an unforgettable adventure.
  
  
            For any additional questions or specific concerns, feel free to reach out to YK Travels. We’re here to ensure you have a safe, enjoyable, and memorable Bali Pass Trek experience.
  `,
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d13662.745469340593!2d78.342619!3d31.118448!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390621cce9db9633%3A0x9d8d10068b57906f!2sBali%20Pass%20Trek!5e0!3m2!1sen!2sin!4v1746034890355!5m2!1sen!2sin",
  },

  {
    id: 9,
    slug: "chopta-chandrashila-trek",
    title: "Chopta Chandrashila Trek",
    state: "Uttarakhand",
    shortDescription:
      "The Chopta Chandrashila Trek, brought to you by YK Travels, is one of the most picturesque and exhilarating treks in the Garhwal Himalayas of Uttarakhand. This trek takes you through the enchanting landscapes of Chopta, often referred to as the “Mini Switzerland of India,” to the summit of Chandrashila, standing at an impressive 13,123 feet (4,000 meters). The trek also includes a visit to the sacred Tungnath Temple, the highest Shiva temple in the world, adding a spiritual dimension to this adventure.",
    duration: "3 Days",
    price: 5999,
    activityTags: [
      "camping",
      "trekking",
      "hiking",
      "peak climbing",
      "star-gazing",
    ],
    overview: {
      title: "Trek Overview",
      description:
        "The Chopta Chandrashila Trek, brought to you by YK Travels, is one of the most picturesque and exhilarating treks in the Garhwal Himalayas of Uttarakhand. This trek takes you through the enchanting landscapes of Chopta, often referred to as the “Mini Switzerland of India,” to the summit of Chandrashila, standing at an impressive 13,123 feet (4,000 meters). The trek also includes a visit to the sacred Tungnath Temple, the highest Shiva temple in the world, adding a spiritual dimension to this adventure.",
      activities: [
        {
          title: "Stunning Scenery",
          description:
            "Marvel at breathtaking views of Himalayan peaks like Nanda Devi, Trishul, Kedar Peak, Bandarpunch, and Chaukhamba, with trails winding through dense pine, oak, and rhododendron forests, verdant meadows, and panoramic vistas.",
          emoji: "🌄",
        },
        {
          title: "Tungnath Temple",
          description:
            "Visit Tungnath, the highest Shiva temple in the world at 12,073 feet, an ancient 1,000-year-old site offering a serene and spiritual experience amidst the stunning Himalayan backdrop.",
          emoji: "🛕",
        },
        {
          title: "Chandrashila Summit",
          description:
            "Reach the Chandrashila summit for a 360-degree panoramic view of Himalayan peaks, with a magical sunrise over snow-capped mountains, creating an unforgettable high-altitude experience.",
          emoji: "🌅",
        },
        {
          title: "Rich Biodiversity",
          description:
            "Explore regions teeming with flora and fauna, including vibrant rhododendron blooms in spring and a variety of bird species, making the trek a paradise for bird watchers and nature enthusiasts.",
          emoji: "🐾",
        },
      ],
    },
    tripInfo: {
      accommodation: {
        text: "Alpine tents or Decathlon tents (4 - 2 sharing base)",
        icon: "building",
      },
      arrivalCity: {
        text: "Delhi, Chopta",
        icon: "city",
      },
      bestSeason: {
        text: "All Around Year",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "13,123 feet (4,000 meters)",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast + Lunch + Eve. Snacks + Dinner",
        icon: "bowl",
      },
      transportation: {
        text: "Bus, Taxi",
        icon: "bus",
      },
    },

    cost: {
      includes: [
        "Accommodation in Swiss Camp at Chopta and Guest House at Sari on Triple/Quad/Double",
        "Meals include from Day 1 dinner to last day breakfast. Meals will be simple, nourishing, hot, fresh, and vegetarian.",
        "Morning & Evening refreshments at the stays",
        "Professional and experienced staff to cater for your needs for the whole Trip",
        `Neat and clean vehicle for the journey with an experienced driver, especially for hilly terrains
          `,
        "Parking fees and other tolls and taxes, wherever payable.",
      ],
      excludes: [
        "Anything not mentioned in the inclusions above.",
        "Any personal expenses during the trip or out of any emergency/ Alcoholic beverages.",
        "Any personal gears",
        "Mules or porters to carry personal luggage.",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Delhi To Rishikesh (Drive: 230 km, Duration: 5 hrs)",
        description:
          "We will pick you up from Botanical Garden metro station, Delhi, at 9 P.M",
      },
      {
        day: 2,
        title:
          "Rishikesh To Chopta (Drive: 220 km, Duration: 9 hrs, Altitude: 8,790 ft)",
        description:
          "Summit Ascent: Wake up early to a refreshing mountain morning and start your ascent to the Nag Tibba summit. The trail offers breathtaking views and diverse landscapes, from dense forests to open meadows. Summit Experience: Upon reaching the summit, relish the panoramic views of the snow-capped Himalayan peaks. Spend some time soaking in the magnificent scenery, taking photographs, and enjoying the sense of accomplishment. Descent: After savoring the summit experience, begin your descent back to the base camp. Enjoy a relaxed evening and a hearty meal at the camp.",
      },
      {
        day: 3,
        title:
          "Chopta To Chandrashila Via Tungnath & Back To Chopta (Trek: 10 km, Duration: 6-8 hrs, Altitude: 12,000 ft)",
        description:
          "Rise to today's mountain morning; this will be a tough day; we have much to look forward to! Today, we begin our trek, an entire of tales and an extended walk up a steep gradient. During this 6 km long trek, you shall experience the land of legends. At 3,680 mts, we will be at Tungnath, one of the Panch Kedars; Tungnath is the highest Shiv Temple in the world. This gives you another reason why you should not miss out on this one! Just trek for a few more km, and we shall reach the highest destination of our trek, Chandrashila Peak. From here, we get a 180-degree view of higher Himalayan peaks such as - Nanda Devi, Bandarpunch, Chaukhamba, and Kedar Peak. Later, we will start our descent back to Chopta. Dinner and overnight stay at Chopta.",
      },
      {
        day: 4,
        title:
          "Chopta To Sari To Deoria Tal And Back To Delhi (Trek: 2.5 km, Drive: 450 km, Duration: 14 hrs, Altitude: 8,956 ft)",
        description:
          "After breakfast, we will move towards Sari and then the 2.5 km-long Deoriatal trek. We will proceed on our journey to Delhi. The Expected time of arrival in Delhi will be 5 a.m",
      },
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/chopta-trek/chopta-1.jpg",
        alt: "Yk Travels Chopta Chandrashila Trek",
      },
      {
        id: 2,
        src: "/images/packages/chopta-trek/chopta-2.jpg",
        alt: "Yk travesls Chopta Chandrashila Trek",
      },
      {
        id: 3,
        src: "/images/packages/chopta-trek/chopta-3.jpg",
        alt: "Yk travesls Chopta Chandrashila Trek",
      },
      {
        id: 4,
        src: "/images/packages/chopta-trek/chopta-4.jpg",
        alt: "Yk travesls Chopta Chandrashila Trek",
      },
      {
        id: 5,
        src: "/images/packages/chopta-trek/chopta-5.jpg",
        alt: "Yk travesls Chopta Chandrashila Trek",
      },
      {
        id: 6,
        src: "/images/packages/chopta-trek/chopta-6.jpg",
        alt: "Yk travesls Chopta Chandrashila Trek",
      },
    ],
    faqs: [
      {
        question:
          "What is the maximum altitude of the Chopta Chandrashila Trek?",
        answer:
          "The Chopta Chandrashila Trek reaches a maximum altitude of 13,123 feet (4,000 meters) at the Chandrashila summit.",
      },
      {
        question: "How difficult is the Chopta Chandrashila Trek?",
        answer:
          "The Chopta Chandrashila Trek is considered a moderate trek. It is suitable for beginners with a reasonable level of fitness as well as experienced trekkers looking for a short and rewarding adventure.",
      },
      {
        question: "What is the best time to do the Chopta Chandrashila Trek?",
        answer: `
          The best time to undertake the Chopta Chandrashila Trek is:

- Spring (March to April): For blooming rhododendrons and pleasant weather.
- Summer (May to June): For clear skies and moderate temperatures.
- Autumn (September to November): For crisp air and excellent visibility of the Himalayan peaks.
- Winter (December to February): For a snowy landscape and a winter trekking experience.
          `,
      },
      {
        question: "How long does the Chopta Chandrashila Trek take?",
        answer:
          "The Chopta Chandrashila Trek typically takes 3 days to complete.",
      },
      {
        question: "What kind of accommodation is provided during the trek?",
        answer:
          "Accommodation during the trek includes guesthouses or lodges in Gangotri and camping in tents at Chirbasa, Bhojbasa, and Tapovan. YK Travels ensures comfortable and safe accommodation arrangements at each stop.",
      },
      {
        question: "What should I pack for the  Trek?",
        answer: `
          Essential items to pack include:

- Comfortable trekking shoes
- Warm clothing (layers, thermal wear, jacket)
- Rain gear (if trekking in monsoon season)
- Sun protection (hat, sunglasses, sunscreen)
- Personal toiletries and a first-aid kit
- Water bottle and snacks
- Trekking poles (optional but recommended)
- Backpack
          `,
      },
      {
        question: "Is it necessary to have travel insurance for the trek?",
        answer:
          "While not mandatory, travel insurance is highly recommended. It can cover medical emergencies, trip cancellations, and other unforeseen events during the trek.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Gangotri and back. Comfortable and safe vehicles with experienced drivers are arranged for the journey.",
      },
      {
        question: "What about drinking water during the trek?",
        answer:
          "We provide safe drinking water during the trek. It’s advisable to carry a reusable water bottle that you can refill at designated points.",
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, it is highly recommended to have travel insurance that covers trekking activities, medical emergencies, and evacuation.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer: `
          YK Travels provides flexible transportation options based on the package selection of the traveler:

- Delhi to Delhi: Transportation from Delhi to Chopta and back.

          `,
      },
      {
        question: "Are there age restrictions for the trek?",
        answer:
          "There are no specific age restrictions, but the trek is recommended for individuals above the age of 12 who are physically fit and able to handle the demands of trekking.",
      },
      {
        question: "Is there a provision for meals during the trek?",
        answer:
          "Yes, meals are provided during the trek. This usually includes breakfast, lunch, and dinner. Vegetarian options are available, and we can accommodate dietary restrictions if informed in advance.",
      },
      {
        question: "Is drinking water available during the trek?",
        answer:
          "Yes, drinking water is available at campsites. Trekkers are advised to carry their own water bottles and purification tablets or filters for use during the trek.",
      },
      {
        question: "Are permits required for the Chopta Chandrashila Trek?",
        answer:
          "Permits may be required for trekking in certain areas. Please check with YK Travels or local authorities to ensure you have the necessary permits before starting the trek.",
      },
      {
        answer:
          "Basic medical facilities and first-aid are available at various points along the trek. However, it is advisable to carry a personal first-aid kit and any specific medications you may need. YK Travels’ guides are trained in first-aid and emergency response.",
        question: "Are there medical facilities available during the trek?",
      },
      {
        question: "What safety measures are in place during the trek?",
        answer:
          "YK Travels prioritizes the safety of all trekkers. Our experienced guides are trained in first aid and emergency response. We use high-quality trekking and camping equipment and ensure that all travel arrangements are secure and reliable.",
      },
      {
        question:
          "How can I book the Chopta Chandrashila Trek with YK Travels?",
        answer: `
        You can book the Chopta Chandrashila Trek through our website or by contacting us directly via phone or email. Our team will assist you with all the details and help you prepare for an unforgettable trekking experience.

For any additional questions or specific concerns, feel free to reach out to YK Travels. We’re here to ensure you have a safe, enjoyable, and memorable trek to Chopta Chandrashila.
        `,
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1760419.226771019!2d79.183896!3d30.483727!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390833e2778d4655%3A0x15488517b688db75!2sChopta%20chandrashila%20Trek!5e0!3m2!1sen!2sin!4v1746068265938!5m2!1sen!2sin",
  },
  {
    id: 10,
    slug: "pangarchulla-trek",
    title: "Pangarchulla Trek",
    state: "Uttarakhand",
    shortDescription:
      "The Pangarchulla Trek, curated by YK Travels, is an exhilarating adventure through the enchanting Garhwal Himalayas in Uttarakhand. Known for its challenging yet rewarding terrain, this trek takes you to the summit of Pangarchulla Peak, which stands at an impressive altitude of 15,069 feet (4,593 meters). The trek offers an incredible mix of dense forests, expansive meadows, and panoramic views of some of the most majestic Himalayan peaks, making it a perfect choice for seasoned trekkers and adventure enthusiasts.",
    duration: "6 Days",
    price: 9999,
    activityTags: ["camping", "trekking", "hiking", "peak climbing"],
    overview: {
      title: "Trek Overview",
      description:
        "The Pangarchulla Trek, curated by YK Travels, is an exhilarating adventure through the enchanting Garhwal Himalayas in Uttarakhand. Known for its challenging yet rewarding terrain, this trek takes you to the summit of Pangarchulla Peak, which stands at an impressive altitude of 15,069 feet (4,593 meters). The trek offers an incredible mix of dense forests, expansive meadows, and panoramic views of some of the most majestic Himalayan peaks, making it a perfect choice for seasoned trekkers and adventure enthusiasts.",
      activities: [
        {
          title: "Stunning Panoramic Views",
          description:
            "Witness breathtaking 360-degree views of Himalayan peaks like Nanda Devi, Chaukhamba, Hathi-Ghoda, Neelkanth, and Mana from the summit, making the strenuous climb a rewarding experience.",
          emoji: "🌄",
        },
        {
          title: "Challenging Terrain",
          description:
            "Navigate diverse terrains including lush forests, rocky ridges, snowy paths, and boulder-strewn ascents to Pangarchulla Peak, offering a thrilling adventure for thrill-seekers.",
          emoji: "🥾",
        },
        {
          title: "Diverse Flora and Fauna",
          description:
            "Trek through oak, rhododendron, and pine forests teeming with diverse flora and fauna, with vibrant rhododendron blooms in spring adding color to the landscape.",
          emoji: "🐾",
        },
        {
          title: "Camping Experience",
          description:
            "Camp at scenic locations like Gulling and Khullara, immersing in nature’s serenity under clear, star-filled night skies for a memorable outdoor experience.",
          emoji: "🏕️",
        },
      ],
    },
    tripInfo: {
      accommodation: {
        text: "Alpine tents or Decathlon tents (4 - 2 sharing base)",
        icon: "building",
      },
      arrivalCity: {
        text: "Joshimath",
        icon: "city",
      },
      bestSeason: {
        text: "Spring (April to June): Pleasant weather, blooming rhododendrons, and clear views. Autumn (September to November): Crisp air, clear skies, and vibrant landscapes.",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "15,069 feet (4,593 meters)",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast + Lunch + Eve. Snacks + Dinner",
        icon: "bowl",
      },
      transportation: {
        text: "Bus, Taxi",
        icon: "bus",
      },
      pickupPoint: {
        text: "Rishikesh, Joshimath",
        icon: "car",
      },
    },

    cost: {
      includes: [
        "Accommodation in alpine and dome camps on a sharing basis.",
        "Meals include dinner from Day 1 to Breakfast on the Last Day. They will be simple and primarily vegetarian (Bed Tea, Breakfast, Lunch, Evening Tea and snacks, and Dinner).",
        "Crampons (spikes for shoes) and Gaitors for snow trekking.",
        "Professional and experienced certified trek guide.",
        "Transportation from Rishikesh to Rishikesh.",
        "Free Jacket or Rucksack if opted offer package.",
        "Free customized printed travel card worth Rs.500, delivery to home address post trek completion.",
        "50% Off on Health check-ups and Health insurance.",
      ],
      excludes: [
        "Anything not mentioned in the inclusions above.",
        "Any personal expenses during the trip or out of any emergency/ Alcoholic beverages.",
        "Any personal trekking gear (such as a pole, shoes, cape, gloves, warm cap, bag, or jacket).",
        "Mules or porters to carry personal luggage.",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    itinerary: [
      {
        day: 1,
        title:
          "Rishikesh to Joshimath/Pipalkoti (Drive: 252m, Duration: 10–11 hours, Altitude: 6,700 ft)",
        description:
          "The trekkers will be picked up from the Rishikesh bus stand at 7:00 a.m., where the journey begins. Dehradun is well connected to Delhi by railway and road routes. Overnight buses and trains are available. You can also take a flight to Dehradun, a 280-kilometer road ride to Joshimath from Dehradun, crossing the cities of Rishikesh, Devprayag, and Rudraprayag. Breakfast and lunch during the trip will be provided in the roadside inns. The individual will pay for the meal during the trip. Arrival at Joshimath/Pipalkoti town and accommodation in a guest house or hotel. A quick introduction and briefing session with the trek leader and fellow travellers follow dinner and rest at the guesthouse or hotel.",
      },
      {
        day: 2,
        title:
          "Joshimath/Pipalkoti to Gulling Campsite Via Tugasi Village (Drive: 12 km, Trek: 7 km, Duration: 4-5 hours, Altitude: 9,600 ft)",
        description:
          "After Breakfast, drive on a 12-kilometre route to Tugasi village from Pipalkoti. The trek will begin in Dhak village. The trail from Tugasi is a gradual ascent and comprises dusty and rocky patches. Lunch and refreshments will be served midway between Tugasi village and Guling Campsite. Arrival at the campsite is followed by evening refreshments, dinner, and rest in the camps.",
      },
      {
        day: 3,
        title:
          "Gulling Campsite to Khulara Campsite (Trek: 6 km, Duration: 5–6 hours, Altitude: 11,100 ft)",
        description:
          "Early morning trek to Khulara after Breakfast, covering a distance of 5 km. Khulara is a vast campsite surrounded by coniferous forests of oak, pine, and rhododendrons, accompanied by water streams all over the campsite area. The trek to Khulara from Gulling is a moderate ascent. Arrival at the campsite until the late lunch hour. Rest at the camp after lunch. An evening acclimatization walk to gear up for the next big day. Evening refreshments post-acclimatization walk. Dinner and rest at the campsite.",
      },
      {
        day: 4,
        title:
          "Khulara to Pangarchulla Summit and Back to Khulara Campsite (Trek: 12 km, Duration: 10–11 hours, Altitude: 15,516 ft)",
        description:
          "After breakfast, we will move towards Sari and then the 2.5 km-long Deoriatal trek. We will proceed on our journey to Delhi. The Expected time of arrival in Delhi will be 5 a.m",
      },
      {
        day: 5,
        title:
          "Khulara Campsite to Joshimath/Pipalkoti (Trek: 10 km, Duration: 4-5 hours, Drive: 12 km, Altitude: 6,700 ft)",
        description:
          "Trek Down to Pipalkoti from Khulara campsite via Guling and Tugasi Village after Breakfast. Trek till Tugasi village, followed by a 12-kilometre drive to Joshimath/Pipalkotifrom Tugasi. Lunch at Pipalkoti. Halt at a guest house or hotel in Joshimath or Pipalkoti. Dinner at Joshimath/Pipalkoti.",
      },
      {
        day: 6,
        title:
          "Departure from Joshimath/Pipalkoti to Rishikesh after Breakfast. Arrival at Rishikesh at night by 9:00 p.m",
        description:
          "Departure from Joshimath/Pipalkoti to Rishikesh after Breakfast. Arrival at Rishikesh at night by 9:00 p.m",
      },
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/pangarchulla-trek/p-1.jpeg",
        alt: "Yk Travels Pangarchulla Trek",
      },
      {
        id: 2,
        src: "/images/packages/pangarchulla-trek/p-2.jpeg",
        alt: "Yk travesls Pangarchulla Trek",
      },
    ],
    faqs: [
      {
        question: "How difficult is the Pangarchulla Trek?",
        answer:
          "The Pangarchulla Trek is considered a moderate to challenging trek. It requires a good level of fitness and prior trekking experience due to the steep ascents, rocky terrain, and possible snow conditions.",
      },
      {
        question: "What is the best time to do the Pangarchulla Trek?",
        answer: `
The best time to undertake the Pangarchulla Trek is during the pre-monsoon (April to June) and post-monsoon (September to November) seasons:

- Spring (April to June): For pleasant weather, blooming rhododendrons, and clear views.
- Autumn (September to November): For crisp air, clear skies, and vibrant landscapes.
          `,
      },
      {
        question: "How long does the Pangarchulla Trek take?",
        answer:
          "The Pangarchulla Trek typically takes 6 days to complete, including travel to and from the starting point at Joshimath.",
      },
      {
        question: "What kind of accommodation is provided during the trek?",
        answer:
          "Accommodation during the trek typically includes camping in tents at designated campsites such as Gulling and Khullara. Basic amenities such as sleeping bags and mats are provided.",
      },
      {
        question: "What should I pack for the  Trek?",
        answer: `
        The right gear and equipment are crucial for a successful and safe Pangarchulla trek in the Himalayas. Here’s a comprehensive list of things to carry for the trek:

- Back Pack (55 L to 65 L)
- Warm clothes (5–6 layers)
- 7-8 pairs of socks
- One pair of gloves
- 1 Warm Jacket
- 1 Pair Warm Cap
- Hiking Shoes
- Raincoat or Poncho
- Toilet Papers
- Water Bottle (thermal water for the snow trek)
- Snacks like dry fruits, energy bars, candies, etc.
- Power bank or charger
- Sunscreen, lip guard, or moisturizer
- Torch
- One valid ID proof
- Tiffin Box

Adjust your packing list based on the season and weather conditions during your Pangarchulla trek. It’s essential to pack efficiently while ensuring you have everything you need to stay comfortable and safe during your adventure in the Himalayas.
          `,
      },
      {
        question: "Is it necessary to have travel insurance for the trek?",
        answer:
          "While not mandatory, travel insurance is highly recommended. It can cover medical emergencies, trip cancellations, and other unforeseen events during the trek.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Gangotri and back. Comfortable and safe vehicles with experienced drivers are arranged for the journey.",
      },
      {
        question: "What about drinking water during the trek?",
        answer:
          "We provide safe drinking water during the trek. It’s advisable to carry a reusable water bottle that you can refill at designated points.",
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, it is highly recommended to have travel insurance that covers trekking activities, medical emergencies, and evacuation.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer: `
YK Travels provides flexible transportation options based on the package selection of the traveler:

- Rishikesh to Rishikesh: Transportation from Dehradun to Joshimath and back.
- Joshimath to Joshimath: For those who arrange their own transport to Joshimath, we start and end the trek at Joshimath.

`,
      },
      {
        question: "Are there age restrictions for the trek?",
        answer:
          "There are no specific age restrictions, but the trek is recommended for individuals above the age of 12 who are physically fit and able to handle the demands of trekking.",
      },
      {
        question: "Is there a provision for meals during the trek?",
        answer:
          "Yes, meals are provided during the trek. This usually includes breakfast, lunch, and dinner. Vegetarian options are available, and we can accommodate dietary restrictions if informed in advance.",
      },
      {
        question: "Is drinking water available during the trek?",
        answer:
          "Yes, drinking water is available at campsites. Trekkers are advised to carry their own water bottles and purification tablets or filters for use during the trek.",
      },
      {
        question: "Are permits required for the Chopta Chandrashila Trek?",
        answer:
          "Permits may be required for trekking in certain areas. Please check with YK Travels or local authorities to ensure you have the necessary permits before starting the trek.",
      },
      {
        answer:
          "Basic medical facilities and first-aid are available at various points along the trek. However, it is advisable to carry a personal first-aid kit and any specific medications you may need. YK Travels’ guides are trained in first-aid and emergency response.",
        question: "Are there medical facilities available during the trek?",
      },
      {
        question: "What safety measures are in place during the trek?",
        answer:
          "YK Travels prioritizes the safety of all trekkers. Our experienced guides are trained in first aid and emergency response. We use high-quality trekking and camping equipment and ensure that all travel arrangements are secure and reliable.",
      },
      {
        question: "How can I book the Pangarchulla Trek with YK Travels?",
        answer: `

        You can book the Pangarchulla Trek through our website or by contacting us directly via phone or email. Our team will assist you with all the details and help you prepare for an unforgettable trekking experience.
        For any additional questions or specific concerns, feel free to reach out to YK Travels. We’re here to ensure you have a safe, enjoyable, and memorable trek to Pangarchulla Peak.
    `,
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d55046.79425968498!2d79.591401!3d30.424062000000003!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39a7770fac899e5d%3A0xd8ffae4096ffaf26!2sPangarchulla%20Peak!5e0!3m2!1sen!2sin!4v1746073071144!5m2!1sen!2sin",
  },
  {
    id: 11,
    slug: "char-dham-yatra",
    title: "Char Dham Yatra",
    state: "Uttarakhand",
    shortDescription:
      "The Char Dham Yatra, organized by YK Travels, is a deeply spiritual journey to the four sacred shrines nestled in the majestic Garhwal Himalayas of Uttarakhand. This pilgrimage circuit, comprising Yamunotri, Gangotri, Kedarnath, and Badrinath, is one of the most revered in Hinduism. It offers not only a profound religious experience but also an opportunity to explore the stunning natural beauty and rich cultural heritage of the Himalayas. Each dham holds immense religious significance, and the journey to these sacred sites is considered a path to salvation and spiritual fulfillment.",
    duration: "10 Days",
    price: 22000,
    activityTags: ["camping", "trekking", "hiking", "peak climbing"],
    overview: {
      title: "Trek Overview",
      description:
        "The Char Dham Yatra, organized by YK Travels, is a deeply spiritual journey to the four sacred shrines nestled in the majestic Garhwal Himalayas of Uttarakhand. This pilgrimage circuit, comprising Yamunotri, Gangotri, Kedarnath, and Badrinath, is one of the most revered in Hinduism. It offers not only a profound religious experience but also an opportunity to explore the stunning natural beauty and rich cultural heritage of the Himalayas. Each dham holds immense religious significance, and the journey to these sacred sites is considered a path to salvation and spiritual fulfillment.",
      activities: [
        {
          title: "Yamunotri Pilgrimage",
          description:
            "Visit Yamunotri at 3,293 meters, the source of the Yamuna River, to worship at the temple dedicated to Goddess Yamuna, explore Surya Kund's thermal springs, Divya Shila, and trek through scenic forests and meadows.",
          emoji: "🛕",
        },
        {
          title: "Gangotri Sacred Trek",
          description:
            "Trek to Gangotri at 3,100 meters, the origin of the holy Ganga River, to visit the Goddess Ganga temple, see Bhagirath Shila, the submerged Shivlinga, and journey toward Gaumukh Glacier.",
          emoji: "🙏",
        },
        {
          title: "Kedarnath Jyotirlinga",
          description:
            "Reach Kedarnath at 3,583 meters, home to one of Lord Shiva’s twelve Jyotirlingas, with a rewarding trek from Gaurikund, the ancient temple near the Mandakini River, and stunning views of Kedarnath Dome.",
          emoji: "🕉️",
        },
        {
          title: "Badrinath Divine Visit",
          description:
            "Explore Badrinath at 3,133 meters, a key Char Dham site dedicated to Lord Vishnu, featuring the temple along the Alaknanda River, Tapt Kund hot springs, and the majestic Neelkanth Peak.",
          emoji: "🏞️",
        },
      ],
    },
    tripInfo: {
      accommodation: {
        text: "Alpine tents or Decathlon tents (4 - 2 sharing base)",
        icon: "building",
      },
      arrivalCity: {
        text: "Haridwar",
        icon: "city",
      },
      bestSeason: {
        text: "Summer (April to June): Pleasant weather, making it ideal for travel and trekking. Autumn (September to November): Clear skies and moderate temperatures, providing excellent visibility and comfortable conditions.",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "11,755 feet (3,583 meters)",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast + Lunch + Eve. Snacks + Dinner",
        icon: "bowl",
      },
      transportation: {
        text: "Bus, Taxi",
        icon: "bus",
      },
      pickupPoint: {
        text: "Haridwar, Dehradun, Delhi, or as per req.",
        icon: "car",
      },
    },

    cost: {
      includes: [
        "Accommodation on Twin or Double / Triple Sharing as per your preferences.",
        "Inclusive of transport services for 8 Nights & 9 Days (Ex Haridwar).",
        "All Breakfast & Dinner.",
        "Transportation from Haridwar to Haridwar (for the designated meeting point). Conditioning does not work in the hills area.",
        "All toll, tax, parking and driver allowances are included.",
        "Free Jacket or Rucksack if opted offer package.",
        "Free customized printed travel card worth Rs.500, delivery to home address post trek completion.",
        "50% Off on Health check-ups and Health insurance.",
      ],
      excludes: [
        "Helicopter Tickets.",
        "Anything not mentioned in the inclusions above.",
        "Any personal expenses during the trip or out of any emergency/ Alcoholic beverages.",
        "Any personal trekking gear (such as a pole, shoes, cape, gloves, warm cap, bag, or jacket).",
        "Mules or porters to carry personal luggage.",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    itinerary: [
      {
        day: 1,
        title:
          "Haridwar to Barkot (Drive: 210 km, Duration: 7-8 hrs, Altitude: 4,002 ft)",
        description:
          "Meet our driver at Haridwar Station, Bus Stand, or Hotel at 8 p.m. and drive for Barkot via Mussoorie, Kempty Fall in Mussoorie. Later, drive straight to Barkot and check in at your pre-booked hotel. Overnight stay at Barkot.",
      },
      {
        day: 2,
        title:
          "Barkot to Yamunotri and Back to Barkot (Drive: 60 km, Trek: 14 km, Duration: 5 hours, Altitude: 10,803 ft)",
        description:
          "Early in the morning, after breakfast, drive to Jankichatti or Phoolchatti and start your trek for Yamunotri (7 km) (either by walking, by horse, or by Doli at your own cost). After bathing in Jamunabai Kund's warm water and having `Darshan` of `Yamunaji,` return to Jankichatti by trek. Later, drive towards Barkot. Overnight stay at Barkot.",
      },
      {
        day: 3,
        title:
          "Barkot to Uttarkashi (drive: 100 km, duration: 4 hours, altitude: 3,799 ft)",
        description:
          "After breakfast: Drive to Uttarkashi. On the way, visit Shiv Gufa. Visit Kashi Vishwanath Temple in Uttarkashi city. Check in to the hotel. The day is free for rest. Overnight stay in Uttarkashi.",
      },
      {
        day: 4,
        title:
          "Uttarkashi to Gangotri and Back to Uttarkashi (Drive: 120 km, Duration: 4 hours, Altitude: 10,498 ft)",
        description:
          "After breakfast, drive to Gangotri; en route to Gangnani, take a holy dip in Garam Kund; further drive to Gangotri via the beautiful Harsil Valley. Harsil is famous for its natural beauty and majestic views of the deodar trees, mountains, and apple gardens. On arrival at Shree Gangotri, take a holy dip in the sacred Ganges, also called Bhagirathi at its origin. Perform Pooja and Darshan, and relax for some time in the lovely surroundings. Later, drive back to Uttarkashi. Overnight stay in Uttarkashi.",
      },
      {
        day: 5,
        title:
          "Uttarkashi to Guptkashi (drive: 220 km, duration: 8–9 hours, altitude: 10,498 ft)",
        description:
          "After breakfast, drive to Guptkashi via Moolgarh and Lambgaon. En route, you can see the beautiful river Mandakini at Tilwara. The Mandakini River comes from Kedarnath. Drive alongside the river to reach Guptkashi. Visit Ardhnarishwar Temple in Guptkashi. Check into the hotel upon arrival in Guptkashi. Overnight stay at Guptkashi.",
      },
      {
        day: 6,
        title:
          "Guptkashi to Kedarnath (drive: 30 km, duration: 2-3 hours, trek: 22 km, altitude: 11,755 ft)",
        description:
          "After a fulfilling breakfast, commence your pilgrimage by driving towards Sonprayag, the gateway to Kedarnath. Depending on your chosen mode of transport, prepare for an early start to make the most of the day's spiritual offerings. Trekking to Kedarnath: For adventurers seeking an authentic experience, embark on a memorable trek spanning approximately 22 kilometres to reach Kedarnath. Upon reaching Sonprayag, board a local transport to Gaurikund, available for a nominal fee of Rs. 50. From Gaurikund, commence your trek amidst the breathtaking Himalayan landscape, culminating in the divine sanctuary of Kedarnath. Pay homage at the sacred Temple and immerse yourself in the serene ambience of this hallowed site. Helicopter Ride to Kedarnath: Alternatively, opt for a convenient helicopter ride for a swift journey to Kedarnath. Our dedicated driver will escort you to the pre-booked helipad, ensuring a seamless transition to your aerial adventure. Following your darshan at the Temple, please return to the helipad, where our car will be waiting to transfer you back to your hotel for a well-deserved rest. Day 6 presents an opportunity for spiritual rejuvenation as you pay homage to Lord Shiva at the sacred abode of Kedarnath. Whether trekking through the rugged terrain or soaring above the majestic peaks, each moment is infused with devotion and reverence. Embrace the divine energy of Kedarnath and create memories that will resonate with the echoes of eternity.",
      },
      {
        day: 7,
        title:
          "Kedarnath to Guptkashi (drive: 30 km, duration: 2-3 hours, trek: 22 km, altitude: 11,102 ft)",
        description:
          "After savouring a nourishing breakfast, begin your journey from Kedarnath towards Guptkashi. Take a moment to absorb the tranquil ambience of the temple town before embarking on your return journey. Trekking Back to Guptkashi: For those seeking an immersive experience, set out on a scenic trek 22 kilometres from Kedarnath to Guptkashi. Your journey begins with a drive to Sonprayag, where you'll board a local jeep to Gaurikund, a short ride for which you'll need to pay Rs. 50. From Gaurikund, commence your trek, retracing the path through picturesque landscapes and pristine valleys. Reflect on the spiritual insights gained during your pilgrimage as you return to Guptkashi. Helicopter Ride to Guptkashi: Opt for a convenient helicopter ride back to Guptkashi. Your dedicated driver will escort you to the pre-booked helipad, where you'll embark on a swift aerial journey. After enjoying darshan at Kedarnath, please return to the helipad, where our car will be waiting to transfer you to your hotel in Guptkashi. Take this opportunity to relax and reflect on the profound experiences of your pilgrimage. Day 7 marks the culmination of your spiritual journey as you bid farewell to Kedarnath and return to Guptkashi. Whether trekking through the majestic Himalayan terrain or soaring above it in a helicopter, each moment is infused with reverence and gratitude. As you reminisce about your pilgrimage, may the blessings of Kedarnath continue to guide and inspire you on your spiritual path.",
      },
      {
        day: 8,
        title:
          "Guptkashi to Badrinath (Drive: 220 km, Duration: 9–10 hours, Altitude: 10,170 ft)",
        description:
          "The morning after breakfast, drive to Badrinath. Check in to the hotel upon arrival in Badrinath. After bathing in the Taptkund, Pilgrims have the Darshan of Badrivishal and Aarti in the evening. Brahamakapal is significant for Pinddan Shraddh's ancestors (Pitrus). Other exciting sightseeing spots include Mana, VyasGufa, Maatamoorti, Charanpaduka, Bhimkund, and the 'Mukh' of the Saraswati River, just within the 3 km of Badrinath ji. Overnight stay at Badrinath.",
      },
      {
        day: 9,
        title:
          "Badrinath-Rudraprayag (Drive: 153 km, Duration: 6-7 hrs, Altitude: 2,936 ft)",
        description:
          "Start your day with the ethereal beauty of a sunrise at Badrinath, a moment of tranquillity amidst the majestic peaks of the Himalayas. Witness the divine spectacle as the first rays of sunlight illuminate the sacred Temple, casting a golden glow upon the surrounding landscape. Following this, partake in the morning aarti ceremony, a spiritual ritual that invokes blessings and divine grace. After breakfast, bid farewell to Badrinath's serene ambience and embark on a picturesque drive to Rudraprayag. The journey spans approximately 153 kilometres and takes 6-7 hours, offering mesmerizing views of the Himalayan vistas and the meandering Alaknanda River. Dinner and Overnight Stay: As the day draws to a close, indulge in a sumptuous dinner featuring local delicacies, tantalizing your taste buds with the flavours of Uttarakhand. Retire to your hotel for a restful night's sleep, rejuvenated by the day's experiences and ready to continue your journey through the heart of the Himalayas.",
      },
      {
        day: 10,
        title:
          "Rudraprayag-Haridwar/Dehradun (Drive: 158 km, Duration: 5–6 hours, Altitude: 1,030 ft)",
        description:
          "Start your day with the serene beauty of a sunrise in Rudraprayag, a moment of quiet reflection amidst the majestic Himalayan landscape. Enjoy a delicious breakfast, fueling up for the day's journey ahead. Journey to Haridwar or Dehradun: After breakfast, bid farewell to Rudraprayag and set out on a picturesque drive to Haridwar or Dehradun, destinations steeped in spirituality and tradition. The drive covers approximately 158 kilometres and takes around 5–6 hours, allowing ample time to soak in the natural splendour of the Garhwal region. Arrival in Haridwar or Dehradun: As you arrive in Haridwar or Dehradun, you'll be greeted by the bustling energy of these ancient cities. Check into your accommodation and unwind after the day's journey, reflecting on the memories and experiences gathered throughout your Himalayan pilgrimage.",
      },
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/char-dham/1.jpg",
        alt: "Yk Travels Char Dham Yatra",
      },
      {
        id: 2,
        src: "/images/packages/char-dham/2.jpg",
        alt: "Yk travesls Char Dham Yatra",
      },
      {
        id: 3,
        src: "/images/packages/char-dham/3.jpg",
        alt: "Yk travesls Char Dham Yatra",
      },
      {
        id: 4,
        src: "/images/packages/char-dham/4.jpg",
        alt: "Yk travesls Char Dham Yatra",
      },
    ],
    faqs: [
      {
        question: "What is the Char Dham Yatra?",
        answer:
          "The Char Dham Yatra is a revered pilgrimage circuit in Uttarakhand, India, encompassing four sacred Hindu shrines: Yamunotri, Gangotri, Kedarnath, and Badrinath. It is considered highly auspicious and is believed to cleanse the soul and bring one closer to salvation.",
      },
      {
        question: "What is the best time to undertake the Char Dham Yatra?",
        answer: `The best time to undertake the Char Dham Yatra is from late April to June and from September to early November. These periods offer pleasant weather and clear paths, making the journey safer and more enjoyable.`,
      },
      {
        question: "How long does the Char Dham Yatra take to complete?",
        answer:
          "The Char Dham Yatra typically takes about 10-12 days to complete, depending on the specific itinerary and the pace at which you travel.",
      },
      {
        question:
          "What kind of accommodation is provided during the Char Dham Yatra?",
        answer:
          "Accommodation during the Char Dham Yatra ranges from guesthouses and lodges to campsites, depending on the location. YK Travels ensures comfortable stays at each stop, with basic amenities for a restful experience.",
      },
      {
        question: "What should I pack for the Char Dham Yatra?",
        answer: `

        Essential items to pack include:

- Warm clothing (layers, thermal wear, jackets)
- Comfortable trekking shoes
- Rain gear
- Personal toiletries and medications
- Sunglasses, sunscreen, and hats
- Water bottle and snacks
- A small first-aid kit Trekking poles (optional)


          `,
      },
      {
        question:
          "Is it necessary to have travel insurance for the Char Dham Yatra??",
        answer:
          "While travel insurance is not mandatory, it is highly recommended. Travel insurance can cover medical emergencies, trip cancellations, and other unforeseen events during the Yatra.",
      },
      {
        question: "What about drinking water during the Char Dham Yatra?",
        answer:
          "We provide safe drinking water during the trek. It’s advisable to carry a reusable water bottle that you can refill at designated points.",
      },
      {
        question: "Is travel insurance necessary for the Char Dham Yatra?",
        answer:
          "While not mandatory, it is highly recommended to have travel insurance that covers trekking activities, medical emergencies, and evacuation.",
      },
      {
        question:
          "What are the transportation arrangements for the Char Dham Yatra?",
        answer: `

        YK Travels provides transportation options depending on the package selected:

- Delhi to Delhi
- Haridwar to Haridwar
- Dehradun to Dehradun
- Haridwar to  Dehradun

All packages include comfortable and safe transportation with experienced drivers.
`,
      },
      {
        question: "Are there any age restrictions for the Char Dham Yatra?",
        answer:
          "There are no specific age restrictions for the Char Dham Yatra. However, due to the physical demands of the trek, it is advisable for elderly pilgrims and those with health issues to consult their doctor before undertaking the journey.",
      },
      {
        question: "Is there a provision for meals during the Char Dham Yatra?",
        answer:
          "Yes, meals are provided during the trek. This usually includes breakfast, lunch, and dinner. Vegetarian options are available, and we can accommodate dietary restrictions if informed in advance.",
      },
      {
        question: "Is drinking water available during the Char Dham Yatra?",
        answer:
          "Yes, drinking water is available at campsites. Trekkers are advised to carry their own water bottles and purification tablets or filters for use during the trek.",
      },
      {
        question: "Are permits required for the Char Dham Yatra?",
        answer:
          "Pilgrims need to register for the Char Dham Yatra. YK Travels assists in obtaining all necessary permits and registrations as part of our service.",
      },
      {
        question: "Are there medical facilities available during the trek?",
        answer:
          "Basic medical facilities and first-aid are available at various points along the Char Dham route. However, it is advisable to carry a personal first-aid kit and any specific medications you may need.",
      },
      {
        question: "What safety measures are in place during the Yatra?",
        answer:
          "YK Travels prioritizes the safety of all trekkers. Our experienced guides are trained in first aid and emergency response. We use high-quality trekking and camping equipment and ensure that all travel arrangements are secure and reliable.",
      },
      {
        question: "How can I book the Char Dham Yatra with YK Travels?",
        answer: `
        You can book the Char Dham Yatra through our website or by contacting us directly via phone or email. Our team will assist you with all the details and help you prepare for a spiritually enriching journey.

For any additional questions or specific concerns, feel free to reach out to YK Travels. We’re here to ensure you have a safe, enjoyable, and memorable Char Dham Yatra.
        `,
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d110621.8067669975!2d78.115926!3d29.952649999999995!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3909470eb8ee57c9%3A0x4e449176a640f5f3!2sHaridwar%2C%20Uttarakhand!5e0!3m2!1sen!2sin!4v1746077160440!5m2!1sen!2sin",
  },

  {
    id: 12,
    slug: "valley-of-flowers",
    title: "Valley of Flowers",
    state: "Uttarakhand",
    shortDescription:
      "The Valley of Flowers Trek is a mesmerizing journey through one of India’s most stunning and botanically rich regions, nestled in the heart of the Garhwal Himalayas in Uttarakhand. This UNESCO World Heritage Site is renowned for its vast expanse of alpine flowers, diverse flora and fauna, and breathtaking landscapes. Offered by YK Travels, this trek is perfect for nature lovers, botanists, and adventure enthusiasts alike.",
    duration: "6 Days",
    price: 6499,
    activityTags: ["camping", "trekking", "adventure", "star gazing"],
    overview: {
      title: "Trek Overview",
      description:
        "The Valley of Flowers Trek is a mesmerizing journey through one of India’s most stunning and botanically rich regions, nestled in the heart of the Garhwal Himalayas in Uttarakhand. This UNESCO World Heritage Site is renowned for its vast expanse of alpine flowers, diverse flora and fauna, and breathtaking landscapes. Offered by YK Travels, this trek is perfect for nature lovers, botanists, and adventure enthusiasts alike.",
      activities: [
        {
          title: "Stunning Floral Diversity",
          description:
            "Witness the vibrant display of thousands of blooming alpine flowers, including rare and endemic species, transforming the Valley of Flowers into a colorful paradise.",
          emoji: "🌸",
        },
        {
          title: "Breathtaking Scenery",
          description:
            "Enjoy panoramic views of snow-capped peaks, cascading waterfalls, and lush meadows, offering a visual feast at every turn of the trek.",
          emoji: "🌄",
        },
        {
          title: "Hemkund Sahib Pilgrimage",
          description:
            "Trek to the sacred Hemkund Sahib at 4,329 meters, a revered Sikh shrine by a glacial lake, surrounded by seven snow-clad peaks, offering a serene and spiritual experience.",
          emoji: "🙏",
        },
        {
          title: "Ghangaria Exploration",
          description:
            "Stay in the quaint hamlet of Ghangaria, the base camp for the trek, and explore local culture, interact with trekkers, and enjoy the peaceful ambiance.",
          emoji: "🏘️",
        },
        {
          title: "Rich Biodiversity",
          description:
            "Encounter diverse flora and fauna, including rare species like the Asiatic black bear, snow leopard, musk deer, and blue sheep in the UNESCO-listed Valley of Flowers National Park.",
          emoji: "🐾",
        },
        {
          title: "Scenic Trekking Trails",
          description:
            "Trek along well-defined trails through pine and oak forests, glacial rivers, and lush meadows, with a mix of easy and challenging segments from Govindghat to Ghangaria and beyond.",
          emoji: "🥾",
        },
        {
          title: "Spiritual and Cultural Insights",
          description:
            "Gain insights into the region’s spiritual significance through visits to sacred sites and interactions with local communities, blending natural beauty with cultural richness.",
          emoji: "🤝",
        },
        {
          title: "High-Altitude Adventure",
          description:
            "Challenge yourself with a trek reaching 4,329 meters at Hemkund Sahib, testing your endurance and rewarding you with unparalleled high-altitude views.",
          emoji: "🏔️",
        },
      ],
    },
    tripInfo: {
      accommodation: {
        text: "Alpine tents or Decathlon tents (4-2 sharing base)",
        icon: "building",
      },
      arrivalCity: {
        text: "Joshimath",
        icon: "city",
      },
      bestSeason: {
        text: "May to June, July to September, September to October",
        icon: "sun",
      },
      guide: {
        text: "Professional Guidance: Expert instructors to ensure a safe and enjoyable experience.",
        icon: "guide",
      },
      maximumAltitude: {
        text: "14400 feet",
        icon: "mountain",
      },

      transportation: {
        text: "Bus, Taxi",
        icon: "bus",
      },
      pickupPoint: {
        text: "Haridwar, Joshimath",
        icon: "car",
      },
    },

    cost: {
      includes: [
        "Accommodation in alpine and dome camps on a sharing basis.",
        "Meals include dinner from Day 1 to Breakfast on the Last Day. They will be simple and primarily vegetarian (Bed Tea, Breakfast, Lunch, Evening Tea and snacks, and Dinner).",
        "Crampons (spikes for shoes) and Gaitors for snow trekking.",
        "Professional and experienced certified trek guide.",
        "Transportation from Dehradun to Dehradun is covered.",
        "Free Jacket or Rucksack if opted offer package.",
        "Free customized printed travel card worth Rs.500, delivery to home address post trek completion.",
        "50% Off on Health check-ups and Health insurance.",
      ],
      excludes: [
        "Anything not mentioned in the inclusions above.",
        "Any personal expenses during the trip or out of any emergency/ Alcoholic beverages.",
        "Any personal trekking gear (such as a trekking pole, trekking shoes, cape, gloves, warm cap, bag, or jacket).",
        "Porters/Mules : If you need help carrying your luggage, mules  or porters are not included in the base price.",
      ],
      notes: [
        "Prices are subject to change during peak seasons",
        "Children below 12 years must be accompanied by adults",
        "Participants should know swimming or inform the instructors beforehand",
        "Activities are subject to weather conditions",
      ],
    },
    itinerary: [
      {
        day: 1,
        title:
          "Haridwar to Joshimath via Govindghat (drive: 270 km, duration: 9 hours, altitude: 6,300 ft)",
        description: `
        Pickup from Haridwar Bus Stop/Railway Station is at around 7 a.m. Everyone is set for the Valley of Flowers trek. All the enthusiastic trekkers shall gather at Haridwar Bus Stop/Railway Station. From the station, it will be an uphill drive, which will be carried out in the vehicles we have arranged for everyone. We will all set forth on our journey to Joshimath/Govindghat, which shall embark on the route via Joshimath and Devprayag. Therefore, the drive will be full of scenic pleasures and famous sights that you will be able to witness. On the way, passing Devprayag, we shall witness the holy confluence of the Bhagirathi and Alaknanda Rivers to form the Ganges. It is truly a natural magnificence to see these two very starkly different and magical rivers come together and give rise to the magnificent holy Ganges. After this, we shall arrive at our destination, Joshimath, which is home to quite a few essential religious pilgrimage locations. We can set ourselves up here for the night. We shall help you with fun activities to help you acclimate yourself to the weather and climate of the place. After some fun interaction and activities, we shall have a healthy dinner and rest for the day.
        `,
      },
      {
        day: 2,
        title:
          "Joshimath/Govindghat to Ghangaria (drive: 4 km, trek: 13 km, duration: 5–6 hours, altitude: 9,600 ft)",
        description:
          "Today is the day our Trek for the Valley of Flowers commences. You shall wake up to an enchanting Himalayan sunrise, and after a sumptuous breakfast, prepare yourselves for the trek. Fill your bottles and pack your energy bars, as well as any other important article you might require. We start trekking toward Ghangaria. The Valley of Flowers trek to Ghangaria is a short, 14-kilometer gradual ascent. Once we reach Ghangaria, we call it a day. We shall settle here for the night, and you are free to explore the tiny hamlet of Ghangaria. We will have some fun activities for you, like games and exercises, which should be entertaining at the same time and keep you fit and energized. Also, the excitement level shall run high amongst all as tomorrow we shall finally visit the much-awaited magnificent Valley of Flowers trek.",
      },
      {
        day: 3,
        title:
          "Ghangaria to the Valley of Flowers and Back to Ghangaria (Trek: 8 km, Duration: 6 hours, Altitude: 11,500 ft)",
        description:
          "We shall wake up to the pleasant morning of Ghangaria, full of excitement and vigour, as today, we shall finally witness the much-awaited pinnacle of this trek. After a healthy and filling breakfast, we shall resume our trek, ascending to 3,858m, to arrive at the Valley of Flowers trek. This soul-relieving sight will take your breath away! Ahead of you lies nature's masterpiece, spread out in the form of a blanket of blooming flowers. The plethora of colours stands out amidst the blue sky and cascading Himalayas. Colourful butterflies you had never seen before will be fluttering in multiples amidst the meadows. You will be left speechless at this magical sight. You are free to spend quality time at this fairytale land persuading activities of your heart's delight, may it be yoga, meditation, photography, nature walks, etc. At the proper hour, we shall start trekking back to Ghangaria, where we are staying. You shall come back with an experience worth remembering for a lifetime, and we shall all have dinner together. We will play games and have fun discussions, or you may rest early as per your wish. The experience of the trek isn't yet over, and tomorrow another adventure awaits.",
      },
      {
        day: 4,
        title:
          "Ghangaria to Hemkund Sahib and Back to Ghangaria (Trek: 12 km, Duration: 9 hrs, Altitude: 14,107 ft)",
        description:
          "Today, we shall trek to the famous Sikh shrine, Hemkund Sahib. The Gurdwara is believed to be the religious site where Guru Gobind Singh, the tenth Sikh Guru, meditated for many years. Encircled by seven snow-capped mountains, this place has the essence of peace that is common to areas of spiritual value. Therefore, after Breakfast, we will trek on a moderately strenuous, steep climb for about 4 hours to reach Hemkund Sahib. We shall spend some peaceful and soul-calming moments here. We will have our lunch here as well, and post-lunch, we shall descend back to Ghangaria. Once we get back, we will have our dinner together here and rest for the night.",
      },
      {
        day: 5,
        title:
          "Ghangaria to Joshimath/Govindghat (drive: 4 km, trek: 13 km, duration: 5–6 hours, altitude: 9,600 ft)",
        description:
          "Today, we finally leave Ghangaria village. After a delicious breakfast, we shall retrace the same trek route from Ghangaria to Govindghat. It is an easy trail to descend to Poolna village. If you wish, you can tour Auli in the evening along with fellow trekkers.",
      },
      {
        day: 6,
        title:
          "Joshimath to Haridwar (Drive: 270 km, Duration: 9 hours, Altitude: 6,300 ft)",
        description:
          "On our sixth day, our enriching experience comes to an end. We shall leave Joshimath/Govindghat early so that everyone can go to Haridwar and go to their respective destinations on time. Thus, our wonderful trek to the Valley of Flowers comes to an end. We expect to reach Haridwar at around 6 p.m.",
      },
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/valley-flowers/1.jpeg",
        alt: "Yk Travels Valley of Flower Trek",
      },
      {
        id: 2,
        src: "/images/packages/valley-flowers/2.jpg",
        alt: "Yk Travels Valley of Flower Trek",
      },
      {
        id: 3,
        src: "/images/packages/valley-flowers/3.jpg",
        alt: "Yk Travels Valley of Flower Trek",
      },
      {
        id: 4,
        src: "/images/packages/valley-flowers/4.jpg",
        alt: "Yk Travels Valley of Flower Trek",
      },
    ],
    faqs: [
      {
        question: "What is the Valley of Flowers Trek?",
        answer:
          "The Valley of Flowers Trek is a high-altitude trek in the Garhwal Himalayas of Uttarakhand, India. This UNESCO World Heritage Site is renowned for its vast expanse of endemic alpine flowers, rich biodiversity, and breathtaking landscapes.",
      },
      {
        question:
          "What is the best time to undertake the Valley of Flowers Trek?",
        answer:
          "The best time to visit the Valley of Flowers is from July to September. During these months, the valley is in full bloom with thousands of vibrant flowers, and the weather is pleasant for trekking.",
      },
      {
        question: "How difficult is the Valley of Flowers Trek?",
        answer: `The Valley of Flowers Trek is considered to be of moderate difficulty. It involves walking on well-defined trails with some steep sections and high-altitude terrain. Basic fitness and stamina are required.`,
      },
      {
        question: "How long is the Valley of Flowers Trek?",
        answer:
          "The trek typically spans 6-8 days, including travel time from Dehradun to the starting point and back. The actual trekking distance from Govindghat to the Valley of Flowers and Hemkund Sahib is approximately 38 kilometers (24 miles).",
      },
      {
        question: "What should I pack for the Valley of Flowers trek?",
        answer: `
     Essential items to pack include:

- Warm clothing (layers, thermal wear, fleece jackets)
- Waterproof trekking shoes with good grip
- Rain gear (poncho or raincoat)
- Personal toiletries and medications
- Sunglasses, sunscreen, and hats
- Water bottles and energy snacks
- A small first-aid kit
- Trekking poles (optional)
- A backpack with a rain cover`,
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, travel insurance is highly recommended. It can cover medical emergencies, trip cancellations, and other unforeseen events during the trek.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Govindghat and back. Safe and comfortable vehicles with experienced drivers are arranged for the journey.",
      },
      {
        answer:
          "There are no specific age restrictions, but the trek is recommended for individuals above the age of 12 who are physically fit and able to handle the demands of high-altitude trekking.",
        question: "Are there age restrictions for the trek?",
      },
      {
        question: "What about drinking water during the trek?",
        answer:
          "Yes, drinking water is available at guesthouses and campsites. Trekkers are advised to carry their own water bottles and purification tablets or filters for use during the trek.",
      },
      {
        question: "What are the transportation arrangements for the trek?",
        answer:
          "YK Travels provides transportation from Dehradun to Sankri and back. Depending on the package you select, we can also arrange transportation from other locations if needed.",
      },
      {
        question: "Is travel insurance necessary for the trek?",
        answer:
          "While not mandatory, it is highly recommended to have travel insurance that covers trekking activities, medical emergencies, and evacuation.",
      },
      {
        question: "What safety measures are in place for the trek?",
        answer:
          "YK Travels prioritizes the safety of all trekkers. Our experienced guides are trained in first aid and emergency response. We use high-quality trekking and camping equipment and ensure that all travel arrangements are secure and reliable.",
      },

      {
        question: "How can I book the Valley of Flowers Trek with YK Travels?",
        answer: `
        You can book the Valley of Flowers Trek through our website or by contacting us directly via phone or email. Our team will assist you with all the details and help you prepare for an unforgettable adventure.

For any additional questions or specific concerns, feel free to reach out to YK Travels. We’re here to ensure you have a safe, enjoyable, and memorable Valley of Flowers Trek experience.
        `,
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d219499.31340345403!2d79.607339!3d30.727486!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39a791153bd771ef%3A0x1f42050f9b6c125f!2sValley%20of%20Flowers%20National%20Park!5e0!3m2!1sen!2sin!4v1746078509709!5m2!1sen!2sin",
  },
  {
    id: 13,
    slug: "rishikesh-yk-travels",
    title: "Rishikesh Trip with Yk Travels",
    shortDescription:
      "Experience the thrill of adventure and spiritual bliss in Rishikesh, the Yoga Capital of the World. YK Travels brings you an unforgettable journey through white water rafting, cliff jumping, camping by the Ganges, and serene yoga sessions with a backdrop of the majestic Himalayas.",
    state: "Uttarakhand",
    price: 5999,
    duration: "3 Days",
    activityTags: [
      "river-rafting",
      "camping",
      "yoga",
      "trekking",
      "bungee-jumping",
      "water-sports",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/uk/uk-10.avif",
        alt: "YK Travels Rishikesh Rafting Adventure",
      },
      {
        id: 2,
        src: "/images/packages/uk/uk-9.avif",
        alt: "YK Travels Rishikesh Yoga Retreat",
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Riverside camps and boutique hotels",
        icon: "building",
      },
      arrivalCity: {
        text: "Rishikesh",
        icon: "city",
      },
      bestSeason: {
        text: "September to June (Avoid monsoon months)",
        icon: "sun",
      },
      guide: {
        text: "Expert rafting instructors and yoga teachers",
        icon: "guide",
      },
      maximumAltitude: {
        text: "1,300 feet",
        icon: "mountain",
      },
      meals: {
        text: "Vegetarian breakfast, lunch and dinner",
        icon: "bowl",
      },
      transportation: {
        text: "AC vehicle transfers from Dehradun/Haridwar",
        icon: "bus",
      },
      pickupPoint: {
        text: "Dehradun Airport/Railway Station or Haridwar Railway Station",
        icon: "location",
      },
    },
    overview: {
      title: "Rishikesh: Adventure Meets Spirituality",
      description:
        "Nestled in the foothills of the Himalayas, Rishikesh offers the perfect blend of adrenaline-pumping adventures and spiritual awakening. With YK Travels' expertly crafted itinerary, dive into the thrilling rapids of the Ganges, find inner peace through guided meditation sessions, trek to hidden waterfalls, and connect with like-minded travelers around evening bonfires. Our experienced guides ensure your safety while providing insightful knowledge about the region's rich cultural heritage.",
      activities: [
        {
          title: "White Water Rafting",
          description:
            "Navigate through Grade III and IV rapids on the sacred Ganges River with YK Travels' certified instructors, experiencing the perfect blend of adventure and natural beauty along a 16km stretch from Shivpuri to Rishikesh.",
          emoji: "🌊",
        },
        {
          title: "Riverside Camping",
          description:
            "Unwind in premium riverside camps exclusively set up by YK Travels, featuring comfortable tents, clean facilities, and evening bonfires with music under the starlit Himalayan sky.",
          emoji: "⛺",
        },
        {
          title: "Yoga & Meditation",
          description:
            "Connect with your inner self through guided yoga and meditation sessions led by experienced instructors at scenic locations carefully selected by YK Travels for their serene energy.",
          emoji: "🧘",
        },
        {
          title: "Bungee Jumping",
          description:
            "Experience the ultimate adrenaline rush with a 83-meter bungee jump at India's highest bungee platform, where YK Travels ensures international safety standards and unforgettable thrills.",
          emoji: "🏊",
        },
        {
          title: "Beatles Ashram & Temple Visit",
          description:
            "Explore the famous Beatles Ashram and attend the mesmerizing Ganga Aarti ceremony at Triveni Ghat, with YK Travels guides sharing fascinating historical and cultural insights.",
          emoji: "🕉️",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival & Rafting Introduction",
        description:
          "Your adventure begins with YK Travels' warm welcome at Dehradun/Haridwar, followed by a scenic drive to Rishikesh. After checking into your comfortable riverside camp, enjoy a delicious lunch before meeting your rafting instructors for safety briefing and equipment familiarization. The evening features a short trek to a nearby viewpoint for spectacular sunset views over the Himalayas, followed by a welcome dinner and bonfire where you'll meet fellow travelers and discuss the exciting days ahead.",
      },
      {
        day: 2,
        title: "White Water Rafting & Cliff Jumping",
        description:
          "Wake up to the sound of the flowing Ganges and start your day with an optional sunrise yoga session. After a nutritious breakfast, YK Travels' expert rafting guides will take you on an exhilarating 16km rafting expedition from Shivpuri to Rishikesh, navigating through thrilling rapids like 'Golf Course' and 'Roller Coaster'. Experience cliff jumping at safe spots selected by our experts. After lunch at a riverside cafe, enjoy free time to relax or explore local markets. The evening concludes with a special riverside barbecue dinner and cultural performances arranged exclusively for YK Travels guests.",
      },
      {
        day: 3,
        title: "Bungee Jumping & Spiritual Exploration",
        description:
          "After breakfast, YK Travels arranges your bungee jumping experience at Jumpin Heights (optional, at additional cost). Feel the rush as you leap from India's highest bungee platform with international safety standards. In the afternoon, explore the spiritual side of Rishikesh with a guided tour of the Beatles Ashram and important temples. Witness the enchanting Ganga Aarti ceremony at Triveni Ghat as the sun sets. Dinner is at a popular local restaurant featuring authentic Garhwali cuisine, carefully selected by YK Travels for an authentic culinary experience.",
      },
      {
        day: 4,
        title: "Waterfall Trek & Departure",
        description:
          "Begin your final day with a rejuvenating yoga and meditation session led by an experienced instructor. After breakfast, embark on a guided trek to the picturesque Neer Garh Waterfall, where you can refresh yourself in the cool mountain waters. Return to camp for lunch and prepare for departure. As your YK Travels adventure concludes, we'll transfer you to Dehradun/Haridwar with unforgettable memories and newfound friends, ensuring a smooth journey back with our comfortable vehicles and experienced drivers.",
      },
    ],
    cost: {
      includes: [
        "Professional white water rafting experience with certified instructors",
        "All necessary rafting equipment and safety gear",
        "Guided trekking to Neer Garh Waterfall",
        "Yoga and meditation sessions with instructor",
        "Transportation from Dehradun/Haridwar to Rishikesh and back",
        "Guided tour of Beatles Ashram and temples",
        "Evening bonfires with light music",
        "All applicable taxes and service charges",
      ],
      excludes: [
        "Bungee jumping and other optional adventure activities",
        "Personal expenses and tips",
        "Travel insurance (highly recommended)",
        "Transportation to Dehradun/Haridwar from your city",
        "Any meals not specified in the inclusions",
        "Alcoholic and non-alcoholic beverages",
        "Anything not mentioned in the inclusions",
      ],
      notes: [
        "Prices are subject to change during peak seasons and holidays",
        "Children below 14 years are not recommended for rafting activities",
        "Participants should know how to swim or inform the instructors beforehand",
        "Rafting activities are subject to weather and river conditions",
        "Advance booking of minimum 7 days is recommended",
      ],
    },
    faqs: [
      {
        question: "Is prior rafting experience required?",
        answer:
          "No, prior rafting experience is not required. YK Travels provides comprehensive safety briefings and our expert guides ensure that beginners feel comfortable on the water. We offer different grades of rapids to match your comfort level and experience.",
      },
      {
        question: "What should I pack for the Rishikesh adventure retreat?",
        answer:
          "We recommend packing light, quick-dry clothing, comfortable footwear that can get wet, sunscreen, hat, sunglasses, insect repellent, a small backpack for day trips, a water bottle, and any personal medications. YK Travels provides a detailed packing list upon booking confirmation.",
      },
      {
        question: "Is this package suitable for families with children?",
        answer:
          "This package is suitable for families with children above 14 years for the rafting activities. Children between 8-14 years can participate in other activities like camping, temple visits, and shorter treks. YK Travels can customize the itinerary to accommodate families with younger children.",
      },
      {
        question:
          "What safety measures are in place for the adventure activities?",
        answer:
          "YK Travels prioritizes guest safety above all. We use international standard equipment, employ certified instructors, conduct thorough safety briefings, and follow strict safety protocols. Our rafting guides are trained in swift water rescue, and first aid professionals are always available during activities.",
      },
      {
        question: "Can you accommodate dietary restrictions?",
        answer:
          "Yes, YK Travels can accommodate vegetarian, vegan, gluten-free, and other dietary requirements. Please inform us of your specific needs at the time of booking so we can make appropriate arrangements with our catering team.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d55044.97107358886!2d78.24936097832036!3d30.087519499999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39093e67cf93f111%3A0xcc78804a6f941bfe!2sRishikesh%2C%20Uttarakhand!5e0!3m2!1sen!2sin!4v1651903429559!5m2!1sen!2sin",
  },
  {
    id: 14,
    slug: "mussoorie-yk-travels",
    title: "Mussoorie Trip with Yk Travels",
    shortDescription:
      "Escape to the colonial charm of Mussoorie with YK Travels' exclusive package that combines panoramic Himalayan vistas, heritage walks through colonial architecture, adventure activities, and tranquil nature trails in the Queen of Hills.",
    state: "Uttarakhand",
    price: 5999,
    duration: "3 Days",
    activityTags: [
      "sightseeing",
      "trekking",
      "cable-car",
      "heritage-walk",
      "nature",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/uk/uk-7.avif",
        alt: "YK Travels Mussoorie Mall Road Experience",
      },
      {
        id: 2,
        src: "/images/packages/uk/uk-8.avif",
        alt: "YK Travels Mussoorie Mountain Views",
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Luxury and heritage hotels",
        icon: "building",
      },
      arrivalCity: {
        text: "Dehradun",
        icon: "city",
      },
      bestSeason: {
        text: "March to June, September to November",
        icon: "sun",
      },
      guide: {
        text: "Experienced local guides with historical knowledge",
        icon: "guide",
      },
      maximumAltitude: {
        text: "7,500 feet",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast included, select lunches and dinners",
        icon: "bowl",
      },
      transportation: {
        text: "Private AC vehicle throughout the trip",
        icon: "bus",
      },
      pickupPoint: {
        text: "Dehradun Airport or Railway Station",
        icon: "location",
      },
    },
    overview: {
      title: "Mussoorie: Colonial Charm Meets Himalayan Grandeur",
      description:
        "Discover why Mussoorie earned its title as the 'Queen of Hills' with YK Travels' meticulously crafted retreat. Perched at an elevation of 7,000 feet, this colonial hill station offers refreshing mountain air, spectacular views of the Doon Valley and snow-capped Himalayan peaks, and a perfect blend of adventure and relaxation. Our package showcases the best of Mussoorie's colonial heritage, natural beauty, and adventure opportunities while ensuring comfortable accommodations and hassle-free transportation.",
      activities: [
        {
          title: "Mall Road Promenade",
          description:
            "Stroll along the historic Mall Road with YK Travels' knowledgeable guide, exploring colonial-era architecture, quaint cafes, and vibrant markets while learning about Mussoorie's fascinating past as a British summer retreat.",
          emoji: "🏛️",
        },
        {
          title: "Kempty Falls Adventure",
          description:
            "Experience the refreshing cascade of Kempty Falls with special access arranged by YK Travels, enjoying water activities and a picnic lunch at this popular natural attraction away from the usual crowds.",
          emoji: "🏞️",
        },
        {
          title: "Gun Hill Cable Car",
          description:
            "Ascend to Gun Hill, Mussoorie's second-highest peak, via cable car for panoramic 360° views of the Himalayan ranges, with YK Travels' guides pointing out major peaks and sharing fascinating geographical insights.",
          emoji: "🚡",
        },
        {
          title: "Heritage Walking Tour",
          description:
            "Explore Landour's literary connections and Anglo-Indian heritage on an exclusive YK Travels walking tour, visiting historic sites like St. Paul's Church, Char Dukan, and Rokeby Manor with stories of famous residents like Ruskin Bond.",
          emoji: "👣",
        },
        {
          title: "Himalayan Nature Trails",
          description:
            "Trek through lush deodar and oak forests on carefully selected YK Travels nature trails, spotting local birds and wildlife while learning about the region's unique Himalayan ecology from our expert naturalist guides.",
          emoji: "🌳",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Dehradun & Drive to Mussoorie",
        description:
          "Your YK Travels journey begins with a warm welcome at Dehradun Airport/Railway Station. Board our comfortable private vehicle for a scenic 1.5-hour drive to Mussoorie through winding mountain roads offering glimpses of the adventure ahead. Upon arrival, check into your carefully selected heritage hotel where a welcome drink awaits. After freshening up, enjoy a guided orientation walk along Mall Road, soaking in the colonial charm and mountain views. The evening is free to explore local cafes, followed by a welcome dinner at a restaurant known for panoramic valley views, specially arranged by YK Travels to begin your trip on a perfect note.",
      },
      {
        day: 2,
        title: "Gun Hill & Camel's Back Road",
        description:
          "After breakfast at your hotel, your YK Travels guide will accompany you to the Gun Hill cable car for a thrilling ride to Mussoorie's second-highest peak. Spend time admiring and photographing the spectacular 360° views of the Himalayan ranges. Next, explore the Picture Palace area before enjoying lunch at a restaurant featuring authentic Garhwali cuisine. In the afternoon, take a leisurely walk along the famous Camel's Back Road, a natural marvel named after the rock formation resembling a camel's hump. The evening concludes with a special cultural performance and dinner arranged exclusively for YK Travels guests.",
      },
      {
        day: 3,
        title: "Kempty Falls & Company Garden",
        description:
          "Today, YK Travels takes you on an excursion to the famous Kempty Falls after a hearty breakfast. Beat the crowds with our early departure and enjoy the refreshing cascade in a more peaceful setting. Our team arranges a picnic lunch by the falls, allowing you to fully experience this natural beauty. In the afternoon, visit the meticulously maintained Company Garden to admire its beautiful flowers, fountains, and mini artificial lake. Return to the hotel for some leisure time before dinner at a local favorite restaurant featuring mountain specialties, carefully selected by the YK Travels team for its authentic flavors and ambiance.",
      },
      {
        day: 4,
        title: "Landour Heritage Walk & Cloud's End",
        description:
          "Begin your day with breakfast before embarking on YK Travels' exclusive heritage walking tour of Landour, Mussoorie's charming sister township. Visit historic sites including St. Paul's Church, Kellogg Memorial Church, and the famous Landour Bakehouse. Your guide will share stories of famous residents like Ruskin Bond and the area's Anglo-Indian heritage. After lunch at the iconic Char Dukan, drive to Cloud's End, marking the geographical end of Mussoorie, for a short forest walk and tea at the historic Cloud's End Forest Lodge. The evening features a special farewell dinner at your hotel with stunning sunset views over the Doon Valley.",
      },
      {
        day: 5,
        title: "Nature Trail & Departure",
        description:
          "On your final day, early risers can join an optional sunrise photography session at a scenic viewpoint. After breakfast, embark on a guided nature trail through deodar and oak forests near Mussoorie. Our YK Travels naturalist will help identify local flora and fauna while explaining the region's unique ecosystem. Enjoy your last lunch in Mussoorie before checking out. As we drive you back to Dehradun for your onward journey, reflect on the beautiful memories created in the Queen of Hills. YK Travels ensures a comfortable and timely transfer, with assistance for your departure arrangements.",
      },
    ],
    cost: {
      includes: [
        "Gun Hill cable car tickets",
        "All applicable entrance fees to monuments and attractions",
        "Special welcome and farewell dinners",
        "Guided heritage walks and nature trails",
        "Assistance at arrival and departure",
        "All applicable taxes",
      ],
      excludes: [
        "Airfare or train fare to/from Dehradun",
        "Meals not specified in the inclusions",
        "Personal expenses and tips",
        "Travel insurance",
        "Optional activities or excursions not mentioned in the itinerary",
        "Camera fees at monuments (if applicable)",
        "Anything not specifically mentioned in the inclusions",
      ],
      notes: [
        "Prices are subject to change during peak seasons (especially during May-June)",
        "The itinerary may be modified due to weather conditions or local circumstances",
        "Early booking is recommended for the best hotel availability",
        "Cable car operations are subject to weather conditions",
        "Special requests for room preferences should be made at the time of booking",
      ],
    },
    faqs: [
      {
        question: "When is the best time to visit Mussoorie?",
        answer:
          "The best time to visit Mussoorie with YK Travels is from March to June for pleasant weather and clear mountain views, and September to November for spectacular autumn colors. July-August is the monsoon season with occasional road blockages, while December-February offers a chance to experience snowfall, though some activities may be limited.",
      },
      {
        question: "Is Mussoorie suitable for family trips?",
        answer:
          "Absolutely! YK Travels' Mussoorie package is perfect for families, offering activities suitable for all age groups. From leisurely walks on Mall Road to exciting cable car rides and nature trails, there's something for everyone. We can customize the itinerary based on your family's specific interests and requirements.",
      },
      {
        question: "How difficult are the walking and trekking portions?",
        answer:
          "The walking tours and nature trails in our package are designed to be moderate and accessible to most fitness levels. The heritage walks are leisurely with frequent stops, while nature trails involve some uphill sections but are not technically challenging. YK Travels always adapts the pace to suit our guests' comfort levels.",
      },
      {
        question:
          "Can YK Travels arrange a meeting with Ruskin Bond during the Landour tour?",
        answer:
          "While we cannot guarantee a meeting with Ruskin Bond, YK Travels can inform you about his public book signing sessions at Cambridge Bookstore if they coincide with your visit. These typically happen on Saturday afternoons, and we're happy to adjust your itinerary if possible to include this special experience.",
      },
      {
        question:
          "What type of accommodation does YK Travels provide in Mussoorie?",
        answer:
          "YK Travels carefully selects accommodations that enhance your Mussoorie experience. Options include colonial-era heritage properties with character and history, as well as luxury hotels with modern amenities and mountain views. All accommodations are chosen for their excellent location, service quality, and unique character that complements the Queen of Hills experience.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d27667.81004618926!2d78.05271674550204!3d30.459270971252838!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3908d0cfa61cda5b%3A0x197fd47d980e85b1!2sMussoorie%2C%20Uttarakhand!5e0!3m2!1sen!2sin!4v1650867340010!5m2!1sen!2sin",
  },
  {
    id: 15,
    slug: "nainital-yk-travels",
    title: "Nainital Trip with YK Travels",
    shortDescription:
      "Immerse yourself in the enchanting Lake City of Nainital with YK Travels' exclusive package featuring boating on the emerald Naini Lake, panoramic Himalayan views from Snow View Point, exciting adventure activities, and serene nature walks in this picturesque hill station.",
    state: "Uttarakhand",
    price: 5999,
    duration: "3 Days",
    activityTags: [
      "water-sports",
      "boating",
      "cable-car",
      "trekking",
      "wildlife",
      "sightseeing",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/uk/uk-5.avif",
        alt: "YK Travels Nainital Lake Experience",
      },
      {
        id: 2,
        src: "/images/packages/uk/uk-6.avif",
        alt: "YK Travels Nainital Mountain Views",
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Lakeside hotels and heritage properties",
        icon: "building",
      },
      arrivalCity: {
        text: "Kathgodam/Haldwani",
        icon: "city",
      },
      bestSeason: {
        text: "March to June, September to November",
        icon: "sun",
      },
      guide: {
        text: "Expert local guides with knowledge of lakes and history",
        icon: "guide",
      },
      maximumAltitude: {
        text: "6,837 feet",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast included, select regional cuisine meals",
        icon: "bowl",
      },
      transportation: {
        text: "Private AC vehicle throughout the trip",
        icon: "bus",
      },
      pickupPoint: {
        text: "Kathgodam Railway Station or Pantnagar Airport",
        icon: "location",
      },
    },
    overview: {
      title: "Nainital: The Jewel of Kumaon's Lake District",
      description:
        "Experience the serene beauty of Nainital, fondly known as the 'Lake District of India,' with YK Travels' specially designed package. Nestled at an elevation of 6,837 feet around the pear-shaped Naini Lake, this charming hill station offers spectacular views of the Kumaon Himalayas, colonial architecture, and tranquil natural settings. Our comprehensive package showcases Nainital's emerald lakes, mountain viewpoints, forested trails, and rich cultural heritage while providing comfortable accommodations and seamless transportation for an unforgettable mountain retreat.",
      activities: [
        {
          title: "Naini Lake Boating",
          description:
            "Enjoy a private boating experience on the emerald waters of Naini Lake with YK Travels, choosing between traditional row boats or paddle boats while your guide shares fascinating legends about the lake's mythological significance and geological formation.",
          emoji: "🚣",
        },
        {
          title: "Snow View Cable Car",
          description:
            "Ascend to Snow View Point via the exciting ropeway for breathtaking panoramic views of the majestic Himalayan peaks including Nanda Devi, with YK Travels' guides identifying major peaks and sharing interesting geological insights.",
          emoji: "🚡",
        },
        {
          title: "Mall Road Heritage Walk",
          description:
            "Explore Nainital's colonial past on an exclusive YK Travels guided walk along the historic Mall Road, visiting landmarks like St. John's Church, the Governor's House, and charming old-world shops with stories of the British era.",
          emoji: "🏛️",
        },
        {
          title: "Sattal Lake Excursion",
          description:
            "Discover the interconnected seven lakes of Sattal with YK Travels' naturalist, enjoying birdwatching opportunities, butterfly spotting, and a guided forest walk in one of Kumaon's most pristine natural environments.",
          emoji: "🦅",
        },
        {
          title: "Tiffin Top Trek",
          description:
            "Trek through fragrant pine forests to the famous Tiffin Top (Dorothy's Seat) with YK Travels' experienced guides, enjoying magnificent views of Nainital town and the surrounding valleys from this historic viewpoint.",
          emoji: "🥾",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival at Kathgodam & Drive to Nainital",
        description:
          "Your Nainital adventure begins with YK Travels' warm welcome at Kathgodam Railway Station/Pantnagar Airport. Board our comfortable private vehicle for a scenic 1-hour drive through winding roads and pine forests to Nainital. Upon arrival, check into your lakeside hotel selected for its prime location and views. After freshening up, join your guide for an orientation walk along the Mall Road and Nainital's famous lakeside promenade. Enjoy the serene atmosphere of Naini Lake as the evening lights begin to reflect on its surface. Your first day concludes with a welcome dinner at a restaurant offering panoramic lake views, specially arranged by YK Travels to introduce you to the flavors of Kumaon cuisine.",
      },
      {
        day: 2,
        title: "Naini Lake & Viewpoints Tour",
        description:
          "After a leisurely breakfast, your YK Travels guide will accompany you to Naini Lake for a private boating experience on its emerald waters. Learn about the mythological significance of the lake as the place where Goddess Sati's eyes (naina) fell. Next, take the thrilling cable car ride to Snow View Point for breathtaking panoramic views of the Himalayan peaks. Enjoy lunch at a mountain cafe featuring local specialties. In the afternoon, visit Tiffin Top via a picturesque trek through pine forests to witness spectacular views of Nainital town and the surrounding valleys. Return to Mall Road for some shopping time at local handicraft stores. The evening features dinner at a heritage restaurant serving traditional Kumaoni dishes, carefully selected by YK Travels for its authentic flavors.",
      },
      {
        day: 3,
        title: "Sattal & Bhimtal Excursion",
        description:
          "Today, YK Travels takes you on a full-day excursion to the less-explored lakes near Nainital. After breakfast, drive to Sattal, the interconnected seven lakes surrounded by dense forests. Enjoy a guided nature walk with our expert naturalist who will help you spot various bird species and butterflies in this biodiversity hotspot. A picnic lunch will be arranged by a lakeside setting. Continue to Bhimtal, the largest lake in the region, named after the mythological character Bhima from Mahabharata. Visit the aquarium on the island in the middle of the lake, followed by some relaxation time by the peaceful waters. Return to Nainital by evening, with dinner at a restaurant known for its lake views and fusion cuisine that blends Kumaoni flavors with international techniques.",
      },
      {
        day: 4,
        title: "Naina Devi Temple & Departure",
        description:
          "On your final day, early risers can join an optional sunrise photography session at Hanuman Garhi, known for spectacular dawn views. After breakfast, visit the sacred Naina Devi Temple, dedicated to the patron goddess of Nainital. Your YK Travels guide will explain the temple's historical and cultural significance. Enjoy some last-minute shopping at the Tibetan Market for souvenirs and local crafts. After a farewell lunch featuring mountain specialties, check out from your hotel. YK Travels ensures a comfortable drive back to Kathgodam/Pantnagar for your onward journey, with our team assisting with departure arrangements and bidding you farewell with memories of the beautiful Lake District of India.",
      },
    ],
    cost: {
      includes: [
        "Professional English-speaking guide for all activities",
        "Naini Lake boating experience",
        "Snow View cable car tickets",
        "All applicable entrance fees to monuments and attractions",
        "Special welcome and farewell meals",
        "Guided nature walks and heritage tours",
        "Assistance at arrival and departure",
        "All applicable taxes",
      ],
      excludes: [
        "Airfare or train fare to/from Kathgodam/Pantnagar",
        "Meals not specified in the inclusions",
        "Personal expenses and tips",
        "Travel insurance",
        "Optional activities or excursions not mentioned in the itinerary",
        "Camera fees at monuments (if applicable)",
        "Anything not specifically mentioned in the inclusions",
      ],
      notes: [
        "Prices are subject to change during peak seasons (May-June)",
        "The itinerary may be modified due to weather conditions or local circumstances",
        "Early booking is recommended, especially during weekends and holidays",
        "Cable car operations and boating are subject to weather conditions",
        "Special requests for lake-view rooms should be made at the time of booking",
      ],
    },
    faqs: [
      {
        question: "When is the best time to visit Nainital?",
        answer:
          "The best time to visit Nainital with YK Travels is from March to June for pleasant weather and clear mountain views, and September to November for comfortable temperatures and post-monsoon greenery. July-August brings monsoon rains with occasional road disruptions but offers lush landscapes. December-February can be quite cold with chances of snowfall, though the winter scenery is magical and perfect for photographers.",
      },
      {
        question: "What makes YK Travels' Nainital package unique?",
        answer:
          "YK Travels' Nainital package stands out for its carefully curated experiences that go beyond typical tourist activities. We provide exclusive access to hidden viewpoints, arrange private boating experiences during less crowded hours, include interactions with local communities, and offer specialized guided walks focused on colonial history, biodiversity, and photography. Our handpicked accommodations and authentic culinary experiences further enhance your connection with the Lake District.",
      },
      {
        question: "Is Nainital suitable for solo travelers?",
        answer:
          "Absolutely! YK Travels' Nainital package is well-suited for solo travelers looking for a safe and enriching mountain experience. Our guides provide personalized attention, and the compact nature of Nainital makes it easy to explore independently during free time. We also offer the option to join small group departures where solo travelers can connect with like-minded adventurers while enjoying special solo traveler rates.",
      },
      {
        question: "How difficult are the treks included in the package?",
        answer:
          "The treks included in YK Travels' Nainital package, such as the Tiffin Top trek, are easy to moderate in difficulty and suitable for most fitness levels. These walks typically involve 1-2 hours of gradual ascent on well-marked trails through scenic forests. Our guides set a comfortable pace with regular stops for rest and photography. Alternative transportation options can be arranged for those who prefer not to trek.",
      },
      {
        question: "Can YK Travels customize the Nainital itinerary?",
        answer:
          "Yes, YK Travels specializes in customizing our Nainital package to match your specific interests and requirements. Whether you'd like to add adventure activities like paragliding at Bhimtal, include more wildlife experiences, focus on photography, or adjust the pace for family travel with children or seniors, our team can tailor the itinerary while maintaining the essence of the Nainital experience. Just mention your preferences during booking.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d27667.81004618926!2d79.46318774550204!3d29.392815971252838!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39a0a1bc28fd9d61%3A0x7cae7ba916987db3!2sNainital%2C%20Uttarakhand!5e0!3m2!1sen!2sin!4v1650867340010!5m2!1sen!2sin",
  },
  {
    id: 16,
    slug: "haridwar-yk-travels",
    title: "Haridwar Trip with YK Travels",
    shortDescription:
      "Experience the spiritual essence of India with YK Travels' exclusive Haridwar package featuring the mesmerizing Ganga Aarti ceremony, sacred temple visits, rejuvenating Ayurvedic experiences, and cultural immersion in one of Hinduism's holiest pilgrimage sites along the sacred banks of the Ganges.",
    state: "Uttarakhand",
    price: 5999,
    duration: "3 Days",
    activityTags: [
      "spiritual",
      "cultural",
      "river-rafting",
      "temples",
      "ayurveda",
      "heritage-walk",
      "camping",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/uk/uk-1.avif",
        alt: "YK Travels Haridwar Ganga Aarti Experience",
      },
      {
        id: 2,
        src: "/images/packages/uk/uk-2.avif",
        alt: "YK Travels Haridwar Temple Views",
      },
    ],
    stays: [
      {
        id: 1,
        images: [
          {
            id: 1,
            src: "/images/hotel-1.jpeg",
            alt: "YK Travels Riverside Heritage Hotel in Haridwar",
          },
        ],
        amenities: [
          "Complimentary WiFi",
          "Vegetarian Restaurant",
          "Daily Housekeeping",
          "Air Conditioning",
          "River View Rooms",
          "24-hour Front Desk",
          "Ayurvedic Spa Services",
          "Meditation Area",
          "Laundry Service",
          "Safe Deposit Box",
          "Travel Desk",
          "Airport/Railway Station Transfers",
        ],
      },
      {
        id: 2,
        images: [
          {
            id: 1,
            src: "/images/hotel-2.jpeg",
            alt: "YK Travels Ashram Accommodation in Haridwar",
          },
        ],
        amenities: [
          "Shared or Private Rooms",
          "Sattvic Vegetarian Meals",
          "Morning and Evening Yoga Sessions",
          "Meditation Halls",
          "Spiritual Library",
          "Vedic Chanting Classes",
          "Simple Furnished Rooms",
          "Community Dining",
          "Ganga View",
          "Walking Distance to Main Ghats",
          "Spiritual Discourse Sessions",
          "Daily Aarti Participation",
        ],
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Heritage riverside hotels and ashram stays",
        icon: "building",
      },
      arrivalCity: {
        text: "Haridwar",
        icon: "city",
      },
      bestSeason: {
        text: "October to March, avoid monsoon months",
        icon: "sun",
      },
      guide: {
        text: "Knowledgeable spiritual guides with religious insights",
        icon: "guide",
      },
      maximumAltitude: {
        text: "1,030 feet",
        icon: "mountain",
      },
      meals: {
        text: "Vegetarian breakfast and select traditional meals",
        icon: "bowl",
      },
      transportation: {
        text: "Private AC vehicle for all transfers and excursions",
        icon: "bus",
      },
      pickupPoint: {
        text: "Haridwar Railway Station or Dehradun Airport",
        icon: "location",
      },
    },
    overview: {
      title: "Haridwar: Sacred Gateway to the Divine",
      description:
        "Embark on a spiritual journey with YK Travels to Haridwar, one of the seven holiest places in Hinduism and an ancient gateway to the Himalayas. Situated on the banks of the sacred Ganges River, Haridwar (literally 'Gateway to God') has been a center of spiritual pilgrimage for centuries. Our carefully crafted package combines profound spiritual experiences, ancient rituals, cultural immersion, and heritage exploration with comfortable accommodations and expert guidance. Witness the mesmerizing Ganga Aarti ceremony, take a purifying dip in the holy waters, explore ancient temples, and understand the rich tapestry of Hindu mythology and traditions in this city where spirituality permeates every corner.",
      activities: [
        {
          title: "Ganga Aarti Ceremony",
          description:
            "Experience the soul-stirring evening Ganga Aarti at Har Ki Pauri with premium seating arranged by YK Travels, witnessing the spectacular ritual of light and sound as priests honor the sacred river with flaming lamps, chants, and music against the backdrop of sunset.",
          emoji: "🪔",
        },
        {
          title: "Sacred Temple Circuit",
          description:
            "Visit Haridwar's most significant temples with YK Travels' knowledgeable spiritual guide who explains the religious significance, architectural elements, and mythological stories associated with each divine abode, including Mansa Devi Temple reached by cable car.",
          emoji: "🛕",
        },
        {
          title: "Ayurvedic Experience",
          description:
            "Indulge in an authentic Ayurvedic consultation and treatment session exclusively arranged by YK Travels at a traditional wellness center, where expert practitioners customize therapies based on your constitution and needs for holistic rejuvenation.",
          emoji: "🌿",
        },
        {
          title: "Spiritual Heritage Walk",
          description:
            "Explore the ancient lanes of Haridwar on a guided heritage walk with YK Travels' expert, discovering hidden shrines, centuries-old trading areas, traditional craft workshops, and sacred ghats while interacting with local priests and artisans.",
          emoji: "👣",
        },
        {
          title: "Vedic Chanting Session",
          description:
            "Participate in a private Vedic chanting session organized by YK Travels with learned Sanskrit scholars, learning simple mantras and understanding their meanings, benefits, and the science behind these ancient sound vibrations that heal and harmonize.",
          emoji: "🕉️",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Haridwar & Evening Ganga Aarti",
        description:
          "Your spiritual journey begins with YK Travels' warm welcome at Haridwar Railway Station or Dehradun Airport. Board our comfortable vehicle for a transfer to your carefully selected riverside hotel, chosen for its peaceful atmosphere and proximity to sacred sites. After check-in and refreshments, your guide will provide an orientation of Haridwar's spiritual significance. Late afternoon, embark on a guided walk through the vibrant streets leading to Har Ki Pauri, the most sacred ghat. YK Travels has arranged premium seating for you to witness the mesmerizing Ganga Aarti ceremony at sunset, where priests perform elaborate rituals with flaming lamps to honor the holy river. Experience the hypnotic chants, bells, and the spiritual energy of this centuries-old tradition. After the ceremony, enjoy a traditional Sattvic dinner at a restaurant overlooking the Ganges, specially selected by YK Travels to introduce you to the pure vegetarian cuisine of the region.",
      },
      {
        day: 2,
        title: "Temple Circuit & Ayurvedic Experience",
        description:
          "Begin your day with an early morning visit to Har Ki Pauri with your YK Travels guide to experience the peaceful morning rituals and witness pilgrims taking sacred dips in the Ganges. After breakfast at your hotel, embark on a temple circuit starting with a cable car ride to Mansa Devi Temple perched on Bilwa Parvat, offering panoramic views of Haridwar and dedicated to the goddess who fulfills desires. Continue to Chandi Devi Temple atop Neel Parvat, followed by visits to Maya Devi Temple and Daksha Mahadev Temple, with your knowledgeable guide explaining the significance and legends of each site. Enjoy a traditional lunch at an authentic local eatery known for its simple yet flavorful preparations. In the afternoon, YK Travels has arranged a special Ayurvedic consultation and treatment session at a respected wellness center, where you'll experience personalized therapies based on ancient healing traditions. The evening is free to explore the local markets for spiritual items, followed by dinner featuring regional specialties.",
      },
      {
        day: 3,
        title: "Spiritual Heritage Walk & Departure",
        description:
          "After breakfast, join your YK Travels expert for an immersive heritage walk through Haridwar's ancient lanes and hidden corners. Discover centuries-old shrines, traditional ashrams, and local craft workshops while interacting with residents who maintain age-old traditions. Visit Pawan Dham with its intricate glass work and Bharat Mata Temple dedicated to Mother India. YK Travels has arranged a special private Vedic chanting session with learned scholars where you'll learn about the profound impact of Sanskrit mantras and participate in simple chanting practices. Enjoy a farewell lunch featuring local delicacies at a heritage restaurant carefully selected for its authentic ambiance and flavors. In the afternoon, visit the Gurukul Kangri Vishwavidyalaya, one of the oldest educational institutions preserving traditional Vedic knowledge, before being transferred to Haridwar Railway Station or Dehradun Airport for your onward journey, carrying the spiritual essence of Haridwar with you.",
      },
    ],
    cost: {
      includes: [
        "Private AC vehicle for all transfers and sightseeing",
        "Expert spiritual guide for all temple visits and activities",
        "Premium seating arrangement for Ganga Aarti ceremony",
        "Cable car tickets to Mansa Devi Temple",
        "Personalized Ayurvedic consultation and treatment session",
        "Private Vedic chanting session with scholars",
        "All applicable temple entrance fees",
        "Assistance at arrival and departure",
        "All applicable taxes",
      ],
      excludes: [
        "Airfare or train fare to/from Haridwar/Dehradun",
        "Meals not specified in the inclusions",
        "Personal expenses and tips",
        "Travel insurance",
        "Optional rituals or ceremonies not mentioned in the itinerary",
        "Camera fees at temples (if applicable)",
        "Offerings at temples and to priests",
        "Anything not specifically mentioned in the inclusions",
      ],
      notes: [
        "Prices may increase during major religious festivals and events",
        "The itinerary may be modified due to religious ceremonies or local circumstances",
        "Early booking is recommended for the best hotel availability",
        "Modest dress code is required for temple visits (shoulders and knees covered)",
        "Leather items are not permitted in many temple complexes",
        "YK Travels can arrange special pujas or rituals at an additional cost",
      ],
    },
    faqs: [
      {
        question: "When is the best time to visit Haridwar?",
        answer:
          "The best time to visit Haridwar with YK Travels is from October to March when the weather is pleasant with temperatures ranging from 15-30°C. These months offer comfortable conditions for temple visits and outdoor activities. April-June can be quite hot, while July-September brings monsoon rains making the Ganges sometimes dangerous for bathing. For those interested in major festivals, planning your trip during Kumbh Mela (which occurs every 12 years) or annual celebrations like Kanwar Mela (July/August) can be rewarding, though accommodations need to be booked well in advance through YK Travels.",
      },
      {
        question: "Is Haridwar suitable for non-Hindu visitors?",
        answer:
          "Absolutely! YK Travels welcomes travelers of all faiths to experience Haridwar. While it is a significant Hindu pilgrimage site, the city's spiritual atmosphere, cultural richness, and historical significance appeal to everyone seeking deeper understanding of Indian traditions. Our guides are trained to explain Hindu practices in an accessible way for visitors from all backgrounds. Non-Hindu visitors are welcome to observe ceremonies and visit temples (where permitted) while maintaining appropriate respect for local customs. The universal themes of devotion, peace, and spiritual seeking resonate with travelers from all traditions.",
      },
      {
        question: "Can YK Travels arrange for a holy dip in the Ganges?",
        answer:
          "Yes, YK Travels can facilitate a safe and meaningful holy dip (snan) in the Ganges for interested travelers. Our guides will accompany you to designated bathing ghats during auspicious morning hours, providing guidance on the traditional ritual process and ensuring your safety and privacy. We provide clean changing facilities, towels, and information on the spiritual significance of this purification ritual. For those who prefer not to take a full dip, we can arrange for symbolic water touching. Safety is our priority, so this activity is subject to current water conditions and may be modified during monsoon months.",
      },
      {
        question:
          "What type of accommodation does YK Travels provide in Haridwar?",
        answer:
          "YK Travels offers carefully selected accommodations in Haridwar ranging from heritage riverside hotels with views of the Ganges to comfortable ashram stays for a more immersive spiritual experience. All our partner properties maintain high standards of cleanliness, offer vegetarian meals, and provide peaceful environments in keeping with Haridwar's sacred atmosphere. Our standard package includes quality mid-range hotels, but we can upgrade to luxury heritage properties or arrange more austere traditional accommodations based on your preferences. All locations are chosen for their proximity to major spiritual sites and the authentic atmosphere they provide.",
      },
      {
        question:
          "Can YK Travels combine Haridwar with visits to nearby spiritual destinations?",
        answer:
          "Yes, YK Travels specializes in creating comprehensive spiritual journeys that combine Haridwar with nearby sacred destinations. We offer seamless extensions to Rishikesh (30 minutes away) for yoga and meditation experiences, Dehradun for its peaceful monasteries, or the Char Dham pilgrimage circuit (seasonal). Our most popular combined package includes Haridwar and Rishikesh over 5-6 days, allowing travelers to experience both the traditional religious practices of Haridwar and the more contemporary spiritual and yogic atmosphere of Rishikesh. These customized itineraries include all transportation, accommodations, and expert guides throughout your journey.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d27667.81004618926!2d78.15271674550204!3d29.945933571252838!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3909470eb8ee57c9%3A0x4e449176a722c10c!2sHaridwar%2C%20Uttarakhand!5e0!3m2!1sen!2sin!4v1650867340010!5m2!1sen!2sin",
  },
  {
    id: 17,
    slug: "jim-corbett-yk-travels",
    title: "Jim Corbett Wildlife Safari with YK Travels",
    shortDescription:
      "Immerse yourself in the wilderness of India's oldest national park with YK Travels' exclusive Jim Corbett package featuring thrilling jeep and elephant safaris, bird watching expeditions, riverside accommodations, nature walks, and conservation experiences in the land of the royal Bengal tiger and diverse Himalayan wildlife.",
    state: "Uttarakhand",
    price: 5999,
    duration: "4 Days",
    activityTags: [
      "wildlife",
      "jungle-safari",
      "river-side",
      "adventure",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/uk/uk-3.avif",
        alt: "YK Travels Jim Corbett Tiger Safari Experience",
      },
      {
        id: 2,
        src: "/images/packages/uk/uk-4.avif",
        alt: "YK Travels Jim Corbett Riverside Retreat",
      },
    ],
    stays: [
      {
        id: 1,
        images: [
          {
            id: 1,
            src: "/images/resort-2.jpeg",
            alt: "YK Travels Luxury Jungle Resort in Corbett",
          },
        ],
        amenities: [
          "Complimentary WiFi in Common Areas",
          "All-Day Restaurant with Multi-Cuisine Options",
          "Daily Housekeeping",
          "Air Conditioning",
          "Forest View Cottages",
          "24-hour Front Desk",
          "Swimming Pool",
          "Wildlife Library",
          "Laundry Service",
          "Safe Deposit Box",
          "Travel Desk",
          "Airport/Railway Station Transfers",
        ],
      },
      {
        id: 2,
        images: [
          {
            id: 1,
            src: "/images/resort-2.jpeg",
            alt: "YK Travels Riverside Camping in Jim Corbett",
          },
        ],
        amenities: [
          "Farm-to-Table Dining",
          "Campfire Evenings",
          "Nature Interpretation Area",
          "Bird Watching Deck",
          "Guided Nature Walks",
          "Comfortable Furnished Tents",
          "Community Dining",
          "River View",
          "Walking Distance to Forest Entry",
          "Wildlife Documentary Screenings",
          "Naturalist on Call",
        ],
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Jungle resorts and luxury riverside camps",
        icon: "building",
      },
      arrivalCity: {
        text: "Ramnagar",
        icon: "city",
      },
      bestSeason: {
        text: "November to June, park closed during monsoon",
        icon: "sun",
      },
      guide: {
        text: "Expert naturalists and wildlife guides",
        icon: "guide",
      },
      maximumAltitude: {
        text: "1,210 feet",
        icon: "mountain",
      },
      meals: {
        text: "All meals included with wilderness dining experiences",
        icon: "bowl",
      },
      transportation: {
        text: "Private AC vehicle and safari jeeps for all transfers",
        icon: "bus",
      },
      pickupPoint: {
        text: "Ramnagar Railway Station or Pantnagar Airport",
        icon: "location",
      },
    },
    overview: {
      title: "Jim Corbett: Wilderness Haven of the Royal Bengal Tiger",
      description:
        "Embark on an unforgettable wildlife adventure with YK Travels to Jim Corbett National Park, India's oldest national park and a premier tiger reserve. Nestled in the foothills of the Himalayas along the Ramganga River, this biodiverse paradise spans over 520 square kilometers of dense forests, rolling grasslands, and riverine belts. Our expertly crafted package combines exhilarating wildlife safaris, serene nature experiences, conservation insights, and comfortable wilderness accommodations with knowledgeable guidance. Track the elusive Bengal tiger, spot wild elephants, observe fascinating bird species, and connect with the rich ecosystem of this magnificent sanctuary that inspired Jim Corbett's famous conservation efforts and writings.",
      activities: [
        {
          title: "Tiger Safari Adventures",
          description:
            "Experience the thrill of tracking the majestic Bengal tiger in its natural habitat with exclusive jeep safaris arranged by YK Travels in the prime zones of Corbett National Park, accompanied by expert naturalists who interpret animal behavior, track movements, and enhance your wildlife viewing with their deep knowledge.",
          emoji: "🐯",
        },
        {
          title: "Riverside Bird Watching",
          description:
            "Discover the vibrant avian diversity of Corbett with YK Travels' specialized bird watching excursions along the Ramganga river, where our ornithologist guides help you spot and identify over 600 species including the crested kingfisher, great hornbill, and Himalayan griffon through professional spotting scopes.",
          emoji: "🦅",
        },
        {
          title: "Elephant Safari Experience",
          description:
            "Enjoy a unique perspective of the forest with a traditional elephant-back safari exclusively arranged by YK Travels, traversing through tall grasslands and shallow streams where vehicles cannot reach, offering intimate wildlife encounters from the safety and elevated vantage point of these gentle giants.",
          emoji: "🐘",
        },
        {
          title: "Conservation Experience",
          description:
            "Participate in a special conservation program organized by YK Travels in partnership with local wildlife authorities, gaining hands-on experience in habitat preservation efforts, learning about anti-poaching measures, and understanding the delicate balance between wilderness protection and community development.",
          emoji: "🌿",
        },
        {
          title: "Wilderness Sunset Dinner",
          description:
            "Indulge in YK Travels' signature dining experience at a carefully selected riverside location, where a gourmet meal prepared by our chef is served as you watch the sun set over the forest canopy, accompanied by the natural symphony of wildlife calls in the background.",
          emoji: "🌅",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival at Jim Corbett & Evening Nature Walk",
        description:
          "Your wildlife adventure begins with YK Travels' warm welcome at Ramnagar Railway Station or Pantnagar Airport. Board our comfortable vehicle for a scenic transfer through the Himalayan foothills to your carefully selected jungle resort, chosen for its proximity to the national park and serene surroundings. After check-in and a refreshing welcome drink, enjoy a leisurely lunch featuring local Kumaoni specialties. In the afternoon, meet your dedicated naturalist who will provide an orientation about Corbett's ecosystem, wildlife, and safety guidelines. As the day cools, embark on a guided nature walk around the resort property and nearby buffer areas where your expert will introduce you to the indigenous flora, smaller fauna, and birdlife while explaining the fascinating interconnections in this ecosystem. As dusk approaches, gather for a special welcome dinner under the stars accompanied by a wildlife documentary screening that prepares you for your upcoming safari adventures. Your naturalist will brief you on tomorrow's early morning safari and help you prepare your gear.",
      },
      {
        day: 2,
        title: "Morning Safari & Riverside Birding Experience",
        description:
          "Wake up before dawn to the sounds of the jungle coming alive. After a quick coffee and light snacks, depart with your YK Travels naturalist for an exclusive morning jeep safari into the core zone of Jim Corbett National Park. This prime morning slot offers the best opportunity to spot the majestic Bengal tiger along with other wildlife including wild elephants, various deer species, and leopards. Your expert guide will interpret animal tracks, alarm calls, and behavior patterns, maximizing your wildlife viewing experience. Return to the resort for a hearty late breakfast and some relaxation time. After lunch, join our specialized ornithologist for a riverside bird watching expedition along the Ramganga River, where you'll observe numerous resident and migratory birds including kingfishers, hornbills, and eagles through professional spotting scopes provided by YK Travels. As the sun begins to set, enjoy a special high tea setup by the riverside while discussing the day's sightings. Return to the resort for an interactive session on tiger conservation, followed by dinner featuring wilderness-inspired cuisine.",
      },
      {
        day: 3,
        title: "Elephant Safari & Conservation Experience",
        description:
          "After breakfast, check out from your resort and transfer to our exclusive riverside luxury camp for a different jungle experience. Once settled in, prepare for YK Travels' special elephant safari experience, a traditional way to explore areas of the park inaccessible to vehicles. Atop these gentle giants, journey through tall grasslands and shallow streams for close encounters with wildlife from a safe, elevated perspective. Return to camp for lunch and a short rest. In the afternoon, participate in our unique conservation experience in partnership with local forest authorities, where you'll learn about habitat preservation efforts, anti-poaching measures, and community-based conservation initiatives. Get hands-on experience with camera trapping techniques and habitat restoration activities. As evening approaches, join a local conservation expert for a presentation on human-wildlife conflict resolution and sustainable tourism practices. The day concludes with YK Travels' signature wilderness sunset dinner at a specially prepared riverside location, where you'll enjoy a gourmet meal while listening to the natural symphony of the forest as night falls.",
      },
      {
        day: 4,
        title: "Dawn Safari & Departure",
        description:
          "Embrace the final morning of your wildlife journey with an early wake-up call for a dawn safari, targeting different zones of the park than previously visited. This last venture into the wilderness offers opportunities to complete your wildlife checklist with YK Travels' naturalist highlighting seasonal specialties and less-noticed aspects of the ecosystem. Return to camp for a farewell breakfast featuring local organic ingredients. After check-out, visit the Jim Corbett Museum housed in the famous hunter-turned-conservationist's former winter home, where your guide will provide fascinating insights into Corbett's life and conservation legacy. Enjoy a final lunch at a heritage forest bungalow carefully selected by YK Travels for its historical significance and ambiance. In the early afternoon, transfer to Ramnagar Railway Station or Pantnagar Airport for your onward journey, carrying memories of your intimate encounters with India's most magnificent wilderness.",
      },
    ],
    cost: {
      includes: [
        "All accommodations (2 nights resort, 1 night luxury camp)",
        "All meals as mentioned in the itinerary",
        "Private AC vehicle for all transfers",
        "Exclusive jeep safaris with naturalist guide",
        "Elephant safari experience",
        "Specialized bird watching excursion with equipment",
        "Conservation experience program",
        "Entry and permit fees for Corbett National Park",
        "Services of expert naturalists throughout the trip",
        "Special wilderness sunset dinner experience",
        "Wildlife documentary screenings",
        "All applicable taxes",
      ],
      excludes: [
        "Airfare or train fare to/from Ramnagar/Pantnagar",
        "Alcoholic beverages",
        "Personal expenses and tips",
        "Travel insurance",
        "Camera fees inside the national park",
        "Activities not mentioned in the inclusions",
        "Specialized photography equipment",
        "Anything not specifically mentioned in the inclusions",
      ],
      notes: [
        "Safari timings and zones are subject to availability and forest department allocation",
        "Wildlife sightings are natural occurrences and cannot be guaranteed",
        "The itinerary may be modified due to weather conditions or park regulations",
        "Early booking is recommended as park permits are limited",
        "Comfortable earth-toned clothing and appropriate footwear are recommended",
        "YK Travels can arrange specialized photography safaris at additional cost",
        "The national park remains closed during monsoon (July-October) with exact dates determined by forest authorities",
      ],
    },
    faqs: [
      {
        question: "What are the chances of spotting a tiger in Jim Corbett?",
        answer:
          "With YK Travels' experienced naturalists and strategic safari planning, our guests enjoy better-than-average tiger sighting opportunities in Jim Corbett, particularly during the summer months (March-June) when tigers frequent water sources. However, we emphasize that wildlife viewing involves many factors including season, time of day, and natural animal behavior. While our tiger sighting success rate is approximately 40-50% across multiple safaris, we ensure every excursion is rewarding by highlighting diverse wildlife including elephants, leopards, various deer species, and rich birdlife. Our guides are expert at tracking and interpreting animal signs, maximizing your chances while helping you appreciate the entire ecosystem beyond just tiger sightings.",
      },
      {
        question:
          "Which safari zones in Jim Corbett are best for wildlife viewing?",
        answer:
          "YK Travels strategically books safaris across different zones based on seasonal wildlife movements and recent sighting patterns. The Dhikala zone, accessible for overnight stays, offers the most comprehensive wildlife experience with its diverse habitats and expansive grasslands ideal for tiger and elephant sightings. Bijrani zone is excellent for tiger sightings with more convenient access, while Jhirna remains open year-round with good opportunities for sloth bear encounters. Durga Devi zone is renowned for its bird diversity and riverside ecosystem. Rather than limiting your experience to one zone, our package typically includes safaris in 2-3 different zones to maximize both wildlife diversity and sighting opportunities based on current conditions and your specific interests.",
      },
      {
        question:
          "What should I pack for a Jim Corbett safari with YK Travels?",
        answer:
          "For an optimal Jim Corbett experience with YK Travels, we recommend packing lightweight, breathable clothing in earth tones (olive green, brown, khaki) as bright colors can distract wildlife. Include a light jacket or fleece for early morning safaris, comfortable hiking shoes, a wide-brimmed hat, and sunglasses. Don't forget sunscreen, insect repellent, personal medications, and a small daypack for carrying essentials during safaris. For wildlife enthusiasts, we suggest bringing binoculars (though we provide shared pairs), and a camera with zoom lens if possible. A reusable water bottle, flashlight, and basic first aid supplies are also useful. YK Travels provides detailed packing recommendations before your trip, and our jungle resorts offer laundry services for longer stays, allowing you to pack lighter.",
      },
      {
        question:
          "Is Jim Corbett National Park suitable for families with children?",
        answer:
          "Absolutely! YK Travels welcomes families and has designed our Jim Corbett experience to be engaging for all ages. For families with children, we recommend our specialized family safaris that include child-friendly naturalists who excel at making wildlife interesting and educational for young minds. We maintain flexibility in safari timings and duration to accommodate children's attention spans and needs. Our accommodations offer family rooms or connected units, and our chefs can prepare child-friendly meals. Beyond safaris, we organize age-appropriate activities like nature scavenger hunts, animal footprint identification, and junior ranger programs. For safety, we recommend this experience for children aged 5 and above, and provide specific safety briefings for families. The minimum age for safari participation is determined by park regulations, which our team can advise on during booking.",
      },
      {
        question:
          "How does YK Travels contribute to conservation efforts in Jim Corbett?",
        answer:
          "YK Travels integrates conservation into all aspects of our Jim Corbett operations through our 'Travel with Purpose' initiative. We directly support local conservation NGOs with a contribution from each booking, funding anti-poaching patrols, wildlife monitoring, and habitat restoration projects. Our partnerships with local communities create sustainable livelihoods that reduce forest dependency, with 70% of our staff coming from surrounding villages. We practice and promote responsible wildlife tourism through small group sizes, strict adherence to park guidelines, and educational components in all our programs. Our accommodations implement eco-friendly practices including water conservation, waste reduction, and energy efficiency. Additionally, we organize periodic conservation activities for guests including camera trap monitoring, nature restoration work, and community education programs, creating meaningful connections between visitors and long-term conservation efforts.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d223109.7433353815!2d79.01232231921924!3d29.552479708467246!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390a24ffffffffff%3A0x3017b72d3d253fd7!2sJim%20Corbett%20National%20Park!5e0!3m2!1sen!2sin!4v1650867440010!5m2!1sen!2sin",
  },

  {
    id: 18,
    slug: "manali-yk-travels",
    title: "Manali Highland Adventure with YK Travels",
    shortDescription:
      "Experience the magic of the Himalayas with YK Travels' exclusive Manali package featuring snow-capped mountains, apple orchards, adventure activities, cultural explorations, riverside accommodations, and serene nature walks in the pristine landscapes of Himachal Pradesh.",
    state: "Himachal Pradesh",
    price: 4999,
    duration: "5 Days",
    activityTags: [
      "mountains",
      "adventure",
      "trekking",
      "river-rafting",
      "cultural",
      "photography",
      "snow",
      "nature",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/hp/hp-8.avif",
        alt: "YK Travels Manali Snow Mountain Experience",
      },
      {
        id: 2,
        src: "/images/packages/hp/hp-7.avif",
        alt: "YK Travels Manali Adventure Activities",
      },
    ],
    stays: [
      {
        id: 1,
        images: [
          {
            id: 1,
            src: "/images/resort-3.jpeg",
            alt: "YK Travels Luxury Mountain Resort in Manali",
          },
        ],
        amenities: [
          "Complimentary WiFi",
          "On-site Restaurant with Himalayan Cuisine",
          "Daily Housekeeping",
          "Room Heating",
          "Mountain View Rooms",
          "24-hour Front Desk",
          "Indoor Games Room",
          "Library with Mountain Literature",
          "Laundry Service",
          "Safe Deposit Box",
          "Travel Desk",
          "Airport/Bus Station Transfers",
        ],
      },
      {
        id: 2,
        images: [
          {
            id: 1,
            src: "/images/hotel-1.jpeg",
            alt: "YK Travels Riverside Cottages in Manali",
          },
        ],
        amenities: [
          "Private River View Balconies",
          "Traditional Himachali Dining",
          "Bonfire Area",
          "Apple Orchard Access",
          "Guided Nature Walks",
          "Cozy Wooden Interiors",
          "Community Dining Option",
          "River View",
          "Walking Distance to Old Manali",
          "Local Art and Craft Displays",
          "Adventure Activity Desk",
        ],
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Mountain resorts and riverside cottages",
        icon: "building",
      },
      arrivalCity: {
        text: "Manali",
        icon: "city",
      },
      bestSeason: {
        text: "Year-round destination with seasonal variations",
        icon: "sun",
      },
      guide: {
        text: "Expert local guides and adventure specialists",
        icon: "guide",
      },
      maximumAltitude: {
        text: "6,725 feet (Manali) / 13,050 feet (Rohtang Pass)",
        icon: "mountain",
      },
      meals: {
        text: "All meals included with local Himachali cuisine experiences",
        icon: "bowl",
      },
      transportation: {
        text: "Private AC vehicle for all transfers",
        icon: "bus",
      },
      pickupPoint: {
        text: "Manali Bus Stand or Bhuntar Airport",
        icon: "location",
      },
    },
    overview: {
      title: "Manali: Himalayan Paradise of Adventure and Serenity",
      description:
        "Embark on a breathtaking Himalayan journey with YK Travels to Manali, a mountain paradise nestled in the Beas River Valley at 6,725 feet. Our expertly crafted package combines thrilling adventure activities, cultural immersion, stunning natural landscapes, and comfortable accommodations with knowledgeable guidance. Experience the charm of old Manali's quaint streets, the adventure of Solang Valley, the tranquility of pristine forests, and the cultural richness of ancient temples. With snow-capped peaks as your backdrop, discover why Manali remains one of India's most beloved mountain destinations through YK Travels' thoughtfully designed experiences.",
      activities: [
        {
          title: "Rohtang Pass Adventure",
          description:
            "Experience the thrill of visiting the legendary Rohtang Pass (weather permitting) with YK Travels' specialized mountain excursion, where you'll witness breathtaking panoramic views of the Himalayan ranges, play in pristine snow (seasonal), and capture unforgettable memories under the guidance of experienced mountain guides.",
          emoji: "🏔️",
        },
        {
          title: "River Rafting Experience",
          description:
            "Feel the rush of adrenaline with YK Travels' exclusive white-water rafting adventure on the Beas River, where professional rafting instructors guide you through exciting rapids while ensuring safety and offering stunning views of the surrounding valley and mountains.",
          emoji: "🚣",
        },
        {
          title: "Cultural Heritage Tour",
          description:
            "Immerse yourself in Manali's rich cultural tapestry with YK Travels' guided tours of ancient wooden temples, including the 16th century Hadimba Temple, traditional Himachali villages, and local handicraft centers where you can interact with artisans and learn about mountain crafting traditions.",
          emoji: "🏯",
        },
        {
          title: "Apple Orchard Experience",
          description:
            "Enjoy YK Travels' signature apple orchard visit where you can walk through fragrant apple trees (seasonal blossoms or fruits), learn about Himachal's famous apple cultivation techniques from local farmers, and sample fresh organic produce and apple-based delicacies.",
          emoji: "🍎",
        },
        {
          title: "Himalayan Sunset Dinner",
          description:
            "Indulge in YK Travels' exclusive mountaintop dining experience at a carefully selected viewpoint, where a gourmet meal prepared with local ingredients is served as you watch the sun set over the snow-capped Himalayan peaks, creating a magical atmosphere.",
          emoji: "🌄",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Manali & Local Orientation",
        description:
          "Your Himalayan adventure begins with YK Travels' warm welcome at Manali Bus Stand or Bhuntar Airport. Board our comfortable vehicle for a scenic transfer through the Beas Valley to your carefully selected mountain resort, chosen for its panoramic views and convenient location. After check-in and a refreshing welcome drink featuring local apple cider, enjoy a leisurely lunch with Himachali specialties. In the afternoon, meet your dedicated guide who will provide an orientation about Manali's geography, climate, culture, and safety guidelines. As the day cools, embark on a guided walking tour of Mall Road and nearby areas where your expert will introduce you to local life, architecture, and mountain lifestyle. Visit the Tibet Market for some souvenir shopping with insider recommendations from your guide. As evening approaches, gather for a special welcome dinner featuring traditional Himachali Dham (feast) accompanied by local folk music. Your guide will brief you on tomorrow's adventures and help you prepare your gear for the higher altitudes.",
      },
      {
        day: 2,
        title: "Solang Valley Adventure Day",
        description:
          "After an early breakfast, depart with your YK Travels guide for an exhilarating day at Solang Valley, Manali's adventure playground. During winter months (December-February), engage in snow activities including skiing and sledding with equipment and instructors arranged by YK Travels. In other seasons, experience the thrill of parachuting, zorbing, and paragliding (activities at additional cost, pre-arranged by YK Travels for priority access). Take the Solang ropeway for panoramic mountain views and photo opportunities guided by our professional photographer guide who shares perfect composition tips. Enjoy a picnic lunch amidst the valley's stunning landscape with specially prepared mountain cuisine. In the afternoon, participate in our exclusive team adventure activities designed to connect you with both nature and fellow travelers. Return to your resort by evening for some relaxation time at the in-house spa (optional). Dinner follows with a special presentation on Himalayan ecology and adventure culture by a local expert mountaineer specially invited by YK Travels.",
      },
      {
        day: 3,
        title:
          "Rohtang Pass Excursion (Seasonal) / Old Manali & Cultural Experience",
        description:
          "After breakfast, check out from your resort and transfer to our exclusive riverside cottages for a different mountain experience. Depending on the season, the day unfolds in one of two ways: During the accessible season (May-November, weather permitting), depart early for the iconic Rohtang Pass with special permits arranged by YK Travels. Witness breathtaking Himalayan panoramas, experience snow activities (seasonal), and enjoy a packed gourmet lunch at 13,050 feet. Your guide will explain the ecological significance of this mountain pass while ensuring your comfort at high altitude. During winter or when Rohtang is inaccessible, embark on YK Travels' exclusive Old Manali cultural immersion tour. Visit the ancient Manu Temple, explore charming cafés with tastings arranged by our culinary expert, and participate in a special wood crafting workshop with local artisans. Enjoy a progressive lunch experience sampling multiple local eateries carefully selected for authenticity and quality. By afternoon, return to your riverside cottages. The evening features YK Travels' signature riverside bonfire dinner with storytelling sessions about local legends and mountain folklore.",
      },
      {
        day: 4,
        title: "Naggar Castle & Beas River Adventure",
        description:
          "Start your day with a serene yoga and meditation session by the riverside, guided by YK Travels' wellness expert. After breakfast, journey to the historic Naggar Castle and Nicholas Roerich Art Gallery, where a specialized art historian accompanies your visit providing insights into the Russian painter's Himalayan works and the region's artistic heritage. Enjoy lunch at a heritage restaurant overlooking the Beas Valley with architecture dating back several centuries. In the afternoon, experience YK Travels' exclusive river adventure with white water rafting on the Beas River (seasonal, weather permitting) with professional instructors ensuring safety while maximizing excitement. For non-rafting seasons or preferences, enjoy our curated riverside nature walk identifying Himalayan flora and learning about riverine ecosystems. In the evening, participate in our special apple orchard visit with tastings of local apple products including jams, ciders, and specialties. The day concludes with YK Travels' signature Himalayan sunset dinner at a specially prepared mountaintop location, where you'll enjoy a fusion meal blending Himachali traditions with international techniques while watching the sun set behind snow-capped peaks.",
      },
      {
        day: 5,
        title: "Hadimba Temple Visit & Departure",
        description:
          "Begin your final day with a leisurely breakfast featuring local buckwheat specialties and fresh mountain honey. Check-out from your accommodation and embark on YK Travels' cultural heritage tour visiting the ancient Hadimba Temple, a 16th-century wooden shrine dedicated to Hadimba Devi and surrounded by towering cedar forests. Your guide will explain the fascinating mythology, architecture, and continued spiritual significance of this site. Continue to nearby Vashisht village known for its hot springs and ancient stone temples where you can experience a traditional hot spring bath (optional) believed to have medicinal properties. Enjoy a farewell lunch at a family-run Himachali home where you'll participate in a brief cooking demonstration of local techniques. Before departure, visit YK Travels' selected sustainable souvenir center where genuine local handicrafts support mountain communities. In the afternoon, transfer to Manali Bus Stand or Bhuntar Airport for your onward journey, carrying memories of your intimate encounters with the majestic Himalayas.",
      },
    ],
    cost: {
      includes: [
        "All meals as mentioned in the itinerary",
        "Private AC vehicle for all transfers",
        "Solang Valley excursion with basic activities",
        "Rohtang Pass permits (seasonal, when accessible)",
        "River rafting experience (seasonal)",
        "Cultural tours and temple visits",
        "Apple orchard experience",
        "Services of expert guides throughout the trip",
        "Special Himalayan sunset dinner experience",
        "Folk music welcome dinner",
        "All applicable taxes",
      ],
      excludes: [
        "Airfare or bus fare to/from Manali",
        "Advanced adventure activities in Solang Valley (paragliding, zorbing, etc.)",
        "Alcoholic beverages",
        "Personal expenses and tips",
        "Travel insurance",
        "Ropeway tickets",
        "Activities not mentioned in the inclusions",
        "Specialized photography equipment",
        "Anything not specifically mentioned in the inclusions",
      ],
      notes: [
        "Rohtang Pass visit is subject to weather conditions and seasonal permits",
        "Adventure activities are subject to weather conditions",
        "The itinerary may be modified due to weather, road conditions, or local festivals",
        "Early booking is recommended during peak seasons (May-June, December-January)",
        "Warm clothing is essential even in summer due to evening temperature drops",
        "YK Travels can arrange specialized photography tours at additional cost",
        "Altitude sickness prevention measures are recommended for Rohtang Pass visits",
      ],
    },
    faqs: [
      {
        question: "When is the best time to visit Manali with YK Travels?",
        answer:
          "Manali offers unique experiences across seasons, and YK Travels tailors each package to maximize seasonal highlights. Summer (March-June) brings pleasant temperatures (15-30°C) ideal for outdoor activities, trekking, and river rafting with lush green landscapes and flowing rivers. This is also the best time for Rohtang Pass visits. Monsoon (July-September) transforms the region with verdant beauty but comes with occasional roadblocks and reduced adventure activities, though our packages include contingency plans and indoor cultural experiences. Autumn (October-November) offers clear mountain views, colorful forests, and fewer crowds—perfect for photography and peaceful experiences. Winter (December-February) brings snow-covered landscapes ideal for snow activities, cozy stays, and festive celebrations, though some high-altitude areas become inaccessible. YK Travels provides season-specific equipment, itinerary adjustments, and expert recommendations to ensure optimal experiences regardless of when you visit.",
      },
      {
        question: "What adventure activities does YK Travels offer in Manali?",
        answer:
          "YK Travels curates a comprehensive range of adventure activities in Manali for all skill levels and preferences. Our summer offerings include professional white-water rafting on the Beas River (grades II-IV rapids), guided trekking expeditions ranging from beginner-friendly nature walks to challenging mountain trails, paragliding with certified instructors providing both tandem flights for beginners and advanced options for experienced flyers, and mountain biking with quality equipment and route planning. During winter, we organize skiing sessions with certified instructors for all skill levels, snowboarding adventures, snow trekking with specialized gear, and snowmobile experiences. Year-round activities include zip-lining across valleys, rock climbing on natural features with safety equipment, rappelling adventures, and camping experiences at scenic locations. All activities include thorough safety briefings, quality equipment, and experienced guides, with customization options available based on your physical condition and adventure preferences.",
      },
      {
        question: "What should I pack for my Manali trip with YK Travels?",
        answer:
          "For your Manali adventure with YK Travels, we recommend layered clothing regardless of season due to variable mountain weather. Essential items include warm jackets (waterproof recommended), thermal innerwear (especially for winter), comfortable trekking shoes with good grip, warm socks (several pairs), gloves, caps/hats for sun and cold protection, sunglasses with UV protection, and a small daypack for excursions. During summer (March-June), include lightweight moisture-wicking clothes for daytime, a light fleece for evenings, rain protection, and swimwear for water activities. For winter visits (December-February), pack heavier insulation including down jackets, waterproof pants, thermal layers, snow boots, and additional accessories like ear muffs and neck warmers. Year-round essentials include sunscreen (minimum SPF 50), lip balm with sun protection, personal medications (especially any altitude-related remedies), a reusable water bottle, basic first aid supplies, power banks, and a camera with extra batteries. YK Travels provides a detailed seasonal packing list before your trip and offers certain equipment rentals for specialized activities to save luggage space.",
      },
      {
        question: "Is Manali suitable for families with young children?",
        answer:
          "Absolutely! YK Travels specializes in family-friendly Manali experiences with thoughtfully designed accommodations and activities suitable for all ages. For families with children, we recommend our specially curated family packages that include kid-friendly accommodations with connecting rooms, child safety features, and dedicated play areas. Our experienced guides are trained to engage young minds with age-appropriate information about mountains, local culture, and nature. For younger children (ages 2-7), we organize gentle nature walks, apple orchard visits, cultural demonstrations, and interactive experiences like cookie-making with local ingredients. Older children (8-12) enjoy moderate adventure activities including short treks, beginner river rafting (seasonal), and snow play areas. Teenagers benefit from our specially designed adventure program with appropriate challenges under strict safety protocols. Our vehicles are equipped with child safety seats upon request, our chefs accommodate special dietary needs and preferences for children, and we maintain flexibility in itinerary timing to match children's energy levels. For infants, we can arrange babysitting services and provide essential supplies if needed.",
      },
      {
        question:
          "How does YK Travels handle altitude acclimatization in Manali?",
        answer:
          "YK Travels prioritizes guest safety with our comprehensive altitude acclimatization protocol for Manali and high-altitude excursions. Our itinerary is deliberately structured to allow gradual acclimatization, starting with activities in Manali town (6,725 feet) before proceeding to higher elevations like Rohtang Pass (13,050 feet). Upon arrival, our trained guides provide a detailed briefing on recognizing and preventing altitude sickness symptoms. We schedule appropriate rest periods, especially on your first day, and our first night's accommodation is selected at moderate elevation with oxygen support available. For high-altitude excursions, we include acclimatization stops where guides monitor guest wellness, and we carry portable oxygen systems and first-aid equipment as precautionary measures. Our vehicles are equipped with medical kits, and guides maintain communication with local medical facilities. We emphasize proper hydration with complimentary water provided throughout the trip, discourage alcohol consumption during high-altitude activities, and offer optional preventive medication guidance when appropriate. For guests with known altitude sensitivity or medical concerns, we provide personalized itinerary modifications while maintaining the essence of the Himalayan experience.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d26997.529104325518!2d77.16961241789855!3d32.24956576783311!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39048708163fd03f%3A0x8129a80ebe5076cd!2sManali%2C%20Himachal%20Pradesh!5e0!3m2!1sen!2sin!4v1650870440010!5m2!1sen!2sin",
  },
  {
    id: 19,
    slug: "shimla-yk-travels",
    title: "Shimla Tour with YK Travels",
    shortDescription:
      "Discover the colonial charm and natural beauty of India's favorite hill station with YK Travels' exclusive Shimla package featuring heritage walks, pine forest trails, Himalayan scenic vistas, luxury colonial-style accommodations, toy train rides, and immersive cultural experiences in the Queen of Hills.",
    state: "Himachal Pradesh",
    price: 5999,
    duration: "4 Days",
    activityTags: [
      "heritage",
      "colonial",
      "mountains",
      "toy-train",
      "nature-walk",
      "photography",
      "culture",
      "panoramic-views",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/hp/hp-6.avif",
        alt: "YK Travels Shimla Experience",
      },
      {
        id: 2,
        src: "/images/packages/hp/hp-5.avif",
        alt: "YK Travels Shimla Mall ",
      },
    ],
    stays: [
      {
        id: 1,
        images: [
          {
            id: 1,
            src: "/images/hotel-1.jpeg",
            alt: "YK Travels Colonial Heritage Hotel in Shimla",
          },
        ],
        amenities: [
          "Complimentary WiFi",
          "Heritage Restaurant with Colonial Recipes",
          "Daily Housekeeping",
          "Room Heating",
          "Mountain View Suites",
          "24-hour Front Desk",
          "Victorian-style Lounge",
          "Heritage Library",
          "Laundry Service",
          "Safe Deposit Box",
          "Travel Desk",
          "Railway Station Transfers",
        ],
      },
      {
        id: 2,
        images: [
          {
            id: 1,
            src: "/images/resort-2.jpeg",
            alt: "YK Travels Pine Forest Retreat in Shimla",
          },
        ],
        amenities: [
          "Private Valley View Balconies",
          "Traditional Himachali Dining",
          "Fireplace Lounges",
          "Forest Walking Trails",
          "Guided Nature Walks",
          "Wooden Interiors with Colonial Touches",
          "High Tea Service",
          "Panoramic Views",
          "Walking Distance to Jakhu Hill",
          "Local Art Gallery",
          "Heritage Tours Desk",
        ],
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Heritage hotels and pine forest retreats",
        icon: "building",
      },
      arrivalCity: {
        text: "Shimla",
        icon: "city",
      },
      bestSeason: {
        text: "Year-round destination, best March-June and September-November",
        icon: "sun",
      },
      guide: {
        text: "Expert local guides with heritage specialization",
        icon: "guide",
      },
      maximumAltitude: {
        text: "7,467 feet (Shimla) / 8,000 feet (Kufri)",
        icon: "mountain",
      },
      meals: {
        text: "All meals included with colonial and local cuisine experiences",
        icon: "bowl",
      },
      transportation: {
        text: "Private AC vehicle and heritage toy train experiences",
        icon: "bus",
      },
      pickupPoint: {
        text: "Kalka Railway Station or Shimla Airport",
        icon: "location",
      },
    },
    overview: {
      title: "Shimla: Colonial Heritage in the Himalayan Queen of Hills",
      description:
        "Step back in time with YK Travels on a journey to Shimla, the charming former summer capital of British India nestled in the Himalayan foothills at 7,467 feet. Our expertly crafted package combines colonial heritage exploration, natural beauty, architectural wonders, and comfortable accommodations with knowledgeable guidance. Walk the historic Mall Road, ride the UNESCO-recognized toy train, explore majestic viceregal buildings, and breathe the fresh pine-scented air amidst stunning mountain vistas. From elegant colonial buildings to traditional Himachali culture, discover why Shimla remains one of India's most beloved hill stations through YK Travels' thoughtfully designed heritage experiences.",
      activities: [
        {
          title: "Heritage Walking Tour",
          description:
            "Immerse yourself in Shimla's colonial past with YK Travels' exclusive guided walk through historic districts, where our heritage expert brings to life stories of the Raj era while visiting iconic buildings like Christ Church, Gaiety Theatre, and the Victorian Gothic Viceregal Lodge with special access to normally restricted areas.",
          emoji: "🏛️",
        },
        {
          title: "Kalka-Shimla Toy Train Journey",
          description:
            "Experience the magic of the UNESCO World Heritage narrow-gauge railway with YK Travels' curated toy train journey, traveling through 102 tunnels and over 800 bridges while enjoying spectacular mountain views and colonial-era stations with refreshments and historical commentary provided by our railway expert.",
          emoji: "🚂",
        },
        {
          title: "High Tea Heritage Experience",
          description:
            "Indulge in YK Travels' signature high tea service at a carefully selected colonial-era venue, where traditional English high tea is served with local Himachali delicacies against the backdrop of mountain views, accompanied by storytelling about the social customs of British Indian society.",
          emoji: "🍵",
        },
        {
          title: "Jakhu Temple Trek",
          description:
            "Embark on YK Travels' guided nature trek to the famous Hanuman Temple at Jakhu Hill, Shimla's highest point, where you'll witness panoramic Himalayan views, encounter playful monkeys, and learn about the fascinating blend of natural ecology and religious significance from our expert naturalist.",
          emoji: "🐒",
        },
        {
          title: "Kufri Adventure Excursion",
          description:
            "Enjoy YK Travels' curated excursion to the picturesque Kufri region, where you can experience seasonal activities including yak riding, winter skiing, panoramic himalayan views, and visits to the Himalayan Wildlife Zoo featuring rare snow leopards and other mountain species.",
          emoji: "🏔️",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Kalka & Toy Train Journey to Shimla",
        description:
          "Your heritage journey begins with YK Travels' warm welcome at Kalka Railway Station. After refreshments at our special arrival lounge, board the historic narrow-gauge toy train for a breathtaking journey to Shimla (subject to train schedules; alternative private vehicle transfer available). This UNESCO World Heritage route winds through 102 tunnels and over 800 bridges with spectacular mountain views. Our heritage specialist accompanies you, sharing fascinating stories about the engineering marvel and its historical significance. Enjoy a specially prepared picnic lunch on board in first-class accommodations reserved by YK Travels. Upon arriving at Shimla's colonial-era station, transfer to your carefully selected heritage hotel, chosen for its historical significance and authentic colonial ambiance. After check-in and welcome tea featuring traditional Himachali cookies, enjoy a leisurely orientation walk around the vicinity. As evening approaches, gather for a special welcome dinner in the hotel's heritage restaurant featuring a fusion of colonial-era recipes and local ingredients. Your guide will brief you on tomorrow's adventures and share insights into Shimla's unique historical significance.",
      },
      {
        day: 2,
        title: "Colonial Heritage Walk & Viceregal Lodge Tour",
        description:
          "After a leisurely breakfast featuring English and Himachali specialties, embark on YK Travels' signature heritage walking tour of Shimla. Begin at the iconic Christ Church with its neo-gothic architecture and original stained glass windows, where your expert guide explains the religious and cultural significance during the Raj era. Continue to the Gaiety Theatre, a Victorian gothic masterpiece where exclusive access to the recently restored interiors has been arranged by YK Travels. Walk along the historic Mall Road with its charming shops, visiting landmarks like the Town Hall and Scandal Point while hearing fascinating colonial anecdotes. Enjoy lunch at a 100-year-old restaurant still serving original recipes from the colonial period. In the afternoon, visit the magnificent Viceregal Lodge (Indian Institute of Advanced Study) where YK Travels has arranged special permission for visiting normally restricted areas. Explore the impressive architecture, manicured gardens, and historical rooms where decisions affecting the entire subcontinent were once made. As evening approaches, experience our exclusive high tea service at a heritage venue with traditional scones, cucumber sandwiches, and local pastries. Return to your hotel for some leisure time before dinner featuring a curated menu of Anglo-Indian cuisine accompanied by a presentation on Shimla's architectural heritage.",
      },
      {
        day: 3,
        title: "Jakhu Temple Trek & Ridge Exploration",
        description:
          "Start your day with an optional yoga session on the hotel's heritage lawn overlooking the valley. After breakfast, check out from your hotel and transfer to our exclusive pine forest retreat for a different mountain experience. Once settled in, prepare for YK Travels' guided trek to Jakhu Temple, Shimla's highest point dominated by a massive Hanuman statue. The moderate uphill walk through cedar forests offers wonderful opportunities to observe local flora, fauna, and playful monkeys. At the temple, your guide explains the fascinating blend of religious significance and natural conservation efforts. Enjoy packed refreshments at a scenic viewpoint with panoramic Himalayan vistas. Descend via an alternate route for varied landscapes and photo opportunities. Return for a late lunch at your forest retreat featuring organic, locally-sourced ingredients. In the afternoon, explore The Ridge, Shimla's large open space with colonial-era buildings and impressive views. Visit the State Museum housed in a colonial mansion where YK Travels has arranged a special curatorial tour highlighting the region's art, history, and culture. As evening approaches, enjoy our specially arranged cultural performance featuring traditional Himachali music and dance with explanations of their historical context and significance. Dinner follows at your retreat with a focus on authentic mountain cuisine accompanied by local fruit wines.",
      },
      {
        day: 4,
        title: "Kufri Excursion & Departure",
        description:
          "After breakfast, depart for a half-day excursion to Kufri, a picturesque area 16 km from Shimla offering different seasonal experiences. During winter months, engage in snow activities and beginner-friendly skiing arranged by YK Travels with equipment and instructors. In other seasons, enjoy pony rides to scenic viewpoints and visits to the Himalayan Nature Park with rare animal species including the snow leopard, guided by our wildlife expert. Experience YK Travels' specially arranged traditional lunch at a heritage forest rest house dating back to the colonial era. On the return journey, stop at key viewpoints for final photographs of the majestic Himalayan panoramas. Visit a carefully selected handicraft center where genuine Himachali shawls, caps, and wood carvings support local artisans (with YK Travels' exclusive discount for our guests). Return to Shimla for some free time for last-minute shopping on Mall Road with guidance from our local expert. Enjoy a farewell high tea at a colonial-era venue before transferring to Shimla railway station or airport for your onward journey, carrying memories of your immersive encounter with the Queen of Hills.",
      },
    ],
    cost: {
      includes: [
        "All meals as mentioned in the itinerary",
        "Private AC vehicle for all transfers",
        "First-class toy train journey (subject to availability)",
        "Kufri excursion with basic activities",
        "Heritage walking tours with expert guides",
        "Special access to historical buildings",
        "High tea heritage experiences",
        "Services of heritage specialist guides throughout the trip",
        "Cultural performance experience",
        "All applicable taxes",
      ],
      excludes: [
        "Airfare or train fare to Kalka/from Shimla",
        "Advanced activities in Kufri (skiing equipment, etc.)",
        "Alcoholic beverages",
        "Personal expenses and tips",
        "Travel insurance",
        "Camera fees at monuments",
        "Activities not mentioned in the inclusions",
        "Specialized photography equipment",
        "Anything not specifically mentioned in inclusions",
      ],

      notes: [
        "The toy train journey is subject to availability and railway schedules",
        "Heritage walking tours may be modified during extreme weather conditions",
        "The itinerary may be modified due to weather, road conditions, or local festivals",
        "Early booking is recommended during peak seasons (April-June, December-January)",
        "Warm clothing is essential even in summer due to evening temperature drops",
        "YK Travels can arrange specialized photography tours at additional cost",
        "Kufri excursions are subject to seasonal weather conditions",
      ],
    },
    faqs: [
      {
        question: "When is the best time to visit Shimla with YK Travels?",
        answer:
          "Shimla offers distinctive seasonal experiences, and YK Travels tailors each package to highlight the best of each season. Summer (March-June) brings pleasant temperatures (15-30°C) ideal for heritage walks, outdoor exploration, and enjoying lush green landscapes. This is peak season with vibrant Mall Road activity and ideal conditions for visiting nearby attractions. Monsoon (July-September) transforms the region with verdant beauty and magical mists rolling through pine forests, though occasional rainfall may limit some activities. Our monsoon packages include enhanced indoor heritage experiences and specially designed colonial architecture tours between showers. Autumn (October-November) offers exceptional mountain clarity with crisp temperatures, making it perfect for photography of colonial buildings against mountain backdrops. Winter (December-February) brings snow-covered landscapes that transform heritage buildings into a magical wonderland, perfect for experiencing the colonial-era winter retreat atmosphere that originally attracted the British. YK Travels provides season-specific heritage insights, itinerary adjustments, and expert recommendations to ensure optimal experiences regardless of when you visit.",
      },
      {
        question:
          "What makes Shimla's colonial heritage special and how does YK Travels showcase it?",
        answer:
          "Shimla's unparalleled colonial heritage stems from its unique status as the summer capital of British India, where the government would relocate annually to escape the plains' heat. YK Travels offers exclusive heritage experiences that go beyond typical tourist visits. Our packages include access to restricted areas of prestigious colonial buildings through special partnerships with authorities and private owners. Guests experience comprehensive historical immersion guided by historians who explain architectural styles, historical significance, and personal stories of notable residents. Our heritage walks cover less-visited colonial quarters with specially arranged interior access to private clubs, residences, and government buildings normally closed to the public. YK Travels has established relationships with descendants of colonial families who provide exclusive storytelling sessions about life during the Raj era. We've researched and recreated authentic colonial experiences including high tea services using original recipes and settings. Our heritage specialists can point out filming locations from movies set in colonial Shimla and explain architectural adaptations that blended British designs with local climate requirements. Throughout the experience, we present balanced historical perspectives that acknowledge both colonial legacies and Indian independence movements that shaped Shimla's unique character.",
      },
      {
        question: "What cultural experiences does YK Travels offer in Shimla?",
        answer:
          "YK Travels' Shimla packages celebrate the region's unique cultural tapestry formed by colonial British influences blended with traditional Himachali heritage. Our cultural immersion begins with specially arranged visits to heritage buildings where you'll experience period-specific activities guided by local historians rather than generic guides. We organize authentic high tea sessions in historic venues where you'll learn about the social customs and etiquette of colonial society in settings little changed from the Raj era. Our heritage walks explore beyond typical tourist sites to reveal hidden architectural gems with access to normally restricted buildings through special permissions. You'll experience local Himachali culture through guided visits to traditional villages coinciding with daily activities and receive explanations of customs from community members. We showcase local arts through private folk dance performances, traditional music demonstrations with Himachali instruments, and opportunities to learn about regional handicrafts. YK Travels partners with local artisans for interactive workshops on traditional crafts including shawl weaving, wood carving, and local culinary techniques. Throughout your journey, our culturally sensitive guides explain the fascinating syncretic traditions that have evolved in this unique mountain community where British and Himalayan influences have merged over generations.",
      },
      {
        question: "What should I pack for my Shimla trip with YK Travels?",
        answer:
          "For your Shimla heritage adventure with YK Travels, layered clothing is essential due to variable mountain weather and day-night temperature fluctuations. Essential items include a warm jacket (waterproof recommended), light/medium sweaters for layering, comfortable walking shoes with good traction for Mall Road and heritage walks, a day backpack for excursions, and a good quality camera with extra batteries for capturing colonial architecture and landscapes. During spring and autumn visits (March-May, October-November), pack both light summer clothes for sunny days and warm layers for evenings when temperatures can drop significantly. For monsoon visits (June-September), include quick-dry clothing, waterproof footwear, umbrella, and rain jacket, plus plastic bags to protect electronics. Winter travelers (December-February) should bring thermal underwear, heavy jackets, gloves, warm hats, and multiple layers as Shimla can experience snowfall. Year-round essentials include sunscreen (mountain UV is intense even in cool weather), lip balm, moisturizer for dry mountain air, any personal medications, and a flashlight for evening walks. We recommend bringing semi-formal attire for high tea experiences and heritage dining events to enhance your colonial immersion. YK Travels provides a detailed seasonal packing list before your trip and offers rentals of period-appropriate accessories for those interested in heritage photoshoots.",
      },
      {
        question:
          "How physically demanding is the Shimla itinerary with YK Travels?",
        answer:
          "YK Travels designs our Shimla experiences with multiple activity levels to accommodate various physical abilities while maintaining authentic heritage experiences. The standard itinerary involves moderate physical activity including walking on uneven terrain, stairs without elevators in heritage buildings, some uphill walking at 7,000+ feet elevation where oxygen levels are approximately 78% of sea level. Key considerations include heritage walks (approximately 2-3 hours at a leisurely pace with rest stops), the Jakhu Temple trek (moderate uphill climb with optional points to turn back), and general navigation of Shimla's characteristic steep pathways. For guests with limited mobility, we offer customized options including exclusive vehicle access to viewpoints, abbreviated walking routes, and strategically timed rest periods. Our guides are trained to adjust pace according to group capabilities while ensuring core experiences remain intact. We recommend all guests spend their first day acclimatizing to the altitude with limited physical activity. Guests with respiratory or cardiac conditions should consult their physician before booking and inform us for appropriate arrangements. For photography enthusiasts requiring tripods or heavy equipment, we can arrange porter assistance. While our standard package is suitable for most reasonably fit adults and children above 8 years, we can tailor experiences for specific requirements including alternative transportation options and modified itineraries.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d27375.699196551386!2d77.15359147461172!3d31.078930836253725!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390578e3e35d6e67%3A0x1f7e7ff6ff9f54b7!2sShimla%2C%20Himachal%20Pradesh!5e0!3m2!1sen!2sin!4v1650870440010!5m2!1sen!2sin",
  },
  {
    id: 20,
    slug: "dharamshala-yk-travels",
    title: "Dharamsala Tibetan Cultural Immersion with YK Travels",
    shortDescription:
      "Experience the spiritual heart of Tibetan exile culture with YK Travels' exclusive Dharamsala package featuring guided monastery visits, meditation sessions with Buddhist monks, Himalayan nature treks, traditional healing practices, authentic Tibetan cuisine, and cultural performances amid the majestic backdrop of the Dhauladhar ranges.",
    state: "Himachal Pradesh",
    price: 5999,
    duration: "3 Days",
    activityTags: [
      "buddhist-culture",
      "meditation",
      "monasteries",
      "himalayan-trekking",
      "tibetan-cuisine",
      "spiritual",
      "cultural-immersion",
      "mountain-views",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/hp/hp-3.avif",
        alt: "YK Travels Dharamsala Monastery Experience",
      },
      {
        id: 2,
        src: "/images/packages/hp/hp-4.avif",
        alt: "YK Travels Tibetan Cultural Adventure",
      },
    ],
    stays: [
      {
        id: 1,
        images: [
          {
            id: 1,
            src: "/images/hotel-2.jpeg",
            alt: "YK Travels Monastery-Inspired Accommodation in Dharamsala",
          },
        ],
        amenities: [
          "Mountain View Rooms",
          "Traditional Tibetan Decor",
          "Meditation Garden",
          "Complimentary WiFi",
          "Daily Yoga Sessions",
          "Tibetan Herbal Tea Service",
          "24-hour Front Desk",
          "Buddhist Library",
          "Organic Vegetarian Restaurant",
          "Laundry Service",
          "Travel Desk",
          "Airport Transfers",
        ],
      },
      {
        id: 2,
        images: [
          {
            id: 1,
            src: "/images/resort-3.jpeg",
            alt: "YK Travels Mountain Lodge in McLeod Ganj",
          },
        ],
        amenities: [
          "Private Balconies with Dhauladhar Views",
          "Traditional & International Dining",
          "Himalayan Cedar Interiors",
          "Sunrise Viewing Terrace",
          "Guided Morning Walks",
          "Tibetan Art Displays",
          "Evening Cultural Performances",
          "Organic Herb Garden",
          "Traditional Healing Spa",
          "Walking Distance to Main Square",
          "Cultural Excursion Desk",
        ],
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Tibetan-inspired boutique lodges and mountain retreats",
        icon: "building",
      },
      arrivalCity: {
        text: "Dharamsala/McLeod Ganj",
        icon: "city",
      },
      bestSeason: {
        text: "March-June and September-November",
        icon: "sun",
      },
      guide: {
        text: "Expert local guides with Tibetan cultural specialization",
        icon: "guide",
      },
      maximumAltitude: {
        text: "6,831 feet (McLeod Ganj) / 9,842 feet (Triund Trek)",
        icon: "mountain",
      },
      meals: {
        text: "All meals included with authentic Tibetan cuisine experiences",
        icon: "bowl",
      },
      transportation: {
        text: "Private AC vehicle and local sustainable transportation",
        icon: "bus",
      },
      pickupPoint: {
        text: "Gaggal Airport or Dharamsala Bus Station",
        icon: "location",
      },
    },
    overview: {
      title: "Dharamsala: Tibetan Cultural Heart in the Himalayan Embrace",
      description:
        "Embark on a soul-stirring journey with YK Travels to Dharamsala and McLeod Ganj, the spiritual home of His Holiness the Dalai Lama and the vibrant center of Tibetan exile culture. Nestled in the magnificent Dhauladhar ranges at 6,831 feet, this unique destination blends profound Tibetan Buddhist traditions with breathtaking Himalayan landscapes. Our expertly crafted package offers authentic spiritual experiences, cultural immersion, natural exploration, and comfortable accommodations with knowledgeable guidance. Meditate with practicing monks, explore ancient monastery complexes, trek through deodar forests, and engage with Tibetan art, cuisine, and medicine. From the bustling streets of McLeod Ganj to the tranquil meditation spaces of Dharamsala, discover why this mountain sanctuary continues to inspire seekers from around the world through YK Travels' thoughtfully designed cultural experiences.",
      activities: [
        {
          title: "Monastery Guided Experience",
          description:
            "Discover the spiritual heart of Tibetan Buddhism with YK Travels' exclusive guided visits to major monasteries including Namgyal Monastery (the Dalai Lama's personal monastery) where our cultural expert explains philosophical concepts, meditation practices, and the significance of sacred art while arranging special access to prayer sessions and monk discussions not available to regular visitors.",
          emoji: "🙏",
        },
        {
          title: "Meditation & Mindfulness Retreat",
          description:
            "Experience authentic Tibetan Buddhist meditation techniques with YK Travels' specially arranged sessions led by practicing monks who provide personal guidance on mindfulness practices, breathing techniques, and philosophical insights in serene monastic settings, followed by reflective discussions on incorporating these principles into daily life.",
          emoji: "✨",
        },
        {
          title: "Tibetan Cooking Workshop",
          description:
            "Engage in YK Travels' immersive culinary experience where you'll learn to prepare authentic Tibetan specialties including momos, thukpa, and butter tea in a traditional kitchen setting, guided by Tibetan chefs who share family recipes, cultural significance of foods, and sustainable cooking practices passed through generations.",
          emoji: "🍲",
        },
        {
          title: "Triund Trek Adventure",
          description:
            "Embark on YK Travels' signature Triund trek, a breathtaking journey through rhododendron and oak forests leading to panoramic views of the Dhauladhar range and Kangra Valley, where our expert Himalayan guide shares insights about local ecology, mountain folklore, and meditation practices specifically connected to these sacred peaks.",
          emoji: "🏔️",
        },
        {
          title: "Tibetan Cultural Performance",
          description:
            "Witness YK Travels' exclusively arranged authentic Tibetan cultural show featuring traditional masked dances, opera performances, and folk music that tell stories of Buddhist mythology and Tibetan history, with expert narration explaining the significance of each art form and its role in preserving cultural heritage in exile.",
          emoji: "🎭",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Dharamsala & Cultural Orientation",
        description:
          "Your Tibetan cultural journey begins with YK Travels' warm welcome at Gaggal Airport or Dharamsala Bus Station. Enjoy a scenic drive to McLeod Ganj with your expert cultural guide pointing out significant landmarks and sharing insights about the region's transformation after becoming home to the Tibetan government-in-exile. Upon arrival at your carefully selected Tibetan-inspired accommodation, refresh with traditional butter tea and locally-made biscuits served in an authentic setting. After check-in, embark on YK Travels' orientation walk through McLeod Ganj's main square and surroundings, where your guide introduces you to the unique blend of Tibetan and local Gaddi culture that characterizes the area. Visit the handicraft center where refugee artisans create traditional items, with special access to workshop areas arranged exclusively for YK Travels' guests. As daylight fades, participate in an evening prayer lamp lighting ceremony at a local shrine, where your guide explains the significance of various Buddhist rituals. The day concludes with a welcome dinner featuring authentic Tibetan cuisine at a restaurant established by first-generation refugees, where you'll taste traditional specialties prepared using time-honored techniques. Your guide will provide insights into the next day's spiritual explorations and answer questions about Tibetan culture and history.",
      },
      {
        day: 2,
        title: "Monastery Circuit & Meditation Introduction",
        description:
          "Begin your day with an optional sunrise yoga and breathing session on the hotel's mountain-view terrace led by a local practitioner selected by YK Travels. After a nutritious breakfast featuring organic local ingredients, embark on our signature monastery circuit tour. Your first stop is the magnificent Namgyal Monastery, the personal monastery of His Holiness the Dalai Lama, where YK Travels has arranged special access to observe morning prayer ceremonies and philosophical debates among monks. Your cultural expert will translate key concepts and explain the intricate symbolism of the thangka paintings and butter sculptures. Continue to Nechung Monastery, seat of the Tibetan state oracle, where your guide shares fascinating stories of prophecies and spiritual guidance that have shaped Tibetan history. Enjoy a mindfully prepared vegetarian lunch at an authentic monastery canteen, where YK Travels' guests are specially welcomed. In the afternoon, visit the Tibet Museum where a curator gives your group an exclusive tour highlighting the journey of Tibetans into exile and their cultural preservation efforts. The day's highlight comes with YK Travels' specially arranged meditation session led by a senior Buddhist practitioner who introduces fundamental techniques and philosophical concepts, tailored to beginners while remaining authentic to Tibetan traditions. Return to your accommodation for some relaxation time before attending an evening talk by a former political prisoner who shares personal experiences of cultural resilience, arranged exclusively for YK Travels' guests. Dinner features fusion Tibetan-Himachali cuisine accompanied by traditional music played on Tibetan instruments.",
      },
      {
        day: 3,
        title: "Tibetan Arts & Healing Traditions",
        description:
          "After breakfast, visit the Norbulingka Institute, a center for preserving Tibetan arts, where YK Travels has arranged a private demonstration of thangka painting, woodcarving, and appliqué creation by master artisans. Participate in a hands-on mini-workshop where you'll learn basic techniques of Tibetan artistic traditions and create a small memento under expert guidance. Explore the institute's beautiful Japanese-inspired gardens designed to reflect the original Norbulingka palace in Lhasa. For lunch, experience an authentic Tibetan family meal in a local home, where your hosts share stories of their journey to India and cultural adaptation while teaching you proper etiquette for eating traditional dishes. In the afternoon, discover Tibetan healing traditions with a visit to Men-Tsee-Khang, the Tibetan Medical Institute, where YK Travels' connections provide special access to observe pharmacists preparing ancient herbal formulations. Learn about the principles of Tibetan medicine, astrological calculations, and holistic wellness approaches from practitioners trained in this 2,500-year-old tradition. Participate in a pulse diagnosis demonstration and receive personalized wellness recommendations based on Tibetan medical principles. Later, visit a center for traditional Tibetan performing arts where you'll witness young artists practicing ancient dance forms and musical techniques preserved in exile. The evening features YK Travels' exclusive cultural performance including masked dances, operatic traditions, and folk music with expert narration explaining their historical and spiritual significance. Dinner includes specialties from different regions of Tibet, introducing you to the diversity within Tibetan cuisine.",
      },
      {
        day: 4,
        title: "Triund Trek & Mountain Meditation",
        description:
          "Prepare for a day of natural splendor with an early breakfast before embarking on YK Travels' signature Triund Trek. Your expert Himalayan guide leads you through scenic paths ascending through mixed forests of rhododendron, oak, and deodar trees, pointing out medicinal plants and sharing local ecological knowledge. The moderate 9 km trek (round trip) includes strategic rest stops where your guide demonstrates mountain breathing techniques used by Buddhist practitioners to maintain energy. Reach the magnificent Triund ridge at 9,842 feet where YK Travels has prepared a special picnic lunch featuring organic trail food and traditional Tibetan wilderness recipes. From this vantage point, marvel at the dramatic Dhauladhar range on one side and the sweeping Kangra Valley on the other as your guide explains the spiritual significance of these mountains in local Buddhism and indigenous traditions. Participate in a mountain meditation session specially designed for this sacred location, connecting with the natural elements in traditional Tibetan fashion. Experienced trekkers can optionally continue higher to Snow Line (seasonal) with an assistant guide, while others can enjoy reflective time at Triund. Descend via an alternate route offering different perspectives and photographic opportunities. Upon return, rejuvenate with a traditional hot foot bath infused with Tibetan herbs prepared by YK Travels' staff. The evening features a specially arranged presentation by a conservation expert on local biodiversity and sustainable practices. Dinner includes high-energy mountain cuisine with Tibetan influences to replenish after the day's trek.",
      },
      {
        day: 5,
        title: "Dalai Lama Complex & Departure",
        description:
          "On your final day, begin with an early visit to the Tsuglagkhang Complex (the Dalai Lama's temple) when morning prayers create the most atmospheric experience. YK Travels has arranged for a senior monk to guide you through the main temple, explaining the significance of the three main Buddha statues and the spiritual practices occurring around you. Observe pilgrims performing circumambulation (kora) and learn about this devotional practice as you join them in walking the sacred path. Visit the Kalachakra Temple with its mesmerizing murals and the peaceful Japanese-built meditation hall where you'll have time for final reflection. Browse the collection at the Tibet Bookstore where YK Travels' guests receive expert recommendations on the most authoritative works on Tibetan Buddhism and culture. For your farewell experience, participate in a traditional Tibetan butter tea ceremony where you'll learn the cultural significance of this daily ritual and receive a blessing scarf (khata) as a memento of your journey. Enjoy a final lunch featuring special occasion Tibetan dishes rarely served in restaurants, prepared exclusively for YK Travels' guests. Before departure, visit the Tibetan Children's Village if time permits, where you'll learn about educational efforts for the next generation of Tibetans in exile. YK Travels facilitates your departure transfer to Gaggal Airport or bus station with a special farewell gift of locally-made Tibetan handicrafts, carrying with you not just souvenirs but the serene wisdom and cultural richness of this unique Himalayan destination.",
      },
    ],
    cost: {
      includes: [
        "All meals as mentioned in the itinerary",
        "Private AC vehicle for all transfers",
        "Special access to monastery sessions",
        "Meditation workshops with qualified instructors",
        "Cultural performance experiences",
        "Guided Triund Trek with experienced mountain guide",
        "Special cultural experiences (cooking class, arts workshop)",
        "Services of Tibetan culture specialist guides throughout",
        "All applicable taxes",
      ],
      excludes: [
        "Airfare or bus fare to/from Dharamsala",
        "Personal donations at religious sites",
        "Advanced meditation retreats",
        "Alcoholic beverages",
        "Personal expenses and tips",
        "Travel insurance",
        "Camera fees at certain institutions",
        "Activities not mentioned in the inclusions",
        "Anything not specifically mentioned in inclusions",
      ],
      notes: [
        "Schedule may be adjusted around potential teachings by His Holiness the Dalai Lama",
        "Triund Trek is weather-dependent and may be substituted with an alternative activity",
        "The itinerary may be modified due to local festivals or monastery schedules",
        "Early booking is recommended during peak seasons (April-June, September-October)",
        "Modest clothing required for monastery visits (shoulders and knees covered)",
        "Photography restrictions exist in certain sacred areas",
        "YK Travels can arrange extended meditation retreats for interested guests",
      ],
    },
    faqs: [
      {
        question:
          "What makes Dharamsala unique and how does YK Travels showcase this specialness?",
        answer:
          "Dharamsala's uniqueness stems from its extraordinary position as the meeting point of ancient Tibetan Buddhist traditions and contemporary exile culture against a backdrop of magnificent Himalayan landscapes. As the home of His Holiness the Dalai Lama and the Tibetan government-in-exile since 1959, this hill station offers unparalleled access to authentic Tibetan spiritual practices, arts, cuisine, and cultural preservation efforts outside of Tibet itself. YK Travels showcases this specialness through exclusive insider experiences not available to independent travelers. Our established relationships with monastery officials provide special access to prayer sessions, philosophical debates, and personal interactions with practicing monks. We've cultivated connections with first-generation Tibetan refugees who share personal exile narratives and cultural knowledge during private sessions arranged only for our guests. Our packages feature specially arranged demonstrations of rare art forms by master practitioners including thangka painting, butter sculpture, and traditional performing arts. YK Travels has developed unique meditation experiences guided by qualified Buddhist practitioners in authentic settings rather than commercial adaptations. Our carefully selected accommodations incorporate authentic Tibetan architectural elements and cultural practices while maintaining comfortable standards. Throughout your journey, our specially trained guides—many from Tibetan backgrounds themselves—provide contextualized understanding of both traditional practices and contemporary challenges facing the exile community, offering balanced perspectives on complex historical and cultural realities that casual visitors might miss.",
      },
      {
        question:
          "How authentically can I experience Tibetan Buddhism with YK Travels in Dharamsala?",
        answer:
          "YK Travels offers extraordinarily authentic Tibetan Buddhist experiences in Dharamsala thanks to our established relationships with the monastic community and cultural institutions. Unlike commercial spirituality packages, our experiences are designed with guidance from practicing Buddhist teachers to ensure respectful and meaningful engagement with living traditions. Our monastery visits coincide with actual prayer times and ritual performances rather than tourist demonstrations, allowing guests to witness genuine spiritual practices. YK Travels has secured special permission for our guests to observe philosophical debates among monks studying for their Geshe (equivalent to PhD) degrees, normally closed to visitors. Our meditation instructions come directly from qualified practitioners within traditional lineages who provide accurate guidance adjusted for beginners while maintaining doctrinal authenticity. We facilitate meaningful conversations with monastics who share insights about how ancient teachings apply to contemporary challenges. For interested guests, we can arrange participation in specific ceremonies with proper guidance on etiquette and meaning, ensuring respectful engagement. Our guides are specifically trained to explain complex philosophical concepts in accessible terms without oversimplification, helping you understand the profound underpinnings of visible practices. We emphasize educational preparation before monastery visits, providing context about appropriate behaviors and offerings. Most importantly, our approach balances authentic exposure with respectful distance—recognizing that these are living religious communities, not tourist attractions. We encourage questions, reflection, and personal engagement while emphasizing that Buddhism is a sophisticated philosophical tradition rather than exotic spectacle. Whether you're a practicing Buddhist seeking deeper connection or simply curious about these traditions, YK Travels ensures encounters that honor both the tradition and your sincere interest.",
      },
      {
        question: "What should I pack for my Dharamsala trip with YK Travels?",
        answer:
          "For your Tibetan cultural immersion with YK Travels in Dharamsala, thoughtful packing enhances both comfort and cultural respectfulness. Essential items include modest clothing for monastery visits (covering shoulders, knees, and chest), with at least one slightly more formal outfit for special cultural events and potential teachings. Layered clothing is crucial for mountain weather variations—pack lightweight moisture-wicking base layers, mid-weight insulation layers, and a waterproof/windproof outer shell jacket. For spring and autumn visits (March-May, September-November), prepare for temperature ranges from 10-25°C (50-77°F) with chilly evenings. Summer travelers (June-August) should bring light clothing with rain protection for monsoon showers, while winter visitors (December-February) need heavy jackets, thermal layers, warm hats, and gloves for temperatures potentially dropping below freezing. Comfortable walking shoes with good traction are essential for Dharamsala's steep paths and the Triund trek. YK Travels recommends bringing a daypack for daily excursions, reusable water bottle (safe drinking water is provided), sunscreen and hat (mountain UV exposure is intense), insect repellent for summer months, personal medications, and a flashlight for evening walks (power outages occur). Cultural considerations include bringing a journal for reflection after meditation sessions, a respectable scarf for monastery visits (white is traditional but not required), and a small amount of cash in small denominations for personal donations at religious sites. Photography enthusiasts should note that some indoor monastery areas restrict photography—our guides will advise accordingly. YK Travels provides comprehensive pre-departure information including seasonal updates and can arrange for rental of specialized trekking equipment if needed.",
      },
      {
        question:
          "How physically demanding is the Triund Trek included in YK Travels' Dharamsala package?",
        answer:
          "The Triund Trek included in YK Travels' Dharamsala package is moderately challenging yet accessible for most reasonably fit travelers without specialized mountaineering experience. This 9-kilometer round-trip hike ascends from approximately 6,800 feet in McLeod Ganj to 9,840 feet at Triund ridge—a net elevation gain of about 3,000 feet. The trek typically takes 3-4 hours uphill and 2-3 hours downhill with our strategically planned rest stops. The trail begins with a gradual forest path that becomes progressively steeper with some rocky sections in the final third, requiring basic surefootedness but no technical climbing. YK Travels has designed this experience with flexibility for varying fitness levels. Our guides set a comfortable pace suitable for diverse groups, emphasizing enjoyment over speed. For guests preferring a less strenuous experience, we offer shorter trekking options with comparable views of the magnificent Dhauladhar range. All hikers should be prepared for effects of altitude (beginning breathlessness is normal), variable mountain weather (conditions can change rapidly), and challenging trail conditions in certain sections. We recommend preparatory walking for several weeks before your trip if you have a sedentary lifestyle. YK Travels provides proper briefing, adequate hydration, energy-rich trail snacks, and appropriately timed rest stops. Our guides carry basic first aid supplies and oxygen supplements as precautionary measures. For guests with knee issues, we recommend bringing trekking poles (available for rent). Physical considerations aside, the breathtaking mountain panoramas, peaceful forest immersion, and sense of accomplishment make this trek a highlight for most participants. YK Travels can arrange alternative activities for guests unable to participate in the trek.",
      },
      {
        question:
          "What opportunities exist for interacting with the Tibetan exile community through YK Travels?",
        answer:
          "YK Travels' Dharamsala package offers exceptional opportunities for meaningful engagement with the Tibetan exile community through carefully developed relationships that benefit both visitors and local residents. Unlike superficial tourist encounters, we facilitate genuine cultural exchange through multiple channels. Our itinerary includes a special home-hosted meal with a Tibetan family who shares personal exile narratives and cultural knowledge in an intimate setting, providing perspective on both historical challenges and contemporary adaptations. YK Travels has established connections with former political prisoners who offer moving testimonials about their experiences and ongoing advocacy work during private speaking sessions exclusive to our guests. We arrange conversations with master artisans at cultural preservation centers like Norbulingka Institute, where you'll learn about traditional arts and their evolution in exile directly from practitioners. Our specially arranged workshops allow you to learn traditional skills such as momo-making, butter sculpture, or thangka painting from community members in small-group settings. YK Travels supports community initiatives through our visits to the Tibetan Children's Village educational project and various NGOs working with new refugees. For guests with professional expertise in relevant fields (education, healthcare, etc.), we can sometimes arrange specialized meetings with corresponding Tibetan institutions upon advance request. Our guides include members of the Tibetan community who provide insider perspectives while navigating cultural nuances. Throughout these interactions, YK Travels emphasizes respectful engagement—preparing visitors with cultural context, facilitating appropriate communication, and ensuring exchanges benefit the community through fair compensation and dignified representation. Many of our guests develop lasting connections with community members that continue beyond their visit.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d27252.759773444144!2d76.30458387461553!3d32.236746436116064!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391b50df65bd7311%3A0x3e08bdb954e6a41!2sMcLeod%20Ganj%2C%20Dharamshala%2C%20Himachal%20Pradesh!5e0!3m2!1sen!2sin!4v1650870440010!5m2!1sen!2sin",
  },
  {
    id: 21,
    slug: "kasol-yk-travels",
    title: "Kasol Himalayan Wilderness Retreat with YK Travels",
    shortDescription:
      "Escape to the pristine Parvati Valley with YK Travels' exclusive Kasol package featuring riverside relaxation, challenging Himalayan treks, traditional Himachali village experiences, rejuvenating hot springs, authentic mountain cuisine, and immersive nature connections in one of India's most breathtaking alpine settings.",
    state: "Himachal Pradesh",
    price: 5999,
    duration: "3 Days",
    activityTags: [
      "trekking",
      "camping",
      "village-homestay",
      "hot-springs",
      "nature-photography",
      "wilderness-experience",
      "mountain-cuisine",
      "adventure",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/hp/hp-2.avif",
        alt: "YK Travels Kasol Riverside Experience",
      },
      {
        id: 2,
        src: "/images/packages/hp/hp-5.avif",
        alt: "YK Travels Parvati Valley Adventure",
      },
    ],
    stays: [
      {
        id: 1,
        images: [
          {
            id: 1,
            src: "/images/tent2.jpg",
            alt: "YK Travels Luxury Riverside Camp in Kasol",
          },
        ],
        amenities: [
          "Premium Waterproof Alpine Tents",
          "Memory Foam Camping Mattresses",
          "Private Riverside Sitting Areas",
          "Wilderness Dining Experience",
          "Campfire Gatherings",
          "Hot Water Availability",
          "Eco-friendly Toiletries",
          "Solar Charging Stations",
          "24-hour Camp Security",
          "First Aid Station",
          "Guided Nature Walks",
          "Mountain View Meditation Spots",
        ],
      },
      {
        id: 2,
        images: [
          {
            id: 1,
            src: "/images/tent3.jpg",
            alt: "YK Travels Traditional Himalayan Lodge in Kasol",
          },
        ],
        amenities: [
          "Rooms with Parvati River Views",
          "Traditional Himachali Architecture",
          "Organic Farm-to-Table Dining",
          "Mountain Herb Gardens",
          "Evening Cultural Performances",
          "Panoramic Viewing Deck",
          "Traditional Wood Stoves",
          "Library with Mountain Literature",
          "Guided Village Walks",
          "Local Craft Demonstrations",
          "Wilderness Excursion Desk",
          "Natural Spring Water Supply",
        ],
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Riverside luxury camping and traditional Himalayan lodges",
        icon: "building",
      },
      arrivalCity: {
        text: "Bhuntar/Kasol",
        icon: "city",
      },
      bestSeason: {
        text: "April-June and September-November",
        icon: "sun",
      },
      guide: {
        text: "Expert local guides with wilderness specialization",
        icon: "guide",
      },
      maximumAltitude: {
        text: "5,577 feet (Kasol) / 11,500 feet (Kheerganga)",
        icon: "mountain",
      },
      meals: {
        text: "All meals included with riverside dining and village cuisine experiences",
        icon: "bowl",
      },
      transportation: {
        text: "Private vehicles and guided trekking experiences",
        icon: "bus",
      },
      pickupPoint: {
        text: "Bhuntar Airport or Kasol Bus Stand",
        icon: "location",
      },
    },
    overview: {
      title: "Kasol: Pristine Wilderness in the Enchanting Parvati Valley",
      description:
        "Discover the untamed beauty of the Himalayas with YK Travels on an immersive journey to Kasol, a serene mountain hamlet nestled along the pristine Parvati River at 5,577 feet. Our expertly crafted package combines wilderness adventures, cultural discoveries, natural wonders, and comfortable accommodations with knowledgeable guidance. Trek through ancient pine forests to hidden villages, soak in natural hot springs with therapeutic minerals, feast on distinctive mountain cuisine, and sleep under canopies of stars while listening to the river's gentle melody. From challenging high-altitude treks to peaceful riverside meditation, experience the multifaceted charm of this unspoiled Himalayan valley through YK Travels' thoughtfully designed nature-focused experiences.",
      activities: [
        {
          title: "Chalal Forest Trek",
          description:
            "Experience the tranquility of ancient Himalayan forests with YK Travels' guided trek to Chalal village, crossing the iconic suspension bridge over the Parvati River and exploring pine-covered paths while our specialist naturalist identifies local flora, fauna, and medicinal plants used in traditional mountain healing practices.",
          emoji: "🌲",
        },
        {
          title: "Kheerganga Hot Springs Expedition",
          description:
            "Embark on YK Travels' signature Kheerganga trek through diverse mountain landscapes to reach natural hot springs at 9,700 feet where you'll soak in mineral-rich waters while gazing at panoramic Himalayan views, with our expert mountain guide sharing legends associated with these sacred healing waters.",
          emoji: "♨️",
        },
        {
          title: "Riverside Wilderness Dining",
          description:
            "Indulge in YK Travels' exclusive dining experience set on the banks of the Parvati River, where our mountain chef prepares traditional Himachali dishes using locally-foraged ingredients and ancient cooking techniques, served in a naturally beautiful setting with the river's melody as background music.",
          emoji: "🍽️",
        },
        {
          title: "Malana Ancient Village Exploration",
          description:
            "Join YK Travels' expert cultural guide for a respectful visit to the unique village of Malana, known for its distinctive customs and claims of Greek ancestry, where you'll learn about their ancient democratic system, unique architectural style, and traditional lifestyle while respecting local protocols.",
          emoji: "🏘️",
        },
        {
          title: "Himalayan Stargazing Experience",
          description:
            "Marvel at the celestial wonders with YK Travels' specially arranged evening stargazing session where our astronomy expert uses professional equipment to showcase the brilliant mountain night sky, identifying constellations, planets, and sharing both scientific knowledge and local mountain folklore about the heavens.",
          emoji: "✨",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Kasol & Riverside Orientation",
        description:
          "Your Himalayan wilderness journey begins with YK Travels' warm welcome at Bhuntar Airport or Kasol Bus Stand. Enjoy a scenic drive along the Parvati River with your expert naturalist guide pointing out distinctive mountain features and introducing the ecological significance of this pristine valley. Arrive at our exclusively selected riverside campsite where luxury alpine tents await with comfortable amenities that balance wilderness immersion with essential comforts. After check-in and refreshments featuring local mountain herbs and fruits, embark on YK Travels' orientation walk along the famous Kasol riverside trail. Your guide will introduce you to the diverse ecosystem of alpine flowers, medicinal plants, and river ecology while pointing out ideal spots for photography and meditation. Experience your first river crossing on a traditional suspension bridge, feeling the thrill of the flowing waters below as your guide explains the river's central role in local life and mythology. Return to camp for a special welcome ceremony where you'll be introduced to traditional Himachali customs with a tikka blessing and local attire experience arranged exclusively for YK Travels' guests. As evening approaches, gather around a campfire for your welcome dinner featuring authentic mountain cuisine prepared with locally-sourced ingredients, as your guide shares fascinating tales of Parvati Valley folklore and outlines the adventures awaiting you in the coming days.",
      },
      {
        day: 2,
        title: "Chalal Trek & Forest Immersion Experience",
        description:
          "Begin your day with an optional sunrise yoga session beside the pristine Parvati River, where the gentle sounds of flowing water create a perfect backdrop for mindfulness practice. After an energizing breakfast featuring organic mountain produce, embark on YK Travels' signature Chalal Forest Trek. Cross the iconic suspension bridge swaying gently above the river as your specialist naturalist guide explains the unique ecological transition zones you'll encounter. The moderate 3 km trail winds through ancient pine and deodar forests where your guide identifies medicinal plants used in traditional Himachali healing practices, pointing out rare alpine flowers and teaching you to recognize signs of wildlife. Reach the serene village of Chalal where YK Travels has arranged a special interaction with local artisans who demonstrate traditional crafts including woolen weaving and wood carving techniques passed down through generations. Participate in a hands-on workshop creating a small souvenir under expert guidance. Enjoy a forest picnic lunch featuring traditional Himachali wilderness recipes prepared with locally-foraged ingredients in a scenic clearing with panoramic valley views. After lunch, your guide leads you to a secluded meditation spot where mountain energy is particularly concentrated, teaching simple techniques to connect with natural elements. For photography enthusiasts, special attention is given to capturing the dramatic forest light and landscapes with expert tips. Return to Kasol via an alternate route offering different perspectives and ecological zones. The evening features YK Travels' exclusive riverside dining experience where our mountain chef prepares a traditional meal on an open flame while sharing culinary stories and techniques. After dinner, gather for a specially arranged stargazing session where our astronomy expert uses professional equipment to showcase the brilliant mountain night sky, identifying constellations and sharing both scientific knowledge and local folklore about celestial patterns.",
      },
      {
        day: 3,
        title: "Kheerganga Hot Springs Trek",
        description:
          "Today brings YK Travels' highlight adventure—the Kheerganga Hot Springs Trek. After an early breakfast with energy-rich mountain staples, drive to the trailhead at Barshaini with your expert trekking guide who provides a comprehensive briefing on the day's journey. Begin the moderate to challenging 12 km trek (round trip) that ascends through diverse ecological zones including pine forests, traditional villages, and alpine meadows. Your guide points out dramatic changes in vegetation as you gain altitude, explaining mountain formation processes and identifying distant peaks. Cross charming wooden bridges over cascading streams, where your guide shares local legends associated with these waters. YK Travels has arranged strategic rest stops at the most scenic points, where refreshments featuring local trail foods are served. Reach the magnificent Kheerganga plateau at 9,700 feet where natural hot springs await, believed to have therapeutic mineral properties. Your guide explains the spiritual significance of these waters in Hindu mythology as you enjoy a restorative soak with panoramic views of snow-capped peaks. YK Travels has prepared a special high-altitude picnic lunch featuring traditional trekking cuisine designed for energy and warmth. After adequate rest and rejuvenation, begin your descent with carefully guided instruction on proper downhill trekking techniques to protect knees and ankles. Return to your accommodation in the evening for a specially arranged Himachali therapeutic massage using local herbs to soothe trekking-tired muscles (optional, at additional cost). Dinner features high-protein recovery cuisine with traditional mountain recipes specifically designed for post-trek replenishment. The evening concludes with an interactive session where your guide shares captivating stories of historical expeditions through these valleys, including ancient trading routes and pilgrimage paths.",
      },
      {
        day: 4,
        title: "Malana Village Experience & Departure",
        description:
          "On your final day, begin with a gentle riverside meditation session focusing on the flowing water as a metaphor for mountain wisdom and impermanence. After breakfast, embark on YK Travels' culturally sensitive visit to the ancient village of Malana, known for its unique customs and distinct social structure. Your expert cultural guide provides comprehensive briefing on respectful visitation protocols, explaining the village's claims of Greek ancestry and their unique democratic system that predates modern governance. Observe the distinctive architectural style featuring intricate woodwork as your guide explains adaptation techniques to harsh mountain environments. YK Travels has arranged a special meeting with village elders (subject to their availability and consent) where you can learn about their traditional lifestyle through translated conversation. In a designated visitor area, participate in a demonstration of local handcrafts and agricultural techniques that sustain this remote community. Enjoy a specially prepared lunch featuring dishes unique to this isolated cultural pocket, with ingredients grown in their traditional mountain fields. As you take in final views of the magnificent Parvati Valley, your guide facilitates a reflective discussion about sustainable mountain tourism and cultural preservation. Before departure, participate in a traditional farewell ceremony where you'll receive a locally-made blessing amulet as a memento of your Himalayan journey. YK Travels arranges your comfortable transfer to Bhuntar Airport or Kasol Bus Stand, with your guide providing recommendations for future Himalayan explorations and sustainable travel practices to preserve these pristine environments for generations to come.",
      },
    ],
    cost: {
      includes: [
        "All meals as mentioned in the itinerary",
        "Private transportation for all transfers",
        "Luxury riverside camping accommodation",
        "Traditional mountain lodge stay",
        "Expert naturalist and cultural guides throughout",
        "Guided trekking experiences with safety equipment",
        "Special cultural experiences and village visits",
        "Stargazing session with equipment",
        "All applicable taxes",
      ],
      excludes: [
        "Airfare or bus fare to/from Bhuntar/Kasol",
        "Personal outdoor equipment (hiking poles, specialized clothing)",
        "Alcoholic beverages",
        "Optional therapeutic massage",
        "Personal expenses and tips",
        "Travel insurance",
        "Activities not mentioned in the inclusions",
        "Anything not specifically mentioned in inclusions",
      ],
      notes: [
        "Kheerganga Trek is weather-dependent and may be substituted with an alternative activity",
        "Malana village visit follows strict local protocols and areas accessible may vary",
        "The itinerary may be modified due to local conditions or seasonal factors",
        "Early booking is recommended during peak seasons (May-June, September-October)",
        "Modest clothing required for village visits",
        "Photography restrictions exist in certain traditional areas",
        "YK Travels can arrange extended wilderness experiences for interested guests",
      ],
    },
    faqs: [
      {
        question:
          "What makes Kasol unique and how does YK Travels showcase this specialness?",
        answer:
          "Kasol's uniqueness stems from its extraordinary position at the confluence of pristine Himalayan wilderness and vibrant mountain culture in the Parvati Valley. This serene hamlet offers an exceptional blend of natural wonders including ancient pine forests, crystal-clear rivers, hot springs, and towering peaks alongside traditional Himachali villages that maintain centuries-old customs. YK Travels showcases this specialness through carefully curated experiences that access the valley's hidden treasures beyond typical tourist routes. Our established relationships with local communities provide exclusive access to traditional homes, ceremonies, and cultural practices typically closed to outside visitors. Our naturalist guides have mapped lesser-known forest paths with exceptional biodiversity viewpoints, identifying both common and rare Himalayan flora with medicinal properties. YK Travels has developed unique riverside experiences including specialized meditation spots where natural acoustics enhance mindfulness practices. Our astronomy expert has identified optimal stargazing locations with minimal light pollution and maximum celestial visibility. Our culinary team works directly with forest foragers and traditional cooks to offer authentic mountain cuisine using ancient techniques and ingredients specific to this valley. Throughout your journey, our specially trained guides—many from local Himachali backgrounds—provide contextualized understanding of both ecological significance and cultural practices, offering balanced perspectives on sustainability challenges and conservation efforts. We've thoughtfully balanced immersive wilderness experiences with comfortable accommodations that reflect the region's architectural heritage while maintaining essential comforts, creating a journey that captures Kasol's authentic character rather than commercial adaptations.",
      },
      {
        question:
          "How physically demanding are the treks included in YK Travels' Kasol package?",
        answer:
          "YK Travels' Kasol package includes two distinct trekking experiences designed to accommodate varying fitness levels while providing authentic Himalayan wilderness immersion. The Chalal Forest Trek (Day 2) is relatively gentle, covering approximately 3 kilometers each way on gradually undulating terrain with minimal elevation gain. This trek typically takes 1.5-2 hours each direction at a comfortable pace with multiple interpretive and photography stops. The trail is well-established though occasionally uneven, requiring basic surefootedness but no specialized equipment or previous trekking experience. This experience is suitable for most travelers with general good health, including older adults and families with children above 8 years. The Kheerganga Hot Springs Trek (Day 3) presents a more substantial challenge, covering approximately 6 kilometers each way with a significant elevation gain of about 4,000 feet from Barshaini (7,700 ft) to Kheerganga (11,500 ft). This moderately difficult trek typically takes 4-5 hours uphill and 3-4 hours downhill with our strategically planned rest stops. The trail features some steep sections with uneven surfaces and narrow paths that require reasonable balance and stamina. Trekkers should be prepared for effects of altitude (beginning breathlessness is normal), variable mountain weather, and challenging trail conditions in certain sections. YK Travels has designed both experiences with flexibility for varying abilities. Our guides set appropriate paces, emphasizing enjoyment over speed and offering alternative turnaround points for those preferring shorter distances. We provide proper briefing, adequate hydration, energy-rich trail snacks, and appropriately timed rest breaks. For guests with knee issues, we recommend bringing trekking poles (available for rent). The breathtaking mountain panoramas, forest immersion, and sense of accomplishment make these treks highlights for most participants, but YK Travels can arrange alternative activities for guests unable to participate in the more strenuous Kheerganga trek.",
      },
      {
        question: "What should I pack for my Kasol trip with YK Travels?",
        answer:
          "For your wilderness retreat with YK Travels in Kasol, thoughtful packing enhances both comfort and enjoyment of the Himalayan environment. Essential clothing items include lightweight moisture-wicking base layers, insulating mid-layers (fleece or wool), a waterproof/windproof outer shell jacket, comfortable hiking pants (preferably quick-dry material), sufficient changes of undergarments and socks (including at least 2 pairs of cushioned hiking socks), sleepwear suitable for potentially cool nights, and modest clothing for village visits (covering shoulders and knees). For spring and autumn visits (April-June, September-November), prepare for temperature ranges from 15-25°C (59-77°F) during days with cooler 5-15°C (41-59°F) evenings. Summer monsoon travelers (July-August) should emphasize quick-dry materials and reliable rain protection, while winter visitors need significantly warmer gear including thermal layers and insulated jackets. Footwear requirements include sturdy hiking shoes/boots with good ankle support and traction for treks (broken-in before the trip), plus comfortable camp shoes or sandals for riverside relaxation. YK Travels recommends bringing a daypack (20-30 liters) with rain cover for treks, a reusable water bottle (1 liter minimum), headlamp or flashlight with extra batteries (essential for evenings), sunglasses with UV protection, sun hat, sunscreen (SPF 30+), personal toiletries including biodegradable soap, quick-dry travel towel, insect repellent (especially for summer months), basic personal medications plus any prescriptions, and a small first aid kit. Optional but recommended items include trekking poles for stability on uneven terrain (especially for Kheerganga), camera with extra batteries/memory cards (charging opportunities may be limited), binoculars for wildlife spotting, lightweight gloves for cool mornings, swimming attire for hot springs, and a journal for wilderness reflections. For camping comfort, consider bringing a sleeping bag liner (though quality bedding is provided), earplugs, eye mask, and a lightweight packable pillow if you have specific sleeping preferences. YK Travels provides comprehensive pre-departure information including seasonal updates and can arrange for rental of specialized equipment if needed.",
      },
      {
        question:
          "What opportunities exist for experiencing local culture through YK Travels in Kasol?",
        answer:
          "YK Travels' Kasol package offers exceptional opportunities for authentic cultural engagement through carefully developed relationships with Himachali communities that benefit both visitors and local residents. Our cultural experiences focus on respectful, meaningful exchanges rather than staged demonstrations. The Malana village experience features specialized cultural interpretation by guides trained in local protocols and traditions, with YK Travels having secured appropriate community permissions for visits to designated areas. During this experience, you'll learn about the village's unique governance system, architectural adaptations to mountain environments, and ancient customs through respectful observation and guided explanation. Our itinerary includes a special meal experience with a traditional Himachali family where you'll learn about daily mountain life, seasonal agricultural practices, and culinary techniques passed through generations. YK Travels has developed relationships with local artisans who provide demonstrations of traditional crafts including woolen weaving, wood carving, and natural dyeing techniques, with opportunities for hands-on participation. Throughout your journey, our guides share insights about Parvati Valley folklore, local legends associated with natural features, and the spiritual significance of mountains in Himalayan traditions. Evening cultural sessions feature traditional music demonstrations with explanations of instruments and their historical context. For interested guests, we can arrange special meetings with traditional healers who explain mountain medicinal practices and herb identification (subject to availability). Our itinerary thoughtfully balances cultural experiences with wilderness activities, recognizing that in Himalayan traditions, nature and culture are inseparably intertwined. Throughout these interactions, YK Travels emphasizes respectful engagement—preparing visitors with cultural context, observing appropriate protocols, and ensuring exchanges benefit communities through fair compensation and dignified representation. Many guests develop lasting appreciation for Himalayan cultural resilience and environmental adaptation strategies through these carefully facilitated experiences.",
      },
      {
        question:
          "What are the hot springs like at Kheerganga and how does YK Travels enhance this experience?",
        answer:
          "The hot springs at Kheerganga represent one of the Parvati Valley's most extraordinary natural treasures, combining therapeutic mineral properties with breathtaking Himalayan panoramas at 9,700 feet elevation. These naturally occurring thermal springs emerge from deep within the mountains at approximately 40°C (104°F), creating a perfect temperature for rejuvenating soaks after the challenging trek. The main spring feeds a constructed stone pool approximately 15 feet in diameter and 3-4 feet deep, accommodating about 15-20 people comfortably. The mineral-rich waters contain natural sulfur compounds believed to relieve muscle tension, improve circulation, and benefit skin conditions—effects particularly welcome after the significant uphill trek. The setting itself is spectacular, with 360-degree views of snow-capped Himalayan peaks and verdant valleys creating a profound sense of wilderness immersion. YK Travels enhances this natural wonder through several thoughtful additions that respect the environment while maximizing comfort and meaning. Our timing strategy avoids peak crowds by starting early and arriving when the pools are less frequented. Our guides provide cultural context about the springs' significance in Hindu mythology, particularly their association with Lord Shiva's meditation practices and the spiritual properties attributed to these waters in local tradition. YK Travels provides special quick-dry microfiber towels for our guests' exclusive use, enhancing comfort without adding environmental burden. For privacy considerations, our guides can arrange gender-separated bathing times or alternative bathing arrangements depending on group preferences. The experience includes a post-soak herbal tea service featuring traditional Himalayan blends that complement the hot springs' detoxifying properties. Our guides are trained in appropriate photography guidance, identifying ideal vantage points while respecting privacy and sanctity. For guests choosing not to enter the waters, we arrange comfortable seating areas with equally magnificent views. Most importantly, our guides emphasize proper springs etiquette and environmental stewardship, ensuring this natural treasure remains pristine for future visitors and maintaining positive relationships with local caretakers who maintain these facilities in this remote location.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13485.497157144897!2d77.30548987486473!3d32.01011993559456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39045c4474a24ad7%3A0xfe9a0086f15cffc3!2sKasol%2C%20Himachal%20Pradesh!5e0!3m2!1sen!2sin!4v1650870440010!5m2!1sen!2sin",
  },

  {
    id: 22,
    slug: "srinagar-yk-travels",
    title: "Srinagar Paradise Lake Expedition with YK Travels",
    shortDescription:
      "Experience the enchanting beauty of Kashmir's crown jewel with YK Travels' exclusive Srinagar package featuring traditional houseboat stays, serene Shikara rides, majestic Mughal gardens, historic old city explorations, authentic Kashmiri cuisine, traditional craft experiences, and mystical mountain excursions in India's most stunning valley setting.",
    state: "Jammu & Kashmir",
    price: 11999,
    duration: "4 Days",
    activityTags: [
      "camping",
      "trekking",
      "star-gazing",
      "shikara-rides",
      "kashmiri-cuisine",
      "heritage-walks",
      "craft-workshops",
      "mountain-excursions",
      "cultural-immersion",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/jammu-kashmir/jk-11.avif",
        alt: "YK Travels Srinagar Dal Lake Experience",
      },
      {
        id: 2,
        src: "/images/packages/jammu-kashmir/jk-12.avif",
        alt: "YK Travels Kashmir Paradise Adventure",
      },
    ],
    stays: [
      {
        id: 1,
        images: [
          {
            id: 1,
            src: "/images/hotel-1.jpeg",
            alt: "YK Travels Premium Houseboat on Dal Lake",
          },
        ],
        amenities: [
          "Handcrafted Traditional Houseboat",
          "Private Lake-View Verandah",
          "Intricately Carved Walnut Wood Interiors",
          "Private Ensuite Bathrooms",
          "24-hour Hot Water Supply",
          "Personal Shikara for Transportation",
          "Dedicated Houseboat Chef",
          "Evening Cultural Performances",
          "Floating Garden Access",
          "Solar-powered Electricity Backup",
          "Traditional Kashmiri Room Heating",
          "High-quality Kashmiri Bedding",
        ],
      },
      {
        id: 2,
        images: [
          {
            id: 1,
            src: "/images/resort-4.jpeg",
            alt: "YK Travels Heritage Hotel in Srinagar",
          },
        ],
        amenities: [
          "Rooms with Panoramic Valley Views",
          "Traditional Kashmiri Architecture",
          "Heritage Walnut Wood Furniture",
          "Wazwan Dining Experience",
          "Saffron Tea Service",
          "Pashmina Collection Display",
          "Mountain-view Terrace",
          "Traditional Kangri Room Warmers",
          "Authentic Kashmiri Decor",
          "Evening Santoor Performance",
          "Local Craft Boutique",
          "Royal Kashmiri Bath Amenities",
        ],
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Authentic luxury houseboats and heritage Kashmiri hotels",
        icon: "building",
      },
      arrivalCity: {
        text: "Srinagar",
        icon: "city",
      },
      bestSeason: {
        text: "April-October with winter special experiences November-March",
        icon: "sun",
      },
      guide: {
        text: "Expert local guides with Kashmiri heritage specialization",
        icon: "guide",
      },
      maximumAltitude: {
        text: "5,200 feet (Srinagar) / 8,990 feet (Gulmarg)",
        icon: "mountain",
      },
      meals: {
        text: "All meals included with traditional Kashmiri Wazwan and floating garden dining experiences",
        icon: "bowl",
      },
      transportation: {
        text: "Private vehicles, traditional Shikaras, and guided mountain excursions",
        icon: "bus",
      },
      pickupPoint: {
        text: "Sheikh ul-Alam International Airport or Srinagar Bus Terminal",
        icon: "location",
      },
    },
    overview: {
      title: "Srinagar: Jewel of Kashmir's Enchanting Valley Paradise",
      description:
        "Discover the timeless magic of Kashmir's crown jewel with YK Travels on an immersive journey through Srinagar, the mystical lake city cradled at 5,200 feet amidst the majestic Himalayan range. Our expertly crafted package combines cultural heritage, natural splendor, artisanal traditions, and luxurious accommodations with knowledgeable guidance. Glide across the mirror-like waters of Dal Lake on traditional Shikaras, explore meticulously designed Mughal gardens with cascading fountains, wander through centuries-old lanes of the historic old city, and savor the incomparable flavors of authentic Kashmiri Wazwan prepared by master chefs. From peaceful houseboat mornings watching sunrise over the Zabarwan mountains to enchanting evening Santoor performances under star-filled skies, experience the multifaceted splendor of this legendary paradise through YK Travels' thoughtfully curated experiences that reveal both famous landmarks and hidden cultural treasures of the Kashmir Valley.",
      activities: [
        {
          title: "Dal Lake Shikara Odyssey",
          description:
            "Embark on YK Travels' signature Shikara journey across the jewel-like waters of Dal Lake, exploring floating gardens, historic Char Chinari island, and vibrant floating markets with our expert boatman sharing generations of lake wisdom while navigating hidden waterways inaccessible to regular tourists.",
          emoji: "🚣",
        },
        {
          title: "Royal Mughal Garden Expedition",
          description:
            "Experience YK Travels' exclusive guided tour of Srinagar's magnificent Mughal gardens including Shalimar Bagh, Nishat Bagh, and Chashme Shahi with our specialist historical guide revealing the sophisticated Persian-influenced design principles, symbolism of water channels, and botanical treasures brought to Kashmir by royal decree.",
          emoji: "🏰",
        },
        {
          title: "Authentic Wazwan Culinary Experience",
          description:
            "Indulge in YK Travels' extraordinary culinary journey featuring Kashmir's legendary multi-course Wazwan feast prepared by traditional Waza chefs using centuries-old techniques, with our culinary expert explaining the cultural significance, preparation rituals, and specific spice combinations of each intricate dish.",
          emoji: "🍲",
        },
        {
          title: "Old City Heritage Exploration",
          description:
            "Join YK Travels' master cultural guide for an immersive walk through Srinagar's atmospheric old city, discovering ancient wooden mosques, bustling spice markets, traditional bakeries, and historic shrines while learning about the unique Kashmiri culture that blends multiple influences into a distinct heritage treasured for generations.",
          emoji: "🕌",
        },
        {
          title: "Traditional Craft Workshop Experience",
          description:
            "Participate in YK Travels' specially arranged workshop with master artisans demonstrating Kashmir's UNESCO-recognized crafts including pashmina weaving, walnut wood carving, paper-mâché creation, and carpet knotting, with opportunities for hands-on learning and understanding the mathematical precision behind these centuries-old artistic traditions.",
          emoji: "🧵",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Srinagar & Houseboat Welcome",
        description:
          "Your journey into Kashmir's paradise begins with YK Travels' warm welcome at Sheikh ul-Alam International Airport, where you're greeted with traditional Kashmiri hospitality including a fragrant kehwa tea service and pashmina shawl presentation. Travel in comfort along the scenic route to Dal Lake with your expert cultural guide introducing the rich tapestry of Srinagar's heritage and pointing out distinctive architectural features along the journey. Arrive at the lake's edge where a beautifully adorned Shikara boat awaits to transport you across the mirrored waters to your meticulously selected luxury houseboat. Step aboard your floating accommodation where generations of craftsmanship are displayed in intricately carved walnut paneling, hand-embroidered furnishings, and traditional Kashmiri design elements expertly balanced with modern comforts. After settling into your lake-view room, your houseboat host presents a special welcome ceremony featuring saffron kahwa tea and traditional Kashmiri snacks served on your private verandah overlooking the tranquil waters and distant mountains. In the afternoon, embark on YK Travels' introductory Shikara cruise around the vicinity of your houseboat, where your expert boatman explains the unique ecosystem of floating gardens, the traditional lifestyle of lake dwellers, and points out significant landmarks visible from this central vantage point. Return to your houseboat as the golden light of evening settles over the lake for a special welcome dinner featuring select Kashmiri specialties prepared by your houseboat's dedicated chef. As twilight transforms into evening, experience a private Santoor performance on the houseboat deck, where a master musician plays this traditional 100-string instrument while your guide explains its cultural significance in Kashmiri music traditions and the meaning behind each melodic composition.",
      },
      {
        day: 2,
        title: "Royal Mughal Gardens & Floating Market Experience",
        description:
          "Begin your day with YK Travels' exclusive sunrise experience as your private Shikara collects you for a peaceful early morning glide across Dal Lake's mirror-like waters when mist often creates ethereal scenes. Observe the lake awakening—lotus flowers unfurling, birds beginning their morning activities, and local fishermen checking their nets. Return to your houseboat for a traditional Kashmiri breakfast featuring fresh lake produce served on your private verandah. After breakfast, your YK Travels expert historical guide arrives to begin your Royal Mughal Garden Expedition. Visit the magnificent terraced gardens of Nishat Bagh, known as 'Garden of Joy,' where your specialist explains the sophisticated mathematical principles behind the garden's design, the symbolic meaning of its nine terraces representing cosmic levels, and the Persian-influenced water features that create both visual beauty and cooling microclimates. Continue to UNESCO-recognized Shalimar Bagh, the crowning achievement of Mughal landscape architecture, where your guide reveals the historical love story behind Emperor Jahangir's creation of this 'Abode of Love' for his beloved queen Nur Jahan. Here, learn about the intricate hydraulic systems designed without modern technology that still power cascading fountains and reflective pools. Complete your garden exploration at Chashme Shahi, the 'Royal Spring Garden,' where you'll taste water from the medicinal spring believed to have healing properties, while your guide shares stories of royal physicians prescribing this water for various ailments. For lunch, YK Travels has arranged a special picnic in a private section of the gardens, featuring traditional Kashmiri dishes served on authentic copper tableware. In the late afternoon, experience one of YK Travels' most distinctive offerings—a guided tour of the floating vegetable markets, where farmers sell produce grown on the lake's famous floating gardens. Your specialist guide explains the centuries-old agricultural techniques of these unique floating plots made from lake vegetation. As evening approaches, enjoy a serene Shikara ride to Char Chinari, the historic island with four distinctive chinar trees planted at perfect compass points. Here, your guide recounts the legends associated with this spiritual spot as the setting sun creates a magical golden light across the lake. Return to your houseboat for a special demonstration of traditional Kashmiri cooking techniques by your houseboat chef, followed by a lakeside dinner under the stars.",
      },
      {
        day: 3,
        title: "Old City Heritage & Craft Masters Experience",
        description:
          "After a peaceful houseboat breakfast watching lake life unfold around you, embark on YK Travels' signature Old City Heritage Exploration. Your cultural expert guides you through ancient neighborhoods that reveal Srinagar's remarkable architectural heritage reflecting Buddhist, Hindu, and Islamic influences integrated into a distinctive Kashmiri style. Begin at the magnificent Shah Hamdan Mosque, one of the oldest wooden mosques in the world, where your guide explains the unique architectural features including intricate papier-mâché work, suspended kingpost trusses, and spire designs found nowhere else. Explore the atmospheric lanes of the old city where your specialist identifies subtle architectural details often missed by visitors—carved doorways with specific symbolic patterns, hidden courtyards, and distinctive window designs that reflect centuries of cultural exchange along ancient trade routes. Visit the historic Zaina Kadal bridge for panoramic views of the old city while your guide recounts the strategic importance of Srinagar's network of bridges connecting ancient neighborhoods. YK Travels has arranged special access to Pathar Masjid, the stone mosque typically closed to visitors, where your guide explains the fascinating history of this structure built by Empress Nur Jahan and its unique construction techniques. Experience the bustling energy of the spice market where your culinary expert identifies the distinctive Kashmiri varieties including special saffron grades, dried vegetables, and unique tea blends, explaining their roles in traditional cuisine. Visit a traditional Kandur bakery to witness the preparation of distinctive Kashmiri breads including tsot, girda, and lavas, with opportunities to taste fresh-baked samples. For lunch, YK Travels has arranged an exceptional authentic experience at a historic old city restaurant known for traditional dishes rarely served to visitors, with your culinary guide explaining each preparation and its cultural significance. In the afternoon, participate in YK Travels' exclusive Craft Masters Experience where you'll visit carefully selected workshops of master artisans practicing Kashmir's UNESCO-recognized crafts. Observe the intricate techniques of pashmina weaving where your textile specialist explains the entire process from harvesting the fine goat wool to the final delicate weaving on traditional looms. At a family-run paper-mâché studio, participate in a hands-on workshop to learn the basics of this distinctive Kashmiri art form, creating a personalized souvenir under expert guidance. Visit a master carpet weaver where you'll learn to read the mathematical 'talim' codes that guide the creation of complex Kashmir carpet patterns, observing how artisans translate these codes into thousands of precise knots. Complete your craft exploration at an atelier specializing in walnut wood carving, where your specialist explains how specific tools create the distinctive deep relief work uniquely associated with Kashmir. Return to your accommodation in the evening for a special dinner featuring dishes from Kashmir's minority communities including Pandit, Buddhist, and Sikh culinary traditions, with your guide explaining the diverse cultural influences that have enriched Kashmiri society.",
      },
      {
        day: 4,
        title: "Gulmarg Alpine Experience & Pari Mahal Visit",
        description:
          "Today brings YK Travels' spectacular mountain excursion to Gulmarg, the 'Meadow of Flowers' set at 8,690 feet in the Pir Panjal range. After an early breakfast, travel along the scenic mountain highway with your naturalist guide pointing out distinctive Himalayan features, traditional Gujjar settlements, and significant environmental zones along the journey. Your specialist provides context about Kashmir's unique geography as you ascend through changing vegetation zones, explaining how the valley's enclosed nature has influenced both environmental conditions and cultural development. Arrive in Gulmarg where meadows covered with wildflowers in summer or pristine snow in winter are surrounded by dramatic mountain vistas. For adventure enthusiasts, YK Travels has arranged the breathtaking Gulmarg Gondola experience—one of the world's highest cable cars—ascending to Kongdori at 10,500 feet with your mountain guide explaining the panoramic views of surrounding peaks including glimpses of Nanga Parbat, the world's ninth-highest mountain. At Kongdori, enjoy a short guided nature walk where your specialist identifies alpine flora and fauna unique to this elevation. For those preferring gentler activities, YK Travels offers a naturalist-guided meadow walk exploring Gulmarg's biodiversity and identifying medicinal herbs used in traditional Kashmiri healing practices. Experience a special mountain picnic lunch featuring traditional Kashmiri trail foods served in a scenic meadow location. In the afternoon, choose between options including horseback riding through mountain paths with local guides sharing stories of Gulmarg's transition from royal summer resort to modern adventure destination, or visiting the historic Maharani Temple and St. Mary's Church with their fascinating colonial architectural elements. During winter months, YK Travels offers specialized snow experiences including sledding and snowman-building competitions in this winter wonderland setting. On your return journey to Srinagar, visit the mystical Pari Mahal or 'Palace of Fairies'—a terraced garden structure combining Islamic and Buddhist architectural elements that once housed an astrological observatory. Here, your historical expert explains the significance of this less-visited monument while you enjoy spectacular panoramic views of Dal Lake and Srinagar sprawled below. Return to your accommodation for an extraordinary cultural evening featuring YK Travels' exclusively arranged Chakri music performance—traditional Kashmiri Sufi music featuring multiple instruments and hypnotic vocals—followed by a demonstration of the traditional spinning dance that accompanies this musical tradition. Dinner features a special Kashmiri Wazwan experience where your culinary guide explains the ceremonial aspects of this elaborate meal tradition as you savor multiple courses prepared according to recipes preserved through generations of specialized chef families.",
      },
      {
        day: 5,
        title: "Spiritual Sanctuaries & Departure",
        description:
          "On your final day, begin with a peaceful lakeside yoga and meditation session incorporating elements of Kashmir Shaivism traditions, guided by a local practitioner who explains the valley's significant contributions to meditative practices. After breakfast, embark on YK Travels' Spiritual Sanctuaries tour exploring sites significant to Kashmir's diverse religious heritage. Visit the ancient Shankaracharya Temple perched on a hill overlooking the valley, where your specialist explains both its Hindu significance and earlier Buddhist origins, while identifying architectural elements that reveal multiple historical layers. Experience the serene atmosphere of Hazratbal Shrine with its distinctive white marble structure reflected in the lake, where your cultural guide explains its significance as Kashmir's most revered Muslim shrine while pointing out unique architectural features blending multiple influences. Visit the peaceful Jama Masjid, one of India's largest wooden mosques, where your historical expert reveals the distinctive elements of Kashmiri mosque architecture including massive deodar pillars and Persian-influenced design elements. YK Travels has arranged a special spiritual experience with a traditional Sufi master who explains the mystical traditions that have flourished in Kashmir's unique cultural environment through an interactive discussion about universal spiritual principles. Enjoy a farewell lunch at a carefully selected organic garden restaurant featuring ingredients grown on the property, prepared using traditional Kashmiri cooking methods with modern, health-conscious adaptations. Before departure, participate in YK Travels' special shopping experience visiting carefully vetted establishments specializing in authentic Kashmiri handicrafts including pashmina shawls, saffron, walnut woodwork, and handmade carpets. Your expert guide provides education about distinguishing quality and authenticity while ensuring fair trade practices that benefit local artisans. During this experience, receive a keepsake handbook detailing Kashmir's traditional crafts with authentication guidelines. As part of YK Travels' commitment to sustainable tourism, participate in a brief lake conservation awareness session to understand environmental challenges facing Dal Lake and community-based solutions being implemented. Before transfer to the airport, enjoy a special farewell ceremony including presentation of a traditional Kashmiri souvenir and kehwa tea service while your guide shares recommendations for future exploration of the greater Kashmir region. YK Travels arranges your comfortable transfer to Sheikh ul-Alam International Airport with your guide providing assistance with any departure formalities, completing your immersive journey through Srinagar's cultural and natural treasures.",
      },
    ],
    cost: {
      includes: [
        "All meals as specified in the itinerary",
        "Luxury houseboat accommodation on Dal Lake",
        "Heritage hotel accommodation in Srinagar",
        "Private transportation for all transfers and excursions",
        "Traditional Shikara rides throughout the program",
        "Expert cultural, historical, and naturalist guides",
        "Gulmarg Gondola tickets (weather permitting)",
        "All entrance fees to monuments and gardens",
        "Craft workshop materials and instruction",
        "Cultural performances and demonstrations",
        "Special farewell gift package",
        "All applicable taxes",
      ],
      excludes: [
        "Airfare to/from Srinagar",
        "Premium alcoholic beverages",
        "Personal expenses and shopping",
        "Optional activities not mentioned in inclusions",
        "Travel insurance",
        "Photography fees at certain monuments",
        "Gratuities for guides and drivers",
        "Special permits for restricted areas",
        "Anything not specifically mentioned in inclusions",
      ],
      notes: [
        "Gulmarg excursion is weather-dependent and may be rescheduled or substituted",
        "Houseboat accommodations are subject to lake conditions and weather factors",
        "Inner areas of certain religious sites may have restricted access during prayer times",
        "The itinerary may be modified due to local conditions, festivals, or seasonal factors",
        "Early booking is recommended during peak seasons (May-July, September-October)",
        "Modest clothing required for visiting religious sites",
        "Photography restrictions exist in certain areas including some religious sites",
        "Winter itinerary (November-March) includes special seasonal experiences with some substitutions",
        "YK Travels can arrange extended Kashmir valley explorations for interested guests",
      ],
    },
    faqs: [
      {
        question:
          "What makes Srinagar unique and how does YK Travels showcase this specialness?",
        answer:
          "Srinagar's uniqueness stems from its extraordinary combination of natural splendor, cultural depth, and living traditions that have evolved over centuries in this geographically isolated valley. The city represents a rare confluence of multiple cultural influences—Persian, Central Asian, Mughal, and indigenous Kashmiri—resulting in distinctive architecture, crafts, cuisine, and social practices found nowhere else. YK Travels showcases this specialness through carefully designed experiences that go far beyond standard tourist offerings. Our established relationships with traditional houseboat families provide guests with authentic floating accommodations featuring genuine craftsmanship rather than commercial adaptations. Our cultural experts have mapped hidden heritage pathways through the old city revealing architectural treasures and living traditions typically invisible to regular visitors. Our culinary program offers genuine access to Kashmiri Wazwan preparation techniques and family recipes normally reserved for local celebrations. Our craft connections facilitate meaningful interactions with master artisans practicing UNESCO-recognized traditions, with opportunities for hands-on engagement rather than passive observation. Our naturalist guides have identified optimal locations for experiencing Dal Lake's unique ecosystem, including hidden waterways and specialized agricultural techniques developed over centuries. We've thoughtfully balanced experiences across cultural, natural, artistic, and culinary domains, connecting seemingly separate elements into a cohesive understanding of Kashmir's distinctive heritage. Our specially trained guides—many from multigenerational Kashmiri families—provide contextualized understanding of both historical significance and contemporary challenges, offering balanced perspectives on sustainability, preservation efforts, and community-based initiatives. Throughout your journey, YK Travels emphasizes respectful cultural exchange that benefits both visitors and local communities, accessing authentic experiences while supporting traditional livelihoods and heritage preservation efforts. Our thoughtfully paced itinerary allows for both active exploration and contemplative appreciation of Kashmir's legendary beauty, creating a journey that captures Srinagar's authentic character and reveals why this valley has captivated travelers for centuries.",
      },
      {
        question:
          "What are houseboats like in Srinagar and how does YK Travels enhance this experience?",
        answer:
          "Srinagar's houseboats represent one of the world's most distinctive accommodation experiences—floating wooden palaces that combine elaborate craftsmanship with cultural heritage in a stunning natural setting. These structures typically measure 80-120 feet in length and feature 2-5 bedrooms supported by communal living areas, dining spaces, and front verandahs. The defining characteristic of authentic Kashmir houseboats is their extraordinary woodwork—intricate walnut paneling carved with precisely patterned designs, detailed ceiling work featuring hundreds of fitted pieces in geometric patterns, and hand-carved furniture created by specialized artisans. Interior aesthetics typically showcase Kashmir's finest crafts including crewel-embroidered curtains, chain-stitch carpets, copper serving pieces, papier-mâché decorative elements, and traditional gabbas (woolen floor coverings). While maintaining traditional character, quality houseboats feature modern amenities including ensuite bathrooms with hot water, comfortable bedding, and adequate electrical supply. YK Travels enhances this already exceptional experience through several distinctive elements that maximize both comfort and cultural authenticity. Our selective partnership process identifies family-owned houseboats with multigenerational heritage rather than commercial operations, ensuring historical accuracy and genuine craftsmanship. Our accommodation partners undergo specialized hospitality training focusing on international comfort standards while maintaining authentic Kashmiri character. Each houseboat features dedicated staff including a specialized chef preparing authentic Kashmiri cuisine adapted to guest preferences. YK Travels provides premium amenities including luxury Kashmiri bedding, organic toiletries featuring local lavender, and traditional copper water vessels in each room. Our guests receive dedicated Shikara boat service for flexible lake exploration, allowing spontaneous excursions beyond scheduled activities. We arrange special cultural enhancements including private musical performances on the houseboat deck, traditional craft demonstrations, and interactive cooking sessions with houseboat chefs. For photography enthusiasts, we provide guidance on optimal lighting conditions and perspectives unique to houseboat locations. Safety enhancements include stabilization systems for guest comfort, fire safety equipment exceeding local requirements, and 24-hour security personnel. Each houseboat stay includes special experiences such as sunrise tea service on the verandah, evening storytelling sessions about lake folklore, and opportunities to interact with family members about houseboat heritage and maintenance techniques. For multi-generational groups or those seeking enhanced privacy, YK Travels can arrange exclusive use of entire houseboats with customized experiences.",
      },
      {
        question: "What should I pack for my Srinagar trip with YK Travels?",
        answer:
          "For your cultural and natural expedition with YK Travels in Srinagar, thoughtful packing enhances both comfort and appropriate engagement with local customs. Essential clothing items include lightweight layering pieces that can accommodate variable temperatures, modest attire suitable for religious site visits (covering shoulders, knees, and chest), comfortable walking shoes for heritage explorations, a light waterproof jacket (essential even in summer due to sudden mountain showers), and a few warmer items for cool evenings—particularly if visiting between October and April. For summer visits (May-September), prepare for temperature ranges from 18-30°C (65-86°F) during days with cooler 12-18°C (54-65°F) evenings, while spring and autumn travelers should add medium-weight layers for 10-25°C (50-77°F) daily variations. Winter visitors (November-February) need significant cold-weather gear including thermal layers, heavy jacket, gloves, and warm headwear for temperatures ranging from -2 to 15°C (28-59°F). Women travelers should include at least one scarf for covering the head when visiting certain religious sites (though YK Travels provides pashmina shawls when needed). For houseboat comfort, consider bringing slippers or comfortable indoor footwear, as Kashmiri tradition involves removing shoes inside these wooden structures. YK Travels recommends bringing a small daypack for excursions, reusable water bottle (pure Himalayan water is provided throughout), sunglasses, sun hat, sunscreen (mountain sun can be intense even in cooler weather), basic personal medications plus any prescriptions, and a small first aid kit. Camera equipment is highly recommended for capturing Srinagar's exceptional photogenic qualities, with spare batteries as charging opportunities may be limited during day excursions. Optional but recommended items include binoculars for birdwatching on Dal Lake (over 60 species inhabit the area), swimwear if interested in visiting hot springs outside Srinagar, and a journal for recording impressions of this uniquely inspiring landscape. For cultural appropriateness, YK Travels suggests packing at least one nicer outfit for special dining experiences or cultural performances. Practical accessories should include wet wipes or hand sanitizer, a lightweight quick-dry towel for unexpected needs, and a small flashlight for navigating during occasional evening power fluctuations. YK Travels provides comprehensive pre-departure information including seasonal updates, cultural guidelines, and can arrange for specialty items if needed. Importantly, we recommend leaving space in your luggage for Kashmir's extraordinary handicrafts—particularly lighter items like pashmina shawls, papier-mâché boxes, or embroidered goods that make exceptional souvenirs of your journey.",
      },
      {
        question:
          "How does YK Travels approach Kashmiri cuisine in the Srinagar package?",
        answer:
          "YK Travels approaches Kashmiri cuisine as a fundamental cultural experience rather than merely sustenance, designing a progressive culinary journey that reveals the depth, sophistication, and historical significance of this distinctive regional tradition. Our culinary program begins with careful selection of dining venues representing authentic preparation techniques—from houseboat kitchens maintaining traditional methods to family homes preserving generational recipes, historic old city restaurants known to locals, and specialized Wazwan practitioners creating ceremonial feasts. Throughout your journey, meals progress from accessible introductory dishes to increasingly specialized preparations, building familiarity with Kashmir's distinctive flavor profile characterized by warming spices (including saffron, Kashmiri chilies, dried ginger, and fennel), slow-cooking techniques, and sophisticated meat preparations balanced by vegetable dishes reflecting the valley's agricultural abundance. YK Travels enhances dining experiences through educational elements including interactive cooking demonstrations where guests learn about specialized techniques such as slow-fire cooking, meat tenderization methods, and the preparation of distinctive dried vegetables that sustained Kashmir through winter months before modern transportation. Our culinary experts provide context about each significant dish, explaining its historical origins, cultural significance, specific preparation techniques, and place within the broader Kashmiri culinary framework. Our program showcases Kashmir's diverse traditions including Muslim Wazwan preparations, Hindu Pandit cuisine with distinctive vegetarian specialties, traditional bakery items from the region's unique tandoor-style ovens, and seasonal preparations utilizing foraged ingredients from mountain regions. Signature culinary experiences include a traditional Wazwan feast where guests observe the ceremonial aspects of this elaborate meal service featuring up to 36 courses prepared by specialized chefs called Wazas, a floating garden lunch featuring ingredients grown on Dal Lake's unique agricultural system, a heritage old city food walk sampling specialties from establishments operating for generations, and tea ceremonies featuring both salty pink noon chai and sweet kehwa variations prepared with traditional samavars. For interested guests, YK Travels arranges market visits with culinary experts who explain distinctive Kashmiri ingredients including regional rice varieties, specialized dried vegetables, unique spice blends, and the world's most precious spice—Kashmiri saffron—with guidance on quality authentication. Throughout these experiences, our culinary program emphasizes the connections between Kashmir's geography, agricultural practices, cultural influences, and resulting food traditions—revealing how complex historical forces have shaped one of India's most distinctive regional cuisines, while providing memorable dining experiences that become highlights of your journey through this extraordinary valley.",
      },
      {
        question:
          "What cultural sensitivities should I be aware of when visiting Srinagar with YK Travels?",
        answer:
          "YK Travels prioritizes respectful cultural engagement in Srinagar, preparing our guests with nuanced understanding that enhances both visitor experience and positive community interactions. Kashmir's cultural landscape reflects complex historical influences including strong Islamic traditions alongside Hindu heritage and Buddhist historical presence—requiring thoughtful navigation of diverse practices. Our pre-departure materials and guide orientations address key sensitivities while fostering authentic connections. Modest dress represents an important consideration in this relatively conservative region—with recommendations for both men and women to cover shoulders and knees in public areas, particularly when visiting religious sites. Women travelers should carry a scarf for covering the head when entering mosques or certain shrines (though YK Travels provides appropriate coverings when needed). Photography requires particular sensitivity in Kashmir, with strict prohibitions at certain religious sites and military installations. Our guides provide clear guidance about appropriate photography locations and practices, particularly regarding photographing local people, where obtaining permission is essential and certain cultural events may prohibit photography entirely. Religious site visitation includes specific protocols—including removal of shoes, covering heads in certain locations, observing designated paths, speaking quietly, and respecting restricted areas during prayer times. YK Travels schedules visits to accommodate prayer schedules and provides guidance about appropriate behaviors specific to each tradition. For houseboat stays, guests should understand the intimate nature of these family-operated accommodations—removing shoes before entering indoor spaces, maintaining reasonable noise levels, and respecting family privacy in certain areas. Appropriate compensation for craft demonstrations, musical performances, and specialized experiences is pre-arranged by YK Travels to ensure fair recognition of cultural knowledge while avoiding uncomfortable direct negotiations. In market settings, our guides provide coaching about appropriate bargaining practices that respect artisan labor while allowing for traditional price negotiations. Certain traditional communities, particularly in outlying villages, maintain specific codes regarding gender segregation, photography, or cultural practices—our guides provide specific orientation before such visits to ensure appropriate interactions. Political sensitivity remains important in Kashmir, with YK Travels recommending guests avoid initiating political discussions or expressing positions on regional issues. Our guides are trained to provide balanced historical information while maintaining appropriate neutrality on contemporary matters. Throughout your journey, YK Travels emphasizes cultural exchange rather than cultural consumption—creating opportunities for meaningful interaction based on mutual respect and genuine interest rather than superficial observation. This approach has established positive community relationships that provide our guests with authentic experiences while maintaining dignity and agency for local participants, creating mutually beneficial interactions that enhance rather than exploit Kashmir's extraordinary cultural heritage.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d105713.535298228!2d74.80675514999999!3d34.1067185!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38e1855686e3c5ef%3A0x66244b7cc1e305c6!2sSrinagar!5e0!3m2!1sen!2sin!4v1746093551092!5m2!1sen!2sin",
  },
  {
    id: 23,
    slug: "gulmarg-yk-travels",
    title: "Gulmarg with YK Travels",
    shortDescription:
      "Experience the magical winter wonderland of Gulmarg with YK Travels' exclusive package featuring world-class skiing, panoramic Himalayan vistas, traditional Kashmiri cultural immersion, luxury alpine accommodations, gourmet mountain cuisine, and unforgettable snow adventures in one of India's most enchanting mountain destinations.",
    state: "Jammu & Kashmir",
    price: 6999,
    duration: "4 Days",
    activityTags: [
      "skiing",
      "snowboarding",
      "gondola-ride",
      "snow-trekking",
      "kashmiri-cuisine",
      "winter-photography",
      "cultural-experiences",
      "alpine-adventure",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/jammu-kashmir/jk-10.jpg",
        alt: "YK Travels Gulmarg Skiing Experience",
      },
      {
        id: 2,
        src: "/images/packages/jammu-kashmir/jk-9.jpg",
        alt: "YK Travels Himalayan Snow Adventure",
      },
    ],
    stays: [
      {
        id: 1,
        images: [
          {
            id: 1,
            src: "/images/resort-2.jpeg",
            alt: "YK Travels Luxury Alpine Resort in Gulmarg",
          },
        ],
        amenities: [
          "Premium Heated Rooms with Mountain Views",
          "Kashmiri Walnut Wood Furnishings",
          "24-hour Hot Water Supply",
          "Traditional Kangri Room Heating",
          "In-house Specialty Restaurant",
          "Ski Equipment Storage",
          "Evening Cultural Performances",
          "Sauna and Steam Facilities",
          "High-altitude Fitness Center",
          "24-hour Power Backup",
          "Traditional Kashmiri Hamam",
          "Mountain Adventure Desk",
        ],
      },
      {
        id: 2,
        images: [
          {
            id: 1,
            src: "/images/packages/jammu-kashmir/jk-10.jpg",
            alt: "YK Travels Alpine Snow Cabins in Gulmarg",
          },
        ],
        amenities: [
          "Private Alpine Wooden Cabins",
          "Panoramic Window Views of Pir Panjal Range",
          "Traditional Bukhari Wood Heating",
          "Himalayan Cedar Wood Interiors",
          "Dedicated Butler Service",
          "Heated Bathroom Facilities",
          "Complimentary Hot Beverages",
          "Morning Tea Service with Local Pastries",
          "Insulated Alpine Construction",
          "Private Snow Viewing Decks",
          "Emergency Satellite Communication",
          "Authentic Kashmiri Interior Design",
        ],
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Luxury Alpine Resort and Traditional Himalayan Snow Cabins",
        icon: "building",
      },
      arrivalCity: {
        text: "Srinagar",
        icon: "city",
      },
      bestSeason: {
        text: "December-March for snow experiences, April-June for alpine meadows",
        icon: "sun",
      },
      guide: {
        text: "Certified ski instructors and local mountain experts",
        icon: "guide",
      },
      maximumAltitude: {
        text: "8,694 feet (Gulmarg Village) / 13,780 feet (Apharwat Peak via Gondola)",
        icon: "mountain",
      },
      meals: {
        text: "All meals included with specialty Kashmiri cuisine and mountain fare",
        icon: "bowl",
      },
      transportation: {
        text: "Private heated vehicles and guided snow experiences",
        icon: "bus",
      },
      pickupPoint: {
        text: "Srinagar International Airport or Srinagar City",
        icon: "location",
      },
    },
    overview: {
      title: "Gulmarg: Alpine Paradise in the Majestic Kashmir Himalayas",
      description:
        "Discover the pristine winter wonderland of Gulmarg with YK Travels on an immersive journey through one of India's most spectacular mountain destinations. Nestled in the Pir Panjal Range at 8,694 feet, Gulmarg transforms into a magical snow-covered landscape that offers world-class skiing, breathtaking panoramic views, and authentic Kashmiri cultural experiences. Our expertly crafted package combines exhilarating snow adventures, cultural discoveries, luxury accommodations, and gourmet mountain cuisine with knowledgeable guidance. Glide down perfectly groomed slopes with expert instructors, ascend to dramatic heights on the Gulmarg Gondola, experience traditional Kashmiri hospitality in charming villages, and indulge in the rich flavors of Wazwan cuisine while enjoying comfortable, heated mountain accommodations. From adrenaline-pumping off-piste skiing to serene snow-blanketed pine forest walks, experience the multifaceted charm of this Himalayan gem through YK Travels' thoughtfully designed winter-focused experiences.",
      activities: [
        {
          title: "Gulmarg Gondola Experience",
          description:
            "Embark on YK Travels' signature gondola journey aboard one of the world's highest cable cars, ascending in two breathtaking phases from 8,700 to 13,780 feet at Apharwat Peak, where our alpine guide reveals panoramic views of Nanga Parbat and the entire Kashmir Valley while sharing fascinating insights about the surrounding geology and ecosystem.",
          emoji: "🚡",
        },
        {
          title: "Supervised Skiing Adventure",
          description:
            "Experience the thrill of skiing on Gulmarg's world-renowned slopes with YK Travels' certified instructors who provide personalized guidance for all skill levels, from beginners on gentle nursery slopes to advanced skiers tackling challenging runs, with top-quality equipment and safety briefings ensuring a confident and exhilarating snow experience.",
          emoji: "⛷️",
        },
        {
          title: "Snow Trek to Baba Reshi Shrine",
          description:
            "Join YK Travels' expert mountain guide for a mesmerizing snow trek through pristine pine forests and snow-blanketed meadows to the 15th-century Baba Reshi shrine, where you'll learn about local spiritual traditions while mastering the art of walking in snow with specialized equipment and techniques.",
          emoji: "🥾",
        },
        {
          title: "Traditional Kashmiri Wazwan Feast",
          description:
            "Indulge in YK Travels' exclusive culinary experience featuring an elaborate multi-course Wazwan feast prepared by a skilled Kashmiri Waza (chef) using centuries-old techniques, with each meticulously crafted dish explained by our culinary expert who shares the cultural significance and traditional etiquette of this royal dining tradition.",
          emoji: "🍲",
        },
        {
          title: "Himalayan Winter Photography Workshop",
          description:
            "Perfect your snow photography skills with YK Travels' specialized winter photography session led by our resident expert who reveals techniques for capturing Gulmarg's dramatic landscapes, delicate snow crystals, and striking light conditions, with guidance on equipment settings, composition, and the unique challenges of alpine photography.",
          emoji: "📸",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Srinagar & Mountain Journey to Gulmarg",
        description:
          "Your Himalayan adventure begins with YK Travels' warm welcome at Srinagar International Airport, where our representative greets you with traditional Kashmiri hospitality. Board our private heated vehicle for the scenic 2-hour journey to Gulmarg, traveling through picturesque villages and apple orchards as your expert local guide introduces the cultural and natural heritage of the Kashmir Valley. As you ascend into the mountains, witness the landscape transform with increasing elevation, with your guide pointing out distinctive Himalayan features and wildlife. Arrive at our carefully selected alpine resort where heated rooms with panoramic mountain views await. After check-in and refreshments featuring Kashmiri Kahwa tea and traditional pastries, embark on YK Travels' orientation walk around Gulmarg village. Your guide will introduce you to this historic hill station, explaining its evolution from royal hunting ground to world-class skiing destination while pointing out key landmarks including the historic St. Mary's Church and the colonial-era Highland Park Hotel. Experience the excitement of your first snow contact with a brief introduction to snow movement techniques, with your guide demonstrating proper footing and balance methods to prepare for upcoming adventures. Return to your accommodation for a special welcome ceremony where you'll be introduced to traditional Kashmiri customs with a presentation of local attire and handicrafts. As evening approaches, gather in the warmth of the resort's elegant dining room for your welcome dinner featuring authentic Kashmiri cuisine with distinctive mountain influences. During dinner, your expedition leader will outline the coming days' adventures, provide weather updates, and share fascinating tales of Gulmarg's history and culture.",
      },
      {
        day: 2,
        title: "Gulmarg Gondola & Alpine Exploration",
        description:
          "Begin your day with an invigorating morning yoga and stretching session specially designed by YK Travels' high-altitude fitness expert to prepare your body for alpine activities. After a hearty breakfast featuring both international and Kashmiri specialties, prepare for today's highlight—the world-famous Gulmarg Gondola experience. With pre-arranged priority boarding organized exclusively for YK Travels' guests, you'll ascend on the first phase of one of the world's highest cable cars, rising from 8,700 to 10,500 feet at Kongdori Station. Marvel at the spectacular views of snow-covered pine forests and the majestic Himalayan peaks as your expert guide identifies key landmarks and shares insights about the remarkable ecosystem. For guests interested in skiing, YK Travels has arranged professional instructors and quality equipment for all skill levels, from absolute beginners to advanced skiers. Beginners will enjoy specialized instruction on the gentle nursery slopes where our patient instructors use proven techniques for building confidence and basic skills. Intermediate and advanced skiers can explore groomed runs with guidance on technique improvement and local slope characteristics. Non-skiers will enjoy an equally memorable experience with our specially designed snow play activities including snow tubing, sledding, and guided snow walks with specialized equipment. Weather permitting, continue your ascent on the second phase of the gondola, reaching the breathtaking altitude of 13,780 feet at Apharwat Peak. Here, the panoramic views extend to Nanga Parbat and across the Kashmir Valley, with your guide providing specialized high-altitude orientation and safety guidelines. Enjoy a carefully prepared alpine picnic lunch featuring insulated containers with hot soups, grilled sandwiches, and mountain trail snacks designed for energy and warmth. In the afternoon, join YK Travels' exclusive Snow Craft Workshop where local experts demonstrate traditional and modern techniques for movement and survival in snow environments, including the construction of snow shelters and recognition of varying snow conditions. Return to your accommodation with time to enjoy the sauna and steam facilities, perfect for refreshing tired muscles after a day in the snow. The evening features a special presentation by a local historian about Gulmarg's fascinating transformation from royal hunting grounds to international ski destination, followed by dinner with specially prepared Kashmiri fusion cuisine.",
      },
      {
        day: 3,
        title: "Snow Trek & Kashmiri Cultural Immersion",
        description:
          "After breakfast, meet your specialized trekking guide for today's adventure—a moderately challenging snow trek to the historic Baba Reshi shrine. YK Travels provides complete snow trekking equipment including insulated boots, gaiters, and trekking poles, with a comprehensive briefing on proper snow walking techniques. Begin your journey through pristine snow-covered landscapes, following a scenic route through magnificent pine forests draped in white. Your naturalist guide identifies animal tracks in the snow and points out resilient winter birds and plant adaptations to extreme temperatures. Reach the historic 15th-century shrine of Baba Reshi, where your cultural expert explains the spiritual significance of this site and its architectural features that blend multiple influences. Throughout the trek, your guide creates strategic photography opportunities at the most scenic viewpoints, with specialized tips for capturing snow landscapes and delicate light conditions. Return to Gulmarg village for a special lunch experience at a local Kashmiri home where YK Travels has arranged an authentic cultural exchange. Learn about traditional Kashmiri winter life, including distinctive architecture designed for heavy snowfall and unique heating systems like the Kangri (traditional fire pot) and Bukhari (wood stove). Participate in a hands-on cooking demonstration featuring traditional winter recipes, with techniques passed through generations. In the afternoon, join our specialized Winter Photography Workshop led by YK Travels' resident expert who provides techniques for capturing Gulmarg's dramatic snowy landscapes, unique light conditions, and intricate snow crystals. Later, witness artisans demonstrating traditional Kashmiri handicrafts including papier-mâché, walnut wood carving, and pashmina weaving, with opportunities to try these ancient techniques under expert guidance. As evening approaches, prepare for the highlight of Kashmiri cuisine—a traditional Wazwan feast featuring multiple courses prepared by a skilled Waza (chef) using centuries-old techniques and recipes. Your culinary guide explains each meticulously prepared dish and the cultural significance of this elaborate dining tradition. The evening concludes with a mesmerizing performance of traditional Kashmiri music featuring santoor and rabab instruments, with your cultural expert explaining the historical context and musical traditions of the region.",
      },
      {
        day: 4,
        title: "Advanced Snow Activities & Departure",
        description:
          "On your final day, early risers can join YK Travels' optional sunrise photography session at a specially selected viewpoint where first light creates magical effects on the snow-covered landscapes. After breakfast, choose from YK Travels' curated selection of specialized snow activities based on your interests and abilities. Advanced skiers can join our mountain guide for off-piste skiing in carefully selected safe zones with pristine powder conditions. Adventure enthusiasts can experience the thrill of snowmobiling on designated trails with comprehensive safety briefing and equipment. Those seeking contemplative experiences can join our mountain naturalist for a guided snowshoe walk through serene forests where the hushed snow-covered environment creates perfect conditions for mindfulness practice. For photography enthusiasts, our specialist leads a macro-photography session focusing on intricate snow crystals and ice formations using specialized equipment. Return to the resort for a special high-mountain cuisine lunch featuring dishes designed for energy and warmth in alpine environments, with our chef explaining the nutritional science behind high-altitude cooking. After lunch, there's time for last explorations of Gulmarg village for souvenir shopping with your guide providing context about authentic local handicrafts and appropriate pricing. Before departure, participate in YK Travels' signature farewell ceremony featuring the presentation of a specially commissioned Kashmiri memento and certificate of alpine achievement. As your mountain journey concludes, board your private transfer for the scenic drive back to Srinagar, with your guide pointing out different perspectives and landmarks visible on the descent. YK Travels arranges your comfortable transfer to Srinagar International Airport or city accommodations, with your guide providing recommendations for future Himalayan adventures and information about seasonal variations in this remarkable mountain destination.",
      },
    ],
    cost: {
      includes: [
        "All meals as mentioned in the itinerary",
        "Private heated vehicle transportation for all transfers",
        "Luxury alpine resort accommodation",
        "Gondola tickets for both phases",
        "Professional skiing instructors and equipment",
        "Specialized snow trekking gear",
        "Expert mountain and cultural guides throughout",
        "Photography workshop with specialist",
        "Traditional Wazwan feast experience",
        "Cultural performances and demonstrations",
        "All applicable taxes and permits",
      ],
      excludes: [
        "Airfare to/from Srinagar",
        "Personal skiing equipment (though rentals are arranged)",
        "Advanced snow sports insurance",
        "Alcoholic beverages",
        "Personal expenses and tips",
        "Travel insurance (mandatory for adventure activities)",
        "Activities not mentioned in the inclusions",
        "Additional gondola rides beyond those mentioned",
        "Anything not specifically mentioned in inclusions",
      ],
      notes: [
        "Gondola operation is weather-dependent and may be modified for safety",
        "Skiing activities are adapted to snow conditions and guest abilities",
        "The itinerary may be adjusted due to weather or local conditions",
        "Early booking is essential during peak winter season (December-February)",
        "Warm clothing is required - detailed packing list provided upon booking",
        "Some activities require signed adventure waiver forms",
        "YK Travels can arrange extended skiing programs for enthusiasts",
        "Children under 12 require special consideration for certain activities",
      ],
    },
    faqs: [
      {
        question:
          "What makes Gulmarg unique and how does YK Travels showcase this specialness?",
        answer:
          "Gulmarg's uniqueness stems from its exceptional combination of world-class skiing terrain, pristine Himalayan beauty, and rich Kashmiri cultural heritage. This remarkable destination offers some of Asia's finest powder snow conditions across diverse terrain suitable for all skill levels, with the Gulmarg Gondola providing access to one of the world's highest skiable mountains peaking at 13,780 feet. The breathtaking location in the Pir Panjal range offers panoramic views extending to Nanga Parbat (the world's ninth-highest peak) and across the entire Kashmir Valley—perspectives found nowhere else in India. YK Travels showcases this specialness through carefully curated experiences that balance adventure, natural immersion, and cultural authenticity. Our established relationships with certified local mountain guides provide access to the safest and most spectacular skiing zones based on daily conditions, including off-piste areas typically unknown to visitors. Our naturalist specialists have mapped unique forest paths that reveal Gulmarg's distinctive Himalayan ecosystem with specialized knowledge of winter wildlife tracking and high-altitude botany. YK Travels has cultivated connections with traditional Kashmiri families who welcome our guests for authentic cultural exchanges, demonstrating winter survival techniques developed over centuries in this snowy environment. Our culinary team works directly with skilled Wazas (traditional Kashmiri chefs) to offer authentic regional cuisine rarely experienced by travelers, including specialized mountain recipes adapted for high-altitude nutrition. Throughout your journey, our specially trained guides—many with international alpine certification—provide contextualized understanding of both the technical aspects of snow sports and the cultural significance of this historic mountain resort, offering balanced perspectives on its development from royal hunting ground to world-renowned ski destination. We've thoughtfully balanced exhilarating snow adventures with comfortable heated accommodations that reflect the region's architectural heritage while maintaining essential comforts, creating a journey that captures Gulmarg's authentic winter magic.",
      },
      {
        question:
          "Is Gulmarg suitable for both skiers and non-skiers, and how does YK Travels accommodate different interests?",
        answer:
          "Gulmarg offers exceptional experiences for both skiing enthusiasts and non-skiers alike, with YK Travels thoughtfully accommodating diverse interests through parallel activity programming that ensures every traveler enjoys memorable Himalayan adventures regardless of skiing preference or ability. For skiing enthusiasts, our package provides comprehensive support including certified instructors matched to your precise skill level—from first-time beginners requiring fundamental instruction to advanced skiers seeking challenging terrain guidance. We arrange quality equipment rentals, provide slope orientation specific to daily conditions, and offer technical improvement sessions focused on terrain-appropriate skills. Beginners benefit from our specially designed progressive learning approach on gentle nursery slopes, while intermediate and advanced skiers receive guidance on suitable runs ranging from groomed slopes to carefully assessed off-piste opportunities. For non-skiers, YK Travels has developed equally compelling parallel activities that incorporate Gulmarg's winter magic without requiring skiing skills. These include expertly guided snow treks with specialized equipment through pristine winter forests, snowshoe adventures to panoramic viewpoints inaccessible by other means, snow tubing and sledding experiences on dedicated slopes, comprehensive photography workshops specializing in alpine light and snow conditions, cultural immersion activities including traditional handicraft demonstrations, and contemplative snow forest walks focusing on mindfulness in nature. Both skiers and non-skiers share certain signature experiences including the breathtaking Gulmarg Gondola journey to Apharwat Peak with its spectacular panoramic views, traditional Kashmiri culinary experiences including the elaborate Wazwan feast, village cultural exchanges, evening programs featuring regional music and historical presentations, and comfortable heated mountain accommodations. YK Travels' flexible scheduling ensures that skiing and non-skiing experiences operate simultaneously with coordinated meeting points for shared meals and key experiences, allowing mixed-interest groups and families to enjoy their preferred activities while maintaining a cohesive travel experience. Our guides communicate daily to ensure weather conditions and individual preferences are accommodated through thoughtful activity adjustments, ensuring that both skiers and non-skiers experience the full magic of Gulmarg's winter wonderland.",
      },
      {
        question:
          "What should I pack for my winter trip to Gulmarg with YK Travels?",
        answer:
          "For your winter adventure with YK Travels in Gulmarg, strategic packing significantly enhances both comfort and enjoyment of the Himalayan snow environment. Essential clothing follows the critical three-layer system: moisture-wicking thermal base layers (2-3 sets recommended, synthetic or merino wool), insulating mid-layers (fleece or down), and waterproof/windproof outer shell jacket and pants specifically designed for snow activities. Accessories must include insulated waterproof gloves/mittens (consider bringing liner gloves as well), warm hat covering ears, neck gaiter or balaclava for face protection, UV-protective snow goggles for mountain conditions, and polarized sunglasses for intense alpine reflection. For winter conditions (December-March), prepare for temperature ranges from -10°C to 5°C (14-41°F) during days with potentially colder conditions at higher elevations and during evening hours, particularly at Apharwat Peak accessed via the gondola. Footwear requirements include waterproof insulated snow boots with good traction for village and trekking activities, complemented by warm wool or synthetic socks (3-4 pairs minimum, avoiding cotton), and comfortable indoor footwear for resort relaxation. YK Travels recommends bringing a small daypack (20-30 liters) with waterproof cover for gondola journeys and day excursions, a reusable water thermos for hot beverages during outdoor activities (cold water is not recommended at these temperatures), chemical hand and foot warmers for extra comfort during extended outdoor activities, high-SPF sunscreen and lip protection (mountain UV exposure is intense even in winter), moisturizing skincare products (mountain air is extremely dry), and personal medications including remedies for potential altitude adjustment. If participating in skiing activities, consider bringing technical ski socks, thin liners for gloves/boots, and any specific skiing accessories you prefer, though YK Travels arranges comprehensive equipment rentals including skis, boots, poles, and helmets. For photography enthusiasts, camera protection from moisture is essential, with spare batteries crucial as cold temperatures significantly reduce battery life—keep spares in inner pockets for warmth. Additional recommended items include a headlamp for early morning or evening visibility, microfiber towel for snow activities, swimwear for hotel sauna facilities, and designated waterproof bag for electronic items and documents. YK Travels provides comprehensive pre-departure information including seasonal updates, current snow conditions, and specific recommendations based on activities selected and weather forecasts closer to your travel dates.",
      },
      {
        question:
          "How challenging is the Gulmarg Gondola experience and what should I expect?",
        answer:
          "The Gulmarg Gondola represents one of the world's highest cable car experiences, comprising two distinct phases that ascend from Gulmarg village (8,694 feet) ultimately reaching the breathtaking height of Apharwat Peak (13,780 feet). This remarkable engineering achievement offers unparalleled Himalayan panoramas but requires some preparation for maximum enjoyment and comfort. The first phase of the gondola travels from Gulmarg village to Kongdori Station (10,500 feet), covering approximately 2.5 kilometers in 9-12 minutes. This enclosed cable car accommodates 6-8 passengers comfortably with large viewing windows providing spectacular vistas of snow-covered pine forests and the Kashmir Valley below. Most travelers find this first phase very accessible with gradual elevation change and minimal physical demands. The experience becomes more adventurous during the second phase, which ascends from Kongdori to Apharwat Peak, rising another 3,280 feet over approximately 2.3 kilometers in 12-15 minutes. This higher section accesses true high-altitude terrain with more pronounced temperature drops and thinner air that some guests may notice through slightly increased breathing rate. YK Travels enhances this natural wonder through several thoughtful additions that maximize comfort and meaning. Our priority boarding arrangements (pre-organized exclusively for our guests) significantly reduce waiting times, which can otherwise be substantial during peak season. Our timing strategy avoids peak crowds by utilizing early morning departures when conditions are typically clearest for photography and views. Throughout the journey, our specialized alpine guides provide continuous interpretation identifying key landmarks including Nanga Parbat, Harmukh Peak, and the sweeping Kashmir Valley panorama extending to Pakistan and Tibet on exceptionally clear days. For photography enthusiasts, our guides recommend ideal camera settings for changing light conditions and point out the most photogenic angles and compositions. YK Travels provides specialized high-altitude comfort items including supplemental oxygen for guests experiencing altitude sensitivity, insulated seating pads for the observation areas at the summit, and insulated containers with hot beverages. For weather contingencies, we maintain constant communication with gondola operators and meteorological services, scheduling journeys during optimal weather windows and arranging alternate activities during gondola closures due to high winds (which occur occasionally). Most importantly, our guides emphasize proper high-altitude acclimatization techniques, appropriate clothing layering, and mindful breathing practices that enhance the safety and enjoyment of this truly spectacular Himalayan experience.",
      },
      {
        question:
          "What is special about Kashmiri cuisine and how does YK Travels incorporate this into the journey?",
        answer:
          "Kashmiri cuisine represents one of India's most distinctive and sophisticated culinary traditions, characterized by aromatic slow-cooking techniques, unique ingredient combinations, and ceremonial serving practices that reflect the region's remarkable cultural heritage. This cuisine developed through centuries of influences including Persian, Central Asian, and indigenous Himalayan traditions, creating flavor profiles unlike any other regional Indian cooking. YK Travels incorporates this extraordinary culinary heritage throughout your Gulmarg journey with progressively immersive experiences that reveal the depth and complexity of Kashmiri food culture. Our journey begins with a welcome featuring traditional Kashmiri Kahwa—a distinctive green tea infused with saffron, cardamom, cinnamon, and crushed almonds served in traditional samovar vessels. This centuries-old preparation sets the stage for the region's unique aromatic sensibilities. Daily breakfasts include authentic Kashmiri elements alongside international options—featuring distinctive breads like girda (round dense bread), lavasa (thin layered bread), and tsot (bagel-like bread) served with mountain honey and local preserves. Specialty dishes like harissa (slow-cooked lamb with rice and spices) provide substantial morning nourishment perfect for mountain activities. Throughout your journey, YK Travels arranges specialized dining experiences including a traditional home-hosted meal where family recipes are shared and explained in cultural context, with our guests participating in techniques like grinding spices with traditional stone tools. Our culinary guide explains distinctive Kashmiri cooking implements including the waza deegcha (special copper vessels), specialized wooden stirring paddles, and unique clay cooking pots that impart distinctive flavors. The culinary highlight of your journey is YK Travels' exclusive arrangement of an authentic Wazwan feast—considered the pinnacle of Kashmiri gastronomy. This elaborate multi-course ceremonial meal prepared by specialized hereditary chefs called Wazas features distinctive preparations including rogan josh (aromatic lamb curry), gushtaba (velvety meatballs in yogurt gravy), rista (spiced meatballs in red gravy), and tabak maaz (crispy spiced ribs). Our cultural expert explains the precise traditional serving order, the significance of communal dining practices, and the historical context of this royal tradition. For interested guests, YK Travels arranges specialized culinary demonstrations where you'll learn techniques for preparing distinctive Kashmiri spice blends—particularly the region's characteristic combination of fennel, ginger, asafoetida, and Kashmiri chili that creates its signature mild heat with pronounced aromatics. Throughout these experiences, our experts highlight the connection between Kashmiri cuisine and the region's unique climate and agriculture, explaining how this high-altitude mountain society developed specialized preservation techniques and cold-weather comfort foods that reflect deep adaptation to the Himalayan environment.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13223.492037133402!2d74.36961842532744!3d34.05005043113701!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38e1af82031d3237%3A0x93a7d373919beebd!2sGulmarg%2C%20Jammu%20and%20Kashmir%20193403!5e0!3m2!1sen!2sin!4v1652457930872!5m2!1sen!2sin",
  },
  {
    id: 24,
    slug: "pahalgam-yk-travels",
    title: "Pahalgam Trip with YK Travels",
    shortDescription:
      "Journey to the 'Valley of Shepherds' with YK Travels' exclusive Pahalgam package featuring pristine meadows, rushing rivers, pine forests, and adventure activities in this jewel of Kashmir nestled amidst snow-capped Himalayan peaks.",
    state: "Jammu & Kashmir",
    price: 7499,
    duration: "5 Days",
    activityTags: [
      "trekking",
      "river-rafting",
      "horse-riding",
      "fishing",
      "nature",
      "golf",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/jammu-kashmir/jk-7.jpg",
        alt: "YK Travels Pahalgam Valley View",
      },
      {
        id: 2,
        src: "/images/packages/jammu-kashmir/jk-8.webp",
        alt: "YK Travels Pahalgam Lidder River",
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Luxury hotels and traditional Kashmiri cottages",
        icon: "building",
      },
      arrivalCity: {
        text: "Srinagar",
        icon: "city",
      },
      bestSeason: {
        text: "April to October, December to February for snow",
        icon: "sun",
      },
      guide: {
        text: "Expert local Kashmiri guides with cultural knowledge",
        icon: "guide",
      },
      maximumAltitude: {
        text: "9,200 feet",
        icon: "mountain",
      },
      meals: {
        text: "All meals included with authentic Kashmiri cuisine",
        icon: "bowl",
      },
      transportation: {
        text: "Private AC vehicle throughout the trip",
        icon: "bus",
      },
      pickupPoint: {
        text: "Srinagar Airport or City Center",
        icon: "location",
      },
    },
    overview: {
      title: "Pahalgam: Kashmir's Enchanting Valley of Shepherds",
      description:
        "Experience the breathtaking beauty of Pahalgam with YK Travels' meticulously crafted journey through Kashmir's most picturesque valley. Situated at an elevation of 7,200 feet along the banks of the pristine Lidder River, Pahalgam offers a perfect blend of natural splendor, adventure activities, and cultural immersion. Our package showcases the emerald meadows, dense pine forests, gushing rivers, and snow-capped mountains that make Pahalgam a paradise for nature lovers and adventure enthusiasts alike, all while providing authentic local experiences and comfortable accommodations.",
      activities: [
        {
          title: "Betaab Valley Exploration",
          description:
            "Wander through the stunning Betaab Valley with YK Travels' naturalist guide, exploring the lush green meadows, crystal-clear streams, and magnificent pine forests of this cinematic location named after a Bollywood film that was shot here.",
          emoji: "🏞️",
        },
        {
          title: "Lidder River Adventures",
          description:
            "Experience the thrill of white-water rafting on the Lidder River with YK Travels' certified instructors, navigating through exciting rapids while surrounded by breathtaking mountain scenery in a safe and professional setting.",
          emoji: "🚣",
        },
        {
          title: "Aru Valley Trek",
          description:
            "Embark on a guided trek through the pristine Aru Valley with YK Travels' experienced mountain guides, discovering hidden meadows, shepherd huts, and panoramic viewpoints while learning about the unique alpine ecosystem.",
          emoji: "🥾",
        },
        {
          title: "Pahalgam Golf Experience",
          description:
            "Play a round of golf or take beginner lessons at one of the world's highest golf courses with stunning Himalayan backdrops, arranged exclusively for YK Travels guests with equipment rental and professional instruction available.",
          emoji: "⛳",
        },
        {
          title: "Kashmiri Cultural Immersion",
          description:
            "Participate in an authentic Wazwan feast and cultural evening organized by YK Travels, featuring traditional Kashmiri cuisine, folk music, and interactions with local artisans who demonstrate their centuries-old crafts.",
          emoji: "🧵",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Srinagar & Drive to Pahalgam",
        description:
          "Your journey begins with YK Travels' warm welcome at Srinagar Airport. Board our comfortable private vehicle for a scenic 2.5-hour drive to Pahalgam through the beautiful Kashmir Valley, with stops at saffron fields and traditional Kashmiri villages. Upon arrival in Pahalgam, check into your carefully selected accommodation where a welcome drink with Kashmiri kahwa tea awaits. After freshening up, take an orientation walk along the Lidder River with your guide, absorbing the tranquil atmosphere and mountain views. The day concludes with a special welcome dinner featuring traditional Kashmiri cuisine, arranged by YK Travels to introduce you to the rich flavors of the region.",
      },
      {
        day: 2,
        title: "Betaab Valley & Chandanwari",
        description:
          "After a hearty breakfast, your YK Travels guide will accompany you to the picturesque Betaab Valley, a cinematic location surrounded by snow-capped mountains and pine forests. Spend time exploring the valley's lush meadows and taking photographs of its pristine beauty. Continue to Chandanwari, the starting point of the sacred Amarnath Yatra, to admire the stunning glacier views. Enjoy a packed lunch amidst nature before returning to Pahalgam. In the evening, participate in a Kashmiri cooking demonstration followed by dinner at your accommodation, where the chef will reveal secrets of traditional Wazwan cuisine exclusively for YK Travels guests.",
      },
      {
        day: 3,
        title: "Aru Valley & Baisaran Meadows",
        description:
          "Today, YK Travels takes you on an excursion to the pristine Aru Valley after breakfast. Enjoy a guided trek through pine forests and meadows, with our naturalist pointing out local flora and fauna. Experience an optional horse ride through the valley (at additional cost) for a traditional Kashmiri highland experience. After a picnic lunch, visit the emerald Baisaran Meadows, often called 'Mini Switzerland,' where you can enjoy panoramic views of snow-capped peaks. Return to your accommodation for some leisure time before dinner at a local restaurant featuring mountain trout and other Kashmiri specialties, carefully selected by YK Travels for authentic flavors.",
      },
      {
        day: 4,
        title: "Adventure Day: Rafting & Golf",
        description:
          "Begin your day with an early breakfast before embarking on an exciting white-water rafting expedition on the Lidder River (seasonal, subject to weather conditions). YK Travels arranges all safety equipment and professional guides for this thrilling yet safe adventure. After rafting, enjoy lunch at a riverside cafe before heading to the Pahalgam Golf Course, one of the world's highest. Here, you can either play a round or take beginner lessons arranged exclusively for YK Travels guests. The evening features a special cultural program with traditional Kashmiri folk music and dance performances, followed by a farewell Wazwan dinner showcasing the best of Kashmiri cuisine.",
      },
      {
        day: 5,
        title: "Fishing Experience & Departure",
        description:
          "On your final day, early risers can participate in an optional sunrise photography session at a scenic viewpoint. After breakfast, enjoy a unique trout fishing experience in the Lidder River under the guidance of local experts arranged by YK Travels. Learn traditional fishing techniques while surrounded by spectacular mountain scenery. Enjoy your last lunch in Pahalgam before checking out. As we drive you back to Srinagar for your onward journey, make stops at local craft shops where you can purchase authentic Kashmiri handicrafts. YK Travels ensures a comfortable and timely transfer, with assistance for your departure arrangements.",
      },
    ],
    cost: {
      includes: [
        "All entry fees to attractions mentioned in the itinerary",
        "Guided treks and nature walks with experienced guides",
        "White-water rafting experience with safety equipment",
        "Special welcome and farewell Wazwan dinners",
        "Cultural program with traditional Kashmiri performances",
        "All applicable taxes",
        "24/7 on-ground support from YK Travels team",
      ],
      excludes: [
        "Airfare to/from Srinagar",
        "Horse riding (available at additional cost)",
        "Personal expenses and tips",
        "Travel insurance",
        "Golf equipment rental and green fees",
        "Optional activities not mentioned in the itinerary",
        "Photography or videography fees (if applicable)",
        "Anything not specifically mentioned in the inclusions",
      ],
      notes: [
        "Prices are subject to change during peak seasons (especially during May-June)",
        "The itinerary may be modified due to weather conditions or local circumstances",
        "Rafting activities are subject to suitable water levels and weather conditions",
        "Early booking is recommended for the best hotel availability",
        "Special permissions may be required for certain areas due to security protocols",
        "Indian citizens require valid ID proof, while foreign nationals need proper permits for Kashmir",
      ],
    },
    faqs: [
      {
        question: "When is the best time to visit Pahalgam?",
        answer:
          "The best time to visit Pahalgam with YK Travels is from April to October for pleasant weather and accessible trekking routes. Summer (May-June) offers lush green landscapes and comfortable temperatures for outdoor activities. Winter (December-February) transforms Pahalgam into a snowy wonderland, perfect for those seeking snow experiences, though some trekking routes may be closed. We generally avoid the monsoon season (July-August) due to occasional landslides.",
      },
      {
        question: "Is Pahalgam safe for tourists?",
        answer:
          "Yes, Pahalgam is considered safe for tourists. YK Travels prioritizes guest safety with continuous monitoring of local conditions and maintaining communication with authorities. Our local guides are well-versed with the region and safety protocols. We recommend following general travel precautions and staying updated with the latest travel advisories. Our team provides 24/7 support throughout your journey for complete peace of mind.",
      },
      {
        question:
          "How physically demanding are the activities in this package?",
        answer:
          "YK Travels' Pahalgam package includes activities suitable for moderate fitness levels. The treks are generally easy to moderate with gradual ascents. White-water rafting is conducted on Grade I-II rapids suitable for beginners with proper safety measures. All activities can be customized based on your comfort level and preferences. Our guides constantly assess conditions and participants' abilities to ensure an enjoyable and safe experience for everyone.",
      },
      {
        question: "What type of clothing should I pack for Pahalgam?",
        answer:
          "For summer visits (April-October), pack light woolens, comfortable trekking shoes, sun protection, and layered clothing as temperatures can vary throughout the day. For winter visits (December-February), bring heavy woolens, insulated jackets, thermal wear, waterproof boots, gloves, and caps. YK Travels provides a detailed packing list before your trip, tailored to the season of your visit and the specific activities planned for your itinerary.",
      },
      {
        question:
          "Can YK Travels arrange special experiences like fishing permits?",
        answer:
          "Absolutely! YK Travels specializes in arranging special permits and unique experiences in Pahalgam. We can secure fishing licenses for trout fishing in the Lidder River, organize private cultural performances, arrange special dining experiences in scenic locations, and coordinate with local artisans for craft demonstrations. Just let us know your interests while booking, and we'll customize your itinerary accordingly.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d26573.078884539244!2d75.30093537135053!3d34.01592966439354!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38e1ffffffff0001%3A0x46c60fadd9ec1f3f!2sPahalgam%2C%20Jammu%20and%20Kashmir!5e0!3m2!1sen!2sin!4v1651903529559!5m2!1sen!2sin",
  },
  {
    id: 25,
    slug: "sonmarg-yk-travels",
    title: "Sonmarg Adventure with YK Travels",
    shortDescription:
      "Discover the 'Meadow of Gold' with YK Travels' exclusive Sonmarg package, featuring glacier treks, alpine lakes, lush meadows, and thrilling adventure activities amidst the majestic Himalayan peaks of Jammu & Kashmir.",
    state: "Jammu & Kashmir",
    price: 14999,
    duration: "5 Days",
    activityTags: [
      "trekking",
      "river-rafting",
      "horse-riding",
      "fishing",
      "nature",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/jammu-kashmir/jk-5.jpg",
        alt: "YK Travels Sonmarg Thajiwas Glacier",
      },
      {
        id: 2,
        src: "/images/packages/jammu-kashmir/jk-6.webp",
        alt: "YK Travels Sonmarg Valley View",
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Luxury hotels and cozy Himalayan lodges",
        icon: "building",
      },
      arrivalCity: {
        text: "Srinagar",
        icon: "city",
      },
      bestSeason: {
        text: "April to October for trekking, December to February for snow activities",
        icon: "sun",
      },
      guide: {
        text: "Expert local Kashmiri guides with adventure expertise",
        icon: "guide",
      },
      maximumAltitude: {
        text: "9,000 feet",
        icon: "mountain",
      },
      meals: {
        text: "All meals included with authentic Kashmiri cuisine",
        icon: "bowl",
      },
      transportation: {
        text: "Private AC vehicle throughout the trip",
        icon: "bus",
      },
      pickupPoint: {
        text: "Srinagar Airport or City Center",
        icon: "location",
      },
    },
    overview: {
      title: "Sonmarg: Kashmir's Golden Meadow Adventure",
      description:
        "Embark on an unforgettable journey to Sonmarg with YK Travels, exploring the pristine beauty of the 'Meadow of Gold' nestled in the Himalayas. Located at 2,690 meters, Sonmarg is renowned for its snow-capped peaks, Thajiwas Glacier, alpine lakes, and adventure opportunities. Our package blends thrilling activities like trekking, river rafting, and horse riding with cultural immersion and serene nature experiences, all while ensuring luxurious accommodations and authentic Kashmiri hospitality.",
      activities: [
        {
          title: "Thajiwas Glacier Trek",
          description:
            "Trek to the stunning Thajiwas Glacier with YK Travels' expert guides, surrounded by snow-capped peaks and alpine meadows, offering breathtaking views and a chance to explore this year-round snowy wonder.",
          emoji: "🧊",
        },
        {
          title: "Vishansar Lake Exploration",
          description:
            "Discover the serene Vishansar Lake, a high-altitude gem, with YK Travels' guided trek, marveling at its crystal-clear waters and lush surroundings, perfect for nature lovers and photographers.",
          emoji: "💧",
        },
        {
          title: "Sindh River Rafting",
          description:
            "Experience the thrill of white-water rafting on the Sindh River with YK Travels' certified instructors, navigating exciting rapids amidst spectacular Himalayan scenery in a safe and professional setting.",
          emoji: "🚣",
        },
        {
          title: "Horse Riding in Baltal Valley",
          description:
            "Enjoy a traditional Kashmiri horse ride through the picturesque Baltal Valley, arranged by YK Travels, with stunning views of glaciers and meadows, offering a unique perspective of Sonmarg’s beauty.",
          emoji: "🐎",
        },
        {
          title: "Kashmiri Cultural Evening",
          description:
            "Immerse in a cultural evening with YK Travels, featuring a traditional Wazwan feast, Kashmiri folk music, and interactions with local artisans showcasing their intricate crafts.",
          emoji: "🧵",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Srinagar & Drive to Sonmarg",
        description:
          "Begin your adventure with YK Travels' warm welcome at Srinagar Airport. Board our private AC vehicle for a scenic 3-hour drive to Sonmarg, passing through lush valleys and charming villages. Upon arrival, check into your cozy accommodation and enjoy a welcome drink of Kashmiri kahwa tea. Take an evening stroll along the Sindh River with your guide, soaking in the serene Himalayan ambiance. Conclude the day with a traditional Kashmiri dinner, curated by YK Travels to showcase local flavors.",
      },
      {
        day: 2,
        title: "Thajiwas Glacier & Local Exploration",
        description:
          "After breakfast, embark on a guided trek to Thajiwas Glacier with YK Travels’ adventure experts, marveling at the snow-covered landscape and panoramic views. Enjoy a packed lunch amidst the glacier’s beauty. In the afternoon, explore local meadows and interact with shepherds to learn about their way of life. Return to your accommodation for a relaxing evening, followed by dinner featuring Kashmiri specialties at a local restaurant handpicked by YK Travels.",
      },
      {
        day: 3,
        title: "Vishansar Lake Trek",
        description:
          "Start the day with a hearty breakfast before setting out on a guided trek to Vishansar Lake, led by YK Travels’ naturalist guides. Traverse alpine meadows and forests, with your guide highlighting the region’s flora and fauna. Enjoy a picnic lunch by the lake’s pristine waters. Return to Sonmarg in the afternoon for some leisure time, followed by a cultural storytelling session about Sonmarg’s history over dinner at your accommodation.",
      },
      {
        day: 4,
        title: "Adventure Day: Rafting & Horse Riding",
        description:
          "Post-breakfast, gear up for an exhilarating white-water rafting session on the Sindh River, organized by YK Travels with professional instructors and safety equipment (seasonal, subject to weather). After lunch at a riverside cafe, head to Baltal Valley for a guided horse-riding experience through scenic trails. The evening features a special cultural program with Kashmiri folk performances and a farewell Wazwan dinner, celebrating the best of Kashmiri cuisine.",
      },
      {
        day: 5,
        title: "Fishing Experience & Departure",
        description:
          "On your final day, opt for an early morning trout fishing experience in the Sindh River, guided by local experts arranged by YK Travels, learning traditional techniques amidst stunning scenery. After breakfast, enjoy some free time to explore Sonmarg’s local market for Kashmiri handicrafts. Check out and board your private AC vehicle for the return to Srinagar, with stops at scenic viewpoints. YK Travels ensures a smooth transfer to Srinagar Airport or City Center for your onward journey.",
      },
    ],
    cost: {
      includes: [
        "All entry fees to attractions mentioned in the itinerary",
        "Guided treks and nature walks with experienced guides",
        "White-water rafting experience with safety equipment",
        "Special welcome and farewell Wazwan dinners",
        "Cultural program with traditional Kashmiri performances",
        "All applicable taxes",
        "24/7 on-ground support from YK Travels team",
      ],
      excludes: [
        "Airfare to/from Srinagar",
        "Horse riding and fishing equipment (available at additional cost)",
        "Personal expenses and tips",
        "Travel insurance",
        "Optional activities not mentioned in the itinerary",
        "Photography or videography fees (if applicable)",
        "Anything not specifically mentioned in the inclusions",
      ],
      notes: [
        "Prices may vary during peak seasons (May-June and winter snow season)",
        "Itinerary may be adjusted due to weather or local conditions",
        "Rafting and fishing are subject to suitable water levels and weather",
        "Early booking recommended for best accommodation availability",
        "Special permits may be required for certain areas due to security protocols",
        "Indian citizens need valid ID proof; foreign nationals require permits for Kashmir",
      ],
    },
    faqs: [
      {
        question: "When is the best time to visit Sonmarg?",
        answer:
          "The best time to visit Sonmarg with YK Travels is April to October for trekking, rafting, and pleasant weather, with lush meadows and clear skies. Winter (December-February) offers snow-covered landscapes ideal for snow activities, though some trails may be inaccessible. We avoid July-August due to potential monsoon-related disruptions.",
      },
      {
        question: "Is Sonmarg safe for tourists?",
        answer:
          "Yes, Sonmarg is safe for tourists. YK Travels ensures safety by monitoring local conditions, coordinating with authorities, and employing experienced guides familiar with the region. We provide 24/7 support and recommend adhering to general travel precautions and checking recent advisories for a worry-free trip.",
      },
      {
        question:
          "How physically demanding are the activities in this package?",
        answer:
          "YK Travels’ Sonmarg package is designed for moderate fitness levels. Treks to Thajiwas Glacier and Vishansar Lake are easy to moderate, with gradual ascents. Rafting involves Grade I-II rapids, suitable for beginners with safety measures. Activities can be tailored to your comfort level, and our guides ensure a safe, enjoyable experience.",
      },
      {
        question: "What should I pack for Sonmarg?",
        answer:
          "For April-October, pack light woolens, trekking shoes, layered clothing, and sun protection due to variable temperatures. For winter (December-February), bring heavy woolens, insulated jackets, thermals, waterproof boots, and gloves. YK Travels provides a season-specific packing list tailored to your itinerary and activities.",
      },
      {
        question: "Can YK Travels arrange special permits or experiences?",
        answer:
          "Yes, YK Travels can arrange fishing permits for the Sindh River, private cultural events, and interactions with local artisans. We also handle necessary trekking permits for restricted areas. Share your preferences during booking, and we’ll customize your Sonmarg experience to make it truly memorable.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3303.594067053674!2d75.27971631515198!3d34.30349698053648!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38e1b7f1c7e5a9b7%3A0x5f8b8e5e5f5f5f5f!2sSonmarg%2C%20Jammu%20and%20Kashmir!5e0!3m2!1sen!2sin!4v1667901234567!5m2!1sen!2sin",
  },
  {
    id: 26,
    slug: "leh-ladakh-yk-travels",
    title: "Enchanting Ladakh Expedition with YK Travels",
    shortDescription:
      "Embark on an unforgettable journey to the 'Land of High Passes' with YK Travels' exclusive Leh-Ladakh package, featuring breathtaking landscapes, ancient monasteries, serene lakes, and thrilling mountain adventures amidst the majestic Himalayas.",
    state: "Ladakh",
    price: 14999,
    duration: "7 Days",
    activityTags: [
      "monasteries",
      "high-altitude-passes",
      "lakes",
      "adventure",
      "culture",
      "photography",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/jammu-kashmir/jk-3.jpg",
        alt: "YK Travels Ladakh Panoramic View",
      },
      {
        id: 2,
        src: "/images/packages/jammu-kashmir/jk-4.jpg",
        alt: "YK Travels Pangong Lake",
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Comfortable hotels and traditional Ladakhi guesthouses",
        icon: "building",
      },
      arrivalCity: {
        text: "Leh",
        icon: "city",
      },
      bestSeason: {
        text: "June to September",
        icon: "sun",
      },
      guide: {
        text: "Expert local Ladakhi guides with cultural and geographical insights",
        icon: "guide",
      },
      maximumAltitude: {
        text: "18,380 feet (Khardung La)",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast and dinner included, with options for local cuisine",
        icon: "bowl",
      },
      transportation: {
        text: "Private SUV or tempo traveler for all transfers and sightseeing",
        icon: "bus",
      },
      pickupPoint: {
        text: "Leh Kushok Bakula Rimpochee Airport or Hotel in Leh",
        icon: "location",
      },
    },
    overview: {
      title: "Leh-Ladakh: A Journey to the Roof of the World with YK Travels",
      description:
        "Discover the raw beauty and spiritual charm of Leh-Ladakh with YK Travels' meticulously planned itinerary. This high-altitude desert, nestled in the rain shadow of the Himalayas, offers a unique blend of dramatic landscapes, ancient Buddhist monasteries perched on cliffs, vibrant local culture, and pristine turquoise lakes. Our package is designed to immerse you in the magic of Ladakh, from exploring its historical sites and interacting with its warm-hearted people to witnessing the grandeur of its snow-capped peaks and traversing some of the world's highest motorable passes. Prepare for an adventure that will leave you spellbound.",
      activities: [
        {
          title: "Monastery Exploration",
          description:
            "Visit iconic monasteries like Hemis, Thiksey, and Spituk with YK Travels' knowledgeable guides, delving into the rich Buddhist heritage, intricate murals, and serene prayer halls that define Ladakh's spiritual landscape.",
          emoji: "🕉️",
        },
        {
          title: "Khardung La Pass Adventure",
          description:
            "Experience the thrill of driving over Khardung La, one of the world's highest motorable passes, with YK Travels ensuring a safe and memorable journey to this breathtaking vantage point offering panoramic views of the Himalayan ranges.",
          emoji: "⛰️",
        },
        {
          title: "Pangong Tso Lake Excursion",
          description:
            "Witness the mesmerizing beauty of Pangong Tso, the high-altitude saltwater lake that changes colors throughout the day, on a carefully curated day trip by YK Travels, allowing ample time for photography and soaking in the tranquil atmosphere.",
          emoji: "🏞️",
        },
        {
          title: "Nubra Valley Discovery",
          description:
            "Explore the unique landscapes of the Nubra Valley, including the sand dunes of Hunder where you can enjoy a double-humped camel ride, arranged exclusively for YK Travels guests, and visit the Diskit Monastery with its towering Maitreya Buddha statue.",
          emoji: "🐪",
        },
        {
          title: "Ladakhi Cultural Immersion",
          description:
            "Engage with the local Ladakhi culture through interactions, visits to local markets, and the opportunity to witness traditional music and dance performances organized by YK Travels, offering a glimpse into the region's vibrant heritage.",
          emoji: "🎭",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Leh & Acclimatization",
        description:
          "Your Ladakh adventure begins with YK Travels' warm welcome at Kushok Bakula Rimpochee Airport in Leh (11,000 ft / 3,350 m). Transfer to your hotel for a full day of rest to acclimatize to the high altitude. Our YK Travels representative will provide essential guidelines for acclimatization and answer any queries. Enjoy a light welcome dinner at the hotel.",
      },
      {
        day: 2,
        title: "Leh Local Sightseeing",
        description:
          "After a relaxed breakfast, embark on a local sightseeing tour of Leh with your YK Travels guide. Visit the Leh Palace, a former royal residence offering panoramic views of the town and surrounding mountains. Explore the Shanti Stupa, a magnificent white-domed structure that symbolizes peace. Later, visit the Leh Market, where you can browse local handicrafts and interact with vendors. Enjoy dinner at a local restaurant, experiencing authentic Ladakhi cuisine.",
      },
      {
        day: 3,
        title: "Nubra Valley via Khardung La",
        description:
          "Today, YK Travels takes you on an exciting journey to the Nubra Valley (approx. 5-6 hours drive) via Khardung La (18,380 ft / 5,602 m), one of the world's highest motorable passes. Capture breathtaking views from the top. Upon arrival in the Nubra Valley, check into your camp or guesthouse in Hunder. In the afternoon, enjoy a unique double-humped Bactrian camel ride on the sand dunes. Visit the Diskit Monastery, famous for its giant Maitreya Buddha statue. Dinner and overnight stay in Nubra.",
      },
      {
        day: 4,
        title: "Hunder & Diskit Exploration",
        description:
          "After breakfast in Hunder, explore more of the Nubra Valley with YK Travels. Visit the charming village of Turtuk (if permits allow), located close to the Line of Control, offering a glimpse into a different cultural heritage. Alternatively, spend more time exploring the monasteries and scenic landscapes around Diskit and Hunder. Enjoy the serenity of the Shyok River. Return to your accommodation in Nubra for dinner.",
      },
      {
        day: 5,
        title: "Pangong Tso Lake Excursion",
        description:
          "Embark on a full-day excursion to the mesmerizing Pangong Tso Lake (approx. 5-6 hours drive each way) via the Chang La pass (17,590 ft / 5,360 m) with YK Travels. Witness the stunning hues of blue as the lake reflects the surrounding mountains. Enjoy the breathtaking scenery and capture memorable photographs. A packed lunch will be provided. Return to Leh in the evening for dinner and overnight stay.",
      },
      {
        day: 6,
        title: "Hemis & Thiksey Monasteries & Rancho's School",
        description:
          "Today, visit some of the most important monasteries in Ladakh with YK Travels. Explore Hemis Monastery, the largest monastic complex in Ladakh, known for its rich collection of Thangkas and murals. Continue to Thiksey Monastery, resembling the Potala Palace in Lhasa, offering stunning views of the Indus Valley. En route, visit the Druk Padma Karpo School, famously known as 'Rancho's School' from the movie '3 Idiots'. Return to Leh for some last-minute souvenir shopping and dinner.",
      },
      {
        day: 7,
        title: "Departure from Leh",
        description:
          "After breakfast, YK Travels will ensure a smooth transfer to Kushok Bakula Rimpochee Airport for your onward flight, carrying cherished memories of your Ladakh expedition.",
      },
    ],
    cost: {
      includes: [
        "Accommodation in comfortable hotels/guesthouses on twin-sharing basis",
        "Breakfast and dinner as per the itinerary",
        "All transfers and sightseeing as per the itinerary in a private vehicle",
        "Inner Line Permits (ILP) for restricted areas",
        "Experienced local Ladakhi guide",
        "Assistance with acclimatization",
        "Environmental and entry fees",
        "YK Travels on-ground support during the trip",
      ],
      excludes: [
        "Airfare to/from Leh",
        "Lunch and other meals not mentioned",
        "Personal expenses such as laundry, phone calls, and souvenirs",
        "Tips for guides and drivers",
        "Travel insurance",
        "Camel safari in Nubra Valley (available at additional cost)",
        "Oxygen cylinder (available on request at extra cost)",
        "Any cost arising due to natural calamities, road blockages, or unforeseen circumstances",
      ],
      notes: [
        "Prices are subject to change based on availability and season.",
        "The itinerary can be customized based on your interests and preferences.",
        "It is essential to allow sufficient time for acclimatization to the high altitude.",
        "Carry warm clothing even during summer as temperatures can drop significantly.",
        "Inner Line Permits are required for certain areas and will be arranged by YK Travels.",
        "Please carry valid photo ID proof.",
        "Network connectivity can be limited in certain remote areas.",
      ],
    },
    faqs: [
      {
        question: "What is the best time to visit Leh-Ladakh?",
        answer:
          "The best time to visit Leh-Ladakh with YK Travels is from June to September when the weather is pleasant, and all roads are generally open. The days are sunny, and the nights are cool. This period offers the best conditions for sightseeing and outdoor activities.",
      },
      {
        question: "Is Leh-Ladakh safe for tourists?",
        answer:
          "Yes, Leh-Ladakh is generally considered safe for tourists. YK Travels prioritizes the safety and well-being of our guests. Our experienced guides are well-aware of the local conditions, and we monitor the situation closely. It's advisable to follow general safety guidelines and respect local customs.",
      },
      {
        question: "How to deal with the high altitude in Ladakh?",
        answer:
          "Acclimatization is crucial in Ladakh due to the high altitude. YK Travels includes a day of rest upon arrival in Leh to help you acclimatize. We recommend drinking plenty of fluids, avoiding strenuous activities, and avoiding alcohol and smoking during the initial days. If you experience any symptoms of altitude sickness, inform your guide immediately.",
      },
      {
        question: "What type of clothing should I pack for Ladakh?",
        answer:
          "Pack in layers as the weather in Ladakh can be unpredictable. Include warm woolens, thermal wear, a windproof jacket, comfortable trekking shoes, a hat, sunglasses, and sunscreen. Even during summer, nights can be cold. YK Travels will provide a detailed packing list before your trip.",
      },
      {
        question: "Can YK Travels arrange for special permits or activities?",
        answer:
          "Yes, YK Travels can assist with obtaining the necessary Inner Line Permits for restricted areas. We can also arrange for special activities like cultural performances, interactions with local communities, and customized sightseeing based on your interests. Please inform us of your preferences during booking.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d26573.078884539244!2d75.30093537135053!3d34.01592966439354!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38e1ffffffff0001%3A0x46c60fadd9ec1f3f!2sPahalgam%2C%20Jammu%20and%20Kashmir!5e0!3m2!1sen!2sin!4v1651903529559!5m2!1sen!2sin",
  },
  {
    id: 27,
    slug: "jammu-kashmir-yk-travels",
    title: "Discover the Wonders of Jammu & Kashmir with YK Travels",
    shortDescription:
      "Experience the diverse beauty of Jammu & Kashmir with YK Travels' combined package, exploring the serene valleys of Kashmir, the pilgrimage sites of Jammu, and the adventure hubs amidst the majestic Himalayas.",
    state: "Jammu & Kashmir",
    price: 24999,
    duration: "9 Days",
    activityTags: [
      "pilgrimage",
      "nature",
      "valleys",
      "adventure",
      "camping",
      "trekking",
      "star-gazing",
      "scenic-drives",
    ],
    images: [
      {
        id: 1,
        src: "/images/packages/jammu-kashmir/jk-1.jpg",
        alt: "YK Travels Jammu & Kashmir package",
      },
      {
        id: 2,
        src: "/images/packages/jammu-kashmir/jk-2.jpeg",
        alt: "YK Travels Vaishno Devi Temple package",
      },
    ],
    tripInfo: {
      accommodation: {
        text: "Comfortable hotels and guesthouses in Jammu, Srinagar, Pahalgam, and Gulmarg",
        icon: "building",
      },
      arrivalCity: {
        text: "Jammu (Airport/Railway Station)",
        icon: "city",
      },
      bestSeason: {
        text: "March to June, September to November",
        icon: "sun",
      },
      guide: {
        text: "Experienced local guides familiar with the history, culture, and geography of the region",
        icon: "guide",
      },
      maximumAltitude: {
        text: "Around 8,800 feet (Gulmarg)",
        icon: "mountain",
      },
      meals: {
        text: "Breakfast included daily, with options for other meals",
        icon: "bowl",
      },
      transportation: {
        text: "Private vehicle for all transfers and sightseeing",
        icon: "bus",
      },
      pickupPoint: {
        text: "Jammu Airport or Railway Station",
        icon: "location",
      },
    },
    overview: {
      title: "A Comprehensive Journey Through Jammu & Kashmir with YK Travels",
      description:
        "Embark on a captivating 9-day exploration of Jammu & Kashmir with YK Travels, designed to showcase the diverse landscapes and cultural tapestry of this enchanting region. Begin your journey in Jammu, the 'City of Temples,' and then venture into the breathtaking Kashmir Valley, with its serene lakes, verdant meadows, and snow-capped peaks. Experience the spiritual aura of Vaishno Devi, the scenic beauty of Srinagar's Mughal Gardens and Dal Lake, the alpine charm of Gulmarg, and the picturesque landscapes of Pahalgam. This combined package offers a perfect blend of pilgrimage, nature, adventure, and cultural immersion, promising an unforgettable experience.",
      activities: [
        {
          title: "Vaishno Devi Pilgrimage",
          description:
            "Undertake a spiritual journey to the holy shrine of Mata Vaishno Devi in Katra, seeking blessings amidst the scenic Trikuta Mountains, with YK Travels ensuring comfortable arrangements for your travel and stay.",
          emoji: "🙏",
        },
        {
          title: "Srinagar Sightseeing",
          description:
            "Explore the summer capital of Jammu & Kashmir, Srinagar, with YK Travels, including visits to the iconic Dal Lake for a Shikara ride, the beautiful Mughal Gardens (Shalimar Bagh, Nishat Bagh, Cheshma Shahi), and the Shankaracharya Temple offering panoramic views.",
          emoji: "🏞️",
        },
        {
          title: "Gulmarg Gondola Ride",
          description:
            "Experience the breathtaking views of the snow-capped Himalayas with a ride on the Gulmarg Gondola, one of the highest cable cars in the world, arranged by YK Travels for an adventurous and scenic experience.",
          emoji: "🚠",
        },
        {
          title: "Pahalgam Exploration",
          description:
            "Discover the picturesque 'Valley of Shepherds,' Pahalgam, with YK Travels, exploring its lush green meadows, pristine rivers like the Lidder, and scenic spots like Betaab Valley and Aru Valley.",
          emoji: "⛰️",
        },
        {
          title: "Jammu City Tour",
          description:
            "Explore the historical and cultural sites of Jammu city with YK Travels, including visits to the Bahu Fort, Raghunath Temple, and Mubarak Mandi Palace, offering insights into the region's rich heritage.",
          emoji: "🏛️",
        },
      ],
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Jammu & Transfer to Katra",
        description:
          "Arrive at Jammu Airport or Railway Station, where you will be met by a YK Travels representative. Transfer to Katra (approx. 2 hours), the base camp for the Vaishno Devi pilgrimage. Check into your hotel and relax for the evening, preparing for your trek.",
      },
      {
        day: 2,
        title: "Vaishno Devi Darshan",
        description:
          "Embark on the holy pilgrimage to the Vaishno Devi shrine. You can trek, hire a pony or palanquin, or take a helicopter (at your own cost). After the darshan, return to Katra for an overnight stay.",
      },
      {
        day: 3,
        title: "Katra to Srinagar Transfer",
        description:
          "After breakfast, check out from your hotel and drive to Srinagar (approx. 6-7 hours). En route, enjoy the scenic landscapes of the Jammu & Kashmir region. Upon arrival in Srinagar, check into your houseboat on Dal Lake or a hotel. Enjoy a relaxing Shikara ride on the lake in the evening.",
      },
      {
        day: 4,
        title: "Srinagar Local Sightseeing",
        description:
          "Explore the famous Mughal Gardens of Srinagar, including Shalimar Bagh, Nishat Bagh, and Cheshma Shahi. Visit the Shankaracharya Temple for panoramic views of the city and Dal Lake. In the evening, explore the local markets for traditional Kashmiri handicrafts.",
      },
      {
        day: 5,
        title: "Srinagar to Gulmarg Excursion",
        description:
          "Take a day trip to Gulmarg (approx. 2 hours drive each way), the 'Meadow of Flowers.' Enjoy a Gondola ride to witness breathtaking views of the snow-capped peaks. You can also enjoy activities like horse riding and skiing (seasonal, at your own cost). Return to Srinagar in the evening for an overnight stay.",
      },
      {
        day: 6,
        title: "Srinagar to Pahalgam Transfer",
        description:
          "After breakfast, drive to Pahalgam (approx. 3 hours), the 'Valley of Shepherds.' En route, visit the saffron fields and apple orchards. Check into your hotel in Pahalgam and spend the afternoon exploring the scenic beauty of the Lidder River and surrounding areas.",
      },
      {
        day: 7,
        title: "Pahalgam Sightseeing",
        description:
          "Explore the picturesque valleys of Pahalgam, including Betaab Valley (named after the Bollywood movie 'Betaab') and Aru Valley, known for their lush meadows and scenic beauty. You can also opt for a pony ride to explore the local trails (at your own cost).",
      },
      {
        day: 8,
        title: "Pahalgam to Jammu Transfer",
        description:
          "After breakfast, drive back to Jammu (approx. 7-8 hours). Upon arrival, check into your hotel and relax for the evening.",
      },
      {
        day: 9,
        title: "Departure from Jammu",
        description:
          "After breakfast, check out from your hotel. YK Travels will arrange for your transfer to Jammu Airport or Railway Station for your onward journey, filled with memories of the beautiful Jammu & Kashmir.",
      },
    ],
    cost: {
      includes: [
        "Accommodation in comfortable hotels and houseboats as per the itinerary",
        "Breakfast daily",
        "All transfers and sightseeing by private vehicle",
        "Shikara ride on Dal Lake",
        "Gondola ride in Gulmarg (Phase 1)",
        "Inner Line Permits (if required)",
        "Experienced local guides",
        "YK Travels on-ground support",
      ],
      excludes: [
        "Airfare or train fare to/from Jammu",
        "Lunch and dinner",
        "Pony rides, helicopter charges, and other optional activities",
        "Entrance fees to monuments and gardens (if any)",
        "Personal expenses such as laundry, phone calls, and souvenirs",
        "Tips for guides and drivers",
        "Travel insurance",
        "Any cost arising due to natural calamities or unforeseen circumstances",
      ],
      notes: [
        "The itinerary can be customized based on your preferences and duration.",
        "Best time to visit Jammu & Kashmir for this combined package is from March to June and September to November for pleasant weather.",
        "Altitude sickness can be a concern in higher regions like Gulmarg; it's advisable to consult your doctor before travel.",
        "Carry warm clothing as temperatures can vary depending on the region and time of year.",
        "Booking in advance is recommended, especially during peak season.",
        "Please carry valid photo ID proof.",
      ],
    },
    faqs: [
      {
        question: "What is the best time to book this Jammu & Kashmir package?",
        answer:
          "The best time to book your Jammu & Kashmir package with YK Travels is at least 4-6 weeks in advance, especially if you are planning to travel during the peak seasons (April-June and September-October). Early booking ensures better availability of accommodations and can sometimes offer better prices.",
      },
      {
        question: "What kind of clothing should I pack for this trip?",
        answer:
          "Given the diverse altitudes and weather conditions in Jammu & Kashmir, it's best to pack in layers. For the Kashmir region (Srinagar, Gulmarg, Pahalgam), carry light woolens and a jacket for summer, and heavy woolens, thermal wear, and a windproof jacket for spring and autumn. For Jammu and Katra, pack light cottons for summer and light woolens for other seasons. Comfortable walking shoes are essential for sightseeing and the Vaishno Devi trek.",
      },
      {
        question: "Is the Vaishno Devi trek included in the package cost?",
        answer:
          "The YK Travels combined Jammu & Kashmir package includes transportation to and from Katra. However, the cost of the Vaishno Devi trek (pony, palanquin, helicopter) is not included and needs to be arranged and paid for directly by the traveler based on their preference.",
      },
      {
        question: "Are meals other than breakfast included?",
        answer:
          "This package includes breakfast daily. Lunch and dinner are generally not included to allow you the flexibility to explore local cuisine at your own pace and preference. Our guides can provide recommendations for good restaurants in each location.",
      },
      {
        question: "Can YK Travels customize this itinerary?",
        answer:
          "Yes, YK Travels understands that travelers may have different interests and time constraints. We are happy to customize this itinerary to suit your specific needs, whether you want to extend your stay in a particular location, include additional sightseeing options, or adjust the pace of the tour. Please contact our travel experts to discuss your requirements.",
      },
    ],
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d26573.078884539244!2d75.30093537135053!3d34.01592966439354!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38e1ffffffff0001%3A0x46c60fadd9ec1f3f!2sPahalgam%2C%20Jammu%20and%20Kashmir!5e0!3m2!1sen!2sin!4v1651903529559!5m2!1sen!2sin",
  },


  {
    id: 28,
    slug: "kauri-pass-trek",
    title: "Kauri Pass Trek",
    shortDescription: "A breathtaking trek through the Garhwal Himalayas with panoramic views of Nanda Devi and other peaks.",
    state: "Uttarakhand",
    price: 8199,
    duration: "6 Days / 5 Nights",
    activityTags: ["Trekking", "Camping", "star-gazing"],
    images: [
      {
        id: 1,
        src: "/images/kauri-pass-trek-1.avif",
        alt: "Kauri Pass Trek with snow-capped peaks"
      },
      {
        id: 2,
        src: "/images/kauri-pass-trek-2.webp",
        alt: "Campsite at Kauri Pass"
      }
    ],
    tripInfo: {
      accommodation: {
        text: "Tents & Guesthouses",
        icon: "fa-bed"
      },
      arrivalCity: {
        text: "Joshimath",
        icon: "fa-map-marker"
      },
      bestSeason: {
        text: "Sep-Nov, Apr-Jun",
        icon: "fa-sun"
      },
      guide: {
        text: "Experienced Trek Leader",
        icon: "fa-user-guide"
      },
      maximumAltitude: {
        text: "4265m (Kauri Pass)",
        icon: "fa-mountain"
      },
      meals: {
        text: "Veg Meals (Breakfast, Lunch, Dinner)",
        icon: "fa-utensils"
      },
      transportation: {
        text: "Shared Vehicle (Joshimath to Joshimath)",
        icon: "fa-bus"
      },
      pickupPoint: {
        text: "Joshimath Bus Stand",
        icon: "fa-map-pin"
      }
    },
    overview: {
      title: "Kauri Pass Trek Overview",
      description:
        "The Kauri Pass Trek is a gem in the Garhwal Himalayas, offering stunning views of peaks like Nanda Devi, Trishul, and Dronagiri. This moderate trek takes you through lush meadows, dense forests, and high-altitude passes, culminating at the Kauri Pass at 4265m.",
      highlights: [
        { id: 1, highlight: "Panoramic views of Nanda Devi and other Himalayan peaks" },
        { id: 2, highlight: "Camping in pristine alpine meadows" },
        { id: 3, highlight: "Trekking through the Curzon Trail" }
      ],
      activities: [
        {
          title: "Trekking",
          description: "Trek through diverse terrains including forests and meadows.",
          emoji: "🥾"
        },
        {
          title: "Camping",
          description: "Spend nights under the stars in high-altitude campsites.",
          emoji: "⛺"
        }
      ]
    },
    itinerary: [
      {
        day: 1,
        title: "Arrival in Joshimath",
        description: "Reach Joshimath, meet the team, and prepare for the trek.",
        activities: ["Briefing", "Gear Check"]
      },
      {
        day: 2,
        title: "Joshimath to Gulling Top",
        description: "Drive to the trailhead and trek to Gulling Top campsite.",
        activities: ["Drive", "Trekking"]
      },
      {
        day: 3,
        title: "Gulling Top to Tali",
        description: "Trek through forests and meadows to Tali campsite.",
        activities: ["Trekking", "Camping"]
      },
      {
        day: 4,
        title: "Tali to Kauri Pass",
        description: "Ascend to Kauri Pass (4265m) and descend to the campsite.",
        activities: ["Trekking", "Photography"]
      },
      {
        day: 5,
        title: "Kauri Pass to Auli",
        description: "Trek down to Auli and drive back to Joshimath.",
        activities: ["Trekking", "Drive"]
      },
      {
        day: 6,
        title: "Departure from Joshimath",
        description: "Depart from Joshimath after breakfast.",
        activities: ["Departure"]
      }
    ],
    cost: {
      includes: [
        "Accommodation (tents, guesthouses)",
        "All meals during the trek",
        "Trekking permits and fees",
        "Experienced trek leader and support staff",
        "Transportation from Joshimath to Joshimath"
      ],
      excludes: [
        "Travel to and from Joshimath",
        "Personal expenses",
        "Any additional meals outside the trek"
      ],
      notes: [
        "GST included in the price",
        "Carry personal trekking gear (backpack, shoes, etc.)"
      ]
    },
    stays: [
      {
        id: 1,
        images: [
          {
            id: 1,
            src: "/images/tent1.jpg",
            alt: "Campsite at Tali"
          }
        ],
        amenities: ["Tents", "Sleeping Bags", "Campfire"]
      }
    ],
    faqs: [
      {
        question: "Is the Kauri Pass Trek suitable for beginners?",
        answer:
          "The trek is moderately challenging and suitable for fit beginners with some prior trekking experience."
      },
      {
        question: "What is the best time to do this trek?",
        answer: "The best time is September to November and April to June for clear views and pleasant weather."
      }
    ],
    mapUrl: "https://maps.google.com/kauri-pass-trek"
  },



  {
    id: 29,
    slug: "dayara-bugyal-trek",
    title: "Dayara Bugyal Trek",
    shortDescription: "A scenic trek to one of the most beautiful alpine meadows in the Indian Himalayas.",
    state: "Uttarakhand",
    price: 5999,
    duration: "4 Days / 3 Nights",
    activityTags: ["trekking", "camping"],
    images: [
      {
        id: 1,
        src: "/images/dayara-bugyal-trek-1.jpg",
        alt: "Dayara Bugyal meadow in summer"
      },
      {
        id: 2,
        src: "/images/dayara-bugyal-trek-2.jpg",
        alt: "Snow-covered Dayara Bugyal"
      }
    ],
    tripInfo: {
      accommodation: {
        text: "Tents & Homestays",
        icon: "fa-bed"
      },
      arrivalCity: {
        text: "Dehradun",
        icon: "fa-map-marker"
      },
      bestSeason: {
        text: "Year-round (Best: Apr-Jun, Sep-Nov)",
        icon: "fa-sun"
      },
      guide: {
        text: "Local Trek Leader",
        icon: "fa-user-guide"
      },
      maximumAltitude: {
        text: "3750m (Dayara Top)",
        icon: "fa-mountain"
      },
      meals: {
        text: "Veg Meals (Breakfast, Lunch, Dinner)",
        icon: "fa-utensils"
      },
      transportation: {
        text: "Shared Vehicle (Dehradun to Barsu)",
        icon: "fa-bus"
      },
      pickupPoint: {
        text: "Dehradun Railway Station",
        icon: "fa-map-pin"
      }
    },
    overview: {
      title: "Dayara Bugyal Trek Overview",
      description:
        "The Dayara Bugyal Trek is perfect for beginners and nature lovers, offering stunning views of vast meadows and Himalayan peaks like Bandarpoonch and Black Peak. This easy-moderate trek is ideal for a short adventure.",
      highlights: [
        { id: 1, highlight: "Explore the expansive Dayara Bugyal meadow" },
        { id: 2, highlight: "Views of Bandarpoonch and other peaks" },
        { id: 3, highlight: "Camping in serene alpine settings" }
      ],
      activities: [
        {
          title: "Trekking",
          description: "Trek through forests and open meadows.",
          emoji: "🥾"
        },
        {
          title: "Photography",
          description: "Capture the beauty of Himalayan landscapes.",
          emoji: "📸"
        }
      ]
    },
    itinerary: [
      {
        day: 1,
        title: "Dehradun to Barsu",
        description: "Drive from Dehradun to Barsu village, the trek starting point.",
        activities: ["Drive", "Village Exploration"]
      },
      {
        day: 2,
        title: "Barsu to Barnala",
        description: "Trek to Barnala campsite through forests and meadows.",
        activities: ["Trekking", "Camping"]
      },
      {
        day: 3,
        title: "Barnala to Dayara Bugyal and Back",
        description: "Trek to Dayara Top (3750m) and return to Barnala.",
        activities: ["Trekking", "Photography"]
      },
      {
        day: 4,
        title: "Barnala to Barsu and Drive to Dehradun",
        description: "Trek back to Barsu and drive to Dehradun.",
        activities: ["Trekking", "Drive"]
      }
    ],
    cost: {
      includes: [
        "Accommodation (tents, homestays)",
        "All meals during the trek",
        "Trekking permits",
        "Local trek leader and support staff",
        "Transportation from Dehradun to Barsu"
      ],
      excludes: [
        "Travel to and from Dehradun",
        "Personal expenses",
        "Any additional meals outside the trek"
      ],
      notes: [
        "GST included in the price",
        "Basic trekking gear recommended"
      ]
    },
    stays: [
      {
        id: 1,
        images: [
          {
            id: 1,
            src: "/images/tent2.jpg",
            alt: "Campsite at Barnala"
          }
        ],
        amenities: ["Tents", "Sleeping Bags", "Basic Toilet Facilities"]
      }
    ],
    faqs: [
      {
        question: "Is Dayara Bugyal Trek good for beginners?",
        answer:
          "Yes, it’s an easy-moderate trek, ideal for beginners with basic fitness."
      },
      {
        question: "Can I do this trek in winter?",
        answer:
          "Yes, winter offers a beautiful snow-covered meadow, but proper gear is required."
      }
    ],
    mapUrl: "https://maps.google.com/dayara-bugyal-trek"
  },
  {
    id: 30,
    slug: "brahmatal-trek",
    title: "Brahmatal Trek",
    state: "Uttarakhand",
    shortDescription: "A scenic high-altitude winter trek in Chamoli, known for frozen alpine lakes and panoramic 360° Himalayan views.",
    price: 9500,
    duration: "5–6 Days",
    activityTags: ["trekking", "camping", "winter-trek", "beginners"],
    images: [
      { id: 1, src: "/images/packages/brahmatal/brahmatal-1.png", alt: "Brahmatal lake in winter" },
      { id: 2, src: "/images/packages/brahmatal/brahmatal-2.png", alt: "Snow covered trail to Brahmatal" }
    ],
    tripInfo: {
      accommodation: { text: "Tents & Homestays (Lohajung)", icon: "fa-bed" },
      arrivalCity: { text: "Lohajung", icon: "fa-map-marker-alt" },
      bestSeason: { text: "Winter (Dec–Mar); Post-monsoon (Sept–Nov)", icon: "fa-snowflake" },
      guide: { text: "Professional Trek Leader & Staff", icon: "fa-user-tie" },
      maximumAltitude: { text: "12,250 ft", icon: "fa-mountain" },
      meals: { text: "Veg Meals (Breakfast, Lunch, Dinner)", icon: "fa-utensils" },
      transportation: { text: "Drive from Rishikesh/Haridwar", icon: "fa-bus" },
      pickupPoint: { text: "Rishikesh/Haridwar", icon: "fa-map-pin" }
    },
    overview: {
      title: "Brahmatal Trek Overview",
      description: "The Brahmatal Trek is a scenic high-altitude trek in the Chamoli district of Uttarakhand. Known for its snow-covered trails, frozen alpine lakes (Brahmatal & Bekaltal), and panoramic mountain views, it is a perfect winter trek for beginners. The route offers 360° views of Himalayan giants like Trishul & Nanda Ghunti.",
      highlights: [
        { id: 1, highlight: "Frozen Bekaltal and Brahmatal Lakes" },
        { id: 2, highlight: "Spectacular views of Mt. Trishul and Nanda Ghunti" },
        { id: 3, highlight: "Trails through dense oak and rhododendron forests" },
        { id: 4, highlight: "360° Himalayan views from Brahmatal Summit/Ridge" }
      ],
      activities: [
        { title: "Snow Trekking", description: "Experience trekking in deep winter snow.", emoji: "❄️" },
        { title: "Photography", description: "Capture stunning 360° Himalayan vistas.", emoji: "📸" }
      ]
    },
    itinerary: [
      { day: 1, title: "Rishikesh to Lohajung", description: "Early morning drive (approx. 9–11 hrs) to Lohajung (base camp). Overnight stay in guesthouse/homestay.", activities: ["Scenic Drive", "Briefing"] },
      { day: 2, title: "Lohajung to Bekaltal Lake", description: "Trek through oak and rhododendron forests on gentle slopes (~5–6 km, 4–5 hrs). Camp by the lake.", activities: ["Forest Trek", "Lakeside Camping"] },
      { day: 3, title: "Bekaltal to Brahmatal Campsite", description: "Gradual ascent through meadows and snow fields (~7 km). Camp near the sacred Brahmatal Lake.", activities: ["Meadow Walk", "Alpine Camping"] },
      { day: 4, title: "Brahmatal Summit Day", description: "Summit push to catch spectacular views of Trishul & Nanda Ghunti. Descent to campsite/lower camp.", activities: ["Summit Push", "Peak Views"] },
      { day: 5, title: "Lohajung and Return", description: "Trek back down to Lohajung village and drive back to Rishikesh or onwards home.", activities: ["Descent", "Return Trip"] }
    ],
    cost: {
      includes: ["Trekking guide & staff", "Forest entry/trek permits", "Accommodation (tents/guesthouse)", "Meals during trek", "Camping gear (sleeping bags, tents)", "Basic safety & first-aid kit"],
      excludes: ["Personal insurance", "Transport to/from base village", "Porter/mule charges for luggage", "Gear rentals (jackets, poles)"],
      notes: ["Government ID (Aadhaar/Passport) required for permits.", "Budget packages start from ~₹7,000."]
    },
    faqs: [
      { question: "What’s the best time for snow views?", answer: "December to March offers snow-covered trails and frozen lakes." },
      { question: "Is it suitable for first-timers?", answer: "Yes, it is an easy to moderate trek suitable for beginners with basic fitness." },
      { question: "Do I need permits?", answer: "Yes, forest permits are required and usually managed by trek operators." },
      { question: "Is there mobile connectivity?", answer: "Limited; often available only near villages or certain ridges." }
    ],
    mapUrl: "https://maps.google.com/brahmatal-trek"
  },

  {
    id: 31,
    slug: "kedarkantha-trek",
    title: "Kedarkantha Trek",
    state: "Uttarakhand",
    shortDescription: "A classic winter summit trek in Uttarkashi, famous for its 360° mountain views and beautiful Juda Ka Talab lake.",
    price: 8500,
    duration: "4–6 Days",
    activityTags: ["trekking", "camping", "winter-trek", "beginners"],
    images: [
      { id: 1, src: "/images/packages/kedarkantha/kedarkantha-1.png", alt: "Kedarkantha summit view" },
      { id: 2, src: "/images/packages/kedarkantha/kedarkantha-2.png", alt: "Juda Ka Talab campsite" }
    ],
    tripInfo: {
      accommodation: { text: "Guesthouse & Alpine Tents", icon: "fa-campground" },
      arrivalCity: { text: "Sankri", icon: "fa-home" },
      bestSeason: { text: "Winter (Dec–Apr); Summer (May–June)", icon: "fa-calendar-alt" },
      guide: { text: "Professional Trek Guide", icon: "fa-walking" },
      maximumAltitude: { text: "12,500 ft", icon: "fa-mountain" },
      meals: { text: "Healthy Veg Meals during trek", icon: "fa-utensils" },
      transportation: { text: "Drive from Dehradun to Sankri", icon: "fa-bus" },
      pickupPoint: { text: "Dehradun", icon: "fa-map-pin" }
    },
    overview: {
      title: "Kedarkantha Trek Overview",
      description: "Located in Govind Wildlife Sanctuary, Kedarkantha is one of India's most popular Himalayan treks. Famous for its clearly defined summit, it gives trekkers a true sense of achievement even on their first high-altitude trek, featuring snow-covered forests and traditional villages.",
      highlights: [
        { id: 1, highlight: "Summit climb with 360° Himalayan views" },
        { id: 2, highlight: "Camping near the frozen Juda Ka Talab (winter)" },
        { id: 3, highlight: "Dense pine and oak forests across the trail" },
        { id: 4, highlight: "Views of Swargarohini, Bandarpoonch & Black Peak" }
      ],
      activities: [
        { title: "Summit Ascent", description: "A thrilling climb to catch the sunrise at 12,500 ft.", emoji: "⛰️" },
        { title: "Winter Camping", description: "Experience sub-zero camping in snow fields.", emoji: "⛺" }
      ]
    },
    itinerary: [
      { day: 1, title: "Dehradun to Sankri", description: "8–10 hour drive through Mussoorie and along the Tons River.", activities: ["Scenic Drive", "Guesthouse Stay"] },
      { day: 2, title: "Sankri to Juda Ka Talab", description: "Trek 4 km through beautiful pine forests to the iconic lake altitude of 9,100 ft.", activities: ["Forest Hike", "Lakeside Camp"] },
      { day: 3, title: "Juda Ka Talab to Kedarkantha Base Camp", description: "Ascend to open meadows below the summit (4 km, 3-4 hrs).", activities: ["Meadow Trek", "Acclimatization"] },
      { day: 4, title: "Summit Day & Return", description: "Early morning summit push to 12,500 ft followed by descent to lower campsites.", activities: ["Summit Achievement", "Descend"] },
      { day: 5, title: "Return to Sankri → Dehradun", description: "Final descent to back to Sankri and drive back to Dehradun railway station.", activities: ["Drive", "Wrap up"] }
    ],
    cost: {
      includes: ["Professional trek guide", "Forest permits & entry fees", "Accommodation (guesthouse + tents)", "All meals during trek", "Camping equipment (bags, tents, mats)", "Basic safety support"],
      excludes: ["Travel to/from Dehradun", "Personal trekking gear", "Backpack offloading/porter charges", "Travel insurance", "Personal expenses"],
      notes: ["Snow trekking from December to April."]
    },
    faqs: [
      { question: "Is Kedarkantha good for beginners?", answer: "Yes, it is one of the best beginner treks in India, especially in winter." },
      { question: "Does it have snow?", answer: "Yes, from December to April, the trek is mostly snow-covered." },
      { question: "Is altitude sickness common?", answer: "Rare, but hydration and slow ascent are always recommended." },
      { question: "Is mobile network available?", answer: "Limited connectivity in Sankri only; no signal during the trek." }
    ],
    mapUrl: "https://maps.google.com/kedarkantha-trek"
  },

  {
    id: 32,
    slug: "pindari-glacier-trek",
    title: "Pindari Glacier Trek",
    state: "Uttarakhand",
    shortDescription: "A legendary glacier trek in Kumaon, offering a gentle approach to the snout of the Pindari Glacier at Zero Point.",
    price: 11000,
    duration: "7 Days",
    activityTags: ["trekking", "glacier-expedition"],
    images: [
      { id: 1, src: "/images/packages/pindari/pindari-1.png", alt: "Pindari glacier snout" },
      { id: 2, src: "/images/packages/uk/uk-1.avif", alt: "Nanda Khat view" }
    ],
    tripInfo: {
      accommodation: { text: "Guesthouses & Lodges (Villages)", icon: "fa-home" },
      arrivalCity: { text: "Kharkiya", icon: "fa-road" },
      bestSeason: { text: "Spring (Apr–June); Autumn (Sept–Nov)", icon: "fa-leaf" },
      guide: { text: "Trek Guide & Local Support", icon: "fa-user" },
      maximumAltitude: { text: "12,300 ft", icon: "fa-mountain" },
      meals: { text: "All Meals during the trek", icon: "fa-utensils" },
      transportation: { text: "Drive from Kathgodam to Kharkiya", icon: "fa-taxi" },
      pickupPoint: { text: "Kathgodam", icon: "fa-train" }
    },
    overview: {
      title: "Pindari Glacier Trek Overview",
      description: "Located in the Kumaon region inside the Nanda Devi Biosphere Reserve, Pindari Glacier is one of India's most accessible glacier treks. It offers a gradual trail through traditional villages.",
      highlights: [
        { id: 1, highlight: "SNout of Pindari Glacier at Zero Point" },
        { id: 2, highlight: "Views of Nanda Devi East, Nanda Khat & Changuch" },
        { id: 3, highlight: "Traditional Kumaoni village experience" }
      ],
      activities: [
        { title: "Glacier Exploration", description: "Reach the snout of the Pindari Glacier.", emoji: "🧊" },
        { title: "Riverside Trekking", description: "Walk along the beautiful Pindar River.", emoji: "🌊" }
      ]
    },
    itinerary: [
      { day: 1, title: "Kathgodam to Kharkiya", description: "Long 9–10 hour drive into the Kumaon Himalayas.", activities: ["Scenic Drive", "Briefing"] },
      { day: 2, title: "Kharkiya to Dhakuri", description: "Gradual climb through forests to Dhakuri (5 km).", activities: ["Forest Trek", "Lodge Stay"] },
      { day: 3, title: "Dhakuri to Khati", description: "Descend into Pindar Valley to Khati, the largest village.", activities: ["Village Walk", "Lodge Stay"] },
      { day: 4, title: "Khati to Dwali", description: "Trek along the Pindar River through dense forests (11 km).", activities: ["Riverside Trek", "Lodge Stay"] },
      { day: 5, title: "Dwali to Phurkia", description: "Trail enters alpine zones with close peak views (5 km).", activities: ["Alpine Ascent", "Lodge Stay"] },
      { day: 6, title: "Zero Point & Return", description: "Explore the glacier snout and return to lower camps.", activities: ["Glacier Snout", "Zero Point"] },
      { day: 7, title: "Return to Kathgodam", description: "Trek back to Kharkiya and drive back to Kathgodam.", activities: ["Descent", "Return Trip"] }
    ],
    cost: {
      includes: ["Trek guide & support staff", "Forest & biosphere permits", "Guesthouse/lodge accommodation", "All meals during trek"],
      excludes: ["Travel to/from Kathgodam", "Personal trekking gear", "Porter/mule services", "Personal expenses"],
      notes: ["Entry permits for Nanda Devi Biosphere Reserve are mandatory."]
    },
    faqs: [
      { question: "Is Pindari Glacier suitable for beginners?", answer: "Yes, it is one of the most beginner-friendly glacier treks in India." },
      { question: "Is camping required?", answer: "No, most nights are spent in villages or lodges." }
    ],
    mapUrl: "https://maps.google.com/pindari-glacier-trek"
  },
  {
    id: 33,
    slug: "roopkund-trek",
    title: "Roopkund Trek",
    state: "Uttarakhand",
    shortDescription: "The mysterious 'Skeleton Lake' trek, a high-altitude adventure through huge alpine meadows to 16,500 ft.",
    price: 16500,
    duration: "8 Days",
    activityTags: ["trekking", "high-altitude", "adventure"],
    images: [
      { id: 1, src: "/images/packages/roopkund/roopkund-1.png", alt: "Roopkund skeletal lake" },
      { id: 2, src: "/images/packages/uk/uk-2.avif", alt: "Ali Bugyal meadows" }
    ],
    tripInfo: {
      accommodation: { text: "Guesthouses & Tents", icon: "fa-campground" },
      arrivalCity: { text: "Lohajung", icon: "fa-mountain" },
      bestSeason: { text: "Summer (May–June); Autumn (Sept–Oct)", icon: "fa-sun" },
      guide: { text: "Certified High Altitude Guide", icon: "fa-user-check" },
      maximumAltitude: { text: "16,500 ft", icon: "fa-mountain" },
      meals: { text: "Nutritious All Meals during trek", icon: "fa-utensils" },
      transportation: { text: "Drive from Rishikesh/Kathgodam", icon: "fa-car" },
      pickupPoint: { text: "Rishikesh/Kathgodam", icon: "fa-map-signs" }
    },
    overview: {
      title: "Roopkund Trek Overview",
      description: "Roopkund is a legendary high-altitude trek famous for the ancient human skeletons at the lake edges. It features the massive alpine meadows of Ali and Bedni Bugyal.",
      highlights: [
        { id: 1, highlight: "Mysterious Roopkund Lake (Skeleton Lake)" },
        { id: 2, highlight: "Expansive Ali Bugyal & Bedni Bugyal meadows" },
        { id: 3, highlight: "Views of Trishul and Nanda Ghunti" }
      ],
      activities: [
        { title: "Meadow Crossing", description: "Walk through Asia's largest alpine meadows.", emoji: "🌱" },
        { title: "Altitude Peak", description: "Reach a high point of 16,500 ft.", emoji: "🏔️" }
      ]
    },
    itinerary: [
      { day: 1, title: "Arrival at Lohajung", description: "9–10 hour drive to the base village at 7,600 ft.", activities: ["Drive", "Briefing"] },
      { day: 2, title: "Lohajung to Didna Village", description: "Descent and climb through forests to Didna (6 km).", activities: ["Forest Trek", "Homestay"] },
      { day: 3, title: "Didna to Ali Bugyal", description: "Steep climb to the massive Ali Bugyal meadows (7 km).", activities: ["Steep Climb", "Meadow Views"] },
      { day: 4, title: "Ali to Bedni Bugyal", description: "A ridge walk with panoramic mountain views (5 km).", activities: ["Ridge Walk", "Camping"] },
      { day: 5, title: "Bedni to Patar Nachauni", description: "Gradual ascent toward the high-altitude zone (5 km).", activities: ["Trek", "Camping"] },
      { day: 6, title: "Patar Nachauni to Bhagwabasa", description: "Climb to the high camp at Bhagwabasa (4 km).", activities: ["Altitude Gain", "High Camp"] },
      { day: 7, title: "Roopkund Lake & Return", description: "Summit push to the lake and return to lower camps.", activities: ["Summit Challenge", "Skeleton Lake"] },
      { day: 8, title: "Descend to Lohajung", description: "Final descent to back to the base village.", activities: ["Descent", "Return Trip"] }
    ],
    cost: {
      includes: ["Certified trek leaders & guides", "Forest permits & camping fees", "Acclimatization support", "All meals during trek", "Camping gear"],
      excludes: ["Travel to base", "Personal gear", "Backpack offloading", "Porter charges"],
      notes: ["Moderate to Difficult grade; high-altitude fitness required."]
    },
    faqs: [
      { question: "Is Roopkund suitable for beginners?", answer: "Not ideal for absolute beginners; prior high-altitude experience preferred." },
      { question: "Why is it called Skeleton Lake?", answer: "Ancient skeletons dating back to the 9th century were found here." }
    ],
    mapUrl: "https://maps.google.com/roopkund-trek"
  },
  {
    id: 34,
    slug: "beas-kund-trek",
    title: "Beas Kund Trek",
    state: "Himachal Pradesh",
    shortDescription: "A short yet rewarding high-altitude trek near Manali leading to the glacial lake source of the Beas River.",
    price: 6000,
    duration: "2-4 days",
    activityTags: ["trekking", "camping", "glacial-lake", "beginners"],
    images: [
      { id: 1, src: "/images/packages/hp/hp-5.avif", alt: "Beas Kund glacial lake" },
      { id: 2, src: "/images/packages/hp/hp-6.avif", alt: "Manali Himalayan views" }
    ],
    tripInfo: {
      accommodation: { text: "Camping Accommodation", icon: "fa-campground" },
      arrivalCity: { text: "Manali", icon: "fa-mountain" },
      bestSeason: { text: "Summer & Early Autumn (May–Oct)", icon: "fa-sun" },
      guide: { text: "Trek Guide & Support Staff", icon: "fa-user-check" },
      maximumAltitude: { text: "12,300 ft", icon: "fa-mountain" },
      meals: { text: "Nutritious Meals during the trek", icon: "fa-utensils" },
      transportation: { text: "Drive from Manali to Solang/Dhundi", icon: "fa-car" },
      pickupPoint: { text: "Manali", icon: "fa-map-marker-alt" }
    },
    overview: {
      title: "Beas Kund Trek Overview",
      description: "The Beas Kund Trek is a short yet rewarding high-altitude trek near Manali. It leads to Beas Kund, a glacial lake considered the source of the Beas River. Surrounded by towering Himalayan peaks, the trek offers dramatic views within a relatively short duration, making it ideal for beginners.",
      highlights: [
        { id: 1, highlight: "Origin point of the Beas River" },
        { id: 2, highlight: "Close views of Shitidhar, Friendship Peak & Hanuman Tibba" },
        { id: 3, highlight: "Alpine meadows and glacial streams" },
        { id: 4, highlight: "Ideal acclimatization trek for beginners" }
      ],
      activities: [
        { title: "Photography", description: "Capture dramatic mountain views.", emoji: "📸" },
        { title: "Camping", description: "Experience high-altitude camping.", emoji: "⛺" }
      ]
    },
    itinerary: [
      { day: 1, title: "Manali to Solang Valley/Dhundi → Campsite", description: "Drive 30-45 mins to Solang/Dhundi and trek 4-5 km through gentle meadows to the campsite.", activities: ["Scenic Drive", "Gentle Trek"] },
      { day: 2, title: "Campsite to Beas Kund & Return", description: "Trek 6-8 km (round trip) to reach the glacial lake at 12,300 ft. Explore the area and return to camp.", activities: ["Lake Exploration", "Photography"] },
      { day: 3, title: "Trek Back to Solang Valley → Manali", description: "Descend to the roadhead and drive back to Manali village.", activities: ["Descent", "Return Trip"] }
    ],
    cost: {
      includes: ["Trek guide & support staff", "Camping accommodation", "Meals during the trek", "Camping equipment (tents, sleeping bags)", "Basic first-aid & safety support"],
      excludes: ["Travel to/from Manali", "Personal trekking gear", "Porter services", "Travel insurance", "Personal expenses"],
      notes: ["Budget packages available starting from ₹4,500."]
    },
    faqs: [
      { question: "Is Beas Kund Trek good for beginners?", answer: "Yes, it is one of the best beginner-friendly treks in Himachal Pradesh." },
      { question: "How high is Beas Kund?", answer: "Approximately 12,300 ft, making it safe with proper pacing." },
      { question: "Is snow available on the trek?", answer: "Snow patches are common in early summer (May–June)." },
      { question: "Is camping mandatory?", answer: "Yes, overnight camping is required on most itineraries." }
    ],
    mapUrl: "https://maps.google.com/beas-kund"
  },

  {
    id: 35,
    slug: "bhrigu-lake-trek",
    title: "Bhrigu Lake Trek",
    state: "Himachal Pradesh",
    shortDescription: "A short high-altitude trek near Manali famous for vast alpine meadows and a sacred glacial lake.",
    price: 6500,
    duration: "2-4 days",
    activityTags: ["trekking", "camping", "alpine-lake", "beginners"],
    images: [
      { id: 1, src: "/images/packages/hp/hp-4.avif", alt: "Bhrigu Lake view" },
      { id: 2, src: "/images/packages/hp/hp-2.avif", alt: "Vibrant alpine meadows" }
    ],
    tripInfo: {
      accommodation: { text: "Camping Accommodation", icon: "fa-campground" },
      arrivalCity: { text: "Manali", icon: "fa-home" },
      bestSeason: { text: "Summer & Autumn (May–Oct)", icon: "fa-calendar-alt" },
      guide: { text: "Trek Guide & Support Staff", icon: "fa-walking" },
      maximumAltitude: { text: "14,100 ft", icon: "fa-mountain" },
      meals: { text: "Nutritious Meals during the trek", icon: "fa-utensils" },
      transportation: { text: "Drive from Manali to Gulaba", icon: "fa-bus-alt" },
      pickupPoint: { text: "Manali", icon: "fa-map-marker-alt" }
    },
    overview: {
      title: "Bhrigu Lake Trek Overview",
      description: "Bhrigu Lake is famous for its vast alpine meadows and a beautiful glacial lake at 14,100 ft. It is ideal for beginners looking for a true high-altitude experience in a short time, offering panoramic views of Hanuman Tibba and Friendship Peak.",
      highlights: [
        { id: 1, highlight: "Expansive grasslands and alpine meadows" },
        { id: 2, highlight: "Quick access to high altitude tree line" },
        { id: 3, highlight: "Scenic Bhrigu Lake surrounded by snow patches" },
        { id: 4, highlight: "Views of Hanuman Tibba & Seven Sisters" }
      ],
      activities: [
        { title: "Meadow Walk", description: "Traverse high altitude alpine meadows.", emoji: "🌱" },
        { title: "Lake Visit", description: "Visit the sacred Bhrigu Lake.", emoji: "🌊" }
      ]
    },
    itinerary: [
      { day: 1, title: "Manali to Gulaba → Campsite", description: "Drive 1 hour to Gulaba and trek 5-6 km through meadows to the campsite.", activities: ["Scenic Drive", "Meadow Trek"] },
      { day: 2, title: "Campsite to Bhrigu Lake & Return", description: "Trek to the lake at 14,100 ft and explore the sacred area before returning to camp.", activities: ["Peak views", "Glacial Lake Visit"] },
      { day: 3, title: "Trek Back to Gulaba → Manali", description: "Descend to Gulaba roadhead and drive back to Manali.", activities: ["Descent", "Return Trip"] }
    ],
    cost: {
      includes: ["Trek guide and support staff", "Camping accommodation", "Meals during the trek", "Camping equipment (tents, mats, bags)", "Basic first-aid & safety support"],
      excludes: ["Travel to/from Manali", "Personal trekking gear", "Porter/mule services", "Travel insurance", "Personal expenses"],
      notes: ["Acclimatization is important due to the quick altitude gain."]
    },
    faqs: [
      { question: "Is Bhrigu Lake Trek good for beginners?", answer: "Yes, it is beginner-friendly, but the rapid altitude gain requires proper pacing." },
      { question: "How high is Bhrigu Lake?", answer: "Approximately 14,100 ft, higher than many beginner treks." },
      { question: "Is snow present on the trek?", answer: "Snow patches are common near the lake until early summer." },
      { question: "Is mobile network available?", answer: "Limited network near Gulaba; none during the trek." }
    ],
    mapUrl: "https://maps.google.com/bhrigu-lake"
  },

  {
    id: 36,
    slug: "kheerganga-trek",
    title: "Kheerganga Trek",
    state: "Himachal Pradesh",
    shortDescription: "A popular short trek in Parvati Valley best known for its natural hot water spring and scenic forest trails.",
    price: 3500,
    duration: "1–2 days",
    activityTags: ["trekking", "hot-spring", "kasol", "beginners"],
    images: [
      { id: 1, src: "/images/packages/hp/hp-7.avif", alt: "Kheerganga natural hot spring" },
      { id: 2, src: "/images/packages/hp/hp-5.avif", alt: "Parvati Valley landscape" }
    ],
    tripInfo: {
      accommodation: { text: "Camps/Guesthouses", icon: "fa-campground" },
      arrivalCity: { text: "Barshaini", icon: "fa-map-marker-alt" },
      bestSeason: { text: "Spring to Autumn (Mar–Nov)", icon: "fa-sun" },
      guide: { text: "Local Trek Guide Available", icon: "fa-user-friends" },
      maximumAltitude: { text: "9,700 ft", icon: "fa-mountain" },
      meals: { text: "Dinner & Breakfast provided", icon: "fa-utensils" },
      transportation: { text: "Drive from Kasol to Barshaini", icon: "fa-car" },
      pickupPoint: { text: "Kasol/Barshaini", icon: "fa-map-pin" }
    },
    overview: {
      title: "Kheerganga Trek Overview",
      description: "The Kheerganga Trek in Parvati Valley is famous for its natural hot water spring at the summit and scenic forest trails. It combines adventure with leisure, making it ideal for beginners and casual travelers.",
      highlights: [
        { id: 1, highlight: "Natural hot water spring at the summit" },
        { id: 2, highlight: "Dense pine and deodar forests" },
        { id: 3, highlight: "Waterfalls and scenic river crossings" },
        { id: 4, highlight: "Ideal short trek from Kasol" }
      ],
      activities: [
        { title: "Hot Spring Soak", description: "Soak in the natural hot springs at the top.", emoji: "🛁" },
        { title: "Forest Trek", description: "Walk through lush Parvati Valley forests.", emoji: "🌲" }
      ]
    },
    itinerary: [
      { day: 1, title: "Barshaini to Kheerganga", description: "Trek 5-6 km through forests and waterfalls. Enjoy the natural hot spring in the evening.", activities: ["Forest Hike", "Hot Spring Bath"] },
      { day: 2, title: "Kheerganga to Barshaini", description: "Descend back via the same route and return to Kasol/Bhuntar.", activities: ["Descent", "Return Journey"] }
    ],
    cost: {
      includes: ["Trek guide (optional)", "Camping or basic accommodation", "Meals (Dinner & Breakfast)", "Basic safety support"],
      excludes: ["Travel to/from Barshaini", "Personal trekking gear", "Porter services", "Travel insurance", "Personal expenses"],
      notes: ["Fit trekkers can complete this in a single long day."]
    },
    faqs: [
      { question: "Is Kheerganga Trek good for beginners?", answer: "Yes, it is one of the easiest and most popular beginner treks in Himachal Pradesh." },
      { question: "Can Kheerganga be done in one day?", answer: "Yes, fit trekkers can complete it in one day, but an overnight stay is recommended." },
      { question: "Is the hot spring open year-round?", answer: "Yes, but access may be restricted during extreme weather." },
      { question: "Is mobile network available?", answer: "Limited connectivity near Barshaini; no network at the top." }
    ],
    mapUrl: "https://maps.google.com/kheerganga"
  },

  {
    id: 37,
    slug: "hampta-pass-trek",
    title: "Hampta Pass Trek",
    state: "Himachal Pradesh",
    shortDescription: "A spectacular crossover trek connecting the lush Kullu Valley with the stark high-altitude desert of Spiti.",
    price: 11000,
    duration: "4–6 days",
    activityTags: ["trekking", "crossover", "adventure", "manali"],
    images: [
      { id: 1, src: "/images/packages/hp/hp-8.avif", alt: "Hampta Pass crossover" },
      { id: 2, src: "/images/packages/hp/hp-3.avif", alt: "Arid desert of Spiti" }
    ],
    tripInfo: {
      accommodation: { text: "Tents during trek", icon: "fa-shield-alt" },
      arrivalCity: { text: "Manali", icon: "fa-city" },
      bestSeason: { text: "Summer to Early Autumn (June–Sept)", icon: "fa-calendar-check" },
      guide: { text: "Certified Trek Leader & Staff", icon: "fa-user-check" },
      maximumAltitude: { text: "14,100 ft", icon: "fa-mountain" },
      meals: { text: "All Meals during the trek", icon: "fa-utensils" },
      transportation: { text: "Drive from Manali to Jobra", icon: "fa-bus" },
      pickupPoint: { text: "Manali", icon: "fa-map-pin" }
    },
    overview: {
      title: "Hampta Pass Trek Overview",
      description: "The Hampta Pass Trek is a spectacular crossover trek connecting the lush Kullu Valley with the stark desert of Lahaul & Spiti. It offers forests, meadows, glacial valleys, and barren mountains all in a single trek.",
      highlights: [
        { id: 1, highlight: "Dramatic transition from green valleys to cold desert" },
        { id: 2, highlight: "Crossing the high altitude Hampta Pass (14,100 ft)" },
        { id: 3, highlight: "Views of Indrasan, Deo Tibba & Mt. Norbu" },
        { id: 4, highlight: "Optional visit to the turquoise Chandratal Lake" }
      ],
      activities: [
        { title: "Pass Crossing", description: "Experience cross-valley trekking.", emoji: "🗺️" },
        { title: "Glacial Crossing", description: "Walk over snow and ice near the pass.", emoji: "❄️" }
      ]
    },
    itinerary: [
      { day: 1, title: "Manali to Jobra → Chika", description: "Drive to Jobra and easy 4 km walk through forests to camp near the river.", activities: ["Forest Walk", "River Camping"] },
      { day: 2, title: "Chika to Balu Ka Ghera", description: "Trek 8 km through meadows and cross glacial streams to reach Balu Ka Ghera.", activities: ["Meadow Trek", "River Crossing"] },
      { day: 3, title: "Balu Ka Ghera → Hampta Pass → Shea Goru", description: "Steep 7-9 hr ascent to the pass and descent into the Lahaul region.", activities: ["Pass Crossing", "Landscape Shift"] },
      { day: 4, title: "Shea Goru to Chatru", description: "Walk 5 km through cold desert terrain to Chatru campsite.", activities: ["Desert Trek", "Spiti Views"] },
      { day: 5, title: "Chatru → Manali", description: "Drive back to Manali via Rohtang Pass (optional Chandratal visit included).", activities: ["Scenic Drive", "Return Trip"] }
    ],
    cost: {
      includes: ["Certified trek leader & support staff", "Forest permits & camping fees", "Tents & Sleeping bags", "All meals during trek", "Medical kit & oxygen"],
      excludes: ["Travel to/from Manali", "Personal gear", "Porter / offloading charges", "Travel insurance"],
      notes: ["Acclimatization is key due to the crossover altitude."]
    },
    faqs: [
      { question: "Is Hampta Pass suitable for beginners?", answer: "Yes, fit beginners can attempt it with proper preparation." },
      { question: "Does Hampta Pass have snow?", answer: "Yes, snow is common near the pass from June to early July." },
      { question: "Is Chandratal Lake included?", answer: "It is usually an optional extension subject to weather and road conditions." },
      { question: "Is river crossing difficult?", answer: "Most crossings are manageable with guidance and safety support." }
    ],
    mapUrl: "https://maps.google.com/hampta-pass"
  },

  {
    id: 38,
    slug: "patalsu-peak-trek",
    title: "Patalsu Peak Trek",
    state: "Himachal Pradesh",
    shortDescription: "A short summit trek near Manali offering a satisfying peak-climbing experience and panoramic Himalayan views.",
    price: 6000,
    duration: "2-3 days",
    activityTags: ["trekking", "summit-climb", "beginner-friendly", "manali"],
    images: [
      { id: 1, src: "/images/packages/uk/uk-1.avif", alt: "Patalsu Peak summit views" },
      { id: 2, src: "/images/packages/uk/uk-9.avif", alt: "Himalayan range from Patalsu" }
    ],
    tripInfo: {
      accommodation: { text: "Camping Accommodation", icon: "fa-campground" },
      arrivalCity: { text: "Manali", icon: "fa-mountain" },
      bestSeason: { text: "Summer to Autumn (May–Oct)", icon: "fa-sun" },
      guide: { text: "Trek Guide & Support Staff", icon: "fa-user-check" },
      maximumAltitude: { text: "13,800 ft", icon: "fa-mountain" },
      meals: { text: "All Meals during the trek", icon: "fa-utensils" },
      transportation: { text: "Drive from Manali to Solang Village", icon: "fa-car" },
      pickupPoint: { text: "Manali", icon: "fa-map-marker-alt" }
    },
    overview: {
      title: "Patalsu Peak Trek Overview",
      description: "Patalsu Peak, rising above the Solang Valley, is a short summit trek often used for training and acclimatization. It offers exceptional views of Hanuman Tibba, Friendship Peak, and the Beas River valley from its 13,800 ft summit.",
      highlights: [
        { id: 1, highlight: "True summit climb near Manali" },
        { id: 2, highlight: "Views of Hanuman Tibba, Friendship Peak & Deo Tibba" },
        { id: 3, highlight: "Alpine meadows and scenic ridgelines" },
        { id: 4, highlight: "Ideal first summit trek for beginners" }
      ],
      activities: [
        { title: "Summit Push", description: "Experience reaching a Himalayan peak.", emoji: "⛰️" },
        { title: "Acclimatization", description: "Great preparation for higher expeditions.", emoji: "🏔️" }
      ]
    },
    itinerary: [
      { day: 1, title: "Manali to Solang Village → Base Camp", description: "Short drive to Solang and 3-4 hour gradual ascent through forests to the base camp.", activities: ["Forest Trek", "Camping"] },
      { day: 2, title: "Base Camp to Patalsu Peak Summit & Return", description: "Early morning start for the summit push to 13,800 ft. Enjoy panoramic views and descend to base camp.", activities: ["Summit Achievement", "Ridge Walk"] },
      { day: 3, title: "Trek Back to Solang Village → Manali", description: "Descend to Solang and drive back to Manali village.", activities: ["Descent", "Return Trip"] }
    ],
    cost: {
      includes: ["Trek guide & support staff", "Camping accommodation", "Meals during the trek", "Basic camping equipment", "First-aid & safety support"],
      excludes: ["Travel to/from Manali", "Personal trekking gear", "Porter / mule services", "Travel insurance", "Personal expenses"],
      notes: ["Fit trekkers sometimes complete this in 2 days."]
    },
    faqs: [
      { question: "Is Patalsu Peak Trek good for beginners?", answer: "Yes, it is one of the best beginner-friendly summit treks near Manali." },
      { question: "Does Patalsu Peak have snow?", answer: "Snow is common near the summit in early summer months." },
      { question: "Is technical climbing required?", answer: "No, the trek does not require mountaineering skills or special equipment." },
      { question: "What is the maximum altitude?", answer: "Approximately 13,800 ft (4,200 m)." }
    ],
    mapUrl: "https://maps.google.com/patalsu-peak"
  },

  {
    id: 39,
    slug: "rani-sui-lake-trek",
    title: "Rani Sui Lake Trek",
    state: "Himachal Pradesh",
    shortDescription: "A scenic and offbeat alpine lake trek in the Tirthan region, perfect for nature lovers and peace seekers.",
    price: 8000,
    duration: "3–4 days",
    activityTags: ["trekking", "offbeat", "alpine-lake", "tirthan"],
    images: [
      { id: 1, src: "/images/packages/uk/uk-2.avif", alt: "Heart-shaped Rani Sui alpine lake" },
      { id: 2, src: "/images/packages/uk/uk-10.avif", alt: "Meadows of Banjar Valley" }
    ],
    tripInfo: {
      accommodation: { text: "Camping & Homestays", icon: "fa-home" },
      arrivalCity: { text: "Banjar/Manali", icon: "fa-map-marker-alt" },
      bestSeason: { text: "Summer & Autumn (May–Oct)", icon: "fa-sun" },
      guide: { text: "Trek Guide & Local Support", icon: "fa-user-check" },
      maximumAltitude: { text: "12,600 ft", icon: "fa-mountain" },
      meals: { text: "Nutritious Meals during the trek", icon: "fa-utensils" },
      transportation: { text: "Drive from Banjar to Base Village", icon: "fa-car" },
      pickupPoint: { text: "Banjar/Aut/Manali", icon: "fa-map-pin" }
    },
    overview: {
      title: "Rani Sui Lake Trek Overview",
      description: "Rani Sui Lake is a scenic and relatively lesser-known 'heart-shaped' lake in Himachal Pradesh. Located above the Banjar Valley, it offers peaceful trails away from crowded circuits, featuring expansive meadows and wide mountain views of the Pir Panjal and Dhauladhar ranges.",
      highlights: [
        { id: 1, highlight: "Serene Rani Sui alpine lake (Heart-Shaped Lake)" },
        { id: 2, highlight: "Quiet, uncrowded offbeat trails through Tirthan" },
        { id: 3, highlight: "Stunning views of Dhauladhar & Pir Panjal ranges" },
        { id: 4, highlight: "Cultural experience in remote Himalayan villages" }
      ],
      activities: [
        { title: "Nature Exploration", description: "Discover the hidden beauty of Banjar valley.", emoji: "🍃" },
        { title: "Photography", description: "Capture the perfect heart-shaped lake reflection.", emoji: "📸" }
      ]
    },
    itinerary: [
      { day: 1, title: "Banjar to Base Village (Sharchi/Lamadugh)", description: "Drive to the base village and stay in a traditional homestay or first campsite.", activities: ["Cultural Welcome", "Orientation"] },
      { day: 2, title: "Base Village to Forest Campsite", description: "Trek 6-7 km through dense oak and cedar forests and lush meadows.", activities: ["Forest Hike", "Alpine Camping"] },
      { day: 3, title: "Campsite to Rani Sui Lake & Return", description: "Reach the sacred lake at 12,600 ft. Explore the area and return to the lower camp.", activities: ["Lake Visit", "Scenic Views"] },
      { day: 4, title: "Return Trek & Drive Back", description: "Descend to the base village and drive back to Banjar/Manali.", activities: ["Descent", "Return Trip"] }
    ],
    cost: {
      includes: ["Trek guide & local support", "Camping accommodation & Homestays", "Meals during the trek", "Camping equipment (tents, mats, bags)", "Basic safety support"],
      excludes: ["Travel to/from Banjar", "Personal gear", "Porter/mule services", "Travel insurance", "Personal expenses"],
      notes: ["An ideal alternative to the crowded routes of Manali."]
    },
    faqs: [
      { question: "Is Rani Sui Lake suitable for beginners?", answer: "Yes, it is a moderate trek suitable for beginners with basic fitness." },
      { question: "Why is it called the heart-shaped lake?", answer: "The lake's natural shoreline forms a heart shape when viewed from the surrounding ridges." },
      { question: "Is the trek crowded?", answer: "No, it is one of the more offbeat and peaceful treks in the region." }
    ],
    mapUrl: "https://maps.google.com/rani-sui-lake"
  },

  {
    id: 40,
    slug: "sar-pass-trek",
    title: "Sar Pass Trek",
    state: "Himachal Pradesh",
    shortDescription: "A legendary trek in Parvati Valley famous for its snowfields, alpine meadows, and a massive 100ft long snow slide.",
    price: 11000,
    duration: "5-6 days",
    activityTags: ["trekking", "snow-trek", "adventure", "parvati-valley"],
    images: [
      { id: 1, src: "/images/packages/uk/uk-3.avif", alt: "Sar Pass snow trail" },
      { id: 2, src: "/images/packages/uk/uk-5.avif", alt: "Frozen lake at the pass" }
    ],
    tripInfo: {
      accommodation: { text: "Camping Accommodation", icon: "fa-campground" },
      arrivalCity: { text: "Kasol", icon: "fa-home" },
      bestSeason: { text: "Summer & Early Autumn (May–Oct)", icon: "fa-snowflake" },
      guide: { text: "Certified Trek Guide & Staff", icon: "fa-user-tie" },
      maximumAltitude: { text: "13,850 ft", icon: "fa-mountain" },
      meals: { text: "Nutritious Veg Meals during trek", icon: "fa-utensils" },
      transportation: { text: "Drive from Kasol to Barshaini (return)", icon: "fa-bus" },
      pickupPoint: { text: "Kasol", icon: "fa-map-pin" }
    },
    overview: {
      title: "Sar Pass Trek Overview",
      description: "Sar Pass is one of the most legendary treks in the Parvati Valley. Reaching an altitude of 13,850 ft, it offers the ultimate high-altitude adventure featuring dense forests, pristine snowfields, and the famous 'snow slide' experience.",
      highlights: [
        { id: 1, highlight: "Crossing the 13,850 ft Sar Pass" },
        { id: 2, highlight: "Thrilling 100ft long natural snow slide" },
        { id: 3, highlight: "Passing through the ancient village of Grahan" },
        { id: 4, highlight: "Multiple frozen high-altitude lakes (Sar)" }
      ],
      activities: [
        { title: "Snow Sliding", description: "Experience safe and fun slides on snow slopes.", emoji: "🛷" },
        { title: "Peak Gaze", description: "Panoramic views of Shivling and Indrasan.", emoji: "🏔️" }
      ]
    },
    itinerary: [
      { day: 1, title: "Kasol to Grahan Village", description: "Trek 7-9 km through dense pine forests alongside the Grahan Nallah to the ancient village.", activities: ["Forest Hike", "Cultural Interaction"] },
      { day: 2, title: "Grahan to Min Thach", description: "Ascend through forests of rhododendron and cedar to the alpine meadow of Min Thach.", activities: ["Forest Ascent", "Meadow Camping"] },
      { day: 3, title: "Min Thach to Nagaru", description: "Steep climb to Nagaru, the highest campsite, offering stunning sunset views over Parvati Valley.", activities: ["Acclimatization Hike", "High Altitude Camp"] },
      { day: 4, title: "Nagaru → Sar Pass → Biskeri Thach", description: "The summit day! Cross the 13,850 ft pass, enjoy the snow slide, and descend to lush Biskeri.", activities: ["Pass Crossing", "Snow Slide"] },
      { day: 5, title: "Biskeri Thach to Barshaini → Kasol", description: "Final descent through forests and village fields to Barshaini, then drive back to Kasol.", activities: ["Descent", "Return Trip"] }
    ],
    cost: {
      includes: ["Certified trek guide & support staff", "Camping accommodation (Tents/Sleeping bags)", "All veg meals during trek", "Forest permits & camping fees", "Safety support"],
      excludes: ["Travel to/from Kasol", "Personal gear", "Porter services", "Travel insurance", "Offloading charges"],
      notes: ["Moderate grade; involves significant snow trekking in early summer."]
    },
    faqs: [
      { question: "Is Sar Pass Trek suitable for beginners?", answer: "Yes, with basic fitness and mental preparation, beginners can complete this trek." },
      { question: "What is the maximum altitude?", answer: "Approximately 13,850 ft (4,223 m)." },
      { question: "Is snow present year-round?", answer: "Snow is heavy until June and patches remain through early summer." },
      { question: "What is the age limit?", answer: "Generally 10 years and above for this moderate grade trek." }
    ],
    mapUrl: "https://maps.google.com/sar-pass"
  },

  {
    id: 41,
    slug: "triund-trek",
    title: "Triund Trek",
    state: "Himachal Pradesh",
    shortDescription: "The crown jewel of Dharamshala, famous for its panoramic views of the Dhauladhar range and Kangra Valley.",
    price: 4500,
    duration: "1–2 days",
    activityTags: ["trekking", "camping", "beginners", "dharamshala"],
    images: [
      { id: 1, src: "/images/packages/uk/uk-4.avif", alt: "Triund ridge overlooking Bhagsu" },
      { id: 2, src: "/images/packages/uk/uk-6.avif", alt: "Sunset views of Dhauladhars" }
    ],
    tripInfo: {
      accommodation: { text: "High Altitude Camping", icon: "fa-campground" },
      arrivalCity: { text: "McLeod Ganj", icon: "fa-city" },
      bestSeason: { text: "Mar–June & Sept–Nov", icon: "fa-sun" },
      guide: { text: "Trek Guide & Support Staff", icon: "fa-walking" },
      maximumAltitude: { text: "9,500 ft", icon: "fa-mountain" },
      meals: { text: "Meals during the trek", icon: "fa-utensils" },
      transportation: { text: "Trek from Galu Devi Temple", icon: "fa-hiking" },
      pickupPoint: { text: "McLeod Ganj", icon: "fa-map-pin" }
    },
    overview: {
      title: "Triund Trek Overview",
      description: "Triund is the easiest and most popular trek in Dharamshala. Known as the 'crown jewel', it features a well-marked trail through lush forests of oak and rhododendron, leading to a ridge with panoramic peak views.",
      highlights: [
        { id: 1, highlight: "Panoramic views of snow-capped Dhauladhar range" },
        { id: 2, highlight: "Camping under a star-filled sky on the ridge" },
        { id: 3, highlight: "Breathtaking sunset and sunrise views" },
        { id: 4, highlight: "The famous '22 curves' final ascent" }
      ],
      activities: [
        { title: "Sunset View", description: "Witness the stunning 'Magic Hour' at Triund top.", emoji: "🌅" },
        { title: "Peak Gaze", description: "Get close-up views of the granite peaks.", emoji: "🏔️" }
      ]
    },
    itinerary: [
      { day: 1, title: "McLeod Ganj to Triund Top", description: "Start the trek from Galu Devi Temple and climb 9-10 km through deodar and oak forests to the Triund ridge.", activities: ["Forest Hike", "Sunset Camping"] },
      { day: 2, title: "Triund to McLeod Ganj", description: "Wake up to a spectacular sunrise. After breakfast, explore the area or Snowline and descend to Galu.", activities: ["Sunrise view", "Return Hike"] }
    ],
    cost: {
      includes: ["Trek guide & support staff", "Forest permits & camping fees", "Camping equipment (tents, mats, bags)", "Meals (Dinner & Breakfast)", "Safety support"],
      excludes: ["Travel to/from McLeod Ganj", "Personal trekking gear", "Porter services", "Travel insurance", "Lunch on Day 2"],
      notes: ["One of the best 'weekend' treks in India for beginners."]
    },
    faqs: [
      { question: "Is Triund Trek suitable for beginners?", answer: "Yes, it is one of the easiest Himalayan treks and perfect for first-timers." },
      { question: "Can it be done in one day?", answer: "Yes, fit trekkers can complete the round trip in 7-8 hours." },
      { question: "Is there mobile network?", answer: "Generally, Airtel and Jio networks work well on the Triund ridge." },
      { question: "What is the best time to visit?", answer: "March to June and September to November offer the best weather." }
    ],
    mapUrl: "https://maps.google.com/triund"
  },


];

export default packages;


